import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  inject,
  HostListener,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../config/editor-environment';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { Subscription } from 'rxjs';
import {
  Mesh,
  MeshStandardMaterial,
  MeshPhysicalMaterial,
  MeshBasicMaterial,
  Color,
  Group,
  LineSegments,
  Line,
  LineBasicMaterial,
  LineDashedMaterial,
  EdgesGeometry,
  BufferGeometry,
  BufferAttribute,
  Raycaster,
  Vector2,
  Vector3,
  Quaternion,
  Euler,
  Matrix3,
  Matrix4,
  Box3,
  Plane,
  Shape,
  Path,
  ShapeGeometry,
  PlaneGeometry,
  DoubleSide,
  BackSide,
  FrontSide,
  Float32BufferAttribute,
  SphereGeometry,
  Object3D,
  ExtrudeGeometry,
  ConeGeometry,
  CylinderGeometry,
  CanvasTexture,
  RepeatWrapping
} from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

import { CADKernelService } from '../../services/cad-kernel.service';
import { CADKernelWorkerService } from '../../services/cad-kernel-worker.service';
import { CADHistoryService } from '../../services/cad-history.service';
import { SnapEngineService } from '../../services/snap-engine.service';
import { SketchService } from '../../services/sketch.service';
import { AssistantFacePick, AssistantFaceSelectionService } from '../../services/assistant-face-selection.service';
import {
  CADHistoryOperation,
  CADShape,
  CADToolbarState,
  ExtrudeParams,
  BooleanParams,
  WASMLoadingState,
  SnapConfig,
  SnapResult,
  ExtrudeProfileData,
  Sketch,
  SketchEntity,
  SketchProfile,
  SketchRectangleEntity,
  SketchCircleEntity,
  SketchPolygonEntity,
  SketchSlotEntity,
  SketchArcEntity,
  SketchLineEntity
} from '../../types';
import { SketchBrowserComponent } from '../sketch-browser/sketch-browser.component';
import { Engine } from '../../../../engine/engine';
import { GuideShape, CADPartData } from '../../../../models/interfaces';
import { TooltipDirective } from '../../../ui/tooltip';
import { SessionService } from '../../../../services/session.service';
import { ToastService } from '../../../../services/toast/toast.service';
import { EmbeddedBridgeService } from '../../../../services/embedded/embedded-bridge.service';
import { TemplateModalService } from '../../../../services/modal/modal.service';
import { CADConfirmModalComponent } from '../cad-confirm-modal/cad-confirm-modal.component';
import { WebSocketService, WebSocketMessage } from '../../../../services/websocket/websocket.service';
import { SpinnerComponent } from '../../../ui/spinner/spinner.component';
import { ButtonComponent, FormControlComponent, InputNumberDirective, ProgressBarComponent, UnitConversionService, UnitPipe, UnitLabelPipe } from '@infinitform/shared-ui';
import { InputComponent } from '../../../ui/forms/input/input.component';
import { SelectComponent } from '../../../ui/forms/select/select.component';

/**
 * CAD Toolbar Component
 *
 * Floating toolbar providing CAD operations.
 * Can be placed in any component that needs CAD functionality.
 *
 * Usage:
 * ```html
 * <cad-toolbar
 *   [engine]="engine"
 *   [selectedShapes]="selectedGuideShapes"
 *   (onExtrude)="handleExtrude($event)"
 *   (onBoolean)="handleBoolean($event)"
 *   (onSave)="handleSave()">
 * </cad-toolbar>
 * ```
 */
@Component({
    selector: 'cad-toolbar',
    imports: [
        CommonModule,
        FormsModule,
        InputNumberDirective,
        TooltipDirective,
        SpinnerComponent,
        ButtonComponent,
        InputComponent,
        SelectComponent,
        FormControlComponent,
        SketchBrowserComponent,
        ProgressBarComponent,
        UnitPipe,
        UnitLabelPipe
    ],
    templateUrl: './cad-toolbar.component.html',
    styleUrls: ['./cad-toolbar.component.scss'],
    animations: [
        trigger('slideIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateX(-10px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
            ]),
            transition(':leave', [animate('150ms ease-in', style({ opacity: 0, transform: 'translateX(-10px)' }))])
        ])
    ]
})
export class CADToolbarComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private http = inject(HttpClient);
  public cadKernel = inject(CADKernelService);
  private cadWorker = inject(CADKernelWorkerService);
  private historyService = inject(CADHistoryService);
  public snapEngine = inject(SnapEngineService);
  public sketchService = inject(SketchService);
  private session = inject(SessionService);
  private wsService = inject(WebSocketService);
  private toastService = inject(ToastService);
  private modalService = inject(TemplateModalService);
  private unitConversionService = inject(UnitConversionService);
  unitSystem = this.unitConversionService.unitSystem;
  private readonly embeddedBridge = inject(EmbeddedBridgeService);

  // Inputs
  @Input() engine: Engine | null = null;
  @Input() selectedShapes: GuideShape[] = [];
  @Input() isEditMode = false;
  @Input() position: 'top' | 'bottom' | 'floating' = 'floating';

  // Outputs
  @Output() onExtrude = new EventEmitter<{ shape: GuideShape; cadShape: CADShape }>();
  @Output() onBoolean = new EventEmitter<{ operation: string; result: CADShape }>();
  @Output() onSave = new EventEmitter<CADShape[]>();
  @Output() onEditModeChange = new EventEmitter<boolean>();
  @Output() onError = new EventEmitter<string>();
  @Output() onExit = new EventEmitter<void>();

  // ═══════════════════════════════════════════════════════════════════════════
  // SKETCH MODE STATE
  // ═══════════════════════════════════════════════════════════════════════════

  isSketchMode = false; // True when editing a sketch
  activeSketch: Sketch | null = null; // Currently active sketch
  showSketchBrowser = true; // Show/hide sketch browser panel
  selectedProfileIds: Set<string> = new Set(); // Selected profiles for extrusion
  sketchEntityMeshes: Map<string, any> = new Map(); // Three.js meshes for entities
  sketchProfileMeshes: Map<string, any> = new Map(); // Three.js meshes for profiles

  // State
  loadingState: WASMLoadingState = 'idle';
  private _isProcessing = false;
  processingMessage = '';
  processingProgress = 0; // 0-100 progress percentage
  cadShapes: CADShape[] = [];
  private isHistoryReplay = false;

  // Getter/setter for isProcessing to auto-reset progress
  get isProcessing(): boolean {
    return this._isProcessing;
  }
  set isProcessing(value: boolean) {
    this._isProcessing = value;
    if (value) {
      this.processingProgress = 0; // Reset progress when starting a new operation
    }
  }
  selectedCADShapeIds: Set<string> = new Set();

  // Track shapes with pending (uncommitted) transforms for deferred OCCT sync
  private pendingTransforms: Set<string> = new Set();
  private transformCommitInFlight: Promise<void> | null = null;
  private transformStartScale: Vector3 | null = null;
  private transformStartObjectUuid: string | null = null;

  // Boolean operation ordering (first = body, rest = tools)
  booleanSelectionOrder: string[] = [];

  // Snap settings
  snapEnabled = true;
  snapToEdges = true;
  snapConfig: SnapConfig;
  constraintMode: 'none' | 'horizontal' | 'vertical' | 'parallel' | 'perpendicular' = 'none';
  private snapDataSignature = '';

  // Extrusion settings
  extrudeHeight = 10;
  extrudeDirection: [number, number, number] = [0, 0, 1];
  extrudePositive = true; // true = positive direction, false = negative
  isExtrusionDragMode = false; // When true, user can drag on canvas to set height
  private extrusionDragStartY = 0;
  private extrusionDragPreview: Mesh | null = null;

  // Primitive settings
  primitiveType: 'box' | 'cylinder' | 'sphere' | 'ring' = 'box';
  boxWidth = 20;
  boxHeight = 20;
  boxDepth = 20;
  cylinderRadius = 10;
  cylinderHeight = 30;
  sphereRadius = 15;
  ringOuterRadius = 15;
  ringInnerRadius = 10;
  ringHeight = 20;

  // Fillet/Chamfer settings
  edgeOperation: 'fillet' | 'chamfer' = 'fillet';
  filletRadius = 2;
  chamferDistance = 2;

  // Measurement display
  showMeasurements = true;
  measurementInfo: {
    boundingBox?: { size: [number, number, number]; center: [number, number, number] };
    volume?: number;
    surfaceArea?: number;
    distanceBetween?: number;
  } = {};

  // Sketch Plane
  sketchPlane: 'XY' | 'XZ' | 'YZ' | 'custom' | null = null;
  sketchPlaneOffset = 0;
  private sketchPlaneHelper: any = null;
  private sketchGridHelper: any = null;

  // Face-based plane picking
  isFacePickingMode = false;
  selectedFaceInfo: {
    position: [number, number, number];
    normal: [number, number, number];
    sourceType: 'cad' | 'assembly';
    sourceId?: string;
    faceIndex?: number;
  } | null = null;

  // Faces picked for the assistant ("make this hole bigger"): kept in a shared service the
  // chat sends with the next message; highlighted here until they are sent or cleared.
  readonly assistantFaces = inject(AssistantFaceSelectionService);
  // Web only: the desktop assistant has no editable-result tools and no CAD-mode chat.
  readonly assistantFacePickAvailable = !environment.IS_DESKTOP;
  isAssistantFacePickMode = false;
  private assistantPickHighlightMeshes: Mesh[] = [];
  private boundOnAssistantPickClick: ((e: MouseEvent) => void) | null = null;
  private boundOnAssistantPickMove: ((e: MouseEvent) => void) | null = null;
  private readonly assistantPickHighlightEffect = effect(() => {
    this.updateAssistantPickHighlights(this.assistantFaces.picks());
  });

  // Edge selection mode
  isEdgeSelectMode = false;
  selectedEdges: { shapeId: string; edgeIndex: number }[] = [];
  private edgeHighlightLines: any[] = [];
  private boundOnEdgePickMove: ((e: MouseEvent) => void) | null = null;
  private boundOnEdgePickClick: ((e: MouseEvent) => void) | null = null;

  // Shell face selection mode
  isShellFaceSelectMode = false;
  selectedShellFaceIndex: number | null = null;
  private shellFaceHighlightMesh: any = null;
  private shellFaceHighlightWire: any = null;  // Wire/edge highlight for shell face
  private boundOnShellFaceMove: ((e: MouseEvent) => void) | null = null;
  private boundOnShellFaceClick: ((e: MouseEvent) => void) | null = null;
  private customPlaneOrigin: [number, number, number] = [0, 0, 0];
  private customPlaneNormal: [number, number, number] = [0, 0, 1];
  private customPlaneXAxis: [number, number, number] = [1, 0, 0];
  private customPlaneYAxis: [number, number, number] = [0, 1, 0];
  private boundOnFacePickClick: ((e: MouseEvent) => void) | null = null;
  private boundOnFacePickMove: ((e: MouseEvent) => void) | null = null;
  private faceHighlightMesh: any = null;
  private faceHighlightWire: any = null;  // Wire/edge highlight for face

  // Active Operation Mode (CAD workflow)
  activeOperation: 'fillet' | 'chamfer' | 'shell' | 'offset' | 'push-pull' | 'boss' | 'pocket' | 'extract-face' | 'thicken' | null = null;
  private hiddenShapesForOperation: Set<string> = new Set();

  // 2D Drawing / Sketch Mode
  drawingMode: 'none' | 'rectangle' | 'circle' | 'line' | 'arc' | 'polygon' | 'slot' = 'none';
  isDrawing = false;
  drawingStartPoint: { x: number; y: number; z: number } | null = null;
  drawingCurrentPoint: { x: number; y: number; z: number } | null = null;
  linePoints: { x: number; y: number; z: number }[] = [];
  currentSnapResult: SnapResult | null = null;
  private snapIndicator: Mesh | null = null;
  private drawingPreviewMesh: any = null;
  private cursorIndicator: Mesh | null = null;
  private pointMarkers: Mesh[] = []; // Markers for clicked points (arc, slot)
  private boundOnDrawMouseDown: ((e: MouseEvent) => void) | null = null;
  private boundOnDrawMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundOnDrawMouseUp: ((e: MouseEvent) => void) | null = null;

  // Arc drawing state (3-point arc)
  arcDrawingState: {
    startPoint: { x: number; y: number; z: number } | null;
    midPoint: { x: number; y: number; z: number } | null;
    endPoint: { x: number; y: number; z: number } | null;
    step: 'start' | 'mid' | 'end';
  } = { startPoint: null, midPoint: null, endPoint: null, step: 'start' };

  // Polygon drawing state
  polygonSides = 6;
  polygonInscribed = true; // inscribed in circle vs circumscribed

  // Slot drawing state
  slotWidth = 10;
  slotDrawingState: {
    startPoint: { x: number; y: number; z: number } | null;
    endPoint: { x: number; y: number; z: number } | null;
    step: 'start' | 'end';
  } = { startPoint: null, endPoint: null, step: 'start' };

  // Dimension input while drawing
  showDimensionInput = false;
  dimensionInputValue = '';
  dimensionInputType: 'width' | 'height' | 'radius' | 'length' | 'sides' = 'width';
  dimensionInputWidth: number | null = null;
  dimensionInputHeight: number | null = null;
  dimensionInputDiameter: number | null = null;
  dimensionInputPosition = { x: 0, y: 0 }; // Fixed position when opened

  // Constraint visualization
  private constraintGuideLine: LineSegments | null = null;
  activeConstraintType: string | null = null;
  showKeyboardShortcuts = false;

  // Keyboard shortcuts list for the panel
  keyboardShortcuts = [
    { key: 'G', description: 'Move (Translate)' },
    { key: 'R', description: 'Rotate' },
    { key: 'S', description: 'Scale' },
    { key: 'Ctrl+A', description: 'Select All' },
    { key: 'Escape', description: 'Clear Selection / Cancel' },
    { key: 'Delete', description: 'Delete Selected' },
    { key: 'Ctrl+C', description: 'Copy' },
    { key: 'Ctrl+V', description: 'Paste' },
    { key: 'Ctrl+D', description: 'Duplicate' },
    { key: 'Ctrl+S', description: 'Save' },
    { key: 'Enter', description: 'Confirm Edit' },
    { key: 'H', description: 'Horizontal Constraint (while drawing)' },
    { key: 'V', description: 'Vertical Constraint (while drawing)' },
    { key: 'P', description: 'Parallel Constraint (while drawing)' },
    { key: 'N', description: 'Perpendicular Constraint (while drawing)' }
  ];

  // Shell/Offset settings
  shellThickness = 2;
  offsetDistance = 5;

  // Surface operations settings
  thickenAmount = 5;
  isExtractFacePickingMode = false;
  selectedExtractFaceIndices: number[] = [];  // Multi-face selection for extract

  // Thicken direction arrow visualization
  private thickenDirectionArrow: Group | null = null;
  private thickenSurfaceNormal: Vector3 | null = null;
  private thickenSurfaceCenter: Vector3 | null = null;
  private thickenArrowMaxLength: number | null = null; // Max arrow length (e.g., cylinder radius)

  // Push/Pull settings
  pushPullDistance = 10;
  isPushPullMode = false;
  selectedPushPullFaceIndex: number | null = null;

  // Push/Pull drag mode (for live preview - same style as extrusion drag)
  isPushPullDragMode = false;
  private pushPullDragInitialDistance = 0;
  private pushPullFaceNormal: Vector3 | null = null;
  private pushPullFaceCenter: Vector3 | null = null;
  private pushPullExtractedFace: { vertices: number[]; indices: number[]; center: [number, number, number] } | null = null;
  private pushPullBodyPreview: Mesh | null = null;
  // CSS2D arrow elements (same style as extrusion)
  private pushPullArrowCSS2D: CSS2DObject | null = null;
  private pushPullArrowElement: HTMLElement | null = null;
  private pushPullArrow3D: Group | null = null;
  private pushPullAxisLine: Line | null = null;
  private isPushPullArrowDragging = false;

  // Extrude on Face (Boss/Pocket)
  isExtrudeOnFaceMode = false;
  extrudeOnFaceOperation: 'boss' | 'pocket' = 'boss';
  extrudeOnFaceHeight = 10;
  extrudeOnFaceProfile: number[][] = [];
  // Circle boss/pocket data (for true CAD cylinder instead of segmented polygon)
  extrudeOnFaceCircle: { centerX: number; centerY: number; radius: number } | null = null;

  // Boss/Pocket tool picker
  showBossPocketToolPicker = false;
  bossPocketToolPickerPosition = { x: 0, y: 0 };

  // Boss/Pocket preview mode (after drawing, before applying)
  isBossPocketPreviewMode = false;
  private bossPocketArrowCSS2D: CSS2DObject | null = null;
  private bossPocketArrowElement: HTMLElement | null = null;
  private bossPocketArrow3D: Group | null = null;
  private bossPocketAxisLine: Line | null = null;
  private bossPocketBodyPreview: Mesh | null = null;
  private isBossPocketArrowDragging = false;
  private bossPocketDragInitialHeight = 0;
  private bossPocketDragBaseWorldPos: Vector3 | null = null; // Base position (profile center in world coords)
  private bossPocketDragNormal: Vector3 | null = null; // Extrusion direction normal
  private bossPocketDragMoveHandler: ((e: MouseEvent) => void) | null = null;
  private bossPocketDragUpHandler: ((e: MouseEvent) => void) | null = null;

  // Pattern operation settings
  linearArrayDirection: 'X' | 'Y' | 'Z' = 'X';
  linearArrayCount = 3;
  linearArraySpacing = 20;
  circularArrayAxis: 'X' | 'Y' | 'Z' = 'Z';
  circularArrayCount = 4;
  circularArrayAngle = 360;

  // Axis options for select dropdowns
  axisOptions = [
    { value: 'X', label: 'X Axis' },
    { value: 'Y', label: 'Y Axis' },
    { value: 'Z', label: 'Z Axis' }
  ];
  mirrorPlane: 'XY' | 'XZ' | 'YZ' = 'YZ';

  // UI state
  shapesListExpanded = false;
  activePanel: 'create' | 'operations' | 'add-body' | 'history' | null = null;
  activeOperationsTab: 'edges' | 'patterns' = 'edges';
  expandedPatternGroup: string | null = null;

  // Shape Renaming
  renamingShapeId: string | null = null;
  renamingShapeName = '';

  // Context Menu
  showContextMenu = false;
  contextMenuPosition = { x: 0, y: 0 };
  contextMenuTarget: string | null = null;

  // Drawing Dimensions Display
  drawingDimensions: { width: number; height: number; radius?: number } | null = null;
  drawingDimensionsPosition = { x: 0, y: 0 };

  // Snap Feedback Label
  snapFeedbackLabel: string | null = null;
  snapFeedbackColor: string = '#ffffff';
  snapFeedbackPosition = { x: 0, y: 0 };

  // Isolation Mode
  isIsolateMode = false;
  isolatedShapeId: string | null = null;

  // Section View / Clipping Planes
  sectionViewEnabled = false;
  sectionPlaneAxis: 'x' | 'y' | 'z' = 'y';
  sectionPlanePosition = 0;
  sectionPlaneMin = -100;
  sectionPlaneMax = 100;
  private sectionPlane: Plane | null = null;
  private sectionPlaneHelper: Mesh | null = null;
  private sectionStencilGroup: Group | null = null;
  private sectionHatchTexture: CanvasTexture | null = null;

  // Construction Mode - new entities will be construction geometry
  isConstructionMode = false;

  // Entity Editing Mode
  isEntityEditMode = false;
  editingEntityId: string | null = null;
  private entityHandles: CSS2DObject[] = [];
  private entityHandleElements: HTMLElement[] = []; // Store DOM elements for cleanup
  private draggingHandle: { type: 'vertex' | 'center' | 'radius'; index: number } | null = null;
  private dragStartPoint: { x: number; y: number } | null = null;
  private originalEntityData: any = null;
  private boundOnEntityEditMouseDown: ((e: MouseEvent) => void) | null = null;
  private boundOnEntityEditMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundOnEntityEditMouseUp: ((e: MouseEvent) => void) | null = null;

  // STEP Import
  private stepFileInput: HTMLInputElement | null = null;

  // CAD Parts Import (JSON)
  private cadPartsFileInput: HTMLInputElement | null = null;

  // Import from Models List
  availableModelParts: { id: string; name: string; color?: string }[] = [];
  selectedModelPartId: string = '';
  isLoadingModelParts = false;

  // Persistence state
  private unsavedChanges = false;

  /** Unsaved CAD edits; also reported to the cabinet when this editor is embedded. */
  get hasUnsavedChanges(): boolean {
    return this.unsavedChanges;
  }

  set hasUnsavedChanges(value: boolean) {
    this.unsavedChanges = value;
    this.embeddedBridge.setDirty('cad', value);
  }

  isSaving = false;
  saveProgress = 0; // 0-100 percentage
  lastSaveTime: Date | null = null;
  private beforeUnloadHandler: ((event: BeforeUnloadEvent) => string | void) | null = null;

  // Backend sync state
  syncToBackend = true; // Whether to also upload to S3/Lambda
  pendingUploads: Map<string, 'pending' | 'uploading' | 'complete' | 'error'> = new Map();
  private uploadResolvers: Map<string, { resolve: () => void; reject: (error: string) => void }> = new Map();

  // Transform Gizmo
  transformMode: 'translate' | 'rotate' | 'scale' = 'translate';
  private transformControls: TransformControls | null = null;
  private isTransforming = false;
  private recentlyTransformed = false; // Flag to prevent deselection after gizmo use
  private multiSelectPivot: Group | null = null; // Temporary pivot for multi-selection transforms
  private multiSelectOriginalTransforms: Map<string, { position: Vector3; rotation: Euler; scale: Vector3 }> = new Map();
  private transformValueDisplay: CSS2DObject | null = null; // Live transform value display
  private transformValueElement: HTMLDivElement | null = null;

  // Clipboard for copy/paste
  clipboard: CADShape[] = [];

  // Scene mesh management
  private sceneMeshes: Map<string, Group> = new Map();
  private cadGroup: Group = new Group();
  private cachedCADMeshes: Mesh[] | null = null;
  private sharedEdgeMaterial: LineMaterial | null = null;
  private snapUpdateTimer: ReturnType<typeof setTimeout> | null = null;

  // Interaction
  private raycaster = new Raycaster();
  private mouse = new Vector2();
  private boundOnClick: ((e: MouseEvent) => void) | null = null;
  private boundOnMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundOnDblClick: ((e: MouseEvent) => void) | null = null;
  private boundOnMouseLeave: ((e: MouseEvent) => void) | null = null;
  private hoveredShapeId: string | null = null;

  // Raycasting throttle for performance
  private rafPending = false;
  private lastMouseEvent: MouseEvent | null = null;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Positioning
  toolbarLeft = 16; // Default left position (public for template binding)
  toolbarHeight = 0; // Default height (public for template binding)
  private resizeObserver: ResizeObserver | null = null;
  private resizeEventListener: ((event: Event) => void) | null = null;

  // Confirm dialog state (prevents stacking dialogs)
  private isConfirmDialogOpen = false;

  constructor() {
    this.snapConfig = this.snapEngine.getConfig();
  }

  /**
   * Check if any modal/dialog is currently open
   * Looks for CDK overlay backdrops or modal elements in the DOM
   */
  private isModalOpen(): boolean {
    // Check our internal flag first
    if (this.isConfirmDialogOpen) return true;

    // Check for CDK overlay backdrop (Angular Material/CDK modals)
    const cdkBackdrop = document.querySelector('.cdk-overlay-backdrop');
    if (cdkBackdrop) return true;

    // Check for app-modal elements
    const appModal = document.querySelector('app-modal');
    if (appModal) return true;

    // Check for any modal container
    const modalContainer = document.querySelector('.cdk-overlay-container .cdk-overlay-pane');
    if (modalContainer && modalContainer.children.length > 0) return true;

    return false;
  }

  /**
   * Check if an element is an input element or inside a form control
   * Used to prevent keyboard shortcuts from interfering with typing
   */
  private isInputElement(element: HTMLElement): boolean {
    // Also check the currently focused element (activeElement)
    // This catches cases where focus is inside shadow DOM
    const activeElement = document.activeElement;
    if (activeElement) {
      if (activeElement instanceof HTMLInputElement) return true;
      if (activeElement instanceof HTMLTextAreaElement) return true;
      if (activeElement instanceof HTMLSelectElement) return true;
      if ((activeElement as HTMLElement).isContentEditable) return true;

      // Check if active element is inside an input-like component
      const activeTagName = activeElement.tagName?.toLowerCase() || '';
      if (activeTagName === 'input' || activeTagName === 'textarea' || activeTagName === 'select') {
        return true;
      }
    }

    // Direct input elements
    if (element instanceof HTMLInputElement) return true;
    if (element instanceof HTMLTextAreaElement) return true;
    if (element instanceof HTMLSelectElement) return true;

    // Contenteditable elements
    if (element.isContentEditable) return true;

    // Check if element or any parent has contenteditable or is a form control
    let parent: HTMLElement | null = element;
    while (parent) {
      if (parent.isContentEditable) return true;

      // Check for custom form controls (app-input, app-select, etc.)
      const tagName = parent.tagName?.toLowerCase() || '';
      if (
        tagName === 'app-input' ||
        tagName === 'app-form-control' ||
        tagName === 'app-select' ||
        tagName === 'app-textarea' ||
        tagName === 'app-modal' ||
        tagName === 'app-cad-confirm-modal' ||
        tagName === 'input'
      ) {
        return true;
      }

      // Check for input/select inside shadow DOM
      if (parent.shadowRoot?.querySelector('input, textarea, select')) {
        return true;
      }

      parent = parent.parentElement;
    }

    return false;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTIVE OPERATION MODE (CAD WORKFLOW)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Enter an operation mode - hides other shapes and expands relevant panel
   * @param operation The operation type to enter
   */
  enterOperationMode(operation: 'fillet' | 'chamfer' | 'shell' | 'offset' | 'push-pull' | 'boss' | 'pocket' | 'extract-face' | 'thicken'): void {
    // Exit any existing operation first
    if (this.activeOperation) {
      this.exitOperationMode(true);
    }

    this.activeOperation = operation;

    // Hide other shapes to focus on the selected one
    this.hideOtherShapes();

    // Expand the relevant panel group
    switch (operation) {
      case 'fillet':
      case 'chamfer':
        this.expandedPatternGroup = 'fillet-chamfer';
        break;
      case 'shell':
        this.expandedPatternGroup = 'shell';
        break;
      case 'offset':
        this.expandedPatternGroup = 'offset';
        break;
      case 'push-pull':
        this.expandedPatternGroup = 'push-pull';
        break;
      case 'boss':
      case 'pocket':
        this.expandedPatternGroup = 'boss-pocket';
        break;
      case 'extract-face':
      case 'thicken':
        this.expandedPatternGroup = 'surface';
        break;
    }

    this.cdr.detectChanges();
  }

  /**
   * Exit operation mode - restores visibility and clears state
   * @param cancelled Whether the operation was cancelled (vs applied)
   */
  exitOperationMode(cancelled: boolean): void {
    if (!this.activeOperation) return;

    const operation = this.activeOperation;

    // Clean up operation-specific state
    switch (operation) {
      case 'fillet':
      case 'chamfer':
        // Always clean up edge selection mode if it's active
        if (this.isEdgeSelectMode) {
          if (cancelled) {
            this.cancelEdgeSelectMode();
          } else {
            this.stopEdgeSelectMode();
          }
        }
        // Also clean up handlers even if flag is false (defensive)
        this.cleanupEdgePickingHandlers();
        // Always clear edge state
        this.selectedEdges = [];
        this.clearEdgeHighlights();
        this.isEdgeSelectMode = false;
        break;
      case 'shell':
        // Always clean up shell face selection mode if active
        if (this.isShellFaceSelectMode) {
          this.stopShellFaceSelectMode(false); // Clear selection
        }
        // Also clean up handlers even if flag is false (defensive)
        this.cleanupShellFaceHandlers();
        // Always clear shell face state
        this.selectedShellFaceIndex = null;
        this.clearShellFaceHighlight();
        this.isShellFaceSelectMode = false;
        break;
      case 'offset':
        // Offset has no selection mode, nothing to clean up
        break;
      case 'push-pull':
        // Clean up push-pull mode - check multiple flags to ensure cleanup even if state is out of sync
        if (this.isPushPullMode || this.isPushPullDragMode || this.pushPullDragMoveHandler) {
          this.stopPushPullMode(cancelled);
        }
        break;
      case 'boss':
      case 'pocket':
        // Clean up boss/pocket mode - check multiple flags to ensure cleanup even if state is out of sync
        if (this.isExtrudeOnFaceMode || this.isBossPocketPreviewMode || this.bossPocketDragMoveHandler) {
          this.cancelExtrudeOnFaceMode();
        }
        break;
      case 'extract-face':
        this.isExtractFacePickingMode = false;
        this.stopFacePickingMode(true);
        this.clearSelectedExtractFace();
        break;
      case 'thicken':
        // Thicken has no per-operation selection state to clean up
        break;
    }

    // Restore hidden shapes
    this.restoreHiddenShapes();

    // Clear operation state
    this.activeOperation = null;

    // Always collapse the expanded panel when exiting operation mode
    // (both on success and cancellation)
    this.expandedPatternGroup = null;

    // Re-attach transform controls if shape is selected
    if (this.selectedCADShapeIds.size === 1) {
      this.attachTransformControls();
    }

    this.cdr.detectChanges();
  }

  /**
   * Apply the current active operation
   */
  async applyActiveOperation(): Promise<void> {
    if (!this.activeOperation) return;

    const operation = this.activeOperation;
    let shouldExit = true;

    try {
      switch (operation) {
        case 'fillet':
        case 'chamfer':
          // Use edgeOperation to determine which operation to run,
          // since user can toggle between fillet/chamfer after entering edge select mode
          if (this.edgeOperation === 'chamfer') {
            await this.performChamfer();
          } else {
            await this.performFillet();
          }
          break;
        case 'shell':
          await this.performShell();
          break;
        case 'offset':
          await this.performOffset();
          break;
        case 'push-pull':
          if (!this.canApplyPushPull()) {
            shouldExit = false;
            return;
          }
          await this.applyPushPull();
          break;
        case 'boss':
        case 'pocket':
          // If in preview mode, apply the boss/pocket
          if (!this.isBossPocketPreviewMode) {
            shouldExit = false;
            return;
          }
          shouldExit = false;
          await this.applyBossPocket();
          return; // applyBossPocket handles its own cleanup
        case 'extract-face':
          if (!this.canExtractFace()) {
            shouldExit = false;
            return;
          }
          shouldExit = false;
          await this.extractSelectedFace();
          return; // extractSelectedFace handles its own cleanup
        case 'thicken':
          if (!this.canThickenSurface() || this.thickenAmount === 0) {
            shouldExit = false;
            return;
          }
          await this.thickenSelectedSurface();
          break;
      }
    } finally {
      // Exit operation mode after applying
      if (shouldExit) {
        this.exitOperationMode(false);
      }
    }
  }

  /**
   * Hide all shapes except the selected one during an operation
   */
  private hideOtherShapes(): void {
    if (this.selectedCADShapeIds.size !== 1) return;

    const selectedId = Array.from(this.selectedCADShapeIds)[0];

    this.sceneMeshes.forEach((group, shapeId) => {
      if (shapeId !== selectedId) {
        // Check if currently visible
        if (group.visible) {
          this.hiddenShapesForOperation.add(shapeId);
          group.visible = false;
        }
      }
    });
  }

  /**
   * Restore shapes that were hidden for the current operation
   */
  private restoreHiddenShapes(): void {
    for (const shapeId of this.hiddenShapesForOperation) {
      const group = this.sceneMeshes.get(shapeId);
      if (group) {
        group.visible = true;
      }
    }
    this.hiddenShapesForOperation.clear();
  }

  /**
   * Get status message for current active operation
   */
  getActiveOperationStatus(): string {
    if (!this.activeOperation) return '';

    switch (this.activeOperation) {
      case 'fillet':
        return `Fillet Mode - Select edges (click to toggle) | Enter: Apply | Esc: Cancel | Edges: ${this.selectedEdges.length}`;
      case 'chamfer':
        return `Chamfer Mode - Select edges (click to toggle) | Enter: Apply | Esc: Cancel | Edges: ${this.selectedEdges.length}`;
      case 'shell':
        return 'Shell Mode - Optionally select faces to exclude | Enter: Apply | Esc: Cancel';
      case 'offset':
        return 'Offset Mode - Set distance and click Apply | Enter: Apply | Esc: Cancel';
      case 'push-pull':
        if (this.isPushPullDragMode) {
          return `Push/Pull Mode - Drag ↕ on handler to adjust: ${this.pushPullDistance.toFixed(1)} mm | Enter: Apply | Esc: Cancel`;
        }
        return 'Push/Pull Mode - Click a face to select | Esc: Cancel';
      case 'boss':
        if (this.isBossPocketPreviewMode) {
          return `Boss Mode - Drag ↕ to adjust height: ${this.extrudeOnFaceHeight.toFixed(1)} mm | Enter: Apply | Esc: Cancel`;
        } else if (this.showBossPocketToolPicker) {
          return 'Boss Mode - Select a drawing tool from the picker | Esc: Cancel';
        } else if (this.selectedFaceInfo && this.drawingMode !== 'none') {
          return `Boss Mode - Draw your profile | Height: ${this.extrudeOnFaceHeight} | Esc: Cancel`;
        } else if (this.selectedFaceInfo) {
          return 'Boss Mode - Select a drawing tool | Esc: Cancel';
        }
        return 'Boss Mode - Click a face to place the profile | Esc: Cancel';
      case 'pocket':
        if (this.isBossPocketPreviewMode) {
          return `Pocket Mode - Drag ↕ to adjust depth: ${Math.abs(this.extrudeOnFaceHeight).toFixed(1)} mm | Enter: Apply | Esc: Cancel`;
        } else if (this.showBossPocketToolPicker) {
          return 'Pocket Mode - Select a drawing tool from the picker | Esc: Cancel';
        } else if (this.selectedFaceInfo && this.drawingMode !== 'none') {
          return `Pocket Mode - Draw your profile | Depth: ${Math.abs(this.extrudeOnFaceHeight)} | Esc: Cancel`;
        } else if (this.selectedFaceInfo) {
          return 'Pocket Mode - Select a drawing tool | Esc: Cancel';
        }
        return 'Pocket Mode - Click a face to place the profile | Esc: Cancel';
      default:
        return '';
    }
  }

  /**
   * Handle click on Fillet/Chamfer panel header
   * If already expanded and active, collapse and exit. Otherwise enter operation mode.
   */
  async onFilletChamferPanelClick(): Promise<void> {
    // If already in fillet/chamfer operation, exit it
    if (this.activeOperation === 'fillet' || this.activeOperation === 'chamfer') {
      this.exitOperationMode(true);
      this.expandedPatternGroup = null;
      return;
    }

    // Check prerequisites
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape for Fillet/Chamfer');
      return;
    }

    // Enter operation mode with current edge operation type
    const operation = this.edgeOperation === 'chamfer' ? 'chamfer' : 'fillet';
    this.enterOperationMode(operation);

    // Auto-start edge selection
    await this.startEdgeSelectMode();
  }

  /**
   * Handle click on Shell panel header
   */
  async onShellPanelClick(): Promise<void> {
    // If already in shell operation, exit it
    if (this.activeOperation === 'shell') {
      this.exitOperationMode(true);
      this.expandedPatternGroup = null;
      return;
    }

    // Check prerequisites
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape for Shell');
      return;
    }

    // Enter operation mode
    this.enterOperationMode('shell');

    // Auto-start face selection (optional for shell)
    await this.startShellFaceSelectMode();
  }

  /**
   * Handle click on Offset panel header
   */
  onOffsetPanelClick(): void {
    // If already in offset operation, exit it
    if (this.activeOperation === 'offset') {
      this.exitOperationMode(true);
      this.expandedPatternGroup = null;
      return;
    }

    // Check prerequisites
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape for Offset');
      return;
    }

    // Enter operation mode (no selection needed for offset)
    this.enterOperationMode('offset');
  }

  /**
   * Handle click on Push/Pull panel header
   */
  async onPushPullPanelClick(): Promise<void> {
    // If already in push-pull operation, exit it
    if (this.activeOperation === 'push-pull') {
      this.exitOperationMode(true);
      this.expandedPatternGroup = null;
      return;
    }

    // Check prerequisites
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape for Push/Pull');
      return;
    }

    // Enter operation mode
    this.enterOperationMode('push-pull');

    // Auto-start push/pull face selection
    await this.startPushPullMode();
  }

  /**
   * Handle click on Boss panel
   */
  async onBossPanelClick(): Promise<void> {
    // If already in boss operation, exit it
    if (this.activeOperation === 'boss') {
      this.exitOperationMode(true);
      this.expandedPatternGroup = null;
      return;
    }

    // Check prerequisites
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape for Boss');
      return;
    }

    // Enter operation mode
    this.enterOperationMode('boss');

    // Auto-start face picking mode
    await this.startExtrudeOnFaceMode('boss');
  }

  /**
   * Handle click on Pocket panel
   */
  async onPocketPanelClick(): Promise<void> {
    // If already in pocket operation, exit it
    if (this.activeOperation === 'pocket') {
      this.exitOperationMode(true);
      this.expandedPatternGroup = null;
      return;
    }

    // Check prerequisites
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape for Pocket');
      return;
    }

    // Enter operation mode
    this.enterOperationMode('pocket');

    // Auto-start face picking mode
    await this.startExtrudeOnFaceMode('pocket');
  }

  /**
   * Handle click on Surface panel header
   */
  async onSurfacePanelClick(): Promise<void> {
    // If already in extract-face or thicken operation, exit it
    if (this.activeOperation === 'extract-face' || this.activeOperation === 'thicken') {
      this.exitOperationMode(true);
      this.expandedPatternGroup = null;
      return;
    }

    // Check prerequisites
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape for Surface operations');
      return;
    }

    // Enter operation mode
    this.enterOperationMode('extract-face');

    // Auto-start face picking
    await this.startExtractFacePickingMode();
  }

  /**
   * Start face picking mode for Extract Face
   */
  async startExtractFacePickingMode(): Promise<void> {
    if (!this.engine?.canvas) return;
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape first');
      return;
    }

    // Enter operation mode if not already active
    if (this.activeOperation !== 'extract-face') {
      this.enterOperationMode('extract-face');
    }

    // Commit pending transforms before face selection (OCCT geometry must match visual)
    await this.commitPendingTransforms();

    // Re-extract faces for the selected shape after transform commit
    await this.refreshShapeFacesAndEdges();

    // Hide gizmo while picking
    this.detachTransformControls();

    this.isExtractFacePickingMode = true;
    // Don't clear selection - allow adding more faces
    this.startFacePickingMode();
  }

  /**
   * Stop face picking mode for Extract Face
   */
  stopExtractFacePickingMode(): void {
    this.isExtractFacePickingMode = false;
    this.stopFacePickingMode(false);
  }

  /**
   * Check if any faces have been selected for extraction
   */
  hasSelectedExtractFace(): boolean {
    return this.selectedExtractFaceIndices.length > 0;
  }

  /**
   * Clear the selected faces for extraction
   */
  clearSelectedExtractFace(): void {
    this.selectedExtractFaceIndices = [];
    this.clearAllExtractFaceHighlights();
  }

  /**
   * Check if faces can be extracted (requires selected shape + at least one selected face)
   */
  canExtractFace(): boolean {
    return this.selectedCADShapeIds.size === 1 && this.selectedExtractFaceIndices.length > 0;
  }

  /**
   * Check if face wires can be extracted (requires selected shape + exactly one selected face)
   */
  canExtractFaceWires(): boolean {
    return this.selectedCADShapeIds.size === 1 && this.selectedExtractFaceIndices.length === 1;
  }

  /**
   * Toggle face selection for extraction (ctrl+click adds/removes, regular click replaces)
   */
  toggleExtractFaceSelection(faceIndex: number, ctrlKey: boolean): void {
    if (ctrlKey) {
      // Toggle: add if not present, remove if present
      const idx = this.selectedExtractFaceIndices.indexOf(faceIndex);
      if (idx >= 0) {
        this.selectedExtractFaceIndices.splice(idx, 1);
      } else {
        this.selectedExtractFaceIndices.push(faceIndex);
      }
    } else {
      // Replace selection with single face
      this.selectedExtractFaceIndices = [faceIndex];
    }
    this.updateExtractFaceHighlights();
  }

  /**
   * Update visual highlights for all selected extract faces
   */
  private extractFaceHighlightMeshes: Mesh[] = [];

  private updateExtractFaceHighlights(): void {
    this.clearAllExtractFaceHighlights();

    if (!this.engine || this.selectedCADShapeIds.size !== 1) return;

    const shapeId = Array.from(this.selectedCADShapeIds)[0];
    const shape = this.cadShapes.find(s => s.id === shapeId);
    if (!shape?.extractedFaces) return;

    const group = this.sceneMeshes.get(shapeId);
    const geometryCenter = group?.userData['geometryCenter'] as Vector3 | undefined;

    for (const faceIndex of this.selectedExtractFaceIndices) {
      const face = shape.extractedFaces.find(f => f.faceIndex === faceIndex);
      if (!face) continue;

      // Create highlight mesh for this face
      const geometry = new BufferGeometry();

      // Copy vertices and apply geometryCenter offset (same as main mesh)
      const vertices = new Float32Array(face.vertices);
      if (geometryCenter) {
        for (let i = 0; i < vertices.length; i += 3) {
          vertices[i] -= geometryCenter.x;
          vertices[i + 1] -= geometryCenter.y;
          vertices[i + 2] -= geometryCenter.z;
        }
      }

      geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));
      if (face.indices) {
        geometry.setIndex(face.indices);
      }
      geometry.computeVertexNormals();

      const material = new MeshBasicMaterial({
        color: 0x00ff00,
        transparent: true,
        opacity: 0.4,
        side: 2, // DoubleSide
        depthTest: true,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1
      });

      const mesh = new Mesh(geometry, material);
      mesh.renderOrder = 999;

      // Apply shape transform from the scene group
      if (group) {
        mesh.position.copy(group.position);
        mesh.rotation.copy(group.rotation);
        mesh.scale.copy(group.scale);
      }

      this.engine.scene.add(mesh);
      this.extractFaceHighlightMeshes.push(mesh);
    }
  }

  private clearAllExtractFaceHighlights(): void {
    for (const mesh of this.extractFaceHighlightMeshes) {
      if (this.engine) {
        this.engine.scene.remove(mesh);
      }
      mesh.geometry?.dispose();
      (mesh.material as MeshBasicMaterial)?.dispose();
    }
    this.extractFaceHighlightMeshes = [];
  }

  /**
   * Extract the currently selected faces as a single sewn surface
   */
  async extractSelectedFace(): Promise<void> {
    if (!this.canExtractFace()) {
      this.onError.emit('Select at least one face on a shape to extract');
      return;
    }

    const initialShapeId = Array.from(this.selectedCADShapeIds)[0];
    if (!initialShapeId || this.selectedExtractFaceIndices.length === 0) return;

    try {
      this.isProcessing = true;
      const faceCount = this.selectedExtractFaceIndices.length;
      this.processingMessage = `Extracting ${faceCount} face${faceCount > 1 ? 's' : ''}...`;

      // Commit any pending transforms before extract operation
      await this.commitPendingTransforms();

      // Re-fetch shape after commit - transform may have changed the shape ID and object
      const currentShapeId = Array.from(this.selectedCADShapeIds)[0];
      const shape = this.cadShapes.find((s) => s.id === currentShapeId);
      if (!shape) return;

      // Extract all selected faces and sew them together into a single surface
      const extractedSurface = await this.cadKernel.extractMultipleFaces(shape, this.selectedExtractFaceIndices);

      // Add the extracted surface to the scene
      this.cadShapes.push(extractedSurface);
      this.addShapeToScene(extractedSurface);

      // Clear face selection and exit operation mode
      this.clearSelectedExtractFace();
      this.isExtractFacePickingMode = false;
      this.stopFacePickingMode(true);
      this.exitOperationMode(false);

      this.processingMessage = '';
    } catch (error: any) {
      console.error('Error extracting face:', error);
      this.onError.emit(`Failed to extract face: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Extract the selected face wires and create a sketch from them
   */
  async extractFaceWiresToSketch(): Promise<void> {
    if (!this.canExtractFaceWires()) {
      this.onError.emit('Select exactly 1 face on a shape to extract wires');
      return;
    }

    const faceIndex = this.selectedExtractFaceIndices[0];

    try {
      this.isProcessing = true;
      this.processingMessage = 'Extracting face wires...';

      // Ensure kernel geometry matches visual transforms
      await this.commitPendingTransforms();
      await this.refreshShapeFacesAndEdges();

      // Re-fetch shape after commit - transforms may have changed shape ID
      const shapeId = Array.from(this.selectedCADShapeIds)[0];
      const shape = this.cadShapes.find((s) => s.id === shapeId);
      if (!shape) return;

      const facePlane = this.getExtractFacePlaneInfo(shapeId, faceIndex);
      if (!facePlane) {
        this.onError.emit('Failed to resolve face plane for wire extraction');
        return;
      }

      const wireResult = await this.cadKernel.extractFaceWires(shape, faceIndex, 32);

      // Set sketch plane from face (also computes local axes)
      this.setPlaneFromFace(facePlane);

      const sketch = this.sketchService.createSketch('custom', `Sketch (Face ${faceIndex})`, {
        planeOffset: 0,
        planeOrigin: this.customPlaneOrigin,
        planeNormal: this.customPlaneNormal,
        planeXAxis: this.customPlaneXAxis,
        planeYAxis: this.customPlaneYAxis,
        referenceFaceId: `face-${faceIndex}`,
        referenceBodyId: shapeId
      });

      // Activate sketch for entity creation
      this.sketchService.setActiveSketch(sketch.id);

      const group = this.sceneMeshes.get(shapeId);
      const geometryCenter = group?.userData['geometryCenter'] as Vector3 | undefined;
      if (group) {
        group.updateMatrixWorld(true);
      }
      const groupMatrix = group?.matrixWorld;

      const toWorldPoint = (pt: [number, number, number]): Vector3 => {
        const vec = new Vector3(pt[0], pt[1], pt[2]);
        if (geometryCenter) {
          vec.sub(geometryCenter);
        }
        if (groupMatrix) {
          vec.applyMatrix4(groupMatrix);
        }
        return vec;
      };

      const minSegmentLength = 1e-3;

      let createdCount = 0;

      for (const loop of wireResult.loops || []) {
        const circleDef = this.getLoopCircleDefinition(loop, toWorldPoint);
        if (circleDef) {
          this.sketchService.createCircle(circleDef.center.x, circleDef.center.y, circleDef.radius, false);
          createdCount++;
          continue;
        }

        const rectDef = this.getLoopRectangleDefinition(loop, toWorldPoint);
        if (rectDef) {
          this.sketchService.createRectangle(
            rectDef.x,
            rectDef.y,
            rectDef.width,
            rectDef.height,
            rectDef.rotation,
            false
          );
          createdCount++;
          continue;
        }

        for (const edge of loop.edges || []) {
          if (edge.type === 'line' && edge.start && edge.end) {
            const startWorld = toWorldPoint(edge.start);
            const endWorld = toWorldPoint(edge.end);
            const start2D = this.worldToPlaneLocal(startWorld);
            const end2D = this.worldToPlaneLocal(endWorld);
            const dx = end2D.x - start2D.x;
            const dy = end2D.y - start2D.y;
            if (Math.hypot(dx, dy) < minSegmentLength) continue;
            this.sketchService.createLine(start2D.x, start2D.y, end2D.x, end2D.y, false);
            createdCount++;
          } else if (edge.type === 'circle' && edge.center && edge.start && edge.end) {
            const centerWorld = toWorldPoint(edge.center);
            const startWorld = toWorldPoint(edge.start);
            const endWorld = toWorldPoint(edge.end);
            const center2D = this.worldToPlaneLocal(centerWorld);
            const start2D = this.worldToPlaneLocal(startWorld);
            const end2D = this.worldToPlaneLocal(endWorld);

            const radius = Math.hypot(start2D.x - center2D.x, start2D.y - center2D.y);
            if (radius < minSegmentLength) continue;

            const closureTol = Math.max(0.01, radius * 0.01);
            const closeLoop = Math.hypot(start2D.x - end2D.x, start2D.y - end2D.y) < closureTol;

            if (closeLoop) {
              this.sketchService.createCircle(center2D.x, center2D.y, radius, false);
              createdCount++;
              continue;
            }

            if (edge.mid) {
              const mid2D = this.worldToPlaneLocal(toWorldPoint(edge.mid));
              const { startAngle, endAngle, clockwise } = this.resolveArcAngles(center2D, start2D, mid2D, end2D);
              this.sketchService.createArc(center2D.x, center2D.y, radius, startAngle, endAngle, clockwise, false);
              createdCount++;
            } else {
              const startAngle = Math.atan2(start2D.y - center2D.y, start2D.x - center2D.x);
              const endAngle = Math.atan2(end2D.y - center2D.y, end2D.x - center2D.x);
              this.sketchService.createArc(center2D.x, center2D.y, radius, startAngle, endAngle, false, false);
              createdCount++;
            }
          } else if (edge.type === 'polyline' && edge.points && edge.points.length >= 6) {
            for (let i = 0; i < edge.points.length - 3; i += 3) {
              const p1World = toWorldPoint([edge.points[i], edge.points[i + 1], edge.points[i + 2]]);
              const p2World = toWorldPoint([edge.points[i + 3], edge.points[i + 4], edge.points[i + 5]]);
              const p1 = this.worldToPlaneLocal(p1World);
              const p2 = this.worldToPlaneLocal(p2World);
              const dx = p2.x - p1.x;
              const dy = p2.y - p1.y;
              if (Math.hypot(dx, dy) < minSegmentLength) continue;
              this.sketchService.createLine(p1.x, p1.y, p2.x, p2.y, false);
              createdCount++;
            }
          }
        }
      }

      if (createdCount === 0) {
        this.onError.emit('No wire segments were generated for the sketch');
      }

      // Clear face selection / exit operation
      if (this.activeOperation === 'extract-face') {
        this.exitOperationMode(false);
      } else {
        this.clearSelectedExtractFace();
        this.isExtractFacePickingMode = false;
        this.stopFacePickingMode(true);
      }

      // Enter sketch mode and render entities
      this.enterSketchMode(sketch.id);
    } catch (error: any) {
      console.error('Error extracting face wires:', error);
      this.onError.emit(`Failed to extract face wires: ${error.message || error}`);
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  private getLoopCircleDefinition(
    loop: { edges?: any[] },
    toWorldPoint: (pt: [number, number, number]) => Vector3
  ): { center: { x: number; y: number }; radius: number } | null {
    const arcCircle = this.getLoopCircleDefinitionFromArcEdges(loop, toWorldPoint);
    if (arcCircle) return arcCircle;

    return this.getLoopFittedCircleDefinition(loop, toWorldPoint);
  }

  private getLoopCircleDefinitionFromArcEdges(
    loop: { edges?: any[] },
    toWorldPoint: (pt: [number, number, number]) => Vector3
  ): { center: { x: number; y: number }; radius: number } | null {
    const edges = loop.edges || [];
    if (edges.length === 0) return null;

    if (!edges.every((edge) => edge.type === 'circle' && edge.center && edge.start)) {
      return null;
    }

    const centers2D = edges.map((edge) => {
      const centerWorld = toWorldPoint(edge.center);
      return this.worldToPlaneLocal(centerWorld);
    });

    const radii = edges.map((edge, index) => {
      const startWorld = toWorldPoint(edge.start);
      const start2D = this.worldToPlaneLocal(startWorld);
      const center2D = centers2D[index];
      return Math.hypot(start2D.x - center2D.x, start2D.y - center2D.y);
    });

    const avgCenter = centers2D.reduce(
      (acc, pt) => ({ x: acc.x + pt.x, y: acc.y + pt.y }),
      { x: 0, y: 0 }
    );
    avgCenter.x /= centers2D.length;
    avgCenter.y /= centers2D.length;

    const avgRadius = radii.reduce((sum, r) => sum + r, 0) / radii.length;
    const centerTol = Math.max(avgRadius * 0.01, 0.01);
    const radiusTol = Math.max(avgRadius * 0.01, 0.01);

    for (let i = 0; i < centers2D.length; i++) {
      const centerDist = Math.hypot(centers2D[i].x - avgCenter.x, centers2D[i].y - avgCenter.y);
      if (centerDist > centerTol) return null;
      if (Math.abs(radii[i] - avgRadius) > radiusTol) return null;
    }

    if (avgRadius <= 1e-6) return null;

    return { center: avgCenter, radius: avgRadius };
  }

  private getLoopFittedCircleDefinition(
    loop: { edges?: any[] },
    toWorldPoint: (pt: [number, number, number]) => Vector3
  ): { center: { x: number; y: number }; radius: number } | null {
    const { pointsWorld, points2D } = this.collectLoopPoints(loop, toWorldPoint);
    if (pointsWorld.length < 6 || points2D.length < 6) return null;

    const fit = this.snapEngine.fitCircleFromPoints(pointsWorld);
    if (!fit || !Number.isFinite(fit.radius) || fit.radius <= 1e-6) return null;

    const center2D = this.worldToPlaneLocal(fit.center);
    const radius = fit.radius;

    let maxError = 0;
    for (const pt of points2D) {
      const dist = Math.hypot(pt.x - center2D.x, pt.y - center2D.y);
      const error = Math.abs(dist - radius);
      if (error > maxError) maxError = error;
    }

    const radiusTol = Math.max(radius * 0.01, 0.02);
    if (maxError > radiusTol) return null;

    const angles = points2D
      .map((pt) => Math.atan2(pt.y - center2D.y, pt.x - center2D.x))
      .filter((angle) => Number.isFinite(angle));
    const uniqueAngles = this.deduplicateAngles(angles, 0.01);
    if (uniqueAngles.length < 6) return null;

    const coverage = this.computeAngularCoverage(uniqueAngles);
    const coverageThreshold = Math.PI * 2 * 0.92;
    if (coverage < coverageThreshold) return null;

    return { center: center2D, radius };
  }

  private collectLoopPoints(
    loop: { edges?: any[] },
    toWorldPoint: (pt: [number, number, number]) => Vector3
  ): { pointsWorld: Vector3[]; points2D: { x: number; y: number }[] } {
    const pointsWorld: Vector3[] = [];
    const points2D: { x: number; y: number }[] = [];

    const addWorldPoint = (world: Vector3): void => {
      pointsWorld.push(world);
      points2D.push(this.worldToPlaneLocal(world));
    };

    const addPlanePoint = (pt: { x: number; y: number }): void => {
      points2D.push(pt);
      pointsWorld.push(this.planeLocalToWorld(pt));
    };

    const edges = loop.edges || [];
    for (const edge of edges) {
      if (edge.type === 'line' && edge.start && edge.end) {
        const startWorld = toWorldPoint(edge.start);
        const endWorld = toWorldPoint(edge.end);
        addWorldPoint(startWorld);
        addWorldPoint(endWorld);
        const midWorld = startWorld.clone().add(endWorld).multiplyScalar(0.5);
        addWorldPoint(midWorld);
      } else if (edge.type === 'polyline' && edge.points && edge.points.length >= 3) {
        for (let i = 0; i < edge.points.length; i += 3) {
          const world = toWorldPoint([edge.points[i], edge.points[i + 1], edge.points[i + 2]]);
          addWorldPoint(world);
        }
      } else if (edge.type === 'circle' && edge.center && edge.start && edge.end) {
        const centerWorld = toWorldPoint(edge.center);
        const startWorld = toWorldPoint(edge.start);
        const endWorld = toWorldPoint(edge.end);
        const center2D = this.worldToPlaneLocal(centerWorld);
        const start2D = this.worldToPlaneLocal(startWorld);
        const end2D = this.worldToPlaneLocal(endWorld);

        if (edge.mid) {
          const midWorld = toWorldPoint(edge.mid);
          const mid2D = this.worldToPlaneLocal(midWorld);
          const samples = this.sampleArcPoints(center2D, start2D, mid2D, end2D);
          for (const pt of samples) {
            addPlanePoint(pt);
          }
        } else {
          addPlanePoint(start2D);
          addPlanePoint(end2D);
        }
      }
    }

    return { pointsWorld, points2D };
  }

  private sampleArcPoints(
    center: { x: number; y: number },
    start: { x: number; y: number },
    mid: { x: number; y: number },
    end: { x: number; y: number }
  ): { x: number; y: number }[] {
    const radius = Math.hypot(start.x - center.x, start.y - center.y);
    if (radius < 1e-6) return [];

    const { startAngle, endAngle } = this.resolveArcAngles(center, start, mid, end);
    const span = endAngle - startAngle;
    const steps = Math.max(8, Math.ceil(Math.abs(span) / (Math.PI / 8)));
    const points: { x: number; y: number }[] = [];

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const angle = startAngle + span * t;
      points.push({
        x: center.x + Math.cos(angle) * radius,
        y: center.y + Math.sin(angle) * radius
      });
    }

    return points;
  }

  private normalizeAngle(angle: number): number {
    const tau = Math.PI * 2;
    let a = angle % tau;
    if (a < 0) a += tau;
    return a;
  }

  private resolveArcAngles(
    center: { x: number; y: number },
    start: { x: number; y: number },
    mid: { x: number; y: number },
    end: { x: number; y: number }
  ): { startAngle: number; endAngle: number; clockwise: boolean } {
    const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
    const midAngle = Math.atan2(mid.y - center.y, mid.x - center.x);
    const endAngle = Math.atan2(end.y - center.y, end.x - center.x);

    const s = this.normalizeAngle(startAngle);
    const m = this.normalizeAngle(midAngle);
    const e = this.normalizeAngle(endAngle);
    const tau = Math.PI * 2;

    const isBetweenCCW = (angle: number, startA: number, endA: number): boolean => {
      let endAdj = endA;
      let angAdj = angle;
      if (endAdj < startA) endAdj += tau;
      if (angAdj < startA) angAdj += tau;
      return angAdj >= startA && angAdj <= endAdj;
    };

    const ccw = isBetweenCCW(m, s, e);
    let startOut = s;
    let endOut = e;
    if (ccw) {
      if (endOut < startOut) endOut += tau;
    } else {
      if (endOut > startOut) endOut -= tau;
    }

    return { startAngle: startOut, endAngle: endOut, clockwise: !ccw };
  }

  private deduplicateAngles(angles: number[], threshold: number): number[] {
    const sorted = angles.map((angle) => this.normalizeAngle(angle)).sort((a, b) => a - b);
    if (sorted.length === 0) return sorted;

    const result: number[] = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
      if (Math.abs(sorted[i] - result[result.length - 1]) > threshold) {
        result.push(sorted[i]);
      }
    }

    const wrapGap = Math.PI * 2 - (result[result.length - 1] - result[0]);
    if (result.length > 1 && wrapGap < threshold) {
      result.pop();
    }

    return result;
  }

  private computeAngularCoverage(angles: number[]): number {
    if (angles.length < 2) return 0;

    const sorted = angles.map((angle) => this.normalizeAngle(angle)).sort((a, b) => a - b);
    let maxGap = 0;

    for (let i = 0; i < sorted.length - 1; i++) {
      const gap = sorted[i + 1] - sorted[i];
      if (gap > maxGap) maxGap = gap;
    }

    const wrapGap = Math.PI * 2 - (sorted[sorted.length - 1] - sorted[0]);
    if (wrapGap > maxGap) maxGap = wrapGap;

    return Math.PI * 2 - maxGap;
  }

  private getLoopRectangleDefinition(
    loop: { edges?: any[] },
    toWorldPoint: (pt: [number, number, number]) => Vector3
  ): { x: number; y: number; width: number; height: number; rotation: number } | null {
    const edges = loop.edges || [];
    if (edges.length === 0) return null;

    const lineEdges = edges.filter((edge) => edge.type === 'line' && edge.start && edge.end);
    if (lineEdges.length !== edges.length) return null;

    const segments = lineEdges.map((edge) => {
      const startWorld = toWorldPoint(edge.start);
      const endWorld = toWorldPoint(edge.end);
      return {
        start: this.worldToPlaneLocal(startWorld),
        end: this.worldToPlaneLocal(endWorld)
      };
    });

    const avgLen =
      segments.reduce((sum, seg) => sum + Math.hypot(seg.end.x - seg.start.x, seg.end.y - seg.start.y), 0) /
      segments.length;
    const connectTol = Math.max(avgLen * 0.001, 0.001);

    const ordered = this.orderLoopSegments(segments, connectTol);
    if (!ordered) return null;

    const merged = this.mergeCollinearSegments(ordered, 0.999, connectTol);
    if (merged.length !== 4) return null;

    return this.rectangleFromSegments(merged, 0.1, 0.02);
  }

  private orderLoopSegments(
    segments: { start: { x: number; y: number }; end: { x: number; y: number } }[],
    connectTol: number
  ): { start: { x: number; y: number }; end: { x: number; y: number } }[] | null {
    if (segments.length === 0) return [];

    const remaining = segments.slice();
    const ordered: { start: { x: number; y: number }; end: { x: number; y: number } }[] = [];

    ordered.push(remaining.shift()!);

    while (remaining.length > 0) {
      const last = ordered[ordered.length - 1];
      let foundIndex = -1;
      let reversed = false;

      for (let i = 0; i < remaining.length; i++) {
        const seg = remaining[i];
        if (this.pointsNear(last.end, seg.start, connectTol)) {
          foundIndex = i;
          reversed = false;
          break;
        }
        if (this.pointsNear(last.end, seg.end, connectTol)) {
          foundIndex = i;
          reversed = true;
          break;
        }
      }

      if (foundIndex === -1) {
        return null;
      }

      const next = remaining.splice(foundIndex, 1)[0];
      ordered.push(reversed ? { start: next.end, end: next.start } : next);
    }

    if (!this.pointsNear(ordered[ordered.length - 1].end, ordered[0].start, connectTol)) {
      return null;
    }

    return ordered;
  }

  private mergeCollinearSegments(
    segments: { start: { x: number; y: number }; end: { x: number; y: number } }[],
    parallelThreshold: number,
    connectTol: number
  ): { start: { x: number; y: number }; end: { x: number; y: number } }[] {
    if (segments.length < 2) return segments.slice();

    const merged = segments.slice();
    let changed = true;

    while (changed && merged.length > 1) {
      changed = false;

      for (let i = 0; i < merged.length; i++) {
        const nextIndex = (i + 1) % merged.length;
        const segA = merged[i];
        const segB = merged[nextIndex];

        if (!this.pointsNear(segA.end, segB.start, connectTol)) {
          continue;
        }

        const dirA = this.segmentDirection(segA);
        const dirB = this.segmentDirection(segB);
        const dot = dirA.x * dirB.x + dirA.y * dirB.y;

        if (dot >= parallelThreshold) {
          const combined = { start: segA.start, end: segB.end };
          merged[i] = combined;
          merged.splice(nextIndex, 1);
          changed = true;
          break;
        }
      }
    }

    return merged;
  }

  private rectangleFromSegments(
    segments: { start: { x: number; y: number }; end: { x: number; y: number } }[],
    rightAngleTol: number,
    lengthTol: number
  ): { x: number; y: number; width: number; height: number; rotation: number } | null {
    if (segments.length !== 4) return null;

    const dirs = segments.map((seg) => this.segmentDirection(seg));
    const lengths = segments.map((seg) => this.segmentLength(seg));

    if (lengths.some((len) => len < 1e-6)) return null;

    for (let i = 0; i < 4; i++) {
      const next = (i + 1) % 4;
      const dot = dirs[i].x * dirs[next].x + dirs[i].y * dirs[next].y;
      if (Math.abs(dot) > rightAngleTol) return null;
    }

    const parallel0 = Math.abs(dirs[0].x * dirs[2].x + dirs[0].y * dirs[2].y);
    const parallel1 = Math.abs(dirs[1].x * dirs[3].x + dirs[1].y * dirs[3].y);
    if (parallel0 < 0.98 || parallel1 < 0.98) return null;

    const len0 = lengths[0];
    const len2 = lengths[2];
    const len1 = lengths[1];
    const len3 = lengths[3];

    if (Math.abs(len0 - len2) / Math.max(len0, len2) > lengthTol) return null;
    if (Math.abs(len1 - len3) / Math.max(len1, len3) > lengthTol) return null;

    let u = dirs[0];
    let v = { x: -u.y, y: u.x };
    const vDot = v.x * dirs[1].x + v.y * dirs[1].y;
    if (vDot < 0) {
      v = { x: -v.x, y: -v.y };
    }

    const points = [
      segments[0].start,
      segments[0].end,
      segments[1].end,
      segments[2].end
    ];

    const projections = points.map((pt) => ({
      u: pt.x * u.x + pt.y * u.y,
      v: pt.x * v.x + pt.y * v.y
    }));

    const minU = Math.min(...projections.map((p) => p.u));
    const maxU = Math.max(...projections.map((p) => p.u));
    const minV = Math.min(...projections.map((p) => p.v));
    const maxV = Math.max(...projections.map((p) => p.v));

    const width = maxU - minU;
    const height = maxV - minV;
    if (width < 1e-6 || height < 1e-6) return null;

    const origin = {
      x: u.x * minU + v.x * minV,
      y: u.y * minU + v.y * minV
    };

    const rotation = Math.atan2(u.y, u.x);

    return { x: origin.x, y: origin.y, width, height, rotation };
  }

  private segmentDirection(seg: { start: { x: number; y: number }; end: { x: number; y: number } }): {
    x: number;
    y: number;
  } {
    const dx = seg.end.x - seg.start.x;
    const dy = seg.end.y - seg.start.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-12) return { x: 0, y: 0 };
    return { x: dx / len, y: dy / len };
  }

  private segmentLength(seg: { start: { x: number; y: number }; end: { x: number; y: number } }): number {
    return Math.hypot(seg.end.x - seg.start.x, seg.end.y - seg.start.y);
  }

  private pointsNear(a: { x: number; y: number }, b: { x: number; y: number }, tol: number): boolean {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return dx * dx + dy * dy <= tol * tol;
  }

  /**
   * Check if any selected shapes are surfaces that can be thickened
   */
  canThickenSurface(): boolean {
    if (this.selectedCADShapeIds.size === 0) return false;
    // Check if at least one selected shape is a surface
    for (const shapeId of this.selectedCADShapeIds) {
      const shape = this.cadShapes.find((s) => s.id === shapeId);
      if (shape && (shape as any).isSurface === true) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get count of selected surfaces
   */
  getSelectedSurfaceCount(): number {
    let count = 0;
    for (const shapeId of this.selectedCADShapeIds) {
      const shape = this.cadShapes.find((s) => s.id === shapeId);
      if (shape && (shape as any).isSurface === true) {
        count++;
      }
    }
    return count;
  }

  /**
   * Check if we should show the tabbed operations section
   * Shows when: single shape selected, OR multiple surfaces selected (for thicken)
   */
  shouldShowTabbedOperations(): boolean {
    // Show for single shape selection (normal operations)
    if (this.selectedCADShapeIds.size === 1) {
      return true;
    }
    // Also show when multiple surfaces are selected (for thicken operation)
    if (this.selectedCADShapeIds.size > 1 && this.canThickenSurface()) {
      return true;
    }
    return false;
  }

  /**
   * Thicken the selected surfaces into solids
   */
  async thickenSelectedSurface(): Promise<void> {
    if (!this.canThickenSurface() || this.thickenAmount === 0) {
      this.onError.emit('Select at least one surface and specify a non-zero thickness');
      return;
    }

    try {
      this.isProcessing = true;
      this.detachTransformControls();

      // Commit any pending transforms before thicken operation
      await this.commitPendingTransforms();

      // Get all selected surface IDs
      const surfaceIds: string[] = [];
      for (const shapeId of this.selectedCADShapeIds) {
        const shape = this.cadShapes.find((s) => s.id === shapeId);
        if (shape && (shape as any).isSurface === true) {
          surfaceIds.push(shapeId);
        }
      }

      const surfaceCount = surfaceIds.length;
      this.processingMessage = `Thickening ${surfaceCount} surface${surfaceCount > 1 ? 's' : ''}...`;

      const newSolidIds: string[] = [];

      // Thicken each surface
      for (const surfaceId of surfaceIds) {
        const surface = this.cadShapes.find((s) => s.id === surfaceId);
        if (!surface) continue;

        const solidShape = await this.cadKernel.thickenSurface(surface, this.thickenAmount);

        // Remove the old surface from scene and list
        this.removeShapeFromScene(surfaceId);
        this.cadShapes = this.cadShapes.filter((s) => s.id !== surfaceId);

        // Add the new solid
        this.cadShapes.push(solidShape);
        this.addShapeToScene(solidShape);
        newSolidIds.push(solidShape.id);
      }

      // Select all new solids
      this.selectedCADShapeIds.clear();
      for (const solidId of newSolidIds) {
        this.selectedCADShapeIds.add(solidId);
        this.updateShapeVisualState(solidId, true);
      }

      // Attach transform controls if single selection
      if (newSolidIds.length === 1) {
        this.attachTransformControls();
      }

      this.processingMessage = '';
    } catch (error: any) {
      console.error('Error thickening surface:', error);
      this.onError.emit(`Failed to thicken surface: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cleanupThickenDirectionArrow();
      this.cdr.detectChanges();
    }
  }

  /**
   * Update the thicken direction arrow when thickness value changes
   * Called from template when ngModel changes
   */
  updateThickenDirectionArrow(): void {
    if (!this.canThickenSurface()) {
      this.cleanupThickenDirectionArrow();
      return;
    }

    // Get the first selected surface to compute its normal
    let surface: any = null;
    for (const shapeId of this.selectedCADShapeIds) {
      const shape = this.cadShapes.find((s) => s.id === shapeId);
      if (shape && (shape as any).isSurface === true) {
        surface = shape;
        break;
      }
    }

    if (!surface) {
      this.cleanupThickenDirectionArrow();
      return;
    }

    // Get surface center from extractedFaces or boundingBox
    let faceCenter: Vector3 | null = null;
    if (surface.extractedFaces && surface.extractedFaces.length > 0) {
      const face = surface.extractedFaces[0];
      if (face.center) {
        faceCenter = new Vector3(face.center[0], face.center[1], face.center[2]);
      }
    }
    if (!faceCenter && surface.boundingBox) {
      const bb = surface.boundingBox;
      faceCenter = new Vector3(
        (bb.min[0] + bb.max[0]) / 2,
        (bb.min[1] + bb.max[1]) / 2,
        (bb.min[2] + bb.max[2]) / 2
      );
    }
    if (!faceCenter) {
      this.cleanupThickenDirectionArrow();
      return;
    }

    // Check surface type directly on shape (set during face extraction from OCCT)
    const surfaceType = surface.surfaceType;
    const cylinderData = surface.cylinderData;
    const sphereData = surface.sphereData;
    const coneData = surface.coneData;
    const torusData = surface.torusData;

    // Get the actual face normal from extractedFaces - this is the true surface normal from OCCT
    // The face normal already correctly represents the surface orientation for any surface type
    let faceNormal: Vector3 | null = null;
    if (surface.extractedFaces && surface.extractedFaces.length > 0) {
      const face = surface.extractedFaces[0];
      if (face.normal) {
        faceNormal = new Vector3(face.normal[0], face.normal[1], face.normal[2]).normalize();
      }
    }

    // Handle curved surfaces - use geometric radial direction with faceNormal to detect inner/outer
    if (surfaceType === 'cylinder' && cylinderData) {
      const axisDir = new Vector3(cylinderData.axis[0], cylinderData.axis[1], cylinderData.axis[2]).normalize();
      const axisPoint = new Vector3(cylinderData.location[0], cylinderData.location[1], cylinderData.location[2]);
      const toCenter = faceCenter.clone().sub(axisPoint);
      const projLength = toCenter.dot(axisDir);
      const closestPointOnAxis = axisPoint.clone().add(axisDir.clone().multiplyScalar(projLength));

      // Compute radial direction from axis toward face center (geometric outward)
      const radialOutward = faceCenter.clone().sub(closestPointOnAxis).normalize();

      // Position arrow on the cylinder wall
      const wallPosition = closestPointOnAxis.clone().add(radialOutward.clone().multiplyScalar(cylinderData.radius));

      // Determine direction: use geometric radial, with sign based on faceNormal orientation
      // Outer surface (faceNormal points outward): use radialOutward
      // Inner surface (faceNormal points inward): use -radialOutward
      let surfaceNormal: Vector3;
      if (faceNormal) {
        const dot = faceNormal.dot(radialOutward);
        surfaceNormal = dot >= 0 ? radialOutward.clone() : radialOutward.clone().negate();
      } else {
        surfaceNormal = radialOutward;
      }

      this.thickenSurfaceNormal = surfaceNormal;
      this.thickenSurfaceCenter = wallPosition;
      this.thickenArrowMaxLength = cylinderData.radius;
      this.createOrUpdateThickenArrow();
    } else if (surfaceType === 'sphere' && sphereData) {
      const sphereCenter = new Vector3(sphereData.center[0], sphereData.center[1], sphereData.center[2]);
      const radialOutward = faceCenter.clone().sub(sphereCenter).normalize();

      let surfaceNormal: Vector3;
      if (faceNormal) {
        const dot = faceNormal.dot(radialOutward);
        surfaceNormal = dot >= 0 ? radialOutward.clone() : radialOutward.clone().negate();
      } else {
        surfaceNormal = radialOutward;
      }

      this.thickenSurfaceNormal = surfaceNormal;
      this.thickenSurfaceCenter = faceCenter;
      this.thickenArrowMaxLength = sphereData.radius;
      this.createOrUpdateThickenArrow();
    } else if (surfaceType === 'cone' && coneData) {
      const axisDir = new Vector3(coneData.axis[0], coneData.axis[1], coneData.axis[2]).normalize();
      const apex = new Vector3(coneData.apex[0], coneData.apex[1], coneData.apex[2]);
      const toCenter = faceCenter.clone().sub(apex);
      const projLength = toCenter.dot(axisDir);
      const closestPointOnAxis = apex.clone().add(axisDir.clone().multiplyScalar(projLength));
      const localRadius = faceCenter.distanceTo(closestPointOnAxis);
      const radialOutward = faceCenter.clone().sub(closestPointOnAxis).normalize();

      // For cone, use faceNormal directly but correct orientation based on inner/outer
      let surfaceNormal: Vector3;
      if (faceNormal) {
        const dot = faceNormal.dot(radialOutward);
        surfaceNormal = dot >= 0 ? faceNormal.clone() : faceNormal.clone().negate();
      } else {
        surfaceNormal = radialOutward;
      }

      this.thickenSurfaceNormal = surfaceNormal;
      this.thickenSurfaceCenter = faceCenter;
      this.thickenArrowMaxLength = localRadius > 0 ? localRadius : null;
      this.createOrUpdateThickenArrow();
    } else if (surfaceType === 'torus' && torusData) {
      const torusCenterPt = new Vector3(torusData.center[0], torusData.center[1], torusData.center[2]);
      const axisDir = new Vector3(torusData.axis[0], torusData.axis[1], torusData.axis[2]).normalize();
      const toFace = faceCenter.clone().sub(torusCenterPt);
      const projOnAxis = axisDir.clone().multiplyScalar(toFace.dot(axisDir));
      const inPlane = toFace.clone().sub(projOnAxis);
      const majorCirclePoint = torusCenterPt.clone().add(inPlane.normalize().multiplyScalar(torusData.majorRadius));
      const radialOutward = faceCenter.clone().sub(majorCirclePoint).normalize();

      let surfaceNormal: Vector3;
      if (faceNormal) {
        const dot = faceNormal.dot(radialOutward);
        surfaceNormal = dot >= 0 ? radialOutward.clone() : radialOutward.clone().negate();
      } else {
        surfaceNormal = radialOutward;
      }

      this.thickenSurfaceNormal = surfaceNormal;
      this.thickenSurfaceCenter = faceCenter;
      this.thickenArrowMaxLength = torusData.minorRadius;
      this.createOrUpdateThickenArrow();
    } else {
      // For planar surfaces or unknown type, use the face normal from extractedFaces
      this.thickenArrowMaxLength = null; // No max length for planar surfaces
      if (surface.extractedFaces && surface.extractedFaces.length > 0) {
        const face = surface.extractedFaces[0];
        if (face.normal) {
          this.thickenSurfaceNormal = new Vector3(face.normal[0], face.normal[1], face.normal[2]);
          this.thickenSurfaceCenter = faceCenter;
          this.createOrUpdateThickenArrow();
        }
      } else {
        // Fallback to Y-up normal
        this.thickenSurfaceNormal = new Vector3(0, 1, 0);
        this.thickenSurfaceCenter = faceCenter;
        this.createOrUpdateThickenArrow();
      }
    }
  }

  /**
   * Create or update the 3D arrow showing thicken direction
   */
  private createOrUpdateThickenArrow(): void {
    if (!this.engine || !this.thickenSurfaceCenter || !this.thickenSurfaceNormal) return;

    // Clean up existing arrow
    if (this.thickenDirectionArrow) {
      this.engine.scene.remove(this.thickenDirectionArrow);
      this.thickenDirectionArrow.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.thickenDirectionArrow = null;
    }

    if (this.thickenAmount === 0) return;

    // Create arrow group
    this.thickenDirectionArrow = new Group();
    this.thickenDirectionArrow.name = 'ThickenDirectionArrow';

    // Default arrow dimensions
    let coneHeight = 10;
    let coneRadius = 4;
    let shaftHeight = 15;
    let shaftRadius = 1.5;

    // If we have a max length (e.g., cylinder radius), scale arrow to fit
    if (this.thickenArrowMaxLength !== null && this.thickenArrowMaxLength > 0) {
      const maxLength = this.thickenArrowMaxLength;
      const totalDefaultHeight = coneHeight + shaftHeight; // 25 default

      // If default arrow exceeds max length, scale it down
      if (totalDefaultHeight > maxLength * 0.9) {
        const scale = (maxLength * 0.8) / totalDefaultHeight;
        coneHeight *= scale;
        coneRadius *= scale;
        shaftHeight *= scale;
        shaftRadius *= scale;

        // Ensure minimum visibility
        coneHeight = Math.max(coneHeight, 3);
        coneRadius = Math.max(coneRadius, 1.5);
        shaftHeight = Math.max(shaftHeight, 5);
        shaftRadius = Math.max(shaftRadius, 0.5);
      }
    }

    // Color based on direction (positive = green, negative = orange/warning)
    const arrowColor = this.thickenAmount > 0 ? 0xa0ff00 : 0xf59e0b;

    // Create cone (arrow head)
    const coneGeometry = new ConeGeometry(coneRadius, coneHeight, 16);
    const coneMaterial = new MeshBasicMaterial({
      color: arrowColor,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    const cone = new Mesh(coneGeometry, coneMaterial);
    cone.position.y = shaftHeight + coneHeight / 2;
    cone.renderOrder = 200;

    // Create shaft (cylinder)
    const shaftGeometry = new CylinderGeometry(shaftRadius, shaftRadius, shaftHeight, 16);
    const shaftMaterial = new MeshBasicMaterial({
      color: arrowColor,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    const shaft = new Mesh(shaftGeometry, shaftMaterial);
    shaft.position.y = shaftHeight / 2;
    shaft.renderOrder = 200;

    this.thickenDirectionArrow.add(cone);
    this.thickenDirectionArrow.add(shaft);

    // Set position at surface center
    this.thickenDirectionArrow.position.copy(this.thickenSurfaceCenter);

    // Orient arrow based on thickness direction
    const normal = this.thickenSurfaceNormal.clone().normalize();
    const up = new Vector3(0, 1, 0);
    const direction = this.thickenAmount >= 0 ? normal : normal.clone().negate();
    const quaternion = new Quaternion().setFromUnitVectors(up, direction);
    this.thickenDirectionArrow.quaternion.copy(quaternion);

    this.engine.scene.add(this.thickenDirectionArrow);
  }

  /**
   * Clean up the thicken direction arrow
   */
  cleanupThickenDirectionArrow(): void {
    if (this.thickenDirectionArrow && this.engine) {
      this.engine.scene.remove(this.thickenDirectionArrow);
      this.thickenDirectionArrow.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.thickenDirectionArrow = null;
    }
    this.thickenSurfaceNormal = null;
    this.thickenSurfaceCenter = null;
    this.thickenArrowMaxLength = null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // KEYBOARD SHORTCUTS
  // ═══════════════════════════════════════════════════════════════════════════

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Only handle if in edit mode
    if (!this.isEditMode) return;

    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey || event.metaKey;

    // FIRST: Check if user is typing in an input field
    // This is the most important check - NEVER intercept typing
    const activeEl = document.activeElement;
    const activeTag = activeEl?.tagName?.toLowerCase() || '';
    if (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select') {
      return; // Let the input handle the keypress
    }

    // Also check the event target
    const target = event.target as HTMLElement;
    const targetTag = target?.tagName?.toLowerCase() || '';
    if (targetTag === 'input' || targetTag === 'textarea' || targetTag === 'select') {
      return; // Let the input handle the keypress
    }

    // Skip if any modal/overlay is visible in the DOM
    // This prevents shortcuts from firing when dialogs are open
    if (this.isModalOpen()) {
      if (key === 'delete' || key === 'backspace') {
        event.stopImmediatePropagation();
      }
      return;
    }

    // Skip if inside a form control component
    if (this.isInputElement(target)) return;

    // History shortcuts
    if (ctrl && key === 'z') {
      event.preventDefault();
      if (event.shiftKey) {
        this.redoHistory();
      } else {
        this.undoHistory();
      }
      return;
    } else if (ctrl && key === 'y') {
      event.preventDefault();
      this.redoHistory();
      return;
    }

    // Transform mode shortcuts
    if (key === 'q' && !ctrl) {
      event.preventDefault();
      this.setTransformMode('translate');
    } else if (key === 'w' && !ctrl) {
      event.preventDefault();
      this.setTransformMode('rotate');
    } else if (key === 'e' && !ctrl) {
      event.preventDefault();
      this.setTransformMode('scale');
    }

    // Selection shortcuts
    else if (key === 'a' && ctrl) {
      event.preventDefault();
      this.selectAll();
    } else if (key === 'escape') {
      event.preventDefault();
      if (this.isAssistantFacePickMode) {
        this.stopAssistantFacePickMode();
      }
      // FIRST: Check for active CAD operation mode
      else if (this.activeOperation) {
        this.exitOperationMode(true); // Cancel the operation
      }
      // Fallback for push/pull mode (should be handled by activeOperation)
      else if (this.isPushPullMode || this.isPushPullDragMode) {
        // Clear drag mode properly
        if (this.isPushPullDragMode) {
          this.exitPushPullDragMode();
        }
        this.stopPushPullMode(false);
        this.restoreHiddenShapes();
        this.activeOperation = null;
      }
      // Fallback for boss/pocket mode (should be handled by activeOperation)
      else if (this.isExtrudeOnFaceMode || this.isBossPocketPreviewMode || this.bossPocketDragMoveHandler) {
        this.cancelExtrudeOnFaceMode();
        this.restoreHiddenShapes();
        this.activeOperation = null;
      }
      // If in sketch mode, exit sketch mode
      else if (this.isSketchMode) {
        this.finishSketchEditing();
      } else if (this.isDrawing) {
        // If drawing, cancel drawing
        this.cancelDrawing();
      } else {
        // Otherwise clear selection
        this.clearSelection();
      }
    } else if (key === 'enter') {
      event.preventDefault();
      // FIRST: Check for active CAD operation mode
      if (this.activeOperation) {
        this.applyActiveOperation(); // Apply the operation
      }
      // If in sketch mode, finish editing
      else if (this.isSketchMode) {
        this.finishSketchEditing();
      }
    }

    // Edit shortcuts
    else if (key === 'delete' || key === 'backspace') {
      // Delete sketch entities if in sketch mode
      if (this.isSketchMode) {
        const selectedEntityIds = this.sketchService.getSelectedEntityIds();
        if (selectedEntityIds.length > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.deleteSelectedSketchEntities();
          return;
        }
      }

      // Delete CAD shapes
      if (this.selectedCADShapeIds.size > 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.confirmDeleteSelected();
      }
    }

    // Copy/Paste shortcuts
    else if (key === 'c' && ctrl) {
      event.preventDefault();
      this.copySelected();
      if (this.clipboard.length > 0) {
      }
    } else if (key === 'v' && ctrl) {
      event.preventDefault();
      this.pasteFromClipboard();
    } else if (key === 'd' && ctrl) {
      event.preventDefault();
      this.duplicateSelected();
    }

    // Tab key for dimension input while drawing
    // Works for: rectangle/circle/polygon (isDrawing=true) and slot/arc (have start points)
    else if (key === 'tab') {
      const canOpenDimensionInput =
        (this.isDrawing && this.drawingStartPoint) || // Rectangle, Circle, Polygon
        (this.drawingMode === 'slot' && this.slotDrawingState.startPoint) || // Slot
        (this.drawingMode === 'arc' && this.arcDrawingState.startPoint); // Arc

      if (canOpenDimensionInput) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.showDimensionInput) {
          this.openDimensionInput();
        }
      }
    }

    // Constraint shortcuts (only when drawing lines)
    else if (this.drawingMode === 'line' && this.linePoints.length > 0) {
      if (key === 'h') {
        event.preventDefault();
        this.setConstraintMode('horizontal');
      } else if (key === 'v') {
        event.preventDefault();
        this.setConstraintMode('vertical');
      } else if (key === 'p') {
        event.preventDefault();
        this.setConstraintMode('parallel');
      } else if (key === 'n') {
        event.preventDefault();
        this.setConstraintMode('perpendicular');
      }
    }

    // Toggle keyboard shortcuts panel
    else if (key === '?' || (key === '/' && event.shiftKey)) {
      event.preventDefault();
      this.showKeyboardShortcuts = !this.showKeyboardShortcuts;
      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    this.toggleEditMode(true);
    this.historyService.clear();

    // Add beforeunload handler to warn about unsaved changes
    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.hasUnsavedChanges && this.isEditMode) {
        event.preventDefault();
        // Modern browsers require returnValue to be set
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    // Subscribe to kernel loading state
    this.subscriptions.push(
      this.cadKernel.onLoadingStateChange.subscribe((state) => {
        this.loadingState = state;
        this.cdr.detectChanges();
      })
    );

    // Subscribe to worker progress events
    this.subscriptions.push(
      this.cadWorker.onProgress.subscribe(({ progress }) => {
        this.processingProgress = progress;
        this.cdr.detectChanges();
      })
    );

    // Subscribe to snap config changes
    this.subscriptions.push(
      this.snapEngine.onConfigChanged.subscribe((config) => {
        this.snapConfig = config;
        this.cdr.detectChanges();
      })
    );

    // Subscribe to sketch events to re-render all visible sketches when not in edit mode
    this.subscriptions.push(
      this.sketchService.onSketchCreated.subscribe(() => {
        if (!this.isSketchMode) {
          this.renderAllVisibleSketches();
        }
      }),
      this.sketchService.onSketchDeleted.subscribe(() => {
        if (!this.isSketchMode) {
          this.renderAllVisibleSketches();
        }
      }),
      this.sketchService.onProfilesUpdated.subscribe(() => {
        if (!this.isSketchMode) {
          this.renderAllVisibleSketches();
        }
      })
    );

    // Setup CAD group in scene
    this.setupCADGroup();

    // Setup interaction handlers
    this.setupInteraction();

    // Setup toolbar positioning relative to sidenav
    this.setupToolbarPositioning();

    // Load saved CAD parts when entering edit mode, then tell the assistant chat this
    // editor is ready for script bodies (it holds any that arrived while it was closed).
    void this.loadSavedCADParts().finally(() => this.session.pubsub.emit({ type: 'cad-toolbar-ready' }));

    // Subscribe to WebSocket events for backend sync
    this.subscriptions.push(
      this.wsService.onMessage<any>().subscribe((message) => {
        this.handleWebSocketMessage(message);
      })
    );

    // Subscribe to AI-generated CAD geometry events
    this.subscriptions.push(
      this.session.pubsub.subscribe((event: any) => {
        if (event.type === 'cad-geometry-created' && event.shape) {
          console.log('CADToolbar: Received AI-generated geometry', event.geometry);
          this.handleAIGeneratedGeometry(event.shape, event.geometry);
        } else if (event.type === 'cad-boolean-operation' && event.booleanRequest) {
          console.log('CADToolbar: Received AI boolean operation request', event.booleanRequest);
          this.handleAIBooleanOperation(event.booleanRequest);
        } else if (event.type === 'cad-slot-operation' && event.slotRequest) {
          console.log('CADToolbar: Received AI slot operation request', event.slotRequest);
          this.handleAISlotOperation(event.slotRequest);
        } else if (event.type === 'cad-delete-geometry' && event.deleteRequest) {
          console.log('CADToolbar: Received AI delete geometry request', event.deleteRequest);
          this.handleAIDeleteGeometry(event.deleteRequest);
        } else if (event.type === 'cad-geometry-transformed' && event.shape) {
          console.log('CADToolbar: Received AI geometry transform', event.geometry);
          this.handleAITransformedGeometry(event.shape, event.geometry);
        } else if (event.type === 'cad-geometry-modified' && event.shape) {
          console.log('CADToolbar: Received AI geometry modification', event.geometry);
          // Modified shapes replace the original — same handling as transform
          this.handleAITransformedGeometry(event.shape, event.geometry);
        } else if (event.type === 'cad-fit-all-after-script') {
          // Auto-frame the camera after the agent has finished dispatching
          // all per-part events for a cad_script_runner result. Defer one
          // microtask so the last addShapeToScene's setTimeout(0) for edges
          // has run, ensuring the new shape's bounding box is finalized.
          setTimeout(() => this.engine?.fitAll(), 0);
        }
      })
    );

    // Render all visible sketches initially (when not in edit mode)
    setTimeout(() => {
      if (!this.isSketchMode) {
        this.renderAllVisibleSketches();
      }
    }, 100);
  }

  /**
   * Handle WebSocket messages for backend sync
   */
  private handleWebSocketMessage(message: WebSocketMessage<any>): void {
    if (!message?.data) return;

    // Also handle project_status messages (used by the backend)
    if (message.type === 'project_status' && message.data?.action) {
      this.handleProjectStatusMessage(message.data);
      return;
    }
  }

  /**
   * Handle AI-generated CAD geometry from the assistant chat
   * Adds the shape to the scene and cadShapes array
   */
  private handleAIGeneratedGeometry(shape: CADShape, geometryInfo: any): void {
    if (!shape || !shape.mesh) {
      console.error('CADToolbar: Invalid shape received from AI');
      return;
    }

    console.log('CADToolbar: Adding AI-generated shape to scene', shape.id, shape.name);

    // Set the shape name from geometry info if available
    if (geometryInfo?.name) {
      shape.name = geometryInfo.name;
    }

    // Add to cadShapes array
    this.cadShapes.push(shape);

    // Add to scene
    this.addShapeToScene(shape);

    // An agent-made body is not saved yet: without this the button read "Saved" and
    // leaving the editor dropped the body without warning.
    this.hasUnsavedChanges = true;

    // Trigger change detection
    this.cdr.detectChanges();

    // Auto-save after adding
    // this.saveCADParts();

    console.log('CADToolbar: AI-generated shape added successfully', {
      id: shape.id,
      name: shape.name,
      totalShapes: this.cadShapes.length
    });
  }

  /**
   * Handle AI-transformed CAD geometry from the assistant chat
   * Replaces the existing shape with the transformed version in the scene
   */
  private handleAITransformedGeometry(newShape: CADShape, geometryInfo: any): void {
    if (!newShape || !newShape.mesh) {
      console.error('CADToolbar: Invalid transformed shape received from AI');
      return;
    }

    const shapeId = geometryInfo?.id || newShape.id;
    console.log('CADToolbar: Updating transformed shape in scene', shapeId, newShape.name);

    // Find the existing shape index
    const existingIndex = this.cadShapes.findIndex(s => s.id === shapeId);

    if (existingIndex === -1) {
      console.warn('CADToolbar: Original shape not found for transform, adding as new:', shapeId);
      // If not found, add as new shape
      this.handleAIGeneratedGeometry(newShape, geometryInfo);
      return;
    }

    // Get the old shape
    const oldShape = this.cadShapes[existingIndex];

    // Remove old mesh from scene using the proper method
    this.removeShapeFromScene(shapeId);

    // Preserve the name from geometry info or old shape
    if (geometryInfo?.name) {
      newShape.name = geometryInfo.name;
    } else if (oldShape.name) {
      newShape.name = oldShape.name;
    }

    // Replace in cadShapes array
    this.cadShapes[existingIndex] = newShape;

    // Add new mesh to scene
    this.addShapeToScene(newShape);
    this.hasUnsavedChanges = true;

    // Trigger change detection
    this.cdr.detectChanges();

    console.log('CADToolbar: AI-transformed shape updated successfully', {
      id: newShape.id,
      name: newShape.name,
      totalShapes: this.cadShapes.length
    });
  }

  /**
   * Handle AI-requested boolean operations (subtract, union)
   * Finds shapes by ID or name and performs the operation
   */
  private async handleAIBooleanOperation(request: any): Promise<void> {
    const { type, targetGeometryId, operandGeometryId, resultName } = request;

    // Log available shapes for debugging
    const availableShapes = this.cadShapes.map(s => ({ id: s.id, name: s.name }));
    console.log('CADToolbar: Processing boolean operation', {
      type,
      targetGeometryId,
      operandGeometryId,
      availableShapes
    });

    // Find shapes by ID or name (case-insensitive)
    const findShape = (searchId: string) => {
      if (!searchId) return null;
      const searchLower = searchId.toLowerCase();
      return this.cadShapes.find(
        (s) => s.id === searchId ||
               s.id?.toLowerCase() === searchLower ||
               s.name?.toLowerCase() === searchLower
      ) || null;
    };

    const targetShape = findShape(targetGeometryId);
    const operandShape = findShape(operandGeometryId);

    if (!targetShape) {
      console.error('CADToolbar: Target shape not found:', targetGeometryId, 'Available:', availableShapes);
      this.session.pubsub.next({
        type: 'cad-boolean-error',
        error: `Target shape "${targetGeometryId}" not found. Available shapes: ${availableShapes.map(s => s.name || s.id).join(', ')}`,
      });
      return;
    }

    if (!operandShape) {
      console.error('CADToolbar: Operand shape not found:', operandGeometryId, 'Available:', availableShapes);
      this.session.pubsub.next({
        type: 'cad-boolean-error',
        error: `Operand shape "${operandGeometryId}" not found. Available shapes: ${availableShapes.map(s => s.name || s.id).join(', ')}`,
      });
      return;
    }

    try {
      this.isProcessing = true;
      this.processingMessage = `Performing ${type} operation...`;

      let result: CADShape | null = null;

      if (type === 'subtract' || type === 'boolean_subtract') {
        result = await this.cadKernel.booleanSubtract(targetShape, operandShape);
      } else if (type === 'union' || type === 'boolean_union') {
        result = await this.cadKernel.booleanUnion(targetShape, operandShape);
      } else {
        console.error('CADToolbar: Unknown boolean operation type:', type);
        return;
      }

      if (result) {
        // Set result name
        result.name = resultName || `${type}_result`;

        // Remove original shapes from scene and array
        this.removeShapeFromScene(targetShape.id);
        this.removeShapeFromScene(operandShape.id);
        this.cadShapes = this.cadShapes.filter((s) => s.id !== targetShape.id && s.id !== operandShape.id);

        // Add result
        this.cadShapes.push(result);
        this.addShapeToScene(result);

        console.log('CADToolbar: Boolean operation completed', {
          type,
          resultId: result.id,
          resultName: result.name,
        });

        this.session.pubsub.next({
          type: 'cad-boolean-complete',
          result: { id: result.id, name: result.name, type },
        });
      }
    } catch (error: any) {
      console.error('CADToolbar: Boolean operation failed:', error);
      this.session.pubsub.next({
        type: 'cad-boolean-error',
        error: error.message || 'Boolean operation failed',
      });
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle AI-requested slot operation
   * Creates a slot geometry from points and subtracts it from the target
   */
  private async handleAISlotOperation(request: any): Promise<void> {
    const { targetGeometryId, targetGeometryName, slot, slots } = request;
    const searchId = targetGeometryId || targetGeometryName;

    // Support both single slot and multiple slots array format
    const slotsToProcess: any[] = slots || (slot ? [slot] : []);

    // Log available shapes for debugging
    const availableShapes = this.cadShapes.map(s => ({ id: s.id, name: s.name }));
    console.log('CADToolbar: Processing slot operation', {
      targetGeometryId,
      targetGeometryName,
      slotsCount: slotsToProcess.length,
      availableShapes
    });

    // Find target shape by ID or name (case-insensitive)
    const findShape = (searchTerm: string) => {
      if (!searchTerm) return null;
      const searchLower = searchTerm.toLowerCase();
      return this.cadShapes.find(
        (s) => s.id === searchTerm ||
               s.id?.toLowerCase() === searchLower ||
               s.name?.toLowerCase() === searchLower
      ) || null;
    };

    let targetShape = findShape(searchId);

    if (!targetShape) {
      console.error('CADToolbar: Target shape not found for slot:', searchId, 'Available:', availableShapes);
      this.session.pubsub.next({
        type: 'cad-slot-error',
        error: `Target shape "${searchId}" not found. Available shapes: ${availableShapes.map(s => s.name || s.id).join(', ')}`,
      });
      return;
    }

    if (slotsToProcess.length === 0 || !slotsToProcess.every(s => s?.points?.length >= 3)) {
      console.error('CADToolbar: Invalid slot points');
      this.session.pubsub.next({
        type: 'cad-slot-error',
        error: 'Invalid slot geometry - insufficient points',
      });
      return;
    }

    try {
      this.isProcessing = true;
      const slotCount = slotsToProcess.length;
      this.processingMessage = slotCount > 1 ? `Creating ${slotCount} slots...` : 'Creating slot...';
      this.cdr.detectChanges();

      // Get target bounds to determine extrusion height for the slots
      const targetBounds = this.cadKernel.getBoundingBox(targetShape);
      const extrudeHeight = targetBounds
        ? (targetBounds.max[2] - targetBounds.min[2]) + 10 // Add margin to ensure full cut
        : 100;
      const zOffset = targetBounds ? targetBounds.min[2] - 5 : 0; // Start below target

      // Process each slot
      let currentTarget = targetShape;
      const originalTargetId = targetShape.id;
      const originalTargetName = targetShape.name || targetGeometryName || 'Shape';

      for (let i = 0; i < slotsToProcess.length; i++) {
        const currentSlot = slotsToProcess[i];
        if (slotCount > 1) {
          this.processingMessage = `Creating slot ${i + 1} of ${slotCount}...`;
          this.cdr.detectChanges();
        }

        // Create slot polygon and extrude it
        const slotPoints: [number, number][] = currentSlot.points.map((p: number[]) => [p[0], p[1]] as [number, number]);
        const slotShape = await this.cadKernel.extrudePolygon({
          points: slotPoints,
          height: extrudeHeight,
          planeOffset: zOffset,
        });

        if (!slotShape) {
          throw new Error(`Failed to create slot geometry ${i + 1}`);
        }

        this.processingMessage = slotCount > 1
          ? `Subtracting slot ${i + 1} of ${slotCount}...`
          : 'Subtracting slot from target...';
        this.cdr.detectChanges();

        // Perform boolean subtraction
        const result = await this.cadKernel.booleanSubtract(currentTarget, slotShape);

        if (!result) {
          // Clean up the slot shape
          this.cadKernel.deleteShape(slotShape.id);
          throw new Error(`Boolean subtraction failed for slot ${i + 1}`);
        }

        // Clean up the previous target (except for the very first one which we handle at the end)
        if (i > 0) {
          // Remove intermediate result from scene and kernel
          this.removeShapeFromScene(currentTarget.id);
          this.cadShapes = this.cadShapes.filter((s) => s.id !== currentTarget.id);
          this.cadKernel.deleteShape(currentTarget.id);
        }

        // Delete the temporary slot shape from kernel
        this.cadKernel.deleteShape(slotShape.id);

        // Update current target for next iteration
        currentTarget = result;
      }

      // Final cleanup and result handling
      // Preserve the original target's name
      currentTarget.name = originalTargetName;

      // Remove the original target from scene, array, and kernel
      this.removeShapeFromScene(originalTargetId);
      this.cadShapes = this.cadShapes.filter((s) => s.id !== originalTargetId);
      this.cadKernel.deleteShape(originalTargetId);

      // Add final result
      this.cadShapes.push(currentTarget);
      this.addShapeToScene(currentTarget);

      console.log('CADToolbar: Slot operation completed', {
        resultId: currentTarget.id,
        resultName: currentTarget.name,
        slotsProcessed: slotCount,
      });

      this.session.pubsub.next({
        type: 'cad-slot-complete',
        result: { id: currentTarget.id, name: currentTarget.name, slotsProcessed: slotCount },
      });
    } catch (error: any) {
      console.error('CADToolbar: Slot operation failed:', error);
      this.session.pubsub.next({
        type: 'cad-slot-error',
        error: error.message || 'Slot operation failed',
      });
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle AI-requested delete geometry operation
   * Finds shape by ID or name and removes it from the scene
   */
  private handleAIDeleteGeometry(request: any): void {
    const { geometryId, geometryName } = request;
    const searchId = geometryId || geometryName;

    // Log available shapes for debugging
    const availableShapes = this.cadShapes.map(s => ({ id: s.id, name: s.name }));
    console.log('CADToolbar: Processing delete geometry request', {
      geometryId,
      geometryName,
      availableShapes
    });

    if (!searchId) {
      console.error('CADToolbar: No geometry ID or name provided for delete');
      this.session.pubsub.next({
        type: 'cad-delete-error',
        error: 'No geometry ID or name provided for delete',
      });
      return;
    }

    // Find shape by ID or name (case-insensitive)
    const searchLower = searchId.toLowerCase();
    const shape = this.cadShapes.find(
      (s) => s.id === searchId ||
             s.id?.toLowerCase() === searchLower ||
             s.name?.toLowerCase() === searchLower
    );

    if (!shape) {
      // Shape not found - may have already been deleted (e.g., by a replacement operation)
      // Log as warning, not error, to handle graceful cleanup scenarios
      console.warn('CADToolbar: Shape not found for delete (may already be removed):', searchId);
      this.session.pubsub.next({
        type: 'cad-delete-complete',
        result: { id: searchId, name: geometryName || searchId, alreadyDeleted: true },
      });
      return;
    }

    try {
      // Remove from scene
      this.removeShapeFromScene(shape.id);

      // Remove from cadShapes array
      this.cadShapes = this.cadShapes.filter((s) => s.id !== shape.id);

      // Remove from kernel
      this.cadKernel.deleteShape(shape.id);
      this.hasUnsavedChanges = true;

      console.log('CADToolbar: Shape deleted successfully', {
        id: shape.id,
        name: shape.name,
        remainingShapes: this.cadShapes.length
      });

      this.session.pubsub.next({
        type: 'cad-delete-complete',
        result: { id: shape.id, name: shape.name },
      });

      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('CADToolbar: Delete geometry failed:', error);
      this.session.pubsub.next({
        type: 'cad-delete-error',
        error: error.message || 'Delete geometry failed',
      });
    }
  }

  /**
   * Handle project_status WebSocket messages
   */
  private handleProjectStatusMessage(data: any): void {
    switch (data.action) {
      case 'browser_cad_complete':
        this.handleBrowserCADComplete(data);
        break;
      case 'browser_cad_batch_complete':
        this.handleBrowserCADBatchComplete(data);
        break;
      case 'browser_cad_error':
        this.handleBrowserCADError(data);
        break;
    }
  }

  /**
   * Handle browser CAD completion
   */
  private async handleBrowserCADComplete(data: any): Promise<void> {
    const { partId, partName, assemblyProtoUrl } = data;
    if (!partId) return;

    this.pendingUploads.set(partId, 'complete');

    // Resolve the upload promise
    const resolver = this.uploadResolvers.get(partId);
    if (resolver) {
      resolver.resolve();
      this.uploadResolvers.delete(partId);
    }

    // Add the part to the engine's model list
    if (assemblyProtoUrl && this.engine) {
      try {
        const protoData = await lastValueFrom(
          this.http.get(assemblyProtoUrl, { responseType: 'arraybuffer' })
        );
        await this.engine.addGeneratedShapePart(protoData, partId, partName || 'CAD Part');
      } catch (error) {}
    }

    this.cdr.detectChanges();
  }

  /**
   * Handle browser CAD batch completion
   */
  private handleBrowserCADBatchComplete(data: any): void {
    const { processedParts, failedParts, assemblyProtoUrl } = data;

    // Resolve the batch completion promise
    const resolver = (this as any)._batchCompletionResolver;
    if (resolver) {
      resolver.resolve({
        processedParts: processedParts || [],
        failedParts: failedParts || [],
        assemblyProtoUrl: assemblyProtoUrl
      });
    }

    this.cdr.detectChanges();
  }

  /**
   * Handle browser CAD error
   */
  private handleBrowserCADError(data: any): void {
    const { partId, partIds, error } = data;

    // Handle both single part and batch errors
    const errorPartIds = partIds || (partId ? [partId] : []);

    for (const id of errorPartIds) {
      this.pendingUploads.set(id, 'error');

      // Reject individual upload promise if exists
      const resolver = this.uploadResolvers.get(id);
      if (resolver) {
        resolver.reject(error || 'Unknown error');
        this.uploadResolvers.delete(id);
      }
    }

    // Reject batch completion promise if exists
    const batchResolver = (this as any)._batchCompletionResolver;
    if (batchResolver) {
      batchResolver.reject(new Error(error || 'Batch processing failed'));
    }

    this.onError.emit(`Backend sync failed: ${error}`);
    this.cdr.detectChanges();
  }

  /**
   * Load saved CAD parts from project (called on init and when entering edit mode)
   */
  private async loadSavedCADParts(): Promise<void> {
    // Only load if we have a session and edit mode is enabled (or will be enabled)
    if (this.session?.project?.cadParts?.length && this.cadShapes.length === 0) {
      await this.loadCADParts();
    }
  }

  /**
   * Keyboard shortcut handler for Ctrl+S (save)
   */
  @HostListener('document:keydown.control.s', ['$event'])
  onCtrlS(event: Event): void {
    // Always prevent default in edit mode to block browser's "save page" dialog
    if (this.isEditMode) {
      event.preventDefault();
    }

    // Check if user is typing in an input field
    const activeEl = document.activeElement;
    const activeTag = activeEl?.tagName?.toLowerCase() || '';
    if (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select') {
      return; // Don't save while typing
    }

    // Check event target
    const target = event.target as HTMLElement;
    if (target && this.isInputElement(target)) {
      return; // Don't save while in form control
    }

    // Skip if modal is open
    if (this.isModalOpen()) {
      return;
    }

    // Now save if there are unsaved changes
    if (this.isEditMode && this.hasUnsavedChanges) {
      this.saveCADParts();
    }
  }

  ngOnDestroy(): void {
    this.session.pubsub.emit({ type: 'cad-toolbar-closed' });
    this.embeddedBridge.setDirty('cad', false);
    // CRITICAL: Symmetric cleanup - exit edit mode if active
    // This restores engine objects, resets CADEditorEnabled, etc.
    if (this.isEditMode) {
      this.toggleEditMode(false);
    }

    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.stopAssistantFacePickMode();
    this.updateAssistantPickHighlights([]);
    this.cleanupInteraction();
    this.removeCADGroup();
    this.cleanupToolbarPositioning();
    this.disableSectionPlane();

    // Remove beforeunload handler
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }

    // Clean up document-level drag handlers for push/pull
    if (this.pushPullDragMoveHandler) {
      document.removeEventListener('mousemove', this.pushPullDragMoveHandler, true);
      this.pushPullDragMoveHandler = null;
    }
    if (this.pushPullDragUpHandler) {
      document.removeEventListener('mouseup', this.pushPullDragUpHandler, true);
      this.pushPullDragUpHandler = null;
    }

    // Clean up document-level drag handlers for boss/pocket
    if (this.bossPocketDragMoveHandler) {
      document.removeEventListener('mousemove', this.bossPocketDragMoveHandler, true);
      this.bossPocketDragMoveHandler = null;
    }
    if (this.bossPocketDragUpHandler) {
      document.removeEventListener('mouseup', this.bossPocketDragUpHandler, true);
      this.bossPocketDragUpHandler = null;
    }

    // Clean up shared GPU resources
    if (this.sharedEdgeMaterial) {
      this.sharedEdgeMaterial.dispose();
      this.sharedEdgeMaterial = null;
    }
    this.cachedCADMeshes = null;

    // Clean up pending timers
    if (this.snapUpdateTimer) {
      clearTimeout(this.snapUpdateTimer);
      this.snapUpdateTimer = null;
    }

    // Clean up thicken direction arrow
    this.cleanupThickenDirectionArrow();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TOOLBAR POSITIONING
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Setup toolbar positioning to follow sidenav-block-content-flow width changes
   */
  private setupToolbarPositioning(): void {
    // Initial position update with delay to ensure DOM is ready
    requestAnimationFrame(() => {
      this.updateToolbarPosition();
    });

    // Observe the flow panel for resize
    const flowPanel = document.querySelector('sidenav-block-content-flow');
    if (flowPanel) {
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          this.updateToolbarPosition();
        });
      });
      this.resizeObserver.observe(flowPanel);
    } else {
      // Retry after a short delay if not found
      setTimeout(() => this.setupToolbarPositioning(), 100);
      return;
    }

    // Listen to custom resize events for immediate updates during drag
    this.resizeEventListener = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.width) {
        this.updateToolbarPosition();
      }
    };
    window.addEventListener('sidenav-flow-resize', this.resizeEventListener);
  }

  /**
   * Update toolbar left position based on flow panel
   */
  private updateToolbarPosition(): void {
    const flowPanel = document.querySelector('sidenav-block-content-flow');
    const cadToolbar = document.querySelector('.cad-toolbar');
    if (flowPanel) {
      const flowRect = flowPanel.getBoundingClientRect();
      if (flowRect.width > 0) {
        // Position 16px from the right edge of the flow panel
        this.toolbarLeft = flowRect.right + 8;
        if (cadToolbar) {
          this.toolbarHeight = cadToolbar.getBoundingClientRect().height + 16;
        }
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Cleanup positioning observers and listeners
   */
  private cleanupToolbarPositioning(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeEventListener) {
      window.removeEventListener('sidenav-flow-resize', this.resizeEventListener);
      this.resizeEventListener = null;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Setup the CAD group in the scene
   */
  private setupCADGroup(): void {
    if (!this.engine) return;

    this.cadGroup.name = 'CADEditorGroup';
    this.engine.scene.add(this.cadGroup);
  }

  /**
   * Remove the CAD group from scene
   */
  private removeCADGroup(): void {
    if (!this.engine) return;

    // Clean up all meshes
    this.sceneMeshes.forEach((group, id) => {
      this.removeShapeFromScene(id);
    });

    this.engine.scene.remove(this.cadGroup);
  }

  /**
   * Add a CAD shape to the Three.js scene
   */
  private addShapeToScene(shape: CADShape): void {
    if (!shape.mesh || !this.engine) return;

    // Calculate bounding box to find the center of the geometry
    shape.mesh.computeBoundingBox();
    const boundingBox = shape.mesh.boundingBox;
    const center = new Vector3();

    if (boundingBox) {
      boundingBox.getCenter(center);
    }

    // Create a group for this shape (mesh + edges)
    // The group will be positioned at the geometry center
    const shapeGroup = new Group();
    shapeGroup.name = `CAD_${shape.id}`;
    shapeGroup.userData['cadShapeId'] = shape.id;
    shapeGroup.userData['isCADShape'] = true;

    // Position the group at the geometry center
    shapeGroup.position.copy(center);

    // Create the solid mesh with polygon offset to push it back in depth buffer
    // This helps edges render on top without z-fighting
    // Using MeshPhysicalMaterial for better visual quality and clearer hole visibility
    const isSurface = (shape as any).isSurface === true;
    // Compute a slightly lighter sheen color for edge highlights
    const baseColor = new Color(shape.color || '#6366f1');
    const sheenColor = baseColor.clone().lerp(new Color(0xffffff), 0.3);

    const material = new MeshPhysicalMaterial({
      color: baseColor,
      metalness: 0.3,
      roughness: 0.35,
      clearcoat: 0.6,
      clearcoatRoughness: 0.2,
      reflectivity: 0.5,
      transparent: false,
      polygonOffset: true,
      polygonOffsetFactor: 2, // Push geometry back so edges are visible
      polygonOffsetUnits: 2,
      envMap: this.engine?.envMap,
      envMapIntensity: 0.8,
      // Sheen creates soft edge highlights on curved surfaces (like fabric sheen)
      // This helps visualize curvature on cylinders/spheres where no hard edges exist
      sheen: 0.5,
      sheenRoughness: 0.4,
      sheenColor: sheenColor,
      side: isSurface ? DoubleSide : FrontSide // Surfaces need DoubleSide to render from both directions
    });

    const mesh = new Mesh(shape.mesh, material);
    mesh.name = `CAD_Mesh_${shape.id}`;
    mesh.userData['cadShapeId'] = shape.id;
    mesh.userData['isCADShape'] = true;
    mesh.userData['sourceType'] = 'cad';  // Required for face selection lookup
    mesh.userData['sourceId'] = shape.id;  // Required for face selection lookup
    mesh.renderOrder = 0; // Render first

    // Compute BVH for accelerated raycasting (fire-and-forget async)
    if (this.engine && shape.mesh.getIndex()) {
      this.engine.computeBVHAsync(shape.mesh).catch(() => {
        // Silently handle BVH computation failures - raycasting will still work, just slower
      });
    }

    // Offset the mesh so that the group origin is at the geometry center
    mesh.position.sub(center);
    shapeGroup.add(mesh);

    // Create edge lines container (edges extracted async for performance)
    const edgesGroup = new Group();
    edgesGroup.name = `CAD_Edges_${shape.id}`;
    edgesGroup.userData['isEdgesGroup'] = true;

    // Add placeholder edges immediately using fast EdgesGeometry
    const placeholderEdgesGeometry = new EdgesGeometry(shape.mesh, 15);
    const placeholderEdgesMaterial = new LineBasicMaterial({
      color: 0x000000,
      linewidth: 2,
      depthTest: true,  // Use depth test - mesh has polygonOffset to push it back
      depthWrite: false
    });
    const placeholderEdges = new LineSegments(placeholderEdgesGeometry, placeholderEdgesMaterial);
    placeholderEdges.name = `CAD_Edges_Placeholder_${shape.id}`;
    placeholderEdges.renderOrder = 999;
    edgesGroup.add(placeholderEdges);

    // Offset edges so that the group origin is at the geometry center
    edgesGroup.position.sub(center);
    shapeGroup.add(edgesGroup);

    // Store the center offset for later use
    shapeGroup.userData['geometryCenter'] = center.clone();

    // Store face data from worker for face selection
    if (shape.triangleToFaceMap) {
      shapeGroup.userData['triangleToFaceMap'] = Array.from(shape.triangleToFaceMap);
    }
    if (shape.extractedFaces) {
      shapeGroup.userData['extractedFaces'] = shape.extractedFaces;
    }

    // Defer edge rendering to next frame for better UI responsiveness
    const shapeId = shape.id;
    const shapeEdges = shape.edges;
    const workerTriangleToFaceMap = shape.triangleToFaceMap;
    const workerExtractedFaces = shape.extractedFaces;
    setTimeout(() => {
      this.renderEdgesAsync(shapeEdges, shapeId, shapeGroup, edgesGroup, placeholderEdges, center, workerTriangleToFaceMap, workerExtractedFaces);
    }, 0);

    // Add to CAD group
    this.cadGroup.add(shapeGroup);
    this.sceneMeshes.set(shape.id, shapeGroup);
    this.invalidateCADMeshCache();

    // Apply clipping plane if section view is active
    if (this.sectionViewEnabled && this.sectionPlane) {
      shapeGroup.traverse((obj) => {
        // Apply to meshes
        if (obj instanceof Mesh && obj.material) {
          const material = obj.material as MeshPhysicalMaterial;
          material.clippingPlanes = [this.sectionPlane!];
          material.clipShadows = true;
          material.needsUpdate = true;
        }
        // Apply to edges (LineSegments)
        if (obj instanceof LineSegments && obj.material) {
          const material = obj.material as LineBasicMaterial;
          material.clippingPlanes = [this.sectionPlane!];
          material.needsUpdate = true;
        }
        // Apply to Line2 (fat lines)
        if (obj instanceof Line2 && obj.material) {
          const material = obj.material as LineMaterial;
          material.clippingPlanes = [this.sectionPlane!];
          material.needsUpdate = true;
        }
      });

      // Recreate stencil to include new shape
      this.createSectionStencil();
    }

    // Schedule snap engine update (debounced to avoid O(N²) on bulk imports)
    this.scheduleSnapUpdate();
  }

  /**
   * Render edges asynchronously from pre-computed edge data and replace placeholder edges
   * Called from setTimeout to not block initial mesh rendering
   * @param edges Pre-computed edge data from worker
   * @param workerTriangleToFaceMap Optional pre-computed triangle-to-face map from worker
   * @param workerExtractedFaces Optional pre-computed face data from worker
   */
  private renderEdgesAsync(
    edges: { edgeIndex: number; points: number[] }[] | undefined,
    shapeId: string,
    shapeGroup: Group,
    edgesGroup: Group,
    placeholderEdges: LineSegments,
    center: Vector3,
    workerTriangleToFaceMap?: Uint32Array,
    workerExtractedFaces?: { faceIndex: number; vertices: number[]; indices: number[]; center: [number, number, number]; normal: [number, number, number] }[]
  ): void {
    // Verify shape still exists (may have been deleted)
    if (!this.sceneMeshes.has(shapeId)) return;

    try {
      // Use pre-computed edges from worker (flat array format)
      if (!edges || edges.length === 0) {
        console.warn('renderEdgesAsync: No edge data available for shape', shapeId);
        return;
      }

      // Get or create shared edge material (reused across all edges for performance)
      if (!this.sharedEdgeMaterial) {
        const canvasWidth = this.engine?.canvas?.clientWidth || 1920;
        const canvasHeight = this.engine?.canvas?.clientHeight || 1080;
        this.sharedEdgeMaterial = new LineMaterial({
          color: 0x000000,
          linewidth: 2,
          resolution: new Vector2(canvasWidth, canvasHeight),
          depthTest: true,  // Use depth test - mesh has polygonOffset to push it back
          depthWrite: false
        });
      }

      // Create edge lines from pre-computed data (reusing shared material)
      edges.forEach((edge, index) => {
        // Edge points are in flat array format [x1,y1,z1, x2,y2,z2, ...]
        if (edge.points.length >= 6) { // At least 2 points (6 values)
          const edgeGeometry = new LineGeometry();
          edgeGeometry.setPositions(edge.points);

          const line = new Line2(edgeGeometry, this.sharedEdgeMaterial!);
          line.name = `CAD_Edge_${shapeId}_${index}`;
          line.userData['edgeIndex'] = edge.edgeIndex;
          line.userData['cadShapeId'] = shapeId;
          line.computeLineDistances();
          line.renderOrder = 999;  // Render on top of mesh
          edgesGroup.add(line);
        }
      });

      // Remove placeholder edges now that real edges are added
      edgesGroup.remove(placeholderEdges);
      placeholderEdges.geometry?.dispose();
      (placeholderEdges.material as LineBasicMaterial)?.dispose();

      // Store edge data for later use (edge selection)
      shapeGroup.userData['extractedEdges'] = edges;

      // Handle face data for face selection (worker-only architecture)
      // Use pre-computed data from worker
      if (workerExtractedFaces) {
        shapeGroup.userData['extractedFaces'] = workerExtractedFaces;
      }
      if (workerTriangleToFaceMap) {
        shapeGroup.userData['triangleToFaceMap'] = Array.from(workerTriangleToFaceMap);
      }

      // Refresh snap data now that extracted edges are available (debounced)
      this.scheduleSnapUpdate();
    } catch (e) {
      // Keep placeholder edges if rendering fails
      console.warn('Edge rendering failed, using placeholder edges');
    }
  }

  /**
   * Remove a CAD shape from the scene
   */
  private removeShapeFromScene(shapeId: string): void {
    const group = this.sceneMeshes.get(shapeId);
    if (!group) return;

    // If this shape is involved in an active operation AND we're not in the middle of
    // applying an operation (isProcessing), cancel the operation.
    // During apply flows, we intentionally remove the old shape to replace it with the new one.
    if (this.activeOperation && this.selectedCADShapeIds.has(shapeId) && !this.isProcessing) {
      this.exitOperationMode(true);
    }

    // If transform controls are attached to this shape, detach them first
    if (this.transformControls?.object === group) {
      this.detachTransformControls();
    }

    // Dispose of all children (geometry and non-shared materials)
    group.traverse((obj) => {
      // Handle Line2 (fat lines for edges) - dispose geometry but NOT shared material
      if (obj instanceof Line2) {
        obj.geometry?.dispose();
        // Don't dispose material - it's the shared sharedEdgeMaterial
        return;
      }
      if (obj instanceof Mesh) {
        obj.geometry?.dispose();
        if (obj.material instanceof MeshPhysicalMaterial) {
          obj.material.dispose();
        }
      }
      if (obj instanceof LineSegments) {
        obj.geometry?.dispose();
        if (obj.material instanceof LineBasicMaterial) {
          obj.material.dispose();
        }
      }
    });

    // Remove from parent (could be cadGroup or multiSelectPivot)
    if (group.parent) {
      group.parent.remove(group);
    }
    // Also try removing from cadGroup in case parent reference is stale
    this.cadGroup.remove(group);
    this.sceneMeshes.delete(shapeId);
    this.invalidateCADMeshCache();

    // Update snap engine after removal (debounced)
    this.scheduleSnapUpdate();
  }

  /**
   * Update visual state when shape is selected/deselected
   * Uses different colors for single vs multi-selection
   */
  private updateShapeVisualState(shapeId: string, selected: boolean): void {
    const group = this.sceneMeshes.get(shapeId);
    if (!group) return;

    const isMultiSelect = this.selectedCADShapeIds.size > 1;

    group.traverse((obj) => {
      if (obj instanceof Mesh && obj.material instanceof MeshPhysicalMaterial) {
        if (selected) {
          if (isMultiSelect) {
            // Orange/amber tint for multi-selected shapes
            obj.material.emissive = new Color(0xcc6600);
            obj.material.emissiveIntensity = 0.7;
          } else {
            // Green tint for single selected shape
            obj.material.emissive = new Color(0x2a5500);
            obj.material.emissiveIntensity = 0.8;
          }
        } else {
          obj.material.emissive = new Color(0x000000);
          obj.material.emissiveIntensity = 1;
        }
      }
    });
  }

  /**
   * Refresh visual state for all selected shapes
   * Called when selection count changes to update colors (single vs multi)
   */
  private refreshSelectedShapesVisualState(): void {
    for (const shapeId of this.selectedCADShapeIds) {
      this.updateShapeVisualState(shapeId, true);
    }
  }

  /**
   * Update visual state for hover
   * Cyan highlight for unselected shapes on hover
   */
  private updateShapeHoverState(shapeId: string | null): void {
    // Reset previous hover
    if (this.hoveredShapeId && this.hoveredShapeId !== shapeId) {
      const prevGroup = this.sceneMeshes.get(this.hoveredShapeId);
      if (prevGroup && !this.selectedCADShapeIds.has(this.hoveredShapeId)) {
        prevGroup.traverse((obj) => {
          if (obj instanceof Mesh && obj.material instanceof MeshPhysicalMaterial) {
            obj.material.emissive = new Color(0x000000);
            obj.material.emissiveIntensity = 1;
          }
        });
      }
    }

    // Set new hover - bright cyan highlight (only for non-selected shapes)
    if (shapeId && !this.selectedCADShapeIds.has(shapeId)) {
      const group = this.sceneMeshes.get(shapeId);
      if (group) {
        group.traverse((obj) => {
          if (obj instanceof Mesh && obj.material instanceof MeshPhysicalMaterial) {
            // Bright cyan tint for hover
            obj.material.emissive = new Color(0x00aacc);
            obj.material.emissiveIntensity = 0.9;
          }
        });
      }
    }

    this.hoveredShapeId = shapeId;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INTERACTION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Setup mouse interaction handlers
   */
  private setupInteraction(): void {
    if (!this.engine?.canvas) return;

    this.boundOnClick = this.onCanvasClick.bind(this);
    this.boundOnMouseMove = this.onCanvasMouseMove.bind(this);
    this.boundOnDblClick = this.onCanvasDblClick.bind(this);
    this.boundOnMouseLeave = this.onCanvasMouseLeave.bind(this);

    this.engine.canvas.addEventListener('click', this.boundOnClick);
    this.engine.canvas.addEventListener('mousemove', this.boundOnMouseMove);
    this.engine.canvas.addEventListener('dblclick', this.boundOnDblClick);
    this.engine.canvas.addEventListener('mouseleave', this.boundOnMouseLeave);

    // Setup transform controls
    this.setupTransformControls();
  }

  /**
   * Cleanup interaction handlers
   */
  private cleanupInteraction(): void {
    if (!this.engine?.canvas) return;

    // Remove transform controls
    this.disposeTransformControls();

    // Cleanup all modal interaction modes
    this.cleanupPushPullFacePicking();
    this.cleanupEdgePickingHandlers();
    this.cleanupShellFaceHandlers();
    this.cleanupFacePickingHandlers();
    this.cleanupDrawingHandlers();
    this.removeEntityEditHandlers();

    if (this.boundOnClick) {
      this.engine.canvas.removeEventListener('click', this.boundOnClick);
    }
    if (this.boundOnMouseMove) {
      this.engine.canvas.removeEventListener('mousemove', this.boundOnMouseMove);
    }
    if (this.boundOnDblClick) {
      this.engine.canvas.removeEventListener('dblclick', this.boundOnDblClick);
    }
    if (this.boundOnMouseLeave) {
      this.engine.canvas.removeEventListener('mouseleave', this.boundOnMouseLeave);
    }
  }

  /**
   * Handle double-click for parametric editing
   */
  private onCanvasDblClick(event: MouseEvent): void {
    if (!this.isEditMode || !this.engine) return;

    const hit = this.raycastCADShapes(event);
    if (hit) {
      const shapeId = hit.object.userData['cadShapeId'];
      if (shapeId) {
        // Double-click opens rename
        this.startRenameShape(shapeId);
      }
    }
  }

  /**
   * Handle canvas click for shape selection
   */
  private onCanvasClick(event: MouseEvent): void {
    if (!this.isEditMode || !this.engine || this.isTransforming || this.recentlyTransformed) return;

    // Skip if in drawing mode - drawing handlers take over
    if (this.drawingMode !== 'none') return;

    // Skip if in sketch mode - sketch entities take precedence, not CAD bodies
    if (this.isSketchMode) return;

    // Skip if in extrusion drag mode - extrusion handlers take over
    if (this.isExtrusionDragMode) return;

    // Skip if in face picking mode - face picking handlers take over
    if (this.isFacePickingMode || this.isExtractFacePickingMode) return;

    const hit = this.raycastCADShapes(event);

    if (hit) {
      const shapeId = hit.object.userData['cadShapeId'];
      if (shapeId) {
        // Ctrl+click for multi-select
        if (event.ctrlKey || event.metaKey) {
          this.toggleCADShapeSelection(shapeId);
        } else {
          // Single select
          this.selectedCADShapeIds.clear();
          this.booleanSelectionOrder = [];
          this.selectedCADShapeIds.add(shapeId);
          this.booleanSelectionOrder.push(shapeId);
          // Clean up arrow when changing selection
          this.cleanupThickenDirectionArrow();
        }

        // Update visuals
        this.sceneMeshes.forEach((_, id) => {
          this.updateShapeVisualState(id, this.selectedCADShapeIds.has(id));
        });

        // Attach transform gizmo (supports both single and multi-selection)
        if (this.selectedCADShapeIds.size > 0) {
          this.attachTransformControls();
        } else {
          this.detachTransformControls();
        }

        // Update measurements
        this.updateMeasurements();

        this.cdr.detectChanges();
      }
    } else {
      // Clicked on empty space - cancel modes or clear selection
      if (!event.ctrlKey && !event.metaKey) {
        // If in push/pull drag mode, ignore outside clicks (user can use Esc to cancel)
        if (this.isPushPullDragMode) {
          return;
        }
        // Cancel push/pull face picking mode (before face is selected)
        if (this.isPushPullMode && !this.isPushPullDragMode) {
          this.exitOperationMode(true);
          return;
        }
        // Cancel boss/pocket mode - use exitOperationMode to properly clean up
        if (this.isExtrudeOnFaceMode) {
          this.exitOperationMode(true);
          return;
        }
        // Clear selection
        this.selectedCADShapeIds.clear();
        this.booleanSelectionOrder = [];
        this.sceneMeshes.forEach((_, id) => {
          this.updateShapeVisualState(id, false);
        });
        this.detachTransformControls();
        this.cleanupThickenDirectionArrow();
        this.updateMeasurements();
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Handle canvas mouse move for hover effect
   */
  private onCanvasMouseMove(event: MouseEvent): void {
    if (!this.isEditMode || !this.engine) return;

    // Skip body hover when in sketch mode
    if (this.isSketchMode) return;

    // Skip body hover when in extrusion drag mode
    if (this.isExtrusionDragMode) return;

    // Throttle raycasting using requestAnimationFrame for performance
    this.lastMouseEvent = event;
    if (!this.rafPending) {
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.rafPending = false;
        if (this.lastMouseEvent) {
          this.performRaycast(this.lastMouseEvent);
        }
      });
    }
  }

  /**
   * Perform the actual raycast (called from rAF throttle)
   */
  private performRaycast(event: MouseEvent): void {
    if (!this.engine) return;

    const hit = this.raycastCADShapes(event);

    if (hit) {
      const shapeId = hit.object.userData['cadShapeId'];
      this.updateShapeHoverState(shapeId);
      this.engine.canvas.style.cursor = 'pointer';
    } else {
      this.updateShapeHoverState(null);
      this.engine.canvas.style.cursor = 'default';
    }
  }

  /**
   * Handle canvas mouse leave - cleanup tooltips and feedback
   */
  private onCanvasMouseLeave(_event: MouseEvent): void {
    // Hide snap feedback when mouse leaves canvas
    this.hideSnapFeedback();
    this.hideSnapIndicator();

    // Reset hover state
    this.updateShapeHoverState(null);

    // Clear drawing dimensions if not actively drawing
    if (!this.isDrawing) {
      this.drawingDimensions = null;
    }

    this.cdr.detectChanges();
  }

  /**
   * Raycast against CAD shapes
   */
  private raycastCADShapes(event: MouseEvent): any {
    if (!this.engine) return null;

    const rect = this.engine.canvas.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.engine.camera);

    // Use cached mesh list for performance (rebuilt only when shapes change)
    if (!this.cachedCADMeshes) {
      this.cachedCADMeshes = [];
      this.cadGroup.traverse((obj) => {
        if (obj instanceof Mesh && obj.userData['isCADShape']) {
          this.cachedCADMeshes!.push(obj);
        }
      });
    }

    const intersects = this.raycaster.intersectObjects(this.cachedCADMeshes, false);
    return intersects.length > 0 ? intersects[0] : null;
  }

  /**
   * Invalidate the cached CAD mesh list (call when shapes are added/removed)
   */
  private invalidateCADMeshCache(): void {
    this.cachedCADMeshes = null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EDIT MODE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Toggle edit mode
   */
  toggleEditMode(forceState?: boolean): void {
    const newMode = forceState !== undefined ? forceState : !this.isEditMode;
    this.isEditMode = newMode;
    this.onEditModeChange.emit(newMode);

    if (newMode) {
      // Entering edit mode - hide all engine objects and initialize CAD kernel
      if (this.engine) {
        this.engine.hideAllObjectsForCADEditor();
      }
      this.initializeKernel();
    } else {
      // Exiting edit mode - detach transform controls and clear selection
      this.stopAssistantFacePickMode();
      this.detachTransformControls();
      this.selectedCADShapeIds.clear();
      this.sceneMeshes.forEach((_, id) => {
        this.updateShapeVisualState(id, false);
      });
      // Clean up thicken direction arrow
      this.cleanupThickenDirectionArrow();
      // Clean up sketch plane
      this.sketchPlane = null;
      this.cleanupSketchPlane();
      // Close any open panels
      this.activePanel = null;
      // Exit sketch mode
      this.finishSketchEditing();
      // Restore all engine objects that were hidden
      if (this.engine) {
        this.engine.restoreAllObjectsForCADEditor();
      }
    }

    if (this.engine && this.engine.selector) this.engine.selector.CADEditorEnabled = newMode;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SKETCH MODE METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a new sketch
   */
  onCreateNewSketch(): void {
    // Use current sketch plane if set, otherwise default to XY
    const plane = this.sketchPlane || 'XY';

    const sketch = this.sketchService.createSketch(
      plane === 'custom' ? 'custom' : plane,
      undefined,
      plane === 'custom'
        ? {
            planeOffset: this.sketchPlaneOffset,
            planeOrigin: this.customPlaneOrigin,
            planeNormal: this.customPlaneNormal,
            planeXAxis: this.customPlaneXAxis,
            planeYAxis: this.customPlaneYAxis
          }
        : { planeOffset: this.sketchPlaneOffset }
    );

    // Activate the sketch for editing
    this.enterSketchMode(sketch.id);
  }

  /**
   * Enter sketch editing mode
   */
  // Sketch entity click handler
  private boundSketchEntityClickHandler: ((event: MouseEvent) => void) | null = null;

  enterSketchMode(sketchId: string): void {
    const sketch = this.sketchService.getSketch(sketchId);
    if (!sketch) {
      return;
    }

    // Clean up any existing state first (in case we were viewing profiles)
    this.removeSketchEntityClickHandler();
    this.clearSketchEntityMeshes();
    this.clearSketchProfileMeshes();
    this.removeSketchPlaneVisual();

    // Now set up the new sketch editing mode
    this.sketchService.setActiveSketch(sketchId);
    this.activeSketch = sketch;
    this.isSketchMode = true;
    this.selectedProfileIds.clear();

    // Set up the sketch plane for drawing
    if (sketch.plane === 'custom') {
      this.sketchPlane = 'custom';
      this.customPlaneOrigin = sketch.planeOrigin;
      this.customPlaneNormal = sketch.planeNormal;
      this.customPlaneXAxis = sketch.planeXAxis;
      this.customPlaneYAxis = sketch.planeYAxis;

      // Update snap engine with custom plane
      this.snapEngine.setSketchPlane(
        'custom',
        sketch.planeNormal as [number, number, number],
        sketch.planeOrigin as [number, number, number],
        sketch.planeXAxis as [number, number, number],
        sketch.planeYAxis as [number, number, number]
      );
    } else {
      this.sketchPlane = sketch.plane;
      // Update snap engine with standard plane
      this.snapEngine.setSketchPlane(sketch.plane);
    }
    this.sketchPlaneOffset = sketch.planeOffset;

    // Setup sketch plane visualization (only for active sketch)
    this.updateSketchPlaneVisual();

    // Setup sketch entity click handler
    this.setupSketchEntityClickHandler();

    // Render only the active sketch entities (other sketches are hidden in edit mode)
    this.renderSketchEntities();

    // Dim 3D bodies
    this.dimCADBodies(true);

    this.cdr.detectChanges();
  }

  /**
   * Exit sketch editing mode (but keep sketch selected for extrusion)
   */
  finishSketchEditing(): void {
    // Store the sketch reference before clearing edit mode
    const sketchForExtrusion = this.activeSketch;

    // End entity editing mode and clean up handles
    if (this.isEntityEditMode) {
      this.endEntityEdit();
    }

    // Always clean up entity handles (even if not in edit mode)
    this.removeEntityHandles();
    this.removeEntityEditHandlers();

    // Cancel any ongoing drawing operation
    if (this.isDrawing) {
      this.cancelDrawing();
    }

    // Close dimension input if open
    if (this.showDimensionInput) {
      this.closeDimensionInput(false); // Don't double-cancel drawing
    }

    // Clear extrusion drag mode and all related visuals
    if (this.isExtrusionDragMode) {
      this.isExtrusionDragMode = false;
      this.removeExtrusionDragHandlers(); // Remove document-level handlers
      this.clearExtrusionDragPreview();
    }

    // Clear edit mode in service but don't clear our local reference
    this.sketchService.setActiveSketch(null);
    this.isSketchMode = false;

    // Remove sketch entity click handler
    this.removeSketchEntityClickHandler();

    // Clear sketch entity visualizations (shapes)
    this.clearSketchEntityMeshes();

    // Clear profile visualizations (will re-render below)
    this.clearSketchProfileMeshes();

    // Clean up sketch plane visual
    this.removeSketchPlaneVisual();

    // Clear sketch snap points
    this.snapEngine.clearSketchSnapPoints();

    // Reset sketch plane selection
    this.sketchPlane = null;
    this.sketchPlaneOffset = 0;
    this.customPlaneOrigin = [0, 0, 0];
    this.customPlaneNormal = [0, 0, 1];
    this.customPlaneXAxis = [1, 0, 0];
    this.customPlaneYAxis = [0, 1, 0];
    this.selectedFaceInfo = null;

    // Restore 3D bodies
    this.dimCADBodies(false);

    // Clear entity selection but keep profile selection available
    this.sketchService.clearSelection();

    // Clear profile selection when exiting sketch mode
    this.selectedProfileIds.clear();

    // Render all visible sketches (without plane/grid)
    this.renderAllVisibleSketches();

    // Keep activeSketch for extrusion panel (if it has profiles)
    if (sketchForExtrusion && sketchForExtrusion.profiles.length > 0) {
      // Re-fetch from service to get updated profiles
      const updatedSketch = this.sketchService.getSketch(sketchForExtrusion.id);
      this.activeSketch = updatedSketch ?? null;
    } else {
      this.activeSketch = null;
      this.selectedProfileIds.clear();
    }

    this.cdr.detectChanges();
  }

  /**
   * Set up sketch plane properties from a sketch object
   */
  private setupSketchPlaneFromSketch(sketch: Sketch): void {
    this.sketchPlane = sketch.plane;
    this.sketchPlaneOffset = sketch.planeOffset;
    if (sketch.plane === 'custom') {
      this.customPlaneOrigin = sketch.planeOrigin || [0, 0, 0];
      this.customPlaneNormal = sketch.planeNormal || [0, 0, 1];
      this.customPlaneXAxis = sketch.planeXAxis || [1, 0, 0];
      this.customPlaneYAxis = sketch.planeYAxis || [0, 1, 0];
    }
  }

  /**
   * Remove sketch plane visual
   */
  private removeSketchPlaneVisual(): void {
    if (this.sketchPlaneHelper && this.engine) {
      this.engine.scene.remove(this.sketchPlaneHelper);
      this.disposeObject3D(this.sketchPlaneHelper);
      this.sketchPlaneHelper = null;
    }
    if (this.sketchGridHelper && this.engine) {
      this.engine.scene.remove(this.sketchGridHelper);
      this.disposeObject3D(this.sketchGridHelper);
      this.sketchGridHelper = null;
    }
  }

  /**
   * Handle sketch selection from browser
   */
  onSketchSelected(sketchId: string): void {
    const sketch = this.sketchService.getSketch(sketchId);
    if (!sketch) return;

    // If clicking on same sketch while already editing, do nothing
    if (this.isSketchMode && this.activeSketch?.id === sketchId) return;

    // Enter sketch edit mode (this handles cleanup of previous state)
    this.enterSketchMode(sketchId);

    this.cdr.detectChanges();
  }

  /**
   * Handle sketch entity selection
   */
  onSketchEntitySelected(event: { sketchId: string; entityId: string }): void {
    // Entity selection is handled by SketchService
    // Visual update will happen through renderSketchEntities
    this.renderSketchEntities();
  }

  /**
   * Handle profile selection
   */
  onSketchProfileSelected(event: { sketchId: string; profileId: string }): void {
    // Update selected profiles set
    if (this.selectedProfileIds.has(event.profileId)) {
      this.selectedProfileIds.delete(event.profileId);
    } else {
      this.selectedProfileIds.add(event.profileId);
    }

    // Auto-enable/disable extrusion drag mode based on profile selection
    this.updateExtrusionDragModeFromSelection();

    // Update profile visualizations
    this.renderSketchProfiles();
    this.cdr.detectChanges();
  }

  // Track hovered items from tree view
  private hoveredFromTreeView: { type: string; id: string; parentId?: string } | null = null;

  /**
   * Handle tree view node hover - highlight corresponding item in scene
   */
  onSketchBrowserNodeHovered(
    event: { type: 'sketch' | 'entity' | 'profile' | 'folder'; id: string; parentId?: string } | null
  ): void {
    if (!this.engine) return;

    // Clear previous hover state
    if (this.hoveredFromTreeView) {
      this.clearSceneHover();
    }

    if (!event) {
      this.hoveredFromTreeView = null;
      return;
    }

    // Don't highlight if sketch is hidden
    if (this.activeSketch?.isVisible === false) {
      this.hoveredFromTreeView = null;
      return;
    }

    this.hoveredFromTreeView = event;

    // Highlight the corresponding item in scene
    switch (event.type) {
      case 'entity':
        // Find the sketch containing this entity and highlight it
        const sketchForEntity = event.parentId ? this.sketchService.getSketch(event.parentId) : this.activeSketch;
        if (sketchForEntity && sketchForEntity.isVisible !== false) {
          const entity = sketchForEntity.entities.find((e) => e.id === event.id);
          if (entity) {
            // Update hover state using material change (not re-render)
            this.setEntityHoveredById(event.id, true);
          }
        }
        break;

      case 'profile':
        // Highlight the profile mesh by changing color
        const profileMesh = this.sketchProfileMeshes.get(event.id);
        if (profileMesh) {
          profileMesh.traverse((child: any) => {
            if (child.material && child.material.color) {
              // Store original color if not already stored
              if (!child.userData.originalColor) {
                child.userData.originalColor = child.material.color.getHex();
              }
              // Set hover color (cyan)
              child.material.color.setHex(0x22d3ee);
              if (child.material.opacity !== undefined) {
                child.userData.originalOpacity = child.material.opacity;
                child.material.opacity = Math.min(child.material.opacity + 0.15, 1);
              }
            }
          });
        }
        break;

      case 'sketch':
        // Highlight all entities and profiles in the sketch if visible
        const sketchToHighlight = this.sketchService.getSketch(event.id);
        if (sketchToHighlight && sketchToHighlight.isVisible !== false) {
          sketchToHighlight.entities.forEach((e) => this.setEntityHoveredById(e.id, true));
        }
        break;
    }
  }

  /**
   * Clear hover state from scene items
   */
  private clearSceneHover(): void {
    if (!this.hoveredFromTreeView) return;

    switch (this.hoveredFromTreeView.type) {
      case 'entity':
        this.setEntityHoveredById(this.hoveredFromTreeView.id, false);
        break;

      case 'profile':
        const profileMesh = this.sketchProfileMeshes.get(this.hoveredFromTreeView.id);
        if (profileMesh) {
          profileMesh.traverse((child: any) => {
            if (child.material && child.material.color && child.userData.originalColor !== undefined) {
              // Restore original color
              child.material.color.setHex(child.userData.originalColor);
              if (child.userData.originalOpacity !== undefined) {
                child.material.opacity = child.userData.originalOpacity;
              }
            }
          });
        }
        break;

      case 'sketch':
        const sketchToClear = this.sketchService.getSketch(this.hoveredFromTreeView.id);
        if (sketchToClear) {
          sketchToClear.entities.forEach((e) => this.setEntityHoveredById(e.id, false));
        }
        break;
    }
  }

  /**
   * Toggle profile selection for extrusion
   */
  toggleProfileSelection(profileId: string): void {
    if (this.selectedProfileIds.has(profileId)) {
      this.selectedProfileIds.delete(profileId);
    } else {
      this.selectedProfileIds.add(profileId);
    }

    // Auto-enable/disable extrusion drag mode based on profile selection
    this.updateExtrusionDragModeFromSelection();

    this.renderSketchProfiles();
    this.cdr.detectChanges();
  }

  /**
   * Edit a sketch (enter sketch mode)
   */
  onEditSketch(sketchId: string): void {
    this.enterSketchMode(sketchId);
  }

  /**
   * Delete a sketch
   */
  onDeleteSketch(sketchId: string): void {
    // Clear all visualizations if this is the active/selected sketch
    if (this.activeSketch?.id === sketchId) {
      this.clearSketchEntityMeshes();
      this.clearSketchProfileMeshes();
      this.removeSketchPlaneVisual();
      this.removeSketchEntityClickHandler();
      this.dimCADBodies(false);
      this.activeSketch = null;
      this.isSketchMode = false;
      this.selectedProfileIds.clear();
      this.sketchService.clearSelection();
    }

    this.sketchService.deleteSketch(sketchId);
    this.cdr.detectChanges();
  }

  /**
   * Handle entity deletion from browser
   */
  onDeleteSketchEntity(event: { sketchId: string; entityId: string }): void {
    // Remove mesh from scene immediately
    this.removeEntityMesh(event.entityId);

    // Delete from service
    this.sketchService.deleteEntity(event.entityId);

    // Wait for profile detection to complete, then re-render
    setTimeout(() => {
      this.refreshSketchAfterChange();
    }, 150);
  }

  /**
   * Handle sketch visibility toggle from browser
   */
  onSketchVisibilityToggle(event: { sketchId: string; visible: boolean }): void {
    // If in sketch edit mode and this is the active sketch
    if (this.isSketchMode && this.activeSketch && this.activeSketch.id === event.sketchId) {
      // Toggle entity meshes visibility (these are Groups)
      this.sketchEntityMeshes.forEach((mesh) => {
        mesh.visible = event.visible;
        mesh.traverse?.((child: any) => {
          child.visible = event.visible;
        });
      });
      
      // Toggle profile meshes visibility (these are Groups with fill + edges)
      this.sketchProfileMeshes.forEach((mesh) => {
        mesh.visible = event.visible;
        mesh.traverse?.((child: any) => {
          child.visible = event.visible;
        });
      });
      
      // Hide the sketch plane helper if exists
      if (this.sketchPlaneHelper) {
        this.sketchPlaneHelper.visible = event.visible;
        this.sketchPlaneHelper.traverse?.((child: any) => {
          child.visible = event.visible;
        });
      }
      
      // Hide the sketch grid helper if exists
      if (this.sketchGridHelper) {
        this.sketchGridHelper.visible = event.visible;
        this.sketchGridHelper.traverse?.((child: any) => {
          child.visible = event.visible;
        });
      }
    } else {
      // Not in sketch mode - re-render all visible sketches
      this.renderAllVisibleSketches();
    }
  }

  /**
   * Delete selected sketch entities
   */
  deleteSelectedSketchEntities(): void {
    const selectedIds = this.sketchService.getSelectedEntityIds();
    if (selectedIds.length === 0) return;

    // Remove meshes from scene immediately
    for (const entityId of selectedIds) {
      this.removeEntityMesh(entityId);
      this.sketchService.deleteEntity(entityId);
    }

    // Wait for profile detection to complete, then re-render
    setTimeout(() => {
      this.refreshSketchAfterChange();
    }, 150);
  }

  /**
   * Remove a single entity mesh from scene
   */
  private removeEntityMesh(entityId: string): void {
    const mesh = this.sketchEntityMeshes.get(entityId);
    if (mesh && this.engine) {
      this.engine.scene.remove(mesh);
      this.disposeObject3D(mesh);
      this.sketchEntityMeshes.delete(entityId);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSTRUCTION MODE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Toggle construction mode - new entities will be construction geometry
   */
  toggleConstructionMode(): void {
    this.isConstructionMode = !this.isConstructionMode;
    this.cdr.detectChanges();
  }

  /**
   * Toggle construction state for selected entities
   */
  toggleSelectedEntitiesConstruction(): void {
    if (!this.activeSketch) return;

    const selectedIds = this.sketchService.getSelectedEntityIds();
    if (selectedIds.length === 0) return;

    for (const entityId of selectedIds) {
      this.sketchService.toggleEntityConstruction(entityId);
    }

    // Re-render entities to update visual style
    this.renderSketchEntities();

    // Re-detect profiles (construction entities are excluded)
    this.sketchService.detectProfiles(this.activeSketch.id);

    setTimeout(() => {
      this.renderSketchProfiles();
    }, 100);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ENTITY EDITING (Move/Resize)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start editing an entity (show handles)
   */
  startEntityEdit(entityId: string): void {
    if (!this.activeSketch || !this.engine) return;

    const entity = this.activeSketch.entities.find((e) => e.id === entityId);
    if (!entity) return;

    // End any existing edit
    this.endEntityEdit();

    this.isEntityEditMode = true;
    this.editingEntityId = entityId;
    this.originalEntityData = { ...entity };

    // Select the entity
    this.sketchService.selectEntity(entityId, false);

    // Create visual handles
    this.createEntityHandles(entity);

    // Set up mouse handlers
    this.setupEntityEditHandlers();

    this.cdr.detectChanges();
  }

  /**
   * End entity editing mode
   */
  endEntityEdit(): void {
    this.isEntityEditMode = false;
    this.editingEntityId = null;
    this.originalEntityData = null;
    this.draggingHandle = null;
    this.dragStartPoint = null;

    // Remove handles from scene
    this.removeEntityHandles();

    // Remove event listeners
    this.removeEntityEditHandlers();

    this.cdr.detectChanges();
  }

  /**
   * Create visual handles for an entity using CSS2D for better visual feedback
   */
  private createEntityHandles(entity: SketchEntity): void {
    this.removeEntityHandles();

    const handles: Array<{
      type: 'vertex' | 'center' | 'radius';
      index: number;
      pos: { x: number; y: number };
      label?: string;
    }> = [];

    switch (entity.type) {
      case 'rectangle':
        const rect = entity as SketchRectangleEntity;
        // Four corner handles
        handles.push({ type: 'vertex', index: 0, pos: { x: rect.x, y: rect.y } });
        handles.push({ type: 'vertex', index: 1, pos: { x: rect.x + rect.width, y: rect.y } });
        handles.push({ type: 'vertex', index: 2, pos: { x: rect.x + rect.width, y: rect.y + rect.height } });
        handles.push({ type: 'vertex', index: 3, pos: { x: rect.x, y: rect.y + rect.height } });
        // Center handle for moving
        handles.push({
          type: 'center',
          index: 0,
          pos: { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 },
          label: '⊕'
        });
        break;

      case 'circle':
        const circle = entity as SketchCircleEntity;
        // Center handle
        handles.push({ type: 'center', index: 0, pos: { x: circle.centerX, y: circle.centerY }, label: '⊕' });
        // Radius handle (right side)
        handles.push({
          type: 'radius',
          index: 0,
          pos: { x: circle.centerX + circle.radius, y: circle.centerY },
          label: 'R'
        });
        break;

      case 'line':
        const line = entity as SketchLineEntity;
        // Start and end handles
        handles.push({ type: 'vertex', index: 0, pos: { x: line.startX, y: line.startY } });
        handles.push({ type: 'vertex', index: 1, pos: { x: line.endX, y: line.endY } });
        // Center handle for moving
        handles.push({
          type: 'center',
          index: 0,
          pos: { x: (line.startX + line.endX) / 2, y: (line.startY + line.endY) / 2 },
          label: '⊕'
        });
        break;

      case 'polygon':
        const poly = entity as SketchPolygonEntity;
        // Center handle
        handles.push({ type: 'center', index: 0, pos: { x: poly.centerX, y: poly.centerY }, label: '⊕' });
        // Radius handle
        handles.push({ type: 'radius', index: 0, pos: { x: poly.centerX + poly.radius, y: poly.centerY }, label: 'R' });
        break;

      case 'arc':
        const arc = entity as SketchArcEntity;
        // Center handle
        handles.push({ type: 'center', index: 0, pos: { x: arc.centerX, y: arc.centerY }, label: '⊕' });
        // Start angle handle
        const startX = arc.centerX + arc.radius * Math.cos(arc.startAngle);
        const startY = arc.centerY + arc.radius * Math.sin(arc.startAngle);
        handles.push({ type: 'vertex', index: 0, pos: { x: startX, y: startY }, label: 'S' });
        // End angle handle
        const endX = arc.centerX + arc.radius * Math.cos(arc.endAngle);
        const endY = arc.centerY + arc.radius * Math.sin(arc.endAngle);
        handles.push({ type: 'vertex', index: 1, pos: { x: endX, y: endY }, label: 'E' });
        break;

      case 'slot':
        const slot = entity as SketchSlotEntity;
        // Start and end handles
        handles.push({ type: 'vertex', index: 0, pos: { x: slot.startX, y: slot.startY }, label: 'S' });
        handles.push({ type: 'vertex', index: 1, pos: { x: slot.endX, y: slot.endY }, label: 'E' });
        // Center handle
        handles.push({
          type: 'center',
          index: 0,
          pos: { x: (slot.startX + slot.endX) / 2, y: (slot.startY + slot.endY) / 2 },
          label: '⊕'
        });
        break;
    }

    // Create CSS2D handles
    for (const handle of handles) {
      const handleDiv = document.createElement('div');
      handleDiv.className = `cad-entity-handle cad-handle-${handle.type}`;

      // All handles are circular now, with different colors
      const colors = {
        center: { bg: '#3b82f6', border: '#60a5fa' },
        radius: { bg: '#f59e0b', border: '#fbbf24' },
        vertex: { bg: '#22c55e', border: '#4ade80' }
      };
      const color = colors[handle.type];

      // Set container styles with proper flexbox centering
      handleDiv.style.cssText = `
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: ${color.bg};
        border: 2px solid ${color.border};
        color: white;
        font-size: var(--font-size-xs);
        font-weight: 600;
        font-family: system-ui, -apple-system, sans-serif;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: ${handle.type === 'center' ? 'move' : 'pointer'};
        user-select: none;
        pointer-events: auto;
        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        transform: translate(-50%, -50%);
      `;

      // Set text content directly - flexbox will center it
      handleDiv.textContent = handle.label || '●';

      // Hover effect (no transition for strict following)
      handleDiv.addEventListener('mouseenter', () => {
        handleDiv.style.transform = 'scale(1.15)';
        handleDiv.style.boxShadow = '0 4px 12px rgba(0,0,0,0.5)';
      });
      handleDiv.addEventListener('mouseleave', () => {
        if (!this.draggingHandle) {
          handleDiv.style.transform = '';
          handleDiv.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
        }
      });

      // Store handle data
      (handleDiv as any).handleType = handle.type;
      (handleDiv as any).handleIndex = handle.index;
      (handleDiv as any).entityId = entity.id;

      // Convert 2D to 3D world position
      const worldPos = this.planeLocalToWorld(handle.pos);

      const css2dObject = new CSS2DObject(handleDiv);
      css2dObject.position.copy(worldPos);
      css2dObject.userData = {
        handleType: handle.type,
        handleIndex: handle.index,
        entityId: entity.id
      };

      this.engine!.scene.add(css2dObject);
      this.entityHandles.push(css2dObject);
      this.entityHandleElements.push(handleDiv);
    }
  }

  /**
   * Remove entity handles from scene
   */
  private removeEntityHandles(): void {
    for (const handle of this.entityHandles) {
      if (this.engine) {
        this.engine.scene.remove(handle);
      }
      // CSS2DObject doesn't have geometry/material to dispose, just remove from scene
    }
    // Clean up DOM elements
    for (const el of this.entityHandleElements) {
      el.remove();
    }
    this.entityHandles = [];
    this.entityHandleElements = [];
  }

  /**
   * Set up mouse handlers for entity editing (CSS2D handles use DOM events)
   */
  private setupEntityEditHandlers(): void {
    if (!this.engine) return;

    // Use document-level handlers for CSS2D interaction
    this.boundOnEntityEditMouseMove = this.onEntityEditMouseMove.bind(this);
    this.boundOnEntityEditMouseUp = this.onEntityEditMouseUp.bind(this);

    document.addEventListener('mousemove', this.boundOnEntityEditMouseMove);
    document.addEventListener('mouseup', this.boundOnEntityEditMouseUp);

    // Set up handle mousedown events
    this.setupHandleMouseEvents();
  }

  /**
   * Setup mousedown events on CSS2D handle elements
   */
  private setupHandleMouseEvents(): void {
    for (const el of this.entityHandleElements) {
      el.addEventListener('mousedown', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const handleType = (el as any).handleType;
        const handleIndex = (el as any).handleIndex;

        this.draggingHandle = {
          type: handleType,
          index: handleIndex
        };

        // Get starting point in 2D
        const worldPoint = this.getPointOnSketchPlane(e);
        if (worldPoint) {
          this.dragStartPoint = this.worldToPlaneLocal(worldPoint);
        }

        // Disable orbit controls while dragging
        if ((this.engine as any)?.orbitControl) {
          (this.engine as any).orbitControl.enabled = false;
        }

        // Visual feedback
        el.style.transform = 'scale(1.3)';
        el.style.boxShadow = '0 4px 16px rgba(0,0,0,0.6)';
      });
    }
  }

  /**
   * Handle mousemove for entity editing
   */
  private onEntityEditMouseMove(e: MouseEvent): void {
    if (!this.engine || !this.draggingHandle || !this.dragStartPoint || !this.editingEntityId) return;

    const worldPoint = this.getPointOnSketchPlane(e);
    if (!worldPoint) return;

    const currentPoint = this.worldToPlaneLocal(worldPoint);
    const dx = currentPoint.x - this.dragStartPoint.x;
    const dy = currentPoint.y - this.dragStartPoint.y;

    // Update entity based on handle type
    const entity = this.activeSketch?.entities.find((e) => e.id === this.editingEntityId);
    if (!entity) return;

    this.updateEntityFromDrag(entity, this.draggingHandle, currentPoint, dx, dy);

    // Re-render the entity
    this.removeEntityMesh(entity.id);
    this.renderSingleSketchEntity(entity);

    // Update handles positions
    this.createEntityHandles(entity);
    this.setupHandleMouseEvents(); // Re-attach events to new handles

    // Update start point for next delta
    this.dragStartPoint = currentPoint;

    e.preventDefault();
  }

  /**
   * Remove entity edit mouse handlers
   */
  private removeEntityEditHandlers(): void {
    if (this.boundOnEntityEditMouseMove) {
      document.removeEventListener('mousemove', this.boundOnEntityEditMouseMove);
      this.boundOnEntityEditMouseMove = null;
    }
    if (this.boundOnEntityEditMouseUp) {
      document.removeEventListener('mouseup', this.boundOnEntityEditMouseUp);
      this.boundOnEntityEditMouseUp = null;
    }
  }

  /**
   * Handle mouseup for entity editing
   */
  private onEntityEditMouseUp(e: MouseEvent): void {
    if (this.draggingHandle) {
      // Re-enable orbit controls
      if ((this.engine as any)?.orbitControl) {
        (this.engine as any).orbitControl.enabled = true;
      }

      // Reset handle visuals
      for (const el of this.entityHandleElements) {
        el.style.transform = 'scale(1)';
        el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
      }

      this.draggingHandle = null;
      this.dragStartPoint = null;

      // Trigger profile re-detection
      if (this.activeSketch) {
        this.sketchService.detectProfiles(this.activeSketch.id);
        setTimeout(() => {
          this.renderSketchProfiles();
        }, 100);
      }
    }
  }

  /**
   * Update entity from drag operation
   */
  private updateEntityFromDrag(
    entity: SketchEntity,
    handle: { type: 'vertex' | 'center' | 'radius'; index: number },
    currentPoint: { x: number; y: number },
    dx: number,
    dy: number
  ): void {
    switch (entity.type) {
      case 'rectangle':
        const rect = entity as SketchRectangleEntity;
        if (handle.type === 'center') {
          // Move entire rectangle
          rect.x += dx;
          rect.y += dy;
        } else if (handle.type === 'vertex') {
          // Resize rectangle
          switch (handle.index) {
            case 0: // Top-left
              const newWidth0 = rect.width - dx;
              const newHeight0 = rect.height - dy;
              if (newWidth0 > 1) {
                rect.x += dx;
                rect.width = newWidth0;
              }
              if (newHeight0 > 1) {
                rect.y += dy;
                rect.height = newHeight0;
              }
              break;
            case 1: // Top-right
              const newWidth1 = rect.width + dx;
              const newHeight1 = rect.height - dy;
              if (newWidth1 > 1) {
                rect.width = newWidth1;
              }
              if (newHeight1 > 1) {
                rect.y += dy;
                rect.height = newHeight1;
              }
              break;
            case 2: // Bottom-right
              const newWidth2 = rect.width + dx;
              const newHeight2 = rect.height + dy;
              if (newWidth2 > 1) {
                rect.width = newWidth2;
              }
              if (newHeight2 > 1) {
                rect.height = newHeight2;
              }
              break;
            case 3: // Bottom-left
              const newWidth3 = rect.width - dx;
              const newHeight3 = rect.height + dy;
              if (newWidth3 > 1) {
                rect.x += dx;
                rect.width = newWidth3;
              }
              if (newHeight3 > 1) {
                rect.height = newHeight3;
              }
              break;
          }
        }
        this.sketchService.replaceEntity(rect);
        break;

      case 'circle':
        const circle = entity as SketchCircleEntity;
        if (handle.type === 'center') {
          circle.centerX += dx;
          circle.centerY += dy;
        } else if (handle.type === 'radius') {
          const newRadius = Math.sqrt(
            Math.pow(currentPoint.x - circle.centerX, 2) + Math.pow(currentPoint.y - circle.centerY, 2)
          );
          if (newRadius > 1) {
            circle.radius = newRadius;
          }
        }
        this.sketchService.replaceEntity(circle);
        break;

      case 'line':
        const line = entity as SketchLineEntity;
        if (handle.type === 'center') {
          line.startX += dx;
          line.startY += dy;
          line.endX += dx;
          line.endY += dy;
        } else if (handle.type === 'vertex') {
          if (handle.index === 0) {
            line.startX = currentPoint.x;
            line.startY = currentPoint.y;
          } else {
            line.endX = currentPoint.x;
            line.endY = currentPoint.y;
          }
        }
        this.sketchService.replaceEntity(line);
        break;

      case 'polygon':
        const poly = entity as SketchPolygonEntity;
        if (handle.type === 'center') {
          poly.centerX += dx;
          poly.centerY += dy;
        } else if (handle.type === 'radius') {
          const newRadius = Math.sqrt(
            Math.pow(currentPoint.x - poly.centerX, 2) + Math.pow(currentPoint.y - poly.centerY, 2)
          );
          if (newRadius > 1) {
            poly.radius = newRadius;
          }
        }
        this.sketchService.replaceEntity(poly);
        break;

      case 'arc':
        const arc = entity as SketchArcEntity;
        if (handle.type === 'center') {
          arc.centerX += dx;
          arc.centerY += dy;
        } else if (handle.type === 'vertex') {
          // Update start/end angle based on dragged point
          const angle = Math.atan2(currentPoint.y - arc.centerY, currentPoint.x - arc.centerX);
          if (handle.index === 0) {
            arc.startAngle = angle;
          } else {
            arc.endAngle = angle;
          }
        }
        this.sketchService.replaceEntity(arc);
        break;

      case 'slot':
        const slot = entity as SketchSlotEntity;
        if (handle.type === 'center') {
          slot.startX += dx;
          slot.startY += dy;
          slot.endX += dx;
          slot.endY += dy;
        } else if (handle.type === 'vertex') {
          if (handle.index === 0) {
            slot.startX = currentPoint.x;
            slot.startY = currentPoint.y;
          } else {
            slot.endX = currentPoint.x;
            slot.endY = currentPoint.y;
          }
        }
        this.sketchService.replaceEntity(slot);
        break;
    }
  }

  /**
   * Render a single sketch entity
   */
  private renderSingleSketchEntity(entity: SketchEntity): void {
    const mesh = this.createSketchEntityMesh(entity);
    if (mesh && this.engine) {
      // Respect sketch visibility
      const isSketchVisible = this.activeSketch?.isVisible !== false;
      mesh.visible = isSketchVisible;
      this.sketchEntityMeshes.set(entity.id, mesh);
      this.engine.scene.add(mesh);
    }
  }

  /**
   * Refresh sketch state after changes (deletion, addition, etc.)
   */
  private refreshSketchAfterChange(): void {
    if (!this.activeSketch) return;

    // Refresh local sketch reference with latest data
    this.activeSketch = this.sketchService.getSketch(this.activeSketch.id) || null;

    if (!this.activeSketch) {
      this.clearSketchEntityMeshes();
      this.clearSketchProfileMeshes();
      return;
    }

    // Re-render entities if in edit mode
    if (this.isSketchMode) {
      this.renderSketchEntities();
    }

    // Always re-render profiles
    this.renderSketchProfiles();

    this.cdr.detectChanges();
  }

  /**
   * Get number of selected sketch entities
   */
  getSelectedSketchEntityCount(): number {
    return this.sketchService.getSelectedEntityIds().length;
  }

  /**
   * Toggle extrusion drag mode for mouse-based height setting
   */
  toggleExtrusionDragMode(): void {
    this.isExtrusionDragMode = !this.isExtrusionDragMode;

    if (this.isExtrusionDragMode) {
      // Hide snap feedback and drawing dimensions when entering drag mode
      this.hideSnapFeedback();
      this.clearDrawingDimensions();
      this.setupExtrusionDragHandlers();
      this.createExtrusionArrow();
      this.createExtrusionBodyPreview();
    } else {
      this.removeExtrusionDragHandlers();
      this.clearExtrusionDragPreview();
    }

    this.cdr.detectChanges();
  }

  /**
   * Auto-enable/disable extrusion drag mode based on profile selection
   * Called when profiles are selected/deselected
   */
  private updateExtrusionDragModeFromSelection(): void {
    const hasProfiles = this.selectedProfileIds.size > 0;

    if (hasProfiles && !this.isExtrusionDragMode) {
      // Auto-enable drag mode when profiles are selected
      this.isExtrusionDragMode = true;
      this.hideSnapFeedback();
      this.clearDrawingDimensions();
      this.setupExtrusionDragHandlers();
      this.createExtrusionArrow();
      this.createExtrusionBodyPreview();
    } else if (!hasProfiles && this.isExtrusionDragMode) {
      // Auto-disable drag mode when no profiles selected
      this.isExtrusionDragMode = false;
      this.removeExtrusionDragHandlers();
      this.clearExtrusionDragPreview();
    }
  }

  // Extrusion arrow visual elements (CSS2D-based)
  private extrusionArrowCSS2D: CSS2DObject | null = null;
  private extrusionArrowElement: HTMLElement | null = null;
  private extrusionBodyPreview: Mesh | null = null;
  private extrusionAxisLine: Line | null = null; // Visual line from base to arrow
  private extrusionArrow3D: Group | null = null; // 3D arrow mesh pointing in extrusion direction

  // Track if we're actively dragging the arrow
  private isArrowDragging = false;
  private extrusionDragInitialHeight = 10; // Store initial height when drag starts
  private extrusionDragBaseWorldPos: Vector3 | null = null; // Base position on sketch plane (height=0)
  private extrusionDragNormal: Vector3 | null = null; // Extrusion direction normal

  private extrusionDragMouseDown = (event: MouseEvent) => {
    // Only start dragging - actual drag happens on arrow element
    if (event.button !== 0) return;
    // Don't prevent default here - allow camera controls to work
  };

  private extrusionDragMouseMove = (event: MouseEvent) => {
    if (!this.isExtrusionDragMode || !this.isArrowDragging) return;
    if (!this.engine || !this.extrusionDragBaseWorldPos || !this.extrusionDragNormal) return;

    event.preventDefault();
    event.stopPropagation();

    // Raycast from mouse to find intersection with a plane containing the extrusion axis
    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    // Create a plane that passes through the base point and is perpendicular to camera's view
    // but we project onto the extrusion normal axis
    const cameraDir = new Vector3();
    this.engine.camera.getWorldDirection(cameraDir);

    // Find the closest point on the extrusion axis line to the ray
    // Extrusion axis: basePoint + t * normal
    // Ray: rayOrigin + s * rayDir
    const rayOrigin = raycaster.ray.origin;
    const rayDir = raycaster.ray.direction;
    const basePoint = this.extrusionDragBaseWorldPos;
    const normal = this.extrusionDragNormal;

    // Calculate closest approach between two lines
    const w0 = rayOrigin.clone().sub(basePoint);
    const a = normal.dot(normal); // always > 0
    const b = normal.dot(rayDir);
    const c = rayDir.dot(rayDir); // always > 0
    const d = normal.dot(w0);
    const e = rayDir.dot(w0);

    const denom = a * c - b * b;

    let newHeight: number;
    if (Math.abs(denom) < 0.0001) {
      // Lines are parallel - fallback to simple calculation
      newHeight = this.extrusionDragInitialHeight;
    } else {
      // Parameter on the extrusion axis for closest point
      const t = (b * e - c * d) / denom;
      // Invert the sign so moving mouse up increases height
      newHeight = -t; // t is the signed distance along the normal from base
    }

    // Apply modifier keys for finer control
    // Ctrl = super fine (0.1mm precision), Shift = fine (1mm precision)
    if (event.ctrlKey) {
      // Round to 0.1mm increments for super fine control
      newHeight = Math.round(newHeight * 10) / 10;
    } else if (event.shiftKey) {
      // Round to 1mm increments for fine control
      newHeight = Math.round(newHeight);
    }

    // Determine direction based on sign
    const isPositive = newHeight >= 0;
    newHeight = Math.abs(newHeight);

    // Snap to grid increments (5mm steps)
    if (this.snapEnabled) {
      newHeight = Math.round(newHeight / 5) * 5;
      if (newHeight < 0.1) newHeight = 0.1;
    }

    this.extrudeHeight = Math.max(0.1, newHeight);
    this.extrudePositive = isPositive;

    // Update visual arrow, body preview, and axis line
    this.updateExtrusionArrow();
    this.updateExtrusionBodyPreview();
    this.updateExtrusionAxisLine();

    this.cdr.detectChanges();
  };

  private extrusionDragMouseUp = (event: MouseEvent) => {
    if (event.button !== 0) return;

    // Reset dragging style if we were dragging
    if (this.isArrowDragging) {
      this.setArrowDraggingStyle(false);
    }

    this.isArrowDragging = false;
    this.extrusionDragBaseWorldPos = null;
    this.extrusionDragNormal = null;
  };

  // Arrow-specific drag handlers
  private onArrowMouseDown = (event: MouseEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.isArrowDragging = true;
    this.extrusionDragStartY = event.clientY;
    // Store current height with sign based on direction
    this.extrusionDragInitialHeight = this.extrudePositive ? this.extrudeHeight : -this.extrudeHeight;

    // Calculate and store the base world position (on sketch plane at height=0) and normal
    this.calculateExtrusionDragBasePosition();

    // Change arrow color to indicate active dragging
    this.setArrowDraggingStyle(true);
  };

  /**
   * Set arrow visual style for dragging state
   */
  private setArrowDraggingStyle(isDragging: boolean): void {
    if (!this.extrusionArrowElement) return;

    const heightLabel = this.extrusionArrowElement.querySelector('.height-label') as HTMLElement;

    if (isDragging) {
      // Yellow/gold color while dragging
      if (heightLabel) {
        heightLabel.style.background = 'rgba(255, 215, 0, 0.95)';
        heightLabel.style.transform = 'scale(1.1)';
      }
      // Update 3D arrow color to gold
      if (this.extrusionArrow3D) {
        this.extrusionArrow3D.traverse((child: any) => {
          if (child.material) {
            child.material.color.setHex(0xffd700);
          }
        });
      }
    } else {
      // Reset to green color
      if (heightLabel) {
        heightLabel.style.background = 'rgba(160, 255, 0, 0.95)';
        heightLabel.style.transform = 'scale(1)';
      }
      // Reset 3D arrow color to green
      if (this.extrusionArrow3D) {
        this.extrusionArrow3D.traverse((child: any) => {
          if (child.material) {
            child.material.color.setHex(0xa0ff00);
          }
        });
      }
    }
  }

  /**
   * Calculate the base position on sketch plane and extrusion normal for drag calculations
   */
  private calculateExtrusionDragBasePosition(): void {
    if (!this.activeSketch) return;

    // Get profile center
    const profiles = Array.from(this.selectedProfileIds);
    if (profiles.length === 0) return;

    const profile = this.activeSketch.profiles.find((p) => p.id === profiles[0]);
    if (!profile) return;

    const center = this.getProfileCenter(profile);
    this.extrusionDragBaseWorldPos = this.getWorldPositionOnPlane(center.x, center.y);
    this.extrusionDragNormal = this.getPlaneNormal();
  }

  private setupExtrusionDragHandlers(): void {
    const canvas = this.engine?.canvas;
    if (!canvas) return;

    // Mouse move/up on document so dragging works even outside canvas
    document.addEventListener('mousemove', this.extrusionDragMouseMove, true);
    document.addEventListener('mouseup', this.extrusionDragMouseUp, true);

    // Add keyboard handlers for confirming
    document.addEventListener('keydown', this.extrusionDragKeyHandler);

    // Canvas keeps default cursor - arrow element has ns-resize
    canvas.style.cursor = 'default';
  }

  private removeExtrusionDragHandlers(): void {
    const canvas = this.engine?.canvas;
    if (!canvas) return;

    document.removeEventListener('mousemove', this.extrusionDragMouseMove, true);
    document.removeEventListener('mouseup', this.extrusionDragMouseUp, true);
    document.removeEventListener('keydown', this.extrusionDragKeyHandler);

    // Restore cursor
    canvas.style.cursor = 'default';

    // Reset drag state
    this.isArrowDragging = false;
  }

  private extrusionDragKeyHandler = (event: KeyboardEvent) => {
    // Guard: only handle keys when in extrusion drag mode
    if (!this.isExtrusionDragMode) return;

    if (event.key === 'Enter') {
      this.isExtrusionDragMode = false;
      this.removeExtrusionDragHandlers();
      this.clearExtrusionDragPreview();
      this.hideSnapFeedback();
      this.extrudeSelectedProfiles();
    } else if (event.key === 'Escape') {
      this.isExtrusionDragMode = false;
      this.removeExtrusionDragHandlers();
      this.clearExtrusionDragPreview();
      this.hideSnapFeedback();
      this.cdr.detectChanges();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      // Arrow keys to adjust height
      event.preventDefault();
      // Ctrl = super fine (0.1mm), Shift = fine (1mm), otherwise 5mm
      const increment = event.ctrlKey ? 0.1 : event.shiftKey ? 1 : 5;

      if (event.key === 'ArrowUp') {
        if (this.extrudePositive) {
          this.extrudeHeight = Math.max(0.1, this.extrudeHeight + increment);
        } else {
          this.extrudeHeight = Math.max(0.1, this.extrudeHeight - increment);
          if (this.extrudeHeight <= 0.1) {
            this.extrudePositive = true;
            this.extrudeHeight = increment;
          }
        }
      } else {
        if (this.extrudePositive) {
          this.extrudeHeight = Math.max(0.1, this.extrudeHeight - increment);
          if (this.extrudeHeight <= 0.1) {
            this.extrudePositive = false;
            this.extrudeHeight = increment;
          }
        } else {
          this.extrudeHeight = Math.max(0.1, this.extrudeHeight + increment);
        }
      }

      this.updateExtrusionArrow();
      this.updateExtrusionBodyPreview();
      this.updateExtrusionAxisLine();
      this.cdr.detectChanges();
    } else if (event.key === 'f' || event.key === 'F') {
      // F to flip direction
      event.preventDefault();
      this.extrudePositive = !this.extrudePositive;
      this.updateExtrusionArrow();
      this.updateExtrusionBodyPreview();
      this.updateExtrusionAxisLine();
      this.cdr.detectChanges();
    }
  };

  /**
   * Create a visual arrow indicator using CSS2D for extrusion direction and height
   */
  private createExtrusionArrow(): void {
    if (!this.engine || !this.activeSketch) return;

    this.clearExtrusionDragPreview();

    // Calculate profile center
    const profiles = Array.from(this.selectedProfileIds);
    if (profiles.length === 0) return;

    const profile = this.activeSketch.profiles.find((p) => p.id === profiles[0]);
    if (!profile) return;

    // Get profile center in local 2D coordinates
    let centerX = 0,
      centerY = 0;
    const outerPoints = profile.outerLoop.points;
    for (const [x, y] of outerPoints) {
      centerX += x;
      centerY += y;
    }
    centerX /= outerPoints.length;
    centerY /= outerPoints.length;

    // Create CSS2D arrow indicator - make it interactive!
    this.extrusionArrowElement = document.createElement('div');
    this.extrusionArrowElement.className = 'extrusion-arrow-indicator';
    this.extrusionArrowElement.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      pointer-events: auto;
      transform: translate(-50%, -50%);
      cursor: ns-resize;
      user-select: none;
    `;

    // Make the arrow draggable
    this.extrusionArrowElement.addEventListener('mousedown', this.onArrowMouseDown);

    // Height label
    const heightLabel = document.createElement('div');
    heightLabel.className = 'height-label';
    heightLabel.style.cssText = `
      background: rgba(160, 255, 0, 0.95);
      color: #000;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: var(--font-size-lg);
      font-weight: 700;
      font-family: 'JetBrains Mono', monospace;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      pointer-events: none;
    `;
    heightLabel.textContent = `${this.extrudeHeight.toFixed(1)} mm`;

    // Hint text
    const hintText = document.createElement('div');
    hintText.className = 'hint-text';
    hintText.style.cssText = `
      font-size: var(--font-size-sm);
      color: rgba(255, 255, 255, 0.9);
      text-shadow: 0 1px 4px rgba(0,0,0,0.8);
      pointer-events: none;
      text-align: center;
      line-height: 1.5;
      background: rgba(0, 0, 0, 0.5);
      padding: 8px 12px;
      border-radius: 6px;
      margin-top: 4px;
    `;
    hintText.innerHTML = 'Drag ↕ (Shift=fine, Ctrl=0.1mm)<br>↑↓ keys • F=flip<br>Enter to apply • Esc to cancel';

    this.extrusionArrowElement.appendChild(heightLabel);
    this.extrusionArrowElement.appendChild(hintText);

    // Create CSS2DObject and position it
    this.extrusionArrowCSS2D = new CSS2DObject(this.extrusionArrowElement);

    // Position at profile center on the sketch plane (world coordinates)
    // Then update to be at top of current extrusion height
    this.positionExtrusionArrowCSS2D(centerX, centerY);

    this.engine.scene.add(this.extrusionArrowCSS2D);

    // Create 3D arrow mesh pointing in extrusion direction
    this.create3DExtrusionArrow(centerX, centerY);

    // Create the axis line from base to arrow
    this.createExtrusionAxisLine(centerX, centerY);

    // Immediately update arrow to be at top of preview
    this.updateExtrusionArrow3DPosition();
  }

  /**
   * Create a 3D arrow mesh that points in the extrusion direction
   */
  private create3DExtrusionArrow(centerX: number, centerY: number): void {
    if (!this.engine || !this.activeSketch) return;

    // Clean up existing arrow
    if (this.extrusionArrow3D) {
      this.engine.scene.remove(this.extrusionArrow3D);
      this.extrusionArrow3D.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.extrusionArrow3D = null;
    }

    // Create arrow group
    this.extrusionArrow3D = new Group();
    this.extrusionArrow3D.name = 'ExtrusionArrow3D';

    // Arrow dimensions
    const coneHeight = 12;
    const coneRadius = 5;
    const shaftHeight = 20;
    const shaftRadius = 2;

    // Create cone (arrow head)
    const coneGeometry = new ConeGeometry(coneRadius, coneHeight, 16);
    const coneMaterial = new MeshBasicMaterial({
      color: 0xa0ff00,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    const cone = new Mesh(coneGeometry, coneMaterial);
    cone.position.y = shaftHeight + coneHeight / 2;
    cone.renderOrder = 200;

    // Create shaft (cylinder)
    const shaftGeometry = new CylinderGeometry(shaftRadius, shaftRadius, shaftHeight, 16);
    const shaftMaterial = new MeshBasicMaterial({
      color: 0xa0ff00,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    const shaft = new Mesh(shaftGeometry, shaftMaterial);
    shaft.position.y = shaftHeight / 2;
    shaft.renderOrder = 200;

    this.extrusionArrow3D.add(cone);
    this.extrusionArrow3D.add(shaft);

    // Position and orient the arrow
    this.update3DExtrusionArrowPosition(centerX, centerY);

    this.engine.scene.add(this.extrusionArrow3D);
  }

  /**
   * Update the 3D arrow position and orientation
   */
  private update3DExtrusionArrowPosition(centerX?: number, centerY?: number): void {
    if (!this.extrusionArrow3D || !this.activeSketch) return;

    // Get center if not provided
    if (centerX === undefined || centerY === undefined) {
      const profile = this.getFirstSelectedProfile();
      if (!profile) return;
      const center = this.getProfileCenter(profile);
      centerX = center.x;
      centerY = center.y;
    }

    // Get the extrusion direction normal
    const normal = this.getPlaneNormal();
    const effectiveHeight = this.extrudePositive ? this.extrudeHeight : -this.extrudeHeight;

    // Get base position on plane
    const basePos = this.getWorldPositionOnPlane(centerX, centerY);

    // Position arrow at the end of the extrusion
    const arrowPos = basePos.clone().add(normal.clone().multiplyScalar(effectiveHeight));
    this.extrusionArrow3D.position.copy(arrowPos);

    // Orient arrow to point in extrusion direction
    // The arrow mesh points in +Y by default, so we need to rotate it to match the normal
    const targetDir = normal.clone().multiplyScalar(this.extrudePositive ? 1 : -1);
    const up = new Vector3(0, 1, 0);

    // Calculate rotation quaternion
    const quaternion = new Quaternion();
    quaternion.setFromUnitVectors(up, targetDir);
    this.extrusionArrow3D.quaternion.copy(quaternion);
  }

  /**
   * Position the CSS2D extrusion arrow at the profile center in world coordinates
   */
  private positionExtrusionArrowCSS2D(centerX: number, centerY: number): void {
    if (!this.extrusionArrowCSS2D || !this.activeSketch) return;

    let worldPos: Vector3;

    switch (this.activeSketch.plane) {
      case 'XY':
        worldPos = new Vector3(centerX, centerY, this.activeSketch.planeOffset);
        break;
      case 'XZ':
        worldPos = new Vector3(centerX, this.activeSketch.planeOffset, centerY);
        break;
      case 'YZ':
        worldPos = new Vector3(this.activeSketch.planeOffset, centerX, centerY);
        break;
      case 'custom':
        const origin = this.activeSketch.planeOrigin || [0, 0, 0];
        const xAxis = new Vector3(
          this.activeSketch.planeXAxis?.[0] || 1,
          this.activeSketch.planeXAxis?.[1] || 0,
          this.activeSketch.planeXAxis?.[2] || 0
        ).normalize();
        const yAxis = new Vector3(
          this.activeSketch.planeYAxis?.[0] || 0,
          this.activeSketch.planeYAxis?.[1] || 1,
          this.activeSketch.planeYAxis?.[2] || 0
        ).normalize();

        // Calculate world position from local 2D center
        worldPos = new Vector3(origin[0], origin[1], origin[2])
          .add(xAxis.clone().multiplyScalar(centerX))
          .add(yAxis.clone().multiplyScalar(centerY));
        break;
      default:
        worldPos = new Vector3(centerX, centerY, 0);
    }

    this.extrusionArrowCSS2D.position.copy(worldPos);
  }

  /**
   * Update the CSS2D extrusion arrow visual based on current height
   */
  private updateExtrusionArrow(): void {
    if (!this.extrusionArrowElement) return;

    const heightLabel = this.extrusionArrowElement.querySelector('.height-label') as HTMLElement;

    if (heightLabel) {
      heightLabel.textContent = `${this.extrudeHeight.toFixed(1)} mm`;
    }

    // Update 3D position to be at top of extrusion
    this.updateExtrusionArrow3DPosition();
  }

  /**
   * Update extrusion arrow 3D position to be at the top of the preview body
   */
  private updateExtrusionArrow3DPosition(): void {
    if (!this.activeSketch) return;

    const profile = this.getFirstSelectedProfile();
    if (!profile) return;

    const center = this.getProfileCenter(profile);
    const effectiveHeight = this.extrudePositive ? this.extrudeHeight : -this.extrudeHeight;
    const worldPos = this.getWorldPositionOnPlane(center.x, center.y, effectiveHeight);

    // Update CSS2D position
    if (this.extrusionArrowCSS2D) {
      this.extrusionArrowCSS2D.position.copy(worldPos);
    }

    // Update 3D arrow position and orientation
    this.update3DExtrusionArrowPosition(center.x, center.y);
  }

  /**
   * Helper to get the first selected profile
   */
  private getFirstSelectedProfile(): SketchProfile | null {
    if (!this.activeSketch) return null;
    const profiles = Array.from(this.selectedProfileIds);
    if (profiles.length === 0) return null;
    return this.activeSketch.profiles.find((p) => p.id === profiles[0]) || null;
  }

  /**
   * Create the visual axis line from base to arrow
   */
  private createExtrusionAxisLine(centerX: number, centerY: number): void {
    if (!this.engine || !this.activeSketch) return;

    const basePos = this.getWorldPositionOnPlane(centerX, centerY);
    const normal = this.getPlaneNormal();
    const effectiveHeight = this.extrudePositive ? this.extrudeHeight : -this.extrudeHeight;
    const endPos = basePos.clone().add(normal.clone().multiplyScalar(effectiveHeight));

    // Create dashed line geometry
    const points = [basePos, endPos];
    const geometry = new BufferGeometry().setFromPoints(points);
    const material = new LineDashedMaterial({
      color: 0xa0ff00,
      dashSize: 3,
      gapSize: 2,
      linewidth: 2,
      transparent: true,
      opacity: 0.8
    });

    this.extrusionAxisLine = new Line(geometry, material);
    this.extrusionAxisLine.computeLineDistances(); // Required for dashed lines
    this.engine.scene.add(this.extrusionAxisLine);
  }

  /**
   * Update the axis line positions based on current extrusion height
   */
  private updateExtrusionAxisLine(): void {
    if (!this.extrusionAxisLine || !this.activeSketch) return;

    const profile = this.getFirstSelectedProfile();
    if (!profile) return;

    const center = this.getProfileCenter(profile);
    const basePos = this.getWorldPositionOnPlane(center.x, center.y);
    const normal = this.getPlaneNormal();
    const effectiveHeight = this.extrudePositive ? this.extrudeHeight : -this.extrudeHeight;
    const endPos = basePos.clone().add(normal.clone().multiplyScalar(effectiveHeight));

    // Update geometry
    const positions = this.extrusionAxisLine.geometry.attributes['position'] as BufferAttribute;
    positions.setXYZ(0, basePos.x, basePos.y, basePos.z);
    positions.setXYZ(1, endPos.x, endPos.y, endPos.z);
    positions.needsUpdate = true;

    // Recompute line distances for dashed line
    this.extrusionAxisLine.computeLineDistances();
  }

  private clearExtrusionDragPreview(): void {
    // Clear CSS2D arrow indicator
    if (this.extrusionArrowCSS2D) {
      // Remove event listener
      if (this.extrusionArrowElement) {
        this.extrusionArrowElement.removeEventListener('mousedown', this.onArrowMouseDown);
        // Remove DOM element explicitly
        if (this.extrusionArrowElement.parentNode) {
          this.extrusionArrowElement.parentNode.removeChild(this.extrusionArrowElement);
        }
      }
      // Remove from scene
      if (this.engine) {
        this.engine.scene.remove(this.extrusionArrowCSS2D);
      }
      this.extrusionArrowCSS2D = null;
      this.extrusionArrowElement = null;
    }

    // Clear 3D arrow mesh
    if (this.extrusionArrow3D && this.engine) {
      this.engine.scene.remove(this.extrusionArrow3D);
      this.extrusionArrow3D.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.extrusionArrow3D = null;
    }

    // Clear axis line
    if (this.extrusionAxisLine && this.engine) {
      this.engine.scene.remove(this.extrusionAxisLine);
      this.extrusionAxisLine.geometry.dispose();
      (this.extrusionAxisLine.material as LineDashedMaterial).dispose();
      this.extrusionAxisLine = null;
    }

    // Reset drag state
    this.isArrowDragging = false;

    // Clear body preview mesh
    if (this.extrusionBodyPreview && this.engine) {
      this.engine.scene.remove(this.extrusionBodyPreview);
      this.extrusionBodyPreview.geometry.dispose();
      if (Array.isArray(this.extrusionBodyPreview.material)) {
        this.extrusionBodyPreview.material.forEach((m) => m.dispose());
      } else {
        this.extrusionBodyPreview.material.dispose();
      }
      this.extrusionBodyPreview = null;
    }

    // Clear old preview mesh if exists
    if (this.extrusionDragPreview) {
      this.engine?.scene.remove(this.extrusionDragPreview);
      this.extrusionDragPreview.geometry.dispose();
      (this.extrusionDragPreview.material as MeshPhysicalMaterial).dispose();
      this.extrusionDragPreview = null;
    }
  }

  /**
   * Create the extruded body preview mesh
   */
  private createExtrusionBodyPreview(): void {
    if (!this.engine || !this.activeSketch) return;

    // Ensure plane info is set from the active sketch (for positioning)
    this.sketchPlane = this.activeSketch.plane;
    this.sketchPlaneOffset = this.activeSketch.planeOffset;
    if (this.activeSketch.plane === 'custom') {
      this.customPlaneOrigin = this.activeSketch.planeOrigin || [0, 0, 0];
      this.customPlaneNormal = this.activeSketch.planeNormal || [0, 0, 1];
      this.customPlaneXAxis = this.activeSketch.planeXAxis || [1, 0, 0];
      this.customPlaneYAxis = this.activeSketch.planeYAxis || [0, 1, 0];
    }

    // Get selected profile points
    const profiles = Array.from(this.selectedProfileIds);
    if (profiles.length === 0) return;

    const profile = this.activeSketch.profiles.find((p) => p.id === profiles[0]);
    if (!profile) return;

    // Create 2D shape from profile
    const shape = new Shape();
    const outerPoints = profile.outerLoop.points;

    if (outerPoints.length < 3) return;

    shape.moveTo(outerPoints[0][0], outerPoints[0][1]);
    for (let i = 1; i < outerPoints.length; i++) {
      shape.lineTo(outerPoints[i][0], outerPoints[i][1]);
    }
    shape.closePath();

    // Add holes
    for (const innerLoop of profile.innerLoops) {
      const holePath = new Path();
      const holePoints = innerLoop.points;
      if (holePoints.length < 3) continue;

      holePath.moveTo(holePoints[0][0], holePoints[0][1]);
      for (let i = 1; i < holePoints.length; i++) {
        holePath.lineTo(holePoints[i][0], holePoints[i][1]);
      }
      holePath.closePath();
      shape.holes.push(holePath);
    }

    // Create initial extruded geometry with small height
    this.updateExtrusionBodyPreview();
  }

  /**
   * Update the extruded body preview mesh with current height
   */
  private updateExtrusionBodyPreview(): void {
    if (!this.engine || !this.activeSketch) return;

    // Ensure plane info is set from the active sketch (for positioning)
    if (!this.sketchPlane) {
      this.sketchPlane = this.activeSketch.plane;
      this.sketchPlaneOffset = this.activeSketch.planeOffset;
      if (this.activeSketch.plane === 'custom') {
        this.customPlaneOrigin = this.activeSketch.planeOrigin || [0, 0, 0];
        this.customPlaneNormal = this.activeSketch.planeNormal || [0, 0, 1];
        this.customPlaneXAxis = this.activeSketch.planeXAxis || [1, 0, 0];
        this.customPlaneYAxis = this.activeSketch.planeYAxis || [0, 1, 0];
      }
    }

    // Get selected profile
    const profiles = Array.from(this.selectedProfileIds);
    if (profiles.length === 0) return;

    const profile = this.activeSketch.profiles.find((p) => p.id === profiles[0]);
    if (!profile) return;

    // Create 2D shape from profile
    const shape = new Shape();
    const outerPoints = profile.outerLoop.points;

    if (outerPoints.length < 3) return;

    shape.moveTo(outerPoints[0][0], outerPoints[0][1]);
    for (let i = 1; i < outerPoints.length; i++) {
      shape.lineTo(outerPoints[i][0], outerPoints[i][1]);
    }
    shape.closePath();

    // Add holes
    for (const innerLoop of profile.innerLoops) {
      const holePath = new Path();
      const holePoints = innerLoop.points;
      if (holePoints.length < 3) continue;

      holePath.moveTo(holePoints[0][0], holePoints[0][1]);
      for (let i = 1; i < holePoints.length; i++) {
        holePath.lineTo(holePoints[i][0], holePoints[i][1]);
      }
      holePath.closePath();
      shape.holes.push(holePath);
    }

    // Clear existing preview
    if (this.extrusionBodyPreview && this.engine) {
      this.engine.scene.remove(this.extrusionBodyPreview);
      this.extrusionBodyPreview.geometry.dispose();
    }

    // Calculate effective height
    const effectiveHeight = this.extrudePositive ? this.extrudeHeight : -this.extrudeHeight;

    // Create extruded geometry
    const extrudeSettings = {
      steps: 1,
      depth: Math.abs(effectiveHeight),
      bevelEnabled: false
    };

    const geometry = new ExtrudeGeometry(shape, extrudeSettings);

    // Create semi-transparent material
    const material = new MeshBasicMaterial({
      color: 0xa0ff00,
      transparent: true,
      opacity: 0.35,
      side: DoubleSide,
      depthWrite: false
    });

    this.extrusionBodyPreview = new Mesh(geometry, material);
    this.extrusionBodyPreview.name = 'ExtrusionBodyPreview';
    this.extrusionBodyPreview.renderOrder = 100;

    // Position the preview on the sketch plane
    this.positionExtrusionBodyPreview(effectiveHeight);

    this.engine.scene.add(this.extrusionBodyPreview);
  }

  /**
   * Position the extrusion body preview on the sketch plane
   * Uses the SAME positioning logic as positionPreviewOnPlane for consistency
   */
  private positionExtrusionBodyPreview(effectiveHeight: number): void {
    if (!this.extrusionBodyPreview) return;

    // Reset position, rotation, and scale before applying new transforms
    this.extrusionBodyPreview.position.set(0, 0, 0);
    this.extrusionBodyPreview.rotation.set(0, 0, 0);
    this.extrusionBodyPreview.scale.set(1, 1, 1);

    // Ensure we have the sketch plane info - use class properties that are set by renderSketchProfiles
    if (!this.sketchPlane && this.activeSketch) {
      // Set plane info from sketch if not already set
      this.sketchPlane = this.activeSketch.plane;
      this.sketchPlaneOffset = this.activeSketch.planeOffset;
      if (this.activeSketch.plane === 'custom') {
        this.customPlaneOrigin = this.activeSketch.planeOrigin || [0, 0, 0];
        this.customPlaneNormal = this.activeSketch.planeNormal || [0, 0, 1];
        this.customPlaneXAxis = this.activeSketch.planeXAxis || [1, 0, 0];
        this.customPlaneYAxis = this.activeSketch.planeYAxis || [0, 1, 0];
      }
    }

    if (!this.sketchPlane) return;

    const offset = this.sketchPlaneOffset;
    const negativeOffset = effectiveHeight < 0 ? effectiveHeight : 0;

    switch (this.sketchPlane) {
      case 'XY':
        this.extrusionBodyPreview.position.set(0, 0, offset + negativeOffset);
        break;
      case 'XZ':
        // XZ plane: rotate +90° around X axis to lay flat (same as 2D shapes)
        // But we need to flip Z scale so extrusion goes in +Y world direction
        this.extrusionBodyPreview.rotation.x = Math.PI / 2;
        this.extrusionBodyPreview.scale.z = -1;
        this.extrusionBodyPreview.position.set(0, offset + negativeOffset, 0);
        break;
      case 'YZ':
        // Use rotation matrix matching kernel transform: Local X → World Y, Local Y → World Z
        const yzBodyMatrix = new Matrix4();
        yzBodyMatrix.makeBasis(
          new Vector3(0, 1, 0),
          new Vector3(0, 0, 1),
          new Vector3(1, 0, 0)
        );
        this.extrusionBodyPreview.rotation.setFromRotationMatrix(yzBodyMatrix);
        this.extrusionBodyPreview.position.set(offset + negativeOffset, 0, 0);
        break;
      case 'custom':
        // Use the class properties (same source as positionPreviewOnPlane)
        const origin = this.customPlaneOrigin;
        const normal = new Vector3(
          this.customPlaneNormal[0],
          this.customPlaneNormal[1],
          this.customPlaneNormal[2]
        ).normalize();
        const xAxis = new Vector3(
          this.customPlaneXAxis[0],
          this.customPlaneXAxis[1],
          this.customPlaneXAxis[2]
        ).normalize();
        const yAxis = new Vector3(
          this.customPlaneYAxis[0],
          this.customPlaneYAxis[1],
          this.customPlaneYAxis[2]
        ).normalize();

        // Create rotation matrix from local axes (SAME as positionPreviewOnPlane)
        const rotMatrix = new Matrix4();
        rotMatrix.makeBasis(xAxis, yAxis, normal);
        this.extrusionBodyPreview.rotation.setFromRotationMatrix(rotMatrix);

        // Position at plane origin (local coords are relative to this)
        // Offset along normal if extruding in negative direction
        const pos = new Vector3(origin[0], origin[1], origin[2]);
        if (effectiveHeight < 0) {
          pos.addScaledVector(normal, effectiveHeight);
        }
        this.extrusionBodyPreview.position.copy(pos);
        break;
    }
  }

  setExtrudeDirection(direction: 'positive' | 'negative'): void {
    this.extrudePositive = direction === 'positive';
    // Update the extrudeDirection based on current sketch plane
    const sign = this.extrudePositive ? 1 : -1;

    if (this.activeSketch) {
      switch (this.activeSketch.plane) {
        case 'XY':
          this.extrudeDirection = [0, 0, sign];
          break;
        case 'XZ':
          this.extrudeDirection = [0, sign, 0];
          break;
        case 'YZ':
          this.extrudeDirection = [sign, 0, 0];
          break;
        case 'custom':
          // Use the plane normal
          const normal = this.activeSketch.planeNormal || [0, 0, 1];
          this.extrudeDirection = [normal[0] * sign, normal[1] * sign, normal[2] * sign];
          break;
      }
    } else {
      this.extrudeDirection = [0, 0, sign];
    }

    this.cdr.detectChanges();
  }

  /**
   * Extrude selected profiles
   */
  async extrudeSelectedProfiles(): Promise<void> {
    if (!this.activeSketch || this.selectedProfileIds.size === 0) return;

    try {
      this.isProcessing = true;
      this.processingMessage = 'Extruding profiles...';

      await this.initializeKernel();

      // Get profile geometry with circle detection
      const profilesData = this.sketchService.getProfilesForExtrusion(
        this.activeSketch.id,
        Array.from(this.selectedProfileIds)
      );

      if (profilesData.length === 0) {
        return;
      }

      const sketchPlane = this.activeSketch.plane === 'custom' ? 'XY' : this.activeSketch.plane;
      const planeOffset = this.activeSketch.planeOffset;
      const customPlane =
        this.activeSketch.plane === 'custom'
          ? {
              origin: this.activeSketch.planeOrigin,
              normal: this.activeSketch.planeNormal,
              xAxis: this.activeSketch.planeXAxis,
              yAxis: this.activeSketch.planeYAxis
            }
          : undefined;
      const historyCustomPlane = customPlane
        ? {
            origin: [...customPlane.origin] as [number, number, number],
            normal: [...customPlane.normal] as [number, number, number],
            xAxis: [...customPlane.xAxis] as [number, number, number],
            yAxis: [...customPlane.yAxis] as [number, number, number]
          }
        : undefined;

      // Calculate effective height based on direction
      const effectiveHeight = this.extrudePositive ? this.extrudeHeight : -this.extrudeHeight;
      const historyProfiles = this.cloneProfilesData(profilesData);

      let cadShape: CADShape | null = null;

      if (profilesData.length === 1) {
        // Single profile - use optimized method for circles and rings
        const profileData = profilesData[0];

        if (profileData.isRing && profileData.ringData) {
          // Use proper ring/tube (outer cylinder - inner cylinder, no segments)
          cadShape = await this.cadKernel.extrudeRing({
            centerX: profileData.ringData.centerX,
            centerY: profileData.ringData.centerY,
            outerRadius: profileData.ringData.outerRadius,
            innerRadius: profileData.ringData.innerRadius,
            height: effectiveHeight,
            sketchPlane,
            planeOffset,
            customPlane
          });
        } else if (profileData.isCircle && profileData.circleData) {
          // Use proper cylinder (no segments)
          cadShape = await this.cadKernel.extrudeCircle({
            centerX: profileData.circleData.centerX,
            centerY: profileData.circleData.centerY,
            radius: profileData.circleData.radius,
            height: effectiveHeight,
            sketchPlane,
            planeOffset,
            customPlane
          });

          // If there are circle holes, subtract them as cylinders
          if (profileData.circleHoles && profileData.circleHoles.length > 0 && cadShape) {
            cadShape = await this.cadKernel.subtractCircleHoles(
              cadShape,
              profileData.circleHoles,
              effectiveHeight,
              sketchPlane,
              planeOffset,
              customPlane
            );
          }

          // If there are polygon holes (non-circular shapes), subtract them as extruded polygons
          if (profileData.holes && profileData.holes.length > 0 && cadShape) {
            cadShape = await this.cadKernel.subtractPolygonHoles(
              cadShape,
              profileData.holes,
              effectiveHeight,
              sketchPlane,
              planeOffset,
              customPlane
            );
          }
        } else if (profileData.isSlot && profileData.slotData) {
          // Use proper slot with arc end caps (no segments)
          cadShape = await this.cadKernel.extrudeSlot({
            startX: profileData.slotData.startX,
            startY: profileData.slotData.startY,
            endX: profileData.slotData.endX,
            endY: profileData.slotData.endY,
            width: profileData.slotData.width,
            height: effectiveHeight,
            sketchPlane,
            planeOffset,
            customPlane
          });
        } else {
          // Use polygon extrusion - don't pass polygon holes to face creation
          // (face creation fails for complex holes like slots)
          // Instead, subtract polygon holes after extrusion for reliability
          const params: ExtrudeParams = {
            points: profileData.outer,
            height: effectiveHeight,
            // Don't include polygon holes in face - subtract them afterward
            circleHoles: profileData.circleHoles,
            sketchPlane,
            planeOffset,
            customPlane
          };
          cadShape = await this.cadKernel.extrudePolygon(params);

          // Subtract polygon holes after extrusion (more reliable for complex shapes like slots)
          if (profileData.holes && profileData.holes.length > 0 && cadShape) {
            cadShape = await this.cadKernel.subtractPolygonHoles(
              cadShape,
              profileData.holes,
              effectiveHeight,
              sketchPlane,
              planeOffset,
              customPlane
            );
          }
        }
      } else {
        // Multiple profiles - use multi-profile extrusion (unions them)
        cadShape = await this.cadKernel.extrudeMultipleProfiles(
          profilesData,
          effectiveHeight,
          sketchPlane,
          planeOffset,
          customPlane
        );
      }

      if (cadShape) {
        cadShape.name = `Extrude_${this.cadShapes.length + 1}`;
        cadShape.color = '#6366f1';
        this.cadShapes.push(cadShape);
        this.addShapeToScene(cadShape);
        this.hasUnsavedChanges = true;

        this.recordHistory({
          id: this.createHistoryId('extrude'),
          createdAt: Date.now(),
          type: 'extrude-profiles',
          outputId: cadShape.id,
          params: {
            profiles: historyProfiles,
            height: effectiveHeight,
            sketchPlane,
            planeOffset,
            customPlane: historyCustomPlane
          },
          name: cadShape.name,
          color: cadShape.color
        });
      }

      this.selectedProfileIds.clear();
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Render all visible sketches (used when not in sketch edit mode)
   * Does NOT render sketch plane/grid - just entities and profiles
   */
  private renderAllVisibleSketches(): void {
    if (!this.engine) return;

    // Clear existing sketch meshes
    this.clearSketchEntityMeshes();
    this.clearSketchProfileMeshes();

    const allSketches = this.sketchService.getAllSketches();
    
    for (const sketch of allSketches) {
      // Skip hidden sketches
      if (sketch.isVisible === false) continue;

      // Render entities for this sketch
      for (const entity of sketch.entities) {
        const mesh = this.createSketchEntityMeshForSketch(entity, sketch);
        if (mesh) {
          mesh.name = `SketchEntity_${entity.id}`;
          this.engine.scene.add(mesh);
          this.sketchEntityMeshes.set(entity.id, mesh);
        }
      }

      // Render profiles for this sketch
      this.renderSketchProfilesForSketch(sketch);
    }
  }

  /**
   * Create a sketch entity mesh for a specific sketch (with plane positioning)
   */
  private createSketchEntityMeshForSketch(entity: SketchEntity, sketch: Sketch): any {
    const mesh = this.createSketchEntityMesh(entity);
    if (mesh) {
      // Position on the sketch's plane
      this.positionMeshOnSketchPlane(mesh, sketch);
    }
    return mesh;
  }

  /**
   * Position a mesh on a specific sketch's plane
   * Uses the same positioning logic as positionPreviewOnPlane for consistency
   */
  private positionMeshOnSketchPlane(mesh: any, sketch: Sketch): void {
    // Reset position and rotation before applying new transforms
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);

    switch (sketch.plane) {
      case 'XY':
        mesh.position.z = sketch.planeOffset + 0.1;
        break;
      case 'XZ':
        // XZ plane: rotate +90° around X axis to lay flat
        mesh.rotation.x = Math.PI / 2;
        mesh.position.y = sketch.planeOffset + 0.1;
        break;
      case 'YZ':
        // Use rotation matrix matching kernel transform: Local X → World Y, Local Y → World Z
        const yzMeshMatrix = new Matrix4();
        yzMeshMatrix.makeBasis(
          new Vector3(0, 1, 0),
          new Vector3(0, 0, 1),
          new Vector3(1, 0, 0)
        );
        mesh.rotation.setFromRotationMatrix(yzMeshMatrix);
        mesh.position.x = sketch.planeOffset + 0.1;
        break;
      case 'custom':
        const origin = sketch.planeOrigin;
        const normal = new Vector3(sketch.planeNormal[0], sketch.planeNormal[1], sketch.planeNormal[2]);

        // Get local axes
        const xAxis = new Vector3(sketch.planeXAxis[0], sketch.planeXAxis[1], sketch.planeXAxis[2]);
        const yAxis = new Vector3(sketch.planeYAxis[0], sketch.planeYAxis[1], sketch.planeYAxis[2]);

        // Create rotation matrix from local axes
        const rotMatrix = new Matrix4();
        rotMatrix.makeBasis(xAxis, yAxis, normal);
        mesh.rotation.setFromRotationMatrix(rotMatrix);

        // Position at plane origin
        mesh.position.set(origin[0], origin[1], origin[2]);
        // Offset slightly along normal to prevent z-fighting
        mesh.position.addScaledVector(normal, 0.1);
        break;
    }
  }

  /**
   * Render profiles for a specific sketch
   */
  private renderSketchProfilesForSketch(sketch: Sketch): void {
    if (!this.engine) return;

    for (const profile of sketch.profiles) {
      const shape = new Shape();
      const outerPoints = profile.outerLoop.points;

      if (outerPoints.length < 3) continue;

      shape.moveTo(outerPoints[0][0], outerPoints[0][1]);
      for (let i = 1; i < outerPoints.length; i++) {
        shape.lineTo(outerPoints[i][0], outerPoints[i][1]);
      }
      shape.closePath();

      // Add holes
      for (const innerLoop of profile.innerLoops) {
        const holePath = new Path();
        holePath.moveTo(innerLoop.points[0][0], innerLoop.points[0][1]);
        for (let i = 1; i < innerLoop.points.length; i++) {
          holePath.lineTo(innerLoop.points[i][0], innerLoop.points[i][1]);
        }
        holePath.closePath();
        shape.holes.push(holePath);
      }

      const geometry = new ShapeGeometry(shape);
      const material = new MeshBasicMaterial({
        color: 0x0891b2, // Teal for non-active sketches
        transparent: true,
        opacity: 0.3,
        side: DoubleSide,
        depthTest: false
      });

      const group = new Group();
      group.name = `SketchProfile_${profile.id}`;
      group.userData = { profileId: profile.id, sketchId: sketch.id };

      const mesh = new Mesh(geometry, material);
      mesh.userData = { profileId: profile.id };
      mesh.renderOrder = 50;
      group.add(mesh);

      // Add edge lines
      const edgeColor = 0x67e8f9;
      const outerEdgePoints2D = outerPoints.map(([x, y]) => new Vector3(x, y, 0));
      outerEdgePoints2D.push(outerEdgePoints2D[0].clone());
      const outerEdgeGeom = new BufferGeometry().setFromPoints(outerEdgePoints2D);
      const edgeMaterial = new LineBasicMaterial({
        color: edgeColor,
        linewidth: 2,
        depthTest: false
      });
      const outerEdge = new Line(outerEdgeGeom, edgeMaterial);
      outerEdge.renderOrder = 110;
      group.add(outerEdge);

      // Inner edges (holes)
      for (const innerLoop of profile.innerLoops) {
        const innerEdgePoints2D = innerLoop.points.map(([x, y]) => new Vector3(x, y, 0));
        innerEdgePoints2D.push(innerEdgePoints2D[0].clone());
        const innerEdgeGeom = new BufferGeometry().setFromPoints(innerEdgePoints2D);
        const innerEdge = new Line(innerEdgeGeom, edgeMaterial.clone());
        innerEdge.renderOrder = 110;
        group.add(innerEdge);
      }

      // Position on sketch plane
      this.positionMeshOnSketchPlane(group, sketch);

      this.engine.scene.add(group);
      this.sketchProfileMeshes.set(profile.id, group);
    }
  }

  /**
   * Hide all sketch meshes except for a specific sketch
   */
  private hideOtherSketchMeshes(activeSketchId: string): void {
    const allSketches = this.sketchService.getAllSketches();
    
    for (const sketch of allSketches) {
      if (sketch.id === activeSketchId) continue;
      
      // Hide entities of other sketches
      for (const entity of sketch.entities) {
        const mesh = this.sketchEntityMeshes.get(entity.id);
        if (mesh) {
          mesh.visible = false;
        }
      }
      
      // Hide profiles of other sketches
      for (const profile of sketch.profiles) {
        const mesh = this.sketchProfileMeshes.get(profile.id);
        if (mesh) {
          mesh.visible = false;
        }
      }
    }
  }

  /**
   * Render sketch entities as 2D shapes on the sketch plane
   */
  private renderSketchEntities(): void {
    if (!this.activeSketch || !this.engine) return;

    // Clear existing meshes
    this.clearSketchEntityMeshes();

    // Check if sketch is visible
    const isSketchVisible = this.activeSketch.isVisible !== false;

    for (const entity of this.activeSketch.entities) {
      const mesh = this.createSketchEntityMesh(entity);
      if (mesh) {
        mesh.name = `SketchEntity_${entity.id}`;
        // Set visibility based on sketch visibility
        mesh.visible = isSketchVisible;
        this.engine.scene.add(mesh);
        this.sketchEntityMeshes.set(entity.id, mesh);
      }
    }

    // Update snap points for sketch entities
    this.updateSketchSnapPoints();

    // Also render profiles
    this.renderSketchProfiles();
  }

  /**
   * Update snap engine with sketch entity snap points
   */
  private updateSketchSnapPoints(): void {
    if (!this.activeSketch) {
      this.snapEngine.clearSketchSnapPoints();
      return;
    }

    // Convert entities to snap point format
    const snapEntities = this.activeSketch.entities.map((entity) => {
      const points = this.sketchService.entityToPoints(entity);

      // Get center and radius for circles, arcs, polygons
      let center: { x: number; y: number } | undefined;
      let radius: number | undefined;

      switch (entity.type) {
        case 'circle':
          const circle = entity as any;
          center = { x: circle.centerX, y: circle.centerY };
          radius = circle.radius;
          break;
        case 'arc':
          const arc = entity as any;
          center = { x: arc.centerX, y: arc.centerY };
          radius = arc.radius;
          break;
        case 'polygon':
          const polygon = entity as any;
          center = { x: polygon.centerX, y: polygon.centerY };
          radius = polygon.radius;
          break;
        case 'rectangle':
          const rect = entity as any;
          center = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
          break;
        case 'slot':
          const slot = entity as any;
          center = { x: (slot.startX + slot.endX) / 2, y: (slot.startY + slot.endY) / 2 };
          break;
        case 'line':
          const line = entity as any;
          center = { x: (line.startX + line.endX) / 2, y: (line.startY + line.endY) / 2 };
          break;
      }

      return {
        id: entity.id,
        type: entity.type,
        points: points as [number, number][],
        center,
        radius
      };
    });

    this.snapEngine.loadSketchEntitySnapPoints(snapEntities, this.activeSketch.planeOffset);
  }

  /**
   * Create Three.js mesh for a sketch entity (with fill for selection)
   */
  private createSketchEntityMesh(entity: SketchEntity): any {
    const points = this.sketchService.entityToPoints(entity);
    if (points.length < 2) return null;

    // Create a group to hold both the fill and outline
    const group = new Group();
    group.userData = { entityId: entity.id, isHovered: false };

    // For closed shapes, create a filled shape for better click detection
    const isClosed = ['circle', 'rectangle', 'polygon', 'slot'].includes(entity.type);

    // Determine visual state
    const isSelected = entity.isSelected;
    const isHovered = entity.isHovered;
    const isConstruction = entity.isConstruction;

    // Color scheme:
    // Normal: amber fill (0xfbbf24), white edges
    // Hovered: cyan fill (0x22d3ee), cyan edges
    // Selected: green fill (0x4ade80), green edges
    // Construction: no fill, gray dashed edges

    if (isClosed && points.length >= 3 && !isConstruction) {
      // Create filled shape using 2D local coordinates
      const shape2D = new Shape();
      shape2D.moveTo(points[0][0], points[0][1]);
      for (let i = 1; i < points.length; i++) {
        shape2D.lineTo(points[i][0], points[i][1]);
      }
      shape2D.closePath();

      const fillGeometry = new ShapeGeometry(shape2D);

      // Fill colors based on state - much brighter for better visibility
      let fillColor = 0xfbbf24; // Normal: amber
      let fillOpacity = 0.2;

      if (isSelected) {
        fillColor = 0x22c55e; // Selected: bright green
        fillOpacity = 0.5;
      } else if (isHovered) {
        fillColor = 0x06b6d4; // Hovered: bright cyan
        fillOpacity = 0.35;
      }

      const fillMaterial = new MeshBasicMaterial({
        color: fillColor,
        transparent: true,
        opacity: fillOpacity,
        side: DoubleSide,
        depthTest: false
      });

      const fillMesh = new Mesh(fillGeometry, fillMaterial);
      fillMesh.userData = { entityId: entity.id, isFill: true };
      fillMesh.renderOrder = 90;
      group.add(fillMesh);
    }

    // Create outline using 2D local coordinates (same as fill)
    const points2D: Vector3[] = points.map(([x, y]) => new Vector3(x, y, 0));
    const closedPoints2D = isClosed ? [...points2D, points2D[0].clone()] : points2D;
    const lineGeometry = new BufferGeometry().setFromPoints(closedPoints2D);

    // Edge colors based on state - very bright for visibility
    let edgeColor = 0xffffff; // Normal: white
    let lineWidth = 1;

    if (isSelected) {
      edgeColor = 0x4ade80; // Selected: bright green
      lineWidth = 3;
    } else if (isHovered) {
      edgeColor = 0x22d3ee; // Hovered: cyan
      lineWidth = 2;
    } else if (isConstruction) {
      edgeColor = 0x9ca3af; // Construction: gray
    }

    let material: LineBasicMaterial | LineDashedMaterial;
    let line: Line;

    if (isConstruction) {
      // Use dashed lines for construction geometry
      material = new LineDashedMaterial({
        color: edgeColor,
        linewidth: 2,
        depthTest: false,
        dashSize: 5,
        gapSize: 3
      });
      line = new Line(lineGeometry, material);
      line.computeLineDistances();
    } else {
      material = new LineBasicMaterial({
        color: edgeColor,
        linewidth: lineWidth,
        depthTest: false
      });
      line = new Line(lineGeometry, material);
    }

    line.userData = { entityId: entity.id, isLine: true };
    line.renderOrder = 100;
    group.add(line);

    // Position the entire group on the sketch plane (applies to both fill and outline)
    this.positionPreviewOnPlane(group);

    return group;
  }

  /**
   * Update entity hover state and re-render
   */
  private setEntityHovered(entityId: string, isHovered: boolean): void {
    if (!this.activeSketch) return;
    
    // Don't re-render if sketch is hidden
    if (this.activeSketch.isVisible === false) return;

    const entity = this.activeSketch.entities.find((e) => e.id === entityId);
    if (!entity || entity.isHovered === isHovered) return;

    entity.isHovered = isHovered;

    // Only re-render when in sketch edit mode (plane properties are set)
    // Otherwise just change material colors without re-rendering position
    if (this.isSketchMode) {
      this.removeEntityMesh(entityId);
      this.renderSingleSketchEntity(entity);
    } else {
      // When not in sketch mode, just update material colors
      const mesh = this.sketchEntityMeshes.get(entityId);
      if (mesh) {
        mesh.traverse((child: any) => {
          if (child.material) {
            if (isHovered) {
              // Store original color
              if (!child.userData.originalColor && child.material.color) {
                child.userData.originalColor = child.material.color.getHex();
              }
              if (!child.userData.originalOpacity && child.material.opacity !== undefined) {
                child.userData.originalOpacity = child.material.opacity;
              }
              // Set hover color (cyan)
              if (child.material.color) {
                child.material.color.setHex(0x22d3ee);
              }
              if (child.material.opacity !== undefined) {
                child.material.opacity = Math.min((child.userData.originalOpacity || 0.3) + 0.15, 1);
              }
            } else {
              // Restore original colors
              if (child.userData.originalColor !== undefined && child.material.color) {
                child.material.color.setHex(child.userData.originalColor);
              }
              if (child.userData.originalOpacity !== undefined && child.material.opacity !== undefined) {
                child.material.opacity = child.userData.originalOpacity;
              }
            }
          }
        });
      }
    }
  }

  /**
   * Set entity hover state by ID (material change only, no re-render)
   * Used for hover effects when not in sketch edit mode
   */
  private setEntityHoveredById(entityId: string, isHovered: boolean): void {
    const mesh = this.sketchEntityMeshes.get(entityId);
    if (!mesh) return;

    mesh.traverse((child: any) => {
      if (child.material) {
        if (isHovered) {
          // Store original color
          if (!child.userData.originalColor && child.material.color) {
            child.userData.originalColor = child.material.color.getHex();
          }
          if (!child.userData.originalOpacity && child.material.opacity !== undefined) {
            child.userData.originalOpacity = child.material.opacity;
          }
          // Set hover color (cyan)
          if (child.material.color) {
            child.material.color.setHex(0x22d3ee);
          }
          if (child.material.opacity !== undefined) {
            child.material.opacity = Math.min((child.userData.originalOpacity || 0.3) + 0.15, 1);
          }
        } else {
          // Restore original colors
          if (child.userData.originalColor !== undefined && child.material.color) {
            child.material.color.setHex(child.userData.originalColor);
          }
          if (child.userData.originalOpacity !== undefined && child.material.opacity !== undefined) {
            child.material.opacity = child.userData.originalOpacity;
          }
        }
      }
    });
  }

  /**
   * Render sketch profiles as filled shapes
   */
  private renderSketchProfiles(): void {
    if (!this.activeSketch || !this.engine) return;

    // Check if sketch is visible
    const isSketchVisible = this.activeSketch.isVisible !== false;

    // Set plane info from the active sketch (needed for positioning)
    this.sketchPlane = this.activeSketch.plane;
    this.sketchPlaneOffset = this.activeSketch.planeOffset;
    if (this.activeSketch.plane === 'custom') {
      this.customPlaneOrigin = this.activeSketch.planeOrigin;
      this.customPlaneNormal = this.activeSketch.planeNormal;
      this.customPlaneXAxis = this.activeSketch.planeXAxis;
      this.customPlaneYAxis = this.activeSketch.planeYAxis;
    }

    // Clear existing profile meshes
    this.clearSketchProfileMeshes();

    for (const profile of this.activeSketch.profiles) {
      // Convert profile points to 2D for Shape
      const shape = new Shape();
      const outerPoints = profile.outerLoop.points;

      if (outerPoints.length < 3) continue;

      shape.moveTo(outerPoints[0][0], outerPoints[0][1]);
      for (let i = 1; i < outerPoints.length; i++) {
        shape.lineTo(outerPoints[i][0], outerPoints[i][1]);
      }
      shape.closePath();

      // Add holes (must be Path, not Shape)
      for (const innerLoop of profile.innerLoops) {
        const holePath = new Path();
        holePath.moveTo(innerLoop.points[0][0], innerLoop.points[0][1]);
        for (let i = 1; i < innerLoop.points.length; i++) {
          holePath.lineTo(innerLoop.points[i][0], innerLoop.points[i][1]);
        }
        holePath.closePath();
        shape.holes.push(holePath);
      }

      const geometry = new ShapeGeometry(shape);
      const isSelected = this.selectedProfileIds.has(profile.id) || profile.isSelected;
      const material = new MeshBasicMaterial({
        // Distinct colors: teal for unselected, bright green for selected
        color: isSelected ? 0x22c55e : 0x0891b2,
        transparent: true,
        opacity: isSelected ? 0.55 : 0.3,
        side: DoubleSide,
        depthTest: false
      });

      // Create group for profile (fill + edges)
      const group = new Group();
      group.name = `SketchProfile_${profile.id}`;
      group.userData = { profileId: profile.id };

      const mesh = new Mesh(geometry, material);
      mesh.userData = { profileId: profile.id };
      mesh.renderOrder = 50;
      group.add(mesh);

      // Add edge lines for better visibility (use 2D local coords like the fill)
      // Very bright edges for profiles - white/cyan for normal, bright green for selected
      const edgeColor = isSelected ? 0x4ade80 : 0x67e8f9;
      const edgeWidth = isSelected ? 3 : 2;

      // Outer edge - use 2D coordinates (z=0) to match the fill mesh
      const outerEdgePoints2D = outerPoints.map(([x, y]) => new Vector3(x, y, 0));
      outerEdgePoints2D.push(outerEdgePoints2D[0].clone());
      const outerEdgeGeom = new BufferGeometry().setFromPoints(outerEdgePoints2D);
      const edgeMaterial = new LineBasicMaterial({
        color: edgeColor,
        linewidth: edgeWidth,
        depthTest: false
      });
      const outerEdge = new Line(outerEdgeGeom, edgeMaterial);
      outerEdge.renderOrder = 110;
      group.add(outerEdge);

      // Inner edges (holes) - use 2D coordinates to match the fill mesh
      for (const innerLoop of profile.innerLoops) {
        const innerEdgePoints2D = innerLoop.points.map(([x, y]) => new Vector3(x, y, 0));
        innerEdgePoints2D.push(innerEdgePoints2D[0].clone());
        const innerEdgeGeom = new BufferGeometry().setFromPoints(innerEdgePoints2D);
        const innerEdge = new Line(innerEdgeGeom, edgeMaterial.clone());
        innerEdge.renderOrder = 110;
        group.add(innerEdge);
      }

      // Position on sketch plane
      this.positionPreviewOnPlane(group);

      // Set visibility based on sketch visibility
      group.visible = isSketchVisible;

      this.engine.scene.add(group);
      this.sketchProfileMeshes.set(profile.id, group);
    }
  }

  /**
   * Clear sketch entity meshes from scene
   */
  /**
   * Dispose an Object3D and all its children (geometry and materials)
   * Handles both Groups and single Mesh/Line objects
   */
  private disposeObject3D(obj: Object3D): void {
    obj.traverse((child: any) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach((m: any) => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
  }

  private clearSketchEntityMeshes(): void {
    if (!this.engine) return;

    this.sketchEntityMeshes.forEach((obj) => {
      this.engine!.scene.remove(obj);
      this.disposeObject3D(obj);
    });
    this.sketchEntityMeshes.clear();
  }

  /**
   * Clear sketch profile meshes from scene
   */
  private clearSketchProfileMeshes(): void {
    if (!this.engine) return;

    this.sketchProfileMeshes.forEach((obj) => {
      this.engine!.scene.remove(obj);
      this.disposeObject3D(obj);
    });
    this.sketchProfileMeshes.clear();
  }

  private boundSketchEntityDblClickHandler: ((e: MouseEvent) => void) | null = null;
  private boundSketchEntityHoverHandler: ((e: MouseEvent) => void) | null = null;
  private hoveredEntityId: string | null = null;

  /**
   * Setup click handler for selecting sketch entities/profiles in scene
   */
  private setupSketchEntityClickHandler(): void {
    if (!this.engine) return;

    // Remove existing handler first
    this.removeSketchEntityClickHandler();

    this.boundSketchEntityClickHandler = (event: MouseEvent) => {
      // Only handle clicks when not drawing
      if (this.isDrawing || this.drawingMode !== 'none') return;

      this.onSketchEntitySceneClick(event);
    };

    // Double-click to edit entity
    this.boundSketchEntityDblClickHandler = (event: MouseEvent) => {
      if (this.isDrawing || this.drawingMode !== 'none') return;
      this.onSketchEntitySceneDblClick(event);
    };

    // Hover handler for entity highlighting
    this.boundSketchEntityHoverHandler = (event: MouseEvent) => {
      if (this.isDrawing || this.drawingMode !== 'none') return;
      this.onSketchEntitySceneHover(event);
    };

    this.engine.canvas.addEventListener('click', this.boundSketchEntityClickHandler);
    this.engine.canvas.addEventListener('dblclick', this.boundSketchEntityDblClickHandler);
    this.engine.canvas.addEventListener('mousemove', this.boundSketchEntityHoverHandler);
  }

  /**
   * Remove sketch entity click handler
   */
  private removeSketchEntityClickHandler(): void {
    if (this.boundSketchEntityClickHandler && this.engine) {
      this.engine.canvas.removeEventListener('click', this.boundSketchEntityClickHandler);
      this.boundSketchEntityClickHandler = null;
    }
    if (this.boundSketchEntityDblClickHandler && this.engine) {
      this.engine.canvas.removeEventListener('dblclick', this.boundSketchEntityDblClickHandler);
      this.boundSketchEntityDblClickHandler = null;
    }
    if (this.boundSketchEntityHoverHandler && this.engine) {
      this.engine.canvas.removeEventListener('mousemove', this.boundSketchEntityHoverHandler);
      this.boundSketchEntityHoverHandler = null;
    }
    // Clear any hover state
    if (this.hoveredEntityId) {
      this.setEntityHovered(this.hoveredEntityId, false);
      this.hoveredEntityId = null;
    }
  }

  /**
   * Handle mouse move over sketch entities for hover effect
   */
  private onSketchEntitySceneHover(event: MouseEvent): void {
    if (!this.engine || !this.activeSketch) return;

    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    // Raycast against entity meshes
    const meshes = Array.from(this.sketchEntityMeshes.values());
    const intersects = raycaster.intersectObjects(meshes, true);

    let newHoveredId: string | null = null;

    if (intersects.length > 0) {
      // Find the entity ID from the intersected object
      let hitObject = intersects[0].object;
      while (hitObject && !hitObject.userData?.['entityId']) {
        hitObject = hitObject.parent as any;
      }
      if (hitObject?.userData?.['entityId']) {
        newHoveredId = hitObject.userData['entityId'];
      }
    }

    // Update hover state only if changed
    if (newHoveredId !== this.hoveredEntityId) {
      // Clear previous hover
      if (this.hoveredEntityId) {
        this.setEntityHovered(this.hoveredEntityId, false);
      }
      // Set new hover
      if (newHoveredId) {
        this.setEntityHovered(newHoveredId, true);
      }
      this.hoveredEntityId = newHoveredId;
    }
  }

  /**
   * Handle double-click to edit entity
   */
  private onSketchEntitySceneDblClick(event: MouseEvent): void {
    if (!this.engine || !this.activeSketch) return;

    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    // Only check entity meshes for editing
    const entityMeshes: Object3D[] = [];
    this.sketchEntityMeshes.forEach((mesh, entityId) => {
      mesh.userData = { ...mesh.userData, entityId };
      entityMeshes.push(mesh);
    });

    const intersects = raycaster.intersectObjects(entityMeshes, true);

    if (intersects.length > 0) {
      const hitObject = intersects[0].object;
      let entityId: string | null = null;

      // Walk up to find entity ID
      let obj: Object3D | null = hitObject;
      while (obj) {
        if (obj.userData?.['entityId']) {
          entityId = obj.userData['entityId'];
          break;
        }
        obj = obj.parent;
      }

      if (entityId) {
        // Start editing the entity
        this.startEntityEdit(entityId);
      }
    }
  }

  /**
   * Handle click on sketch entities in scene
   */
  private onSketchEntitySceneClick(event: MouseEvent): void {
    if (!this.engine || !this.activeSketch) return;

    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    // Collect all entity meshes
    const entityMeshes: Object3D[] = [];
    this.sketchEntityMeshes.forEach((mesh, entityId) => {
      mesh.userData = { ...mesh.userData, entityId };
      entityMeshes.push(mesh);
    });

    // Also check profile meshes
    this.sketchProfileMeshes.forEach((mesh, profileId) => {
      mesh.userData = { ...mesh.userData, profileId };
      entityMeshes.push(mesh);
    });

    const intersects = raycaster.intersectObjects(entityMeshes, true);

    if (intersects.length > 0) {
      const hitObject = intersects[0].object;
      let entityId: string | null = null;
      let profileId: string | null = null;

      // Walk up to find entity/profile ID
      let obj: Object3D | null = hitObject;
      while (obj) {
        if (obj.userData?.['entityId']) {
          entityId = obj.userData['entityId'];
          break;
        }
        if (obj.userData?.['profileId']) {
          profileId = obj.userData['profileId'];
          break;
        }
        obj = obj.parent;
      }

      const ctrlKey = event.ctrlKey || event.metaKey;
      const shiftKey = event.shiftKey;
      const addToSelection = ctrlKey || shiftKey;

      // Only allow profile selection via mouse (entities are selected in tree view)
      if (profileId) {
        // Update local selection set
        if (addToSelection) {
          if (this.selectedProfileIds.has(profileId)) {
            this.selectedProfileIds.delete(profileId);
          } else {
            this.selectedProfileIds.add(profileId);
          }
        } else {
          this.selectedProfileIds.clear();
          this.selectedProfileIds.add(profileId);
        }

        // Update service selection state too
        this.sketchService.selectProfile(profileId, addToSelection);

        this.renderSketchProfiles(); // Re-render to show selection
        this.cdr.detectChanges();
      }
    } else {
      // Click on empty area - clear selection
      this.selectedProfileIds.clear();
      this.sketchService.clearSelection();

      // End entity editing mode and clean up handles
      if (this.isEntityEditMode) {
        this.endEntityEdit();
      }

      // Re-render to show cleared selection
      if (this.activeSketch) {
        this.renderSketchProfiles();
        this.renderSketchEntities(); // Re-render entities to update selection visuals
      }
      this.cdr.detectChanges();
    }
  }

  /**
   * Dim/restore CAD bodies during sketch mode
   */
  private dimCADBodies(dim: boolean): void {
    this.sceneMeshes.forEach((group, id) => {
      const mesh = group.children.find((c: any) => c.type === 'Mesh') as Mesh;
      if (mesh && mesh.material) {
        const mat = mesh.material as MeshPhysicalMaterial;
        if (dim) {
          mat.transparent = true;
          mat.opacity = 0.3;
        } else {
          mat.transparent = false;
          mat.opacity = 1;
        }
        mat.needsUpdate = true;
      }
    });
  }

  /**
   * Get the center point of a profile's outer loop
   * @param profile The sketch profile
   * @returns The center as {x, y} in 2D local coordinates
   */
  private getProfileCenter(profile: SketchProfile): { x: number; y: number } {
    const outerPoints = profile.outerLoop.points;
    if (outerPoints.length === 0) return { x: 0, y: 0 };

    let centerX = 0,
      centerY = 0;
    for (const [x, y] of outerPoints) {
      centerX += x;
      centerY += y;
    }
    return {
      x: centerX / outerPoints.length,
      y: centerY / outerPoints.length
    };
  }

  /**
   * Get world position from 2D local coords + height offset along the sketch plane normal
   * @param localX X in local 2D plane coordinates
   * @param localY Y in local 2D plane coordinates
   * @param heightOffset Offset along the plane normal (default: 0)
   * @returns World position as Vector3
   */
  private getWorldPositionOnPlane(localX: number, localY: number, heightOffset: number = 0): Vector3 {
    switch (this.sketchPlane) {
      case 'XY':
        return new Vector3(localX, localY, this.sketchPlaneOffset + heightOffset);
      case 'XZ':
        return new Vector3(localX, this.sketchPlaneOffset + heightOffset, localY);
      case 'YZ':
        return new Vector3(this.sketchPlaneOffset + heightOffset, localX, localY);
      case 'custom':
        const origin = new Vector3(this.customPlaneOrigin[0], this.customPlaneOrigin[1], this.customPlaneOrigin[2]);
        const xAxis = new Vector3(
          this.customPlaneXAxis[0],
          this.customPlaneXAxis[1],
          this.customPlaneXAxis[2]
        ).normalize();
        const yAxis = new Vector3(
          this.customPlaneYAxis[0],
          this.customPlaneYAxis[1],
          this.customPlaneYAxis[2]
        ).normalize();
        const normal = new Vector3(
          this.customPlaneNormal[0],
          this.customPlaneNormal[1],
          this.customPlaneNormal[2]
        ).normalize();

        return origin
          .add(xAxis.clone().multiplyScalar(localX))
          .add(yAxis.clone().multiplyScalar(localY))
          .add(normal.clone().multiplyScalar(heightOffset));
      default:
        return new Vector3(localX, localY, heightOffset);
    }
  }

  /**
   * Get the normal vector for the current sketch plane
   */
  private getPlaneNormal(): Vector3 {
    switch (this.sketchPlane) {
      case 'XY':
        return new Vector3(0, 0, 1);
      case 'XZ':
        return new Vector3(0, 1, 0);
      case 'YZ':
        return new Vector3(1, 0, 0);
      case 'custom':
        return new Vector3(this.customPlaneNormal[0], this.customPlaneNormal[1], this.customPlaneNormal[2]).normalize();
      default:
        return new Vector3(0, 0, 1);
    }
  }

  /**
   * Convert plane local coordinates to world coordinates
   */
  private planeLocalToWorld(point: { x: number; y: number }): Vector3 {
    // If we have an active sketch, use its plane info
    if (this.activeSketch) {
      const origin = new Vector3(...this.activeSketch.planeOrigin);
      const xAxis = new Vector3(...this.activeSketch.planeXAxis);
      const yAxis = new Vector3(...this.activeSketch.planeYAxis);
      const normal = new Vector3(...this.activeSketch.planeNormal);

      const offsetOrigin = origin.clone().add(normal.clone().multiplyScalar(this.activeSketch.planeOffset));

      return offsetOrigin.clone().add(xAxis.clone().multiplyScalar(point.x)).add(yAxis.clone().multiplyScalar(point.y));
    }

    // Otherwise, use the current sketchPlane settings
    switch (this.sketchPlane) {
      case 'XY':
        return new Vector3(point.x, point.y, this.sketchPlaneOffset);
      case 'XZ':
        return new Vector3(point.x, this.sketchPlaneOffset, point.y);
      case 'YZ':
        // Kernel transform: (localX, localY) → (0, localX, localY)
        return new Vector3(this.sketchPlaneOffset, point.x, point.y);
      case 'custom':
        // Use custom plane axes
        const origin = new Vector3(this.customPlaneOrigin[0], this.customPlaneOrigin[1], this.customPlaneOrigin[2]);
        const xAxis = new Vector3(this.customPlaneXAxis[0], this.customPlaneXAxis[1], this.customPlaneXAxis[2]);
        const yAxis = new Vector3(this.customPlaneYAxis[0], this.customPlaneYAxis[1], this.customPlaneYAxis[2]);

        return origin.clone().add(xAxis.clone().multiplyScalar(point.x)).add(yAxis.clone().multiplyScalar(point.y));
      default:
        return new Vector3(point.x, point.y, 0);
    }
  }

  /**
   * Finish drawing and add entity to sketch (instead of creating 3D shape)
   */
  private finishDrawingAsSketchEntity(): void {
    if (!this.activeSketch || !this.drawingStartPoint) {
      this.cancelDrawing();
      return;
    }

    let entity: SketchEntity | null = null;

    // Convert world coordinates to local 2D coordinates
    const start2D = this.worldToPlaneLocal(this.drawingStartPoint);
    const current2D = this.drawingCurrentPoint ? this.worldToPlaneLocal(this.drawingCurrentPoint) : start2D;

    if (this.drawingMode === 'rectangle') {
      const width = Math.abs(current2D.x - start2D.x);
      const height = Math.abs(current2D.y - start2D.y);

      if (width < 1 || height < 1) {
        this.cancelDrawing();
        return;
      }

      const x = Math.min(start2D.x, current2D.x);
      const y = Math.min(start2D.y, current2D.y);

      entity = this.sketchService.createRectangle(x, y, width, height, 0, this.isConstructionMode);
    } else if (this.drawingMode === 'circle') {
      const dx = current2D.x - start2D.x;
      const dy = current2D.y - start2D.y;
      const radius = Math.sqrt(dx * dx + dy * dy);

      if (radius < 1) {
        this.cancelDrawing();
        return;
      }

      entity = this.sketchService.createCircle(start2D.x, start2D.y, radius, this.isConstructionMode);
    } else if (this.drawingMode === 'polygon') {
      const dx = current2D.x - start2D.x;
      const dy = current2D.y - start2D.y;
      const radius = Math.sqrt(dx * dx + dy * dy);

      if (radius < 1) {
        this.cancelDrawing();
        return;
      }

      entity = this.sketchService.createPolygon(
        start2D.x,
        start2D.y,
        radius,
        this.polygonSides,
        0,
        this.isConstructionMode
      );
    } else if (this.drawingMode === 'line' && this.linePoints.length >= 2) {
      // For polylines, create multiple line entities
      for (let i = 0; i < this.linePoints.length - 1; i++) {
        const pt1 = this.worldToPlaneLocal(this.linePoints[i]);
        const pt2 = this.worldToPlaneLocal(this.linePoints[i + 1]);
        this.sketchService.createLine(pt1.x, pt1.y, pt2.x, pt2.y, this.isConstructionMode);
      }
      // Close if it's a closed shape (first and last points close)
      if (this.linePoints.length >= 3) {
        const first = this.worldToPlaneLocal(this.linePoints[0]);
        const last = this.worldToPlaneLocal(this.linePoints[this.linePoints.length - 1]);
        const dist = Math.sqrt((first.x - last.x) ** 2 + (first.y - last.y) ** 2);
        if (dist > 1) {
          // Close the shape
          this.sketchService.createLine(last.x, last.y, first.x, first.y, this.isConstructionMode);
        }
      }
      entity = {} as SketchEntity; // Just for the toast
    }

    if (entity) {
      this.renderSketchEntities();
    }

    // Reset drawing state
    this.cancelDrawing();
    this.setDrawingMode('none');
  }

  /**
   * Toggle flyout panel visibility
   */
  togglePanel(panel: 'create' | 'operations' | 'add-body' | 'history'): void {
    if (this.activePanel === panel) {
      this.activePanel = null;
    } else {
      this.activePanel = panel;

      // Load available model parts when opening create or add-body panel
      if (panel === 'create' || panel === 'add-body') {
        this.loadAvailableModelParts();
      }
    }
  }

  /**
   * Toggle pattern group expand/collapse (accordion behavior)
   */
  togglePatternGroup(groupId: string): void {
    this.expandedPatternGroup = this.expandedPatternGroup === groupId ? null : groupId;
  }

  /**
   * Close all flyout panels
   */
  closePanel(): void {
    this.activePanel = null;
  }

  /**
   * Exit the CAD editor
   * Shows confirmation dialog if there are unsaved changes
   */
  exitEditor(): void {
    if (this.hasUnsavedChanges) {
      this.showConfirm(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to exit? All changes will be lost.',
        () => {
          this.hasUnsavedChanges = false;
          this.onExit.emit();
        }
      );
    } else {
      this.onExit.emit();
    }
  }

  /**
   * Initialize CAD kernel (lazy load WASM)
   */
  async initializeKernel(): Promise<void> {
    if (this.cadKernel.isReady()) {
      return;
    }

    try {
      this.isProcessing = true;
      this.processingMessage = 'Loading CAD engine...';
      await this.cadKernel.ensureLoaded();
      this.processingMessage = '';
    } catch (error) {
      this.onError.emit('Failed to load CAD engine');
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EXTRUSION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check if extrusion is possible
   */
  canExtrude(): boolean {
    return this.selectedShapes.length === 1 && !this.isProcessing;
  }

  /**
   * Extrude selected shape
   */
  async extrudeSelected(): Promise<void> {
    if (!this.canExtrude()) return;

    const shape = this.selectedShapes[0];

    try {
      this.isProcessing = true;
      this.processingMessage = 'Extruding shape...';

      await this.initializeKernel();

      const params: ExtrudeParams = {
        points: shape.points,
        height: shape.extrude_height || this.extrudeHeight,
        direction: this.extrudeDirection,
        holes: shape.holes
      };

      const cadShape = await this.cadKernel.extrudePolygon(params);
      cadShape.color = shape.color || '#6366f1';
      cadShape.name = shape.name || 'Extrusion';

      // Add to local list and scene
      this.cadShapes.push(cadShape);
      this.addShapeToScene(cadShape);

      this.onExtrude.emit({ shape, cadShape });
      this.processingMessage = '';

      const historyParams: ExtrudeParams = {
        points: params.points.map((point) => [...point]),
        height: params.height,
        direction: params.direction ? [...params.direction] as [number, number, number] : undefined,
        holes: params.holes ? params.holes.map((hole) => hole.map((point) => [...point])) : undefined
      };

      this.recordHistory({
        id: this.createHistoryId('extrude'),
        createdAt: Date.now(),
        type: 'extrude-polygon',
        outputId: cadShape.id,
        params: historyParams,
        name: cadShape.name,
        color: cadShape.color
      });
    } catch (error: any) {
      this.onError.emit(`Extrusion failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Extrude all selected shapes
   */
  async extrudeAll(): Promise<void> {
    if (this.selectedShapes.length === 0 || this.isProcessing) return;

    try {
      this.isProcessing = true;
      await this.initializeKernel();

      for (const shape of this.selectedShapes) {
        this.processingMessage = `Extruding ${shape.name}...`;

        const params: ExtrudeParams = {
          points: shape.points,
          height: shape.extrude_height || this.extrudeHeight,
          direction: this.extrudeDirection,
          holes: shape.holes
        };

        const cadShape = await this.cadKernel.extrudePolygon(params);
        cadShape.color = shape.color || '#6366f1';
        cadShape.name = shape.name || 'Extrusion';

        // Add to local list and scene
        this.cadShapes.push(cadShape);
        this.addShapeToScene(cadShape);

        this.onExtrude.emit({ shape, cadShape });

        const historyParams: ExtrudeParams = {
          points: params.points.map((point) => [...point]),
          height: params.height,
          direction: params.direction ? [...params.direction] as [number, number, number] : undefined,
          holes: params.holes ? params.holes.map((hole) => hole.map((point) => [...point])) : undefined
        };

        this.recordHistory({
          id: this.createHistoryId('extrude'),
          createdAt: Date.now(),
          type: 'extrude-polygon',
          outputId: cadShape.id,
          params: historyParams,
          name: cadShape.name,
          color: cadShape.color
        });
      }

      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Extrusion failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BOOLEAN OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check if boolean operation is possible
   */
  canBoolean(): boolean {
    return this.cadShapes.length >= 2 && this.selectedCADShapeIds.size >= 2 && !this.isProcessing;
  }

  /**
   * Get selected CAD shapes for boolean
   */
  private getSelectedCADShapes(): CADShape[] {
    return this.cadShapes.filter((s) => this.selectedCADShapeIds.has(s.id));
  }

  /**
   * Perform union of selected CAD shapes
   */
  async performUnion(): Promise<void> {
    if (!this.canBoolean()) return;

    // Cleanup multi-select pivot first to restore shapes to cadGroup
    this.cleanupMultiSelectPivot();
    this.detachTransformControls();

    // Commit any pending transforms before boolean operation
    await this.commitPendingTransforms();

    const selected = this.getSelectedCADShapesInOrder();
    if (selected.length < 2) return;
    const inputIds = selected.map((s) => s.id);

    try {
      this.isProcessing = true;
      this.processingMessage = 'Performing union...';

      // Collect IDs of original shapes to remove
      const originalIds = new Set(selected.map((s) => s.id));

      // Track intermediate results for cleanup
      const intermediateIds: string[] = [];

      let result = selected[0];
      for (let i = 1; i < selected.length; i++) {
        const newResult = await this.cadKernel.booleanUnion(result, selected[i]);

        // If this isn't the first iteration, the previous result was intermediate
        if (i > 1) {
          intermediateIds.push(result.id);
        }
        result = newResult;
      }

      // Remove original shapes from scene, list, and kernel
      for (const id of originalIds) {
        this.removeShapeFromScene(id);
        this.cadKernel.deleteShape(id);
      }

      // Clean up intermediate shapes from kernel
      for (const id of intermediateIds) {
        this.cadKernel.deleteShape(id);
      }

      this.cadShapes = this.cadShapes.filter((s) => !originalIds.has(s.id));

      // Add final result
      this.cadShapes.push(result);
      this.addShapeToScene(result);

      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(result.id);
      this.booleanSelectionOrder = [result.id];
      this.updateShapeVisualState(result.id, true);
      this.attachTransformControls();

      this.onBoolean.emit({ operation: 'union', result });
      this.processingMessage = '';
      this.hasUnsavedChanges = true;

      this.recordHistory({
        id: this.createHistoryId('boolean'),
        createdAt: Date.now(),
        type: 'boolean',
        outputId: result.id,
        params: {
          operation: 'union',
          inputIds
        },
        name: result.name,
        color: result.color
      });
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Perform subtraction (body - tool)
   * Body = first selected, Tool = second selected
   */
  async performSubtract(): Promise<void> {
    if (!this.canBoolean()) return;

    // Cleanup multi-select pivot first to restore shapes to cadGroup
    this.cleanupMultiSelectPivot();
    this.detachTransformControls();

    // Commit any pending transforms before boolean operation
    await this.commitPendingTransforms();

    const selected = this.getSelectedCADShapesInOrder();
    if (selected.length < 2) {
      return;
    }
    const inputIds = selected.map((s) => s.id);

    try {
      this.isProcessing = true;
      this.processingMessage = 'Performing subtraction...';

      const originalIds = new Set(selected.map((s) => s.id));
      const result = await this.cadKernel.booleanSubtract(selected[0], selected[1]);

      // Remove original shapes from scene, list, and kernel
      for (const id of originalIds) {
        this.removeShapeFromScene(id);
        this.cadKernel.deleteShape(id);
      }
      this.cadShapes = this.cadShapes.filter((s) => !originalIds.has(s.id));

      // Add result
      this.cadShapes.push(result);
      this.addShapeToScene(result);

      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(result.id);
      this.booleanSelectionOrder = [result.id];
      this.updateShapeVisualState(result.id, true);
      this.attachTransformControls();

      this.onBoolean.emit({ operation: 'subtract', result });
      this.processingMessage = '';
      this.hasUnsavedChanges = true;

      this.recordHistory({
        id: this.createHistoryId('boolean'),
        createdAt: Date.now(),
        type: 'boolean',
        outputId: result.id,
        params: {
          operation: 'subtract',
          inputIds
        },
        name: result.name,
        color: result.color
      });
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Perform intersection (keep only overlapping region)
   */
  async performIntersect(): Promise<void> {
    if (!this.canBoolean()) return;

    // Cleanup multi-select pivot first to restore shapes to cadGroup
    this.cleanupMultiSelectPivot();
    this.detachTransformControls();

    // Commit any pending transforms before boolean operation
    await this.commitPendingTransforms();

    const selected = this.getSelectedCADShapesInOrder();
    if (selected.length < 2) {
      return;
    }

    const inputIds = selected.map((s) => s.id);

    try {
      this.isProcessing = true;
      this.processingMessage = 'Performing intersection...';

      const originalIds = new Set(selected.map((s) => s.id));
      const result = await this.cadKernel.booleanIntersect(selected[0], selected[1]);

      // Remove original shapes from scene, list, and kernel
      for (const id of originalIds) {
        this.removeShapeFromScene(id);
        this.cadKernel.deleteShape(id);
      }
      this.cadShapes = this.cadShapes.filter((s) => !originalIds.has(s.id));

      // Add result
      this.cadShapes.push(result);
      this.addShapeToScene(result);

      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(result.id);
      this.booleanSelectionOrder = [result.id];
      this.updateShapeVisualState(result.id, true);
      this.attachTransformControls();

      this.onBoolean.emit({ operation: 'intersect', result });
      this.processingMessage = '';
      this.hasUnsavedChanges = true;

      this.recordHistory({
        id: this.createHistoryId('boolean'),
        createdAt: Date.now(),
        type: 'boolean',
        outputId: result.id,
        params: {
          operation: 'intersect',
          inputIds
        },
        name: result.name,
        color: result.color
      });
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FILLET & CHAMFER
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check if fillet/chamfer operation is possible
   */
  canFillet(): boolean {
    return this.selectedCADShapeIds.size === 1 && !this.isProcessing;
  }

  /**
   * Apply fillet to all edges of selected shape
   */
  async performFillet(): Promise<void> {
    if (!this.canFillet()) return;

    // Commit any pending transforms before fillet operation
    await this.commitPendingTransforms();

    const selectedId = Array.from(this.selectedCADShapeIds)[0];
    const shape = this.cadShapes.find((s) => s.id === selectedId);
    if (!shape) return;

    try {
      this.isProcessing = true;
      this.detachTransformControls();

      let result: CADShape;

      // Check if specific edges are selected
      const selectedEdgesForShape = this.selectedEdges.filter((e) => e.shapeId === selectedId);

      if (selectedEdgesForShape.length > 0) {
        // Apply fillet to selected edges only
        const edgeIndices = selectedEdgesForShape.map((e) => e.edgeIndex);
        this.processingMessage = `Applying fillet to ${edgeIndices.length} edge(s)...`;
        result = await this.cadKernel.filletSelectedEdges(shape, this.filletRadius, edgeIndices);
      } else {
        // Apply fillet to all edges
        this.processingMessage = 'Applying fillet to all edges...';
        result = await this.cadKernel.filletAllEdges(shape, this.filletRadius);
      }

      // Remove original from scene and list
      this.removeShapeFromScene(shape.id);
      this.cadShapes = this.cadShapes.filter((s) => s.id !== shape.id);

      // Add result
      this.cadShapes.push(result);
      this.addShapeToScene(result);

      // Clear edge selection (edges have changed)
      this.selectedEdges = [];
      this.clearEdgeHighlights();

      // Select the result
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(result.id);
      this.updateShapeVisualState(result.id, true);
      this.attachTransformControls();

      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Fillet failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Apply chamfer to all edges of selected shape
   */
  async performChamfer(): Promise<void> {
    if (!this.canFillet()) return;

    // Commit any pending transforms before chamfer operation
    await this.commitPendingTransforms();

    const selectedId = Array.from(this.selectedCADShapeIds)[0];
    const shape = this.cadShapes.find((s) => s.id === selectedId);
    if (!shape) return;

    try {
      this.isProcessing = true;
      this.detachTransformControls();

      let result: CADShape;

      // Check if specific edges are selected
      const selectedEdgesForShape = this.selectedEdges.filter((e) => e.shapeId === selectedId);

      if (selectedEdgesForShape.length > 0) {
        // Apply chamfer to selected edges only
        const edgeIndices = selectedEdgesForShape.map((e) => e.edgeIndex);
        this.processingMessage = `Applying chamfer to ${edgeIndices.length} edge(s)...`;
        result = await this.cadKernel.chamferSelectedEdges(shape, this.chamferDistance, edgeIndices);
      } else {
        // Apply chamfer to all edges
        this.processingMessage = 'Applying chamfer to all edges...';
        result = await this.cadKernel.chamferAllEdges(shape, this.chamferDistance);
      }

      // Remove original from scene and list
      this.removeShapeFromScene(shape.id);
      this.cadShapes = this.cadShapes.filter((s) => s.id !== shape.id);

      // Add result
      this.cadShapes.push(result);
      this.addShapeToScene(result);

      // Clear edge selection (edges have changed)
      this.selectedEdges = [];
      this.clearEdgeHighlights();

      // Select the result
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(result.id);
      this.updateShapeVisualState(result.id, true);
      this.attachTransformControls();

      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Chamfer failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MEASUREMENTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Toggle measurement display
   */
  toggleMeasurements(): void {
    this.showMeasurements = !this.showMeasurements;
    if (this.showMeasurements) {
      this.updateMeasurements();
    }
  }

  /**
   * Update measurements based on current selection
   */
  updateMeasurements(): void {
    if (!this.showMeasurements) {
      this.measurementInfo = {};
      return;
    }

    const selectedShapes = this.getSelectedCADShapes();

    if (selectedShapes.length === 0) {
      this.measurementInfo = {};
      return;
    }

    // Single shape - show bounding box, volume, area
    if (selectedShapes.length === 1) {
      const shape = selectedShapes[0];
      const bbox = this.cadKernel.getBoundingBox(shape);
      const volume = this.cadKernel.getVolume(shape);
      const surfaceArea = this.cadKernel.getSurfaceArea(shape);

      this.measurementInfo = {
        boundingBox: bbox ? { size: bbox.size, center: bbox.center } : undefined,
        volume: volume !== null ? volume : undefined,
        surfaceArea: surfaceArea !== null ? surfaceArea : undefined
      };
    }
    // Two shapes - show distance between centers
    else if (selectedShapes.length === 2) {
      const distance = this.cadKernel.getDistanceBetweenShapes(selectedShapes[0], selectedShapes[1]);
      this.measurementInfo = {
        distanceBetween: distance !== null ? distance : undefined
      };
    }
    // More shapes - just show count
    else {
      this.measurementInfo = {};
    }

    this.cdr.detectChanges();
  }

  /**
   * Format number for display
   */
  formatNumber(value: number, decimals = 2): string {
    return value.toFixed(decimals);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SHELL / OFFSET
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Apply shell (hollow) to selected shape
   */
  async performShell(): Promise<void> {
    if (!this.canFillet()) return;

    const initialSelectedId = Array.from(this.selectedCADShapeIds)[0];
    if (!initialSelectedId) return;

    try {
      this.isProcessing = true;
      this.detachTransformControls();

      // Commit any pending transforms before shell operation
      await this.commitPendingTransforms();

      // Re-fetch shape after commit - transform may have changed the shape ID and object
      const currentSelectedId = Array.from(this.selectedCADShapeIds)[0];
      const shape = this.cadShapes.find((s) => s.id === currentSelectedId);
      if (!shape) return;

      let result: CADShape;

      // Check if a specific face is selected for removal
      if (this.selectedShellFaceIndex !== null) {
        // Shell with the selected face removed (open shell)
        this.processingMessage = 'Creating shell with open face...';
        result = await this.cadKernel.shellShapeWithOpenFaces(shape, this.shellThickness, [
          this.selectedShellFaceIndex
        ]);
      } else {
        // Fully enclosed shell
        this.processingMessage = 'Creating enclosed shell...';
        result = await this.cadKernel.shellShape(shape, this.shellThickness);
      }

      // Remove original from scene and list
      this.removeShapeFromScene(shape.id);
      this.cadShapes = this.cadShapes.filter((s) => s.id !== shape.id);

      // Add result
      this.cadShapes.push(result);
      this.addShapeToScene(result);

      // Clear face selection
      this.selectedShellFaceIndex = null;
      this.clearShellFaceHighlight();

      // Select the result
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(result.id);
      this.updateShapeVisualState(result.id, true);
      this.attachTransformControls();
      this.updateMeasurements();

      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Shell failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Apply offset to selected shape
   */
  async performOffset(): Promise<void> {
    if (!this.canFillet()) return;

    const initialSelectedId = Array.from(this.selectedCADShapeIds)[0];
    if (!initialSelectedId) return;

    try {
      this.isProcessing = true;
      this.processingMessage = 'Applying offset...';
      this.detachTransformControls();

      // Commit any pending transforms before offset operation
      await this.commitPendingTransforms();

      // Re-fetch shape after commit - transform may have changed the shape ID and object
      const currentSelectedId = Array.from(this.selectedCADShapeIds)[0];
      const shape = this.cadShapes.find((s) => s.id === currentSelectedId);
      if (!shape) return;

      const result = await this.cadKernel.offsetShape(shape, this.offsetDistance);

      // Remove original from scene and list
      this.removeShapeFromScene(shape.id);
      this.cadShapes = this.cadShapes.filter((s) => s.id !== shape.id);

      // Add result
      this.cadShapes.push(result);
      this.addShapeToScene(result);

      // Select the result
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(result.id);
      this.updateShapeVisualState(result.id, true);
      this.attachTransformControls();
      this.updateMeasurements();

      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Offset failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUSH/PULL FACE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start push/pull face mode
   */
  async startPushPullMode(): Promise<void> {
    if (this.selectedCADShapeIds.size !== 1) return;

    // Clean up any existing push-pull state first to ensure clean start
    if (this.isPushPullDragMode || this.pushPullDragMoveHandler) {
      this.exitPushPullDragMode();
    }
    this.cleanupPushPullFacePicking();

    // Commit pending transforms before face selection (OCCT geometry must match visual)
    await this.commitPendingTransforms();

    // Re-extract faces for the selected shape after transform commit
    await this.refreshShapeFacesAndEdges();

    // Reset all push-pull state
    this.isPushPullMode = true;
    this.isPushPullDragMode = false;
    this.isPushPullArrowDragging = false;
    this.selectedPushPullFaceIndex = null;
    this.pushPullFaceNormal = null;
    this.pushPullFaceCenter = null;
    this.pushPullExtractedFace = null;
    this.detachTransformControls();

    // Setup face picking handlers
    this.setupPushPullFacePicking();

    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = 'crosshair';
    }

    this.cdr.detectChanges();
  }

  /**
   * Stop push/pull face mode
   * @param keepSelection If true, keeps the selected face index
   */
  stopPushPullMode(keepSelection: boolean = true): void {
    this.isPushPullMode = false;
    this.cleanupPushPullFacePicking();

    // Always clean up drag mode - check handlers directly, not just flag
    // This ensures cleanup happens even if state got out of sync
    if (this.isPushPullDragMode || this.pushPullDragMoveHandler || this.pushPullDragUpHandler) {
      this.exitPushPullDragMode();
    }

    if (!keepSelection) {
      this.selectedPushPullFaceIndex = null;
      this.pushPullFaceNormal = null;
      this.pushPullFaceCenter = null;
      this.pushPullExtractedFace = null;
      // Reset distance to default for clean next operation
      this.pushPullDistance = 10;
    }

    // Clear the highlight (selection is stored in selectedPushPullFaceIndex)
    this.clearShellFaceHighlight();

    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = '';
    }

    // Re-attach gizmo
    if (this.selectedCADShapeIds.size === 1) {
      this.attachTransformControls();
    }

    this.cdr.detectChanges();
  }

  /**
   * Setup face picking for push/pull
   */
  private pushPullMoveHandler: ((e: MouseEvent) => void) | null = null;
  private pushPullClickHandler: ((e: MouseEvent) => void) | null = null;

  private setupPushPullFacePicking(): void {
    if (!this.engine?.canvas) return;

    this.pushPullMoveHandler = (e: MouseEvent) => this.onPushPullFaceMove(e);
    this.pushPullClickHandler = (e: MouseEvent) => this.onPushPullFaceClick(e);

    this.engine.canvas.addEventListener('mousemove', this.pushPullMoveHandler);
    this.engine.canvas.addEventListener('click', this.pushPullClickHandler);
  }

  private cleanupPushPullFacePicking(): void {
    if (!this.engine?.canvas) return;

    if (this.pushPullMoveHandler) {
      this.engine.canvas.removeEventListener('mousemove', this.pushPullMoveHandler);
      this.pushPullMoveHandler = null;
    }
    if (this.pushPullClickHandler) {
      this.engine.canvas.removeEventListener('click', this.pushPullClickHandler);
      this.pushPullClickHandler = null;
    }

    // Remove highlight using the proper method
    this.clearShellFaceHighlight();
  }

  private onPushPullFaceMove(event: MouseEvent): void {
    if (!this.engine || !this.isPushPullMode) return;

    // Get selected shape for face detection
    const selectedShapeId = Array.from(this.selectedCADShapeIds)[0];
    if (!selectedShapeId) return;

    const faceInfo = this.getCADFaceUnderCursor(event, selectedShapeId);

    // Clear previous highlight
    this.clearShellFaceHighlight();

    if (faceInfo) {
      this.createShellFaceHighlight(faceInfo);
      if (this.engine.canvas) {
        this.engine.canvas.style.cursor = 'pointer';
      }
    } else {
      if (this.engine.canvas) {
        this.engine.canvas.style.cursor = 'crosshair';
      }
    }
  }

  private onPushPullFaceClick(event: MouseEvent): void {
    if (!this.engine || !this.isPushPullMode) return;

    const selectedShapeId = Array.from(this.selectedCADShapeIds)[0];
    if (!selectedShapeId) return;

    const faceInfo = this.getCADFaceUnderCursor(event, selectedShapeId);

    if (faceInfo && faceInfo.faceIndex !== undefined) {
      this.selectedPushPullFaceIndex = faceInfo.faceIndex;

      // Store face normal, center, and geometry for drag preview
      if (faceInfo.normal) {
        this.pushPullFaceNormal = new Vector3(faceInfo.normal[0], faceInfo.normal[1], faceInfo.normal[2]);
      }
      if (faceInfo.extractedFace) {
        this.pushPullExtractedFace = faceInfo.extractedFace;
        // Use extracted face center for stable anchor (not hit point)
        this.pushPullFaceCenter = new Vector3(
          faceInfo.extractedFace.center[0],
          faceInfo.extractedFace.center[1],
          faceInfo.extractedFace.center[2]
        );
      } else if (faceInfo.position) {
        // Fallback to hit point if no extracted face
        this.pushPullFaceCenter = new Vector3(faceInfo.position[0], faceInfo.position[1], faceInfo.position[2]);
      }

      // Enter drag mode instead of stopping
      this.enterPushPullDragMode(event);
      this.cdr.detectChanges();
    } else {
      // Clicked on empty space during face picking - use exitOperationMode for proper cleanup
      this.exitOperationMode(true);
    }
  }

  /**
   * Enter push/pull drag mode after face selection
   */
  private pushPullDragMoveHandler: ((e: MouseEvent) => void) | null = null;
  private pushPullDragUpHandler: ((e: MouseEvent) => void) | null = null;

  private enterPushPullDragMode(_event: MouseEvent): void {
    if (!this.engine?.canvas) return;

    // Validate we have the required data for drag mode
    if (!this.pushPullFaceCenter || !this.pushPullFaceNormal) {
      this.onError.emit('Could not determine face position. Please try selecting another face.');
      this.exitOperationMode(true);
      return;
    }

    this.isPushPullDragMode = true;

    // DON'T start dragging immediately - wait for user to click on the arrow/label
    // This matches the drag-to-extrude behavior
    this.isPushPullArrowDragging = false;

    // Stop the face picking mode but keep the face highlighted
    this.cleanupPushPullFacePicking();

    // Clear any existing preview
    this.clearPushPullDragPreview();

    // Warn if face geometry extraction failed (arrow will show but no body preview)
    if (!this.pushPullExtractedFace) {
      console.warn('Push/Pull: Face geometry extraction failed. Body preview will not be shown.');
    }

    // Create all preview elements (same as extrusion)
    this.createPushPullArrowLabel();
    this.createPushPullArrow3D();
    this.createPushPullAxisLine();
    this.createPushPullBodyPreview();

    // Setup document-level drag handlers (like extrusion - persistent until exit)
    this.pushPullDragMoveHandler = (e: MouseEvent) => this.onPushPullDragMove(e);
    this.pushPullDragUpHandler = (e: MouseEvent) => this.onPushPullDragUp(e);

    document.addEventListener('mousemove', this.pushPullDragMoveHandler, true);
    document.addEventListener('mouseup', this.pushPullDragUpHandler, true);

    // Reset cursor - not dragging yet
    this.engine.canvas.style.cursor = '';

    // Trigger change detection to update UI
    this.cdr.detectChanges();
  }

  /**
   * Handle mouse move during push/pull drag (same pattern as extrusion)
   * Uses ray-to-axis projection for accurate plane-independent movement
   */
  private onPushPullDragMove(event: MouseEvent): void {
    if (!this.isPushPullDragMode || !this.isPushPullArrowDragging) return;
    if (!this.engine || !this.pushPullFaceCenter || !this.pushPullFaceNormal) return;

    event.preventDefault();
    event.stopPropagation();

    // Raycast from mouse to find intersection with a plane containing the push/pull axis
    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    // Find the closest point on the push/pull axis line to the ray
    // Push/Pull axis: basePoint + t * normal
    // Ray: rayOrigin + s * rayDir
    const rayOrigin = raycaster.ray.origin;
    const rayDir = raycaster.ray.direction;
    const basePoint = this.pushPullFaceCenter;
    const normal = this.pushPullFaceNormal.clone().normalize();

    // Calculate closest approach between two lines
    const w0 = rayOrigin.clone().sub(basePoint);
    const a = normal.dot(normal); // always > 0
    const b = normal.dot(rayDir);
    const c = rayDir.dot(rayDir); // always > 0
    const d = normal.dot(w0);
    const e = rayDir.dot(w0);

    const denom = a * c - b * b;

    let newDistance: number;
    if (Math.abs(denom) < 0.0001) {
      // Lines are parallel - fallback to initial distance
      newDistance = this.pushPullDragInitialDistance;
    } else {
      // Parameter on the push/pull axis for closest point
      const t = (b * e - c * d) / denom;
      // Invert the sign so moving mouse up increases distance
      newDistance = -t;
    }

    // Apply modifier keys for finer control (same as extrusion)
    // Ctrl = super fine (0.1mm precision), Shift = fine (1mm precision)
    if (event.ctrlKey) {
      newDistance = Math.round(newDistance * 10) / 10;
    } else if (event.shiftKey) {
      newDistance = Math.round(newDistance);
    }

    // Snap to grid increments (5mm steps) when snap is enabled
    if (this.snapEnabled) {
      // Keep sign but snap magnitude
      const sign = newDistance >= 0 ? 1 : -1;
      newDistance = sign * Math.round(Math.abs(newDistance) / 5) * 5;
    }

    this.pushPullDistance = newDistance;

    // Update all preview elements
    this.updatePushPullBodyPreview();

    this.cdr.detectChanges();
  }

  /**
   * Handle mouse up to confirm push/pull drag (same pattern as extrusion)
   * Note: Does NOT remove listeners - they persist until exitPushPullDragMode
   */
  private onPushPullDragUp(event: MouseEvent): void {
    if (event.button !== 0) return;

    // Reset arrow dragging state (same as extrusion - just reset flag, don't remove listeners)
    if (this.isPushPullArrowDragging) {
      this.setPushPullArrowDraggingStyle(false);
    }

    this.isPushPullArrowDragging = false;

    // Don't exit drag mode or clear preview - keep everything visible
    // User can continue to click the arrow to drag more or apply with Enter
    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = '';
    }

    this.cdr.detectChanges();
  }

  /**
   * Exit push/pull drag mode
   */
  private exitPushPullDragMode(): void {
    this.isPushPullDragMode = false;
    this.isPushPullArrowDragging = false;

    // Remove document-level listeners (same as extrusion)
    if (this.pushPullDragMoveHandler) {
      document.removeEventListener('mousemove', this.pushPullDragMoveHandler, true);
      this.pushPullDragMoveHandler = null;
    }
    if (this.pushPullDragUpHandler) {
      document.removeEventListener('mouseup', this.pushPullDragUpHandler, true);
      this.pushPullDragUpHandler = null;
    }

    // Reset cursor
    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = '';
    }

    // Clear all preview elements (arrow, label, axis line, body preview)
    this.clearPushPullDragPreview();
    this.removePushPullBodyPreview();
  }

  /**
   * Create the push/pull body preview showing the extruded face volume
   * Uses same style as drag-to-extrude preview (semi-transparent green)
   */
  private createPushPullBodyPreview(): void {
    if (!this.engine || !this.pushPullExtractedFace || !this.pushPullFaceNormal) return;

    this.removePushPullBodyPreview();

    // Create initial preview with current distance
    this.updatePushPullBodyPreview();
  }

  /**
   * Update the body preview based on current distance
   * Recreates the geometry with the new extrusion depth
   */
  private updatePushPullBodyPreview(): void {
    if (!this.engine || !this.pushPullExtractedFace || !this.pushPullFaceNormal) return;

    // Remove existing preview mesh (we'll recreate it)
    if (this.pushPullBodyPreview) {
      this.engine.scene.remove(this.pushPullBodyPreview);
      this.pushPullBodyPreview.geometry.dispose();
      if (this.pushPullBodyPreview.material instanceof MeshBasicMaterial) {
        this.pushPullBodyPreview.material.dispose();
      }
    }

    const distance = this.pushPullDistance;
    if (distance === 0) return; // Don't show preview for zero distance

    const face = this.pushPullExtractedFace;
    const normal = this.pushPullFaceNormal.clone().normalize();

    // Create geometry for the preview
    // We'll show the face offset by distance, plus side faces connecting to original
    const geometry = new BufferGeometry();

    // Get face vertices (3D positions)
    const faceVertices = face.vertices;
    const faceIndices = face.indices;
    const numVertices = faceVertices.length / 3;

    // Create arrays for the preview geometry:
    // - Original face vertices
    // - Offset face vertices (translated by distance * normal)
    // - Side faces connecting them
    const positions: number[] = [];
    const indices: number[] = [];

    // Add original face vertices (index 0 to numVertices-1)
    for (let i = 0; i < faceVertices.length; i++) {
      positions.push(faceVertices[i]);
    }

    // Add offset face vertices (index numVertices to 2*numVertices-1)
    for (let i = 0; i < numVertices; i++) {
      const x = faceVertices[i * 3] + normal.x * distance;
      const y = faceVertices[i * 3 + 1] + normal.y * distance;
      const z = faceVertices[i * 3 + 2] + normal.z * distance;
      positions.push(x, y, z);
    }

    // Add original face triangles (front face)
    for (let i = 0; i < faceIndices.length; i++) {
      indices.push(faceIndices[i]);
    }

    // Add offset face triangles (back face - reversed winding for correct normals)
    for (let i = 0; i < faceIndices.length; i += 3) {
      indices.push(
        faceIndices[i] + numVertices,
        faceIndices[i + 2] + numVertices,
        faceIndices[i + 1] + numVertices
      );
    }

    // For side faces, we need to find the boundary edges of the face
    // A boundary edge is one that only appears once in the index array
    const edgeCounts = new Map<string, { v1: number; v2: number; count: number }>();

    for (let i = 0; i < faceIndices.length; i += 3) {
      const a = faceIndices[i];
      const b = faceIndices[i + 1];
      const c = faceIndices[i + 2];

      // Check each edge of the triangle
      const edges = [
        [a, b],
        [b, c],
        [c, a]
      ];

      for (const [v1, v2] of edges) {
        const key = v1 < v2 ? `${v1}-${v2}` : `${v2}-${v1}`;
        const existing = edgeCounts.get(key);
        if (existing) {
          existing.count++;
        } else {
          edgeCounts.set(key, { v1, v2, count: 1 });
        }
      }
    }

    // Create side quads for boundary edges (edges that appear only once)
    for (const [, edge] of edgeCounts) {
      if (edge.count === 1) {
        // This is a boundary edge - create a quad connecting to offset face
        const v1 = edge.v1;
        const v2 = edge.v2;
        const v1Offset = v1 + numVertices;
        const v2Offset = v2 + numVertices;

        // Create two triangles for the quad
        // Winding depends on push/pull direction
        if (distance > 0) {
          indices.push(v1, v2, v2Offset);
          indices.push(v1, v2Offset, v1Offset);
        } else {
          indices.push(v1, v2Offset, v2);
          indices.push(v1, v1Offset, v2Offset);
        }
      }
    }

    geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    // Create semi-transparent material - green for push, red for pull
    const isPull = distance < 0;
    const previewColor = isPull ? 0xff6464 : 0xa0ff00;
    const material = new MeshBasicMaterial({
      color: previewColor,
      transparent: true,
      opacity: 0.35,
      side: DoubleSide,
      depthWrite: false
    });

    this.pushPullBodyPreview = new Mesh(geometry, material);
    this.pushPullBodyPreview.name = 'PushPullBodyPreview';
    this.pushPullBodyPreview.renderOrder = 100;

    this.engine.scene.add(this.pushPullBodyPreview);

    // Update arrow, label, and axis line positions
    this.updatePushPullArrowPosition();
  }

  /**
   * Create CSS2D arrow label for push/pull (same style as extrusion)
   */
  private createPushPullArrowLabel(): void {
    if (!this.engine || !this.pushPullFaceCenter || !this.pushPullFaceNormal) return;

    // Color based on distance sign: negative = pull (red), positive = push (green)
    const isPull = this.pushPullDistance < 0;
    const bgColor = isPull ? 'rgba(255, 100, 100, 0.95)' : 'rgba(160, 255, 0, 0.95)';

    // Create CSS2D arrow indicator - same style as extrusion
    this.pushPullArrowElement = document.createElement('div');
    this.pushPullArrowElement.className = 'pushpull-arrow-indicator';
    this.pushPullArrowElement.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      pointer-events: auto;
      transform: translate(-50%, -50%);
      cursor: ns-resize;
      user-select: none;
    `;

    // Make the arrow draggable
    this.pushPullArrowElement.addEventListener('mousedown', this.onPushPullArrowMouseDown);

    // Distance label
    const distanceLabel = document.createElement('div');
    distanceLabel.className = 'distance-label';
    distanceLabel.style.cssText = `
      background: ${bgColor};
      color: #000;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: var(--font-size-lg);
      font-weight: 700;
      font-family: 'JetBrains Mono', monospace;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      pointer-events: none;
    `;
    distanceLabel.textContent = `${this.pushPullDistance.toFixed(1)} mm`;

    // Hint text
    const hintText = document.createElement('div');
    hintText.className = 'hint-text';
    hintText.style.cssText = `
      font-size: var(--font-size-sm);
      color: rgba(255, 255, 255, 0.9);
      text-shadow: 0 1px 4px rgba(0,0,0,0.8);
      pointer-events: none;
      text-align: center;
      line-height: 1.5;
      background: rgba(0, 0, 0, 0.5);
      padding: 8px 12px;
      border-radius: 6px;
      margin-top: 4px;
    `;
    hintText.innerHTML = 'Drag ↕ (Shift=fine, Ctrl=0.1mm)<br>Enter to apply • Esc to cancel';

    this.pushPullArrowElement.appendChild(distanceLabel);
    this.pushPullArrowElement.appendChild(hintText);

    // Create CSS2DObject and position at offset location (not face center)
    this.pushPullArrowCSS2D = new CSS2DObject(this.pushPullArrowElement);

    // Calculate offset position based on current distance
    const normal = this.pushPullFaceNormal.clone().normalize();
    const offsetPos = this.pushPullFaceCenter.clone().add(normal.multiplyScalar(this.pushPullDistance));
    this.pushPullArrowCSS2D.position.copy(offsetPos);

    this.engine.scene.add(this.pushPullArrowCSS2D);
  }

  /**
   * Arrow mouse down handler for push/pull (same pattern as extrusion)
   */
  private onPushPullArrowMouseDown = (event: MouseEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.isPushPullArrowDragging = true;
    this.pushPullDragInitialDistance = this.pushPullDistance;

    // Change arrow color to indicate active dragging
    this.setPushPullArrowDraggingStyle(true);

    // Set cursor for dragging
    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = 'ns-resize';
    }
  };

  /**
   * Set arrow visual style for dragging state
   */
  private setPushPullArrowDraggingStyle(isDragging: boolean): void {
    if (!this.pushPullArrowElement) return;

    const distanceLabel = this.pushPullArrowElement.querySelector('.distance-label') as HTMLElement;

    if (isDragging) {
      // Yellow/gold color while dragging
      if (distanceLabel) {
        distanceLabel.style.background = 'rgba(255, 215, 0, 0.95)';
        distanceLabel.style.transform = 'scale(1.1)';
      }
      // Update 3D arrow color to gold
      if (this.pushPullArrow3D) {
        this.pushPullArrow3D.traverse((child: any) => {
          if (child.material) {
            child.material.color.setHex(0xffd700);
          }
        });
      }
    } else {
      // Reset to green color
      if (distanceLabel) {
        distanceLabel.style.background = 'rgba(160, 255, 0, 0.95)';
        distanceLabel.style.transform = 'scale(1)';
      }
      // Reset 3D arrow color to green
      if (this.pushPullArrow3D) {
        this.pushPullArrow3D.traverse((child: any) => {
          if (child.material) {
            child.material.color.setHex(0xa0ff00);
          }
        });
      }
    }
  }

  /**
   * Create 3D arrow for push/pull preview (matches extrusion arrow style)
   */
  private createPushPullArrow3D(): void {
    if (!this.engine || !this.pushPullFaceCenter || !this.pushPullFaceNormal) return;

    // Clean up existing arrow
    if (this.pushPullArrow3D) {
      this.engine.scene.remove(this.pushPullArrow3D);
      this.pushPullArrow3D.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.pushPullArrow3D = null;
    }

    // Create arrow group
    this.pushPullArrow3D = new Group();
    this.pushPullArrow3D.name = 'PushPullArrow3D';

    // Arrow dimensions (same as extrusion arrow)
    const coneHeight = 12;
    const coneRadius = 5;
    const shaftHeight = 20;
    const shaftRadius = 2;

    // Create cone (arrow head)
    const coneGeometry = new ConeGeometry(coneRadius, coneHeight, 16);
    const coneMaterial = new MeshBasicMaterial({
      color: 0xa0ff00,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    const cone = new Mesh(coneGeometry, coneMaterial);
    cone.position.y = shaftHeight + coneHeight / 2;
    cone.renderOrder = 200;

    // Create shaft (cylinder)
    const shaftGeometry = new CylinderGeometry(shaftRadius, shaftRadius, shaftHeight, 16);
    const shaftMaterial = new MeshBasicMaterial({
      color: 0xa0ff00,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    const shaft = new Mesh(shaftGeometry, shaftMaterial);
    shaft.position.y = shaftHeight / 2;
    shaft.renderOrder = 200;

    this.pushPullArrow3D.add(cone);
    this.pushPullArrow3D.add(shaft);

    // Set initial position and orientation before adding to scene
    const normal = this.pushPullFaceNormal.clone().normalize();
    const distance = this.pushPullDistance;
    const offsetPos = this.pushPullFaceCenter.clone().add(normal.clone().multiplyScalar(distance));
    this.pushPullArrow3D.position.copy(offsetPos);

    // Orient arrow to point along the normal
    const up = new Vector3(0, 1, 0);
    const direction = distance >= 0 ? normal : normal.clone().negate();
    const quaternion = new Quaternion().setFromUnitVectors(up, direction);
    this.pushPullArrow3D.quaternion.copy(quaternion);

    this.engine.scene.add(this.pushPullArrow3D);
  }

  /**
   * Create axis line for push/pull (matches extrusion axis line style)
   */
  private createPushPullAxisLine(): void {
    if (!this.engine || !this.pushPullFaceCenter || !this.pushPullFaceNormal) return;

    // Clean up existing axis line
    if (this.pushPullAxisLine) {
      this.engine.scene.remove(this.pushPullAxisLine);
      this.pushPullAxisLine.geometry.dispose();
      (this.pushPullAxisLine.material as LineDashedMaterial).dispose();
      this.pushPullAxisLine = null;
    }

    // Create dashed line from base to offset position
    const normal = this.pushPullFaceNormal.clone().normalize();
    const distance = this.pushPullDistance;
    const basePos = this.pushPullFaceCenter.clone();
    const endPos = basePos.clone().add(normal.clone().multiplyScalar(distance));

    const geometry = new BufferGeometry().setFromPoints([basePos, endPos]);
    const material = new LineDashedMaterial({
      color: 0xa0ff00,
      dashSize: 3,
      gapSize: 2,
      transparent: true,
      opacity: 0.8
    });

    this.pushPullAxisLine = new Line(geometry, material);
    this.pushPullAxisLine.computeLineDistances();
    this.pushPullAxisLine.renderOrder = 150;

    this.engine.scene.add(this.pushPullAxisLine);
  }

  /**
   * Update arrow, label, and axis line positions based on current distance
   */
  private updatePushPullArrowPosition(): void {
    if (!this.pushPullFaceCenter || !this.pushPullFaceNormal) return;

    const normal = this.pushPullFaceNormal.clone().normalize();
    const distance = this.pushPullDistance;
    const offsetPos = this.pushPullFaceCenter.clone().add(normal.clone().multiplyScalar(distance));

    // Color based on distance sign: negative = pull (red), positive = push (green)
    const isPull = distance < 0;
    const hexColor = isPull ? 0xff6464 : 0xa0ff00;
    const cssColor = isPull ? 'rgba(255, 100, 100, 0.95)' : 'rgba(160, 255, 0, 0.95)';

    // Update CSS2D label position and color
    if (this.pushPullArrowCSS2D) {
      this.pushPullArrowCSS2D.position.copy(offsetPos);

      // Update distance text and color
      const distanceLabel = this.pushPullArrowElement?.querySelector('.distance-label') as HTMLElement;
      if (distanceLabel) {
        distanceLabel.textContent = `${this.pushPullDistance.toFixed(1)} mm`;
        distanceLabel.style.background = cssColor;
      }
    }

    // Update 3D arrow position, orientation, and color
    if (this.pushPullArrow3D) {
      this.pushPullArrow3D.position.copy(offsetPos);

      // Orient arrow to point along the normal
      const up = new Vector3(0, 1, 0);
      const direction = distance >= 0 ? normal : normal.clone().negate();
      const quaternion = new Quaternion().setFromUnitVectors(up, direction);
      this.pushPullArrow3D.quaternion.copy(quaternion);

      // Update arrow color
      this.pushPullArrow3D.traverse((child: any) => {
        if (child.material) {
          child.material.color.setHex(hexColor);
        }
      });
    }

    // Update axis line
    if (this.pushPullAxisLine && this.engine) {
      this.engine.scene.remove(this.pushPullAxisLine);
      this.pushPullAxisLine.geometry.dispose();
      (this.pushPullAxisLine.material as LineDashedMaterial).dispose();

      const basePos = this.pushPullFaceCenter.clone();
      const endPos = basePos.clone().add(normal.clone().multiplyScalar(distance));

      const geometry = new BufferGeometry().setFromPoints([basePos, endPos]);
      const material = new LineDashedMaterial({
        color: hexColor,
        dashSize: 3,
        gapSize: 2,
        transparent: true,
        opacity: 0.8
      });

      this.pushPullAxisLine = new Line(geometry, material);
      this.pushPullAxisLine.computeLineDistances();
      this.pushPullAxisLine.renderOrder = 150;

      this.engine.scene.add(this.pushPullAxisLine);
    }
  }

  /**
   * Clear all push/pull preview elements
   */
  private clearPushPullDragPreview(): void {
    // Clear CSS2D label
    if (this.pushPullArrowCSS2D) {
      if (this.pushPullArrowElement) {
        this.pushPullArrowElement.removeEventListener('mousedown', this.onPushPullArrowMouseDown);
        if (this.pushPullArrowElement.parentNode) {
          this.pushPullArrowElement.parentNode.removeChild(this.pushPullArrowElement);
        }
      }
      if (this.engine) {
        this.engine.scene.remove(this.pushPullArrowCSS2D);
      }
      this.pushPullArrowCSS2D = null;
      this.pushPullArrowElement = null;
    }

    // Clear 3D arrow
    if (this.pushPullArrow3D && this.engine) {
      this.engine.scene.remove(this.pushPullArrow3D);
      this.pushPullArrow3D.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.pushPullArrow3D = null;
    }

    // Clear axis line
    if (this.pushPullAxisLine && this.engine) {
      this.engine.scene.remove(this.pushPullAxisLine);
      this.pushPullAxisLine.geometry.dispose();
      (this.pushPullAxisLine.material as LineDashedMaterial).dispose();
      this.pushPullAxisLine = null;
    }

    // Clear body preview
    if (this.pushPullBodyPreview && this.engine) {
      this.engine.scene.remove(this.pushPullBodyPreview);
      this.pushPullBodyPreview.geometry.dispose();
      if (this.pushPullBodyPreview.material instanceof MeshBasicMaterial) {
        this.pushPullBodyPreview.material.dispose();
      }
      this.pushPullBodyPreview = null;
    }
  }

  /**
   * Remove only the body preview from scene (not other preview elements like arrow, axis line)
   */
  private removePushPullBodyPreview(): void {
    if (this.pushPullBodyPreview && this.engine) {
      this.engine.scene.remove(this.pushPullBodyPreview);
      this.pushPullBodyPreview.geometry.dispose();
      if (this.pushPullBodyPreview.material instanceof MeshBasicMaterial) {
        this.pushPullBodyPreview.material.dispose();
      }
      this.pushPullBodyPreview = null;
    }
  }

  /**
   * Check if push/pull can be applied
   */
  canApplyPushPull(): boolean {
    return (
      this.selectedCADShapeIds.size === 1 && this.selectedPushPullFaceIndex !== null && this.pushPullDistance !== 0
    );
  }

  /**
   * Apply push/pull to selected face
   */
  async applyPushPull(): Promise<void> {
    if (!this.canApplyPushPull()) return;

    const initialSelectedId = Array.from(this.selectedCADShapeIds)[0];
    if (!initialSelectedId || this.selectedPushPullFaceIndex === null) return;

    try {
      this.isProcessing = true;
      this.processingMessage = this.pushPullDistance > 0 ? 'Pushing face...' : 'Pulling face...';
      this.stopPushPullMode();

      // Commit any pending transforms before push/pull operation
      await this.commitPendingTransforms();

      // Re-fetch shape after commit - transform may have changed the shape ID and object
      const currentSelectedId = Array.from(this.selectedCADShapeIds)[0];
      const shape = this.cadShapes.find((s) => s.id === currentSelectedId);
      if (!shape) return;

      const result = await this.cadKernel.pushPullFace(shape, this.selectedPushPullFaceIndex, this.pushPullDistance);

      // Remove original from scene and list
      this.removeShapeFromScene(shape.id);
      this.cadShapes = this.cadShapes.filter((s) => s.id !== shape.id);

      // Add result
      this.cadShapes.push(result);
      this.addShapeToScene(result);

      // Select the result
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(result.id);
      this.updateShapeVisualState(result.id, true);
      this.attachTransformControls();
      this.updateMeasurements();

      // Reset
      this.selectedPushPullFaceIndex = null;
      this.clearShellFaceHighlight();
      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Push/Pull failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EXTRUDE ON FACE (Boss/Pocket)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start extrude on face mode (Boss = add material, Pocket = remove material)
   * User workflow: 1) Pick face -> 2) Draw profile on face -> 3) Extrude
   */
  async startExtrudeOnFaceMode(operation: 'boss' | 'pocket'): Promise<void> {
    if (this.selectedCADShapeIds.size !== 1) return;

    // Clean up any existing boss/pocket state first to ensure clean start
    if (this.isBossPocketPreviewMode || this.bossPocketDragMoveHandler) {
      this.clearBossPocketPreview();
    }
    this.stopFacePickingMode(true);

    // Commit pending transforms before face selection (OCCT geometry must match visual)
    await this.commitPendingTransforms();

    // Re-extract faces for the selected shape after transform commit
    await this.refreshShapeFacesAndEdges();

    // Reset all boss/pocket state
    this.isExtrudeOnFaceMode = true;
    this.extrudeOnFaceOperation = operation;
    this.extrudeOnFaceProfile = [];
    this.extrudeOnFaceCircle = null;
    this.isBossPocketPreviewMode = false;
    this.isBossPocketArrowDragging = false;
    this.selectedFaceInfo = null;
    this.showBossPocketToolPicker = false;
    this.detachTransformControls();

    // Start face picking - after face is picked, user can draw profile
    this.startFacePickingMode();

    this.cdr.detectChanges();
  }

  /**
   * Cancel extrude on face mode
   */
  cancelExtrudeOnFaceMode(): void {
    this.isExtrudeOnFaceMode = false;
    this.extrudeOnFaceProfile = [];
    this.extrudeOnFaceCircle = null;
    this.selectedFaceInfo = null;
    this.showBossPocketToolPicker = false;
    this.stopFacePickingMode(true);
    this.cleanupSketchPlane();

    // Clear boss/pocket preview if active
    if (this.isBossPocketPreviewMode || this.bossPocketDragMoveHandler) {
      this.clearBossPocketPreview();
      // Clean up document-level drag handlers
      if (this.bossPocketDragMoveHandler) {
        document.removeEventListener('mousemove', this.bossPocketDragMoveHandler, true);
        this.bossPocketDragMoveHandler = null;
      }
      if (this.bossPocketDragUpHandler) {
        document.removeEventListener('mouseup', this.bossPocketDragUpHandler, true);
        this.bossPocketDragUpHandler = null;
      }
    }
    // Reset preview mode flag
    this.isBossPocketPreviewMode = false;
    this.isBossPocketArrowDragging = false;

    // Clear any active drawing
    if (this.isDrawing || this.drawingMode !== 'none') {
      this.cancelDrawing();
      this.setDrawingMode('none');
    }

    // Clear sketch plane settings
    this.sketchPlane = null;
    this.customPlaneOrigin = [0, 0, 0];
    this.customPlaneNormal = [0, 0, 1];
    this.customPlaneXAxis = [1, 0, 0];
    this.customPlaneYAxis = [0, 1, 0];

    // Re-attach transform controls if shape is selected (don't clear selection)
    if (this.selectedCADShapeIds.size === 1) {
      this.attachTransformControls();
    }

    this.cdr.detectChanges();
  }

  /**
   * Handle tool selection from the Boss/Pocket tool picker
   */
  selectBossPocketTool(tool: 'rectangle' | 'circle' | 'polygon' | 'line'): void {
    // Hide the tool picker
    this.showBossPocketToolPicker = false;

    // Start the selected drawing mode
    this.setDrawingMode(tool);

    this.cdr.detectChanges();
  }

  /**
   * Close the Boss/Pocket tool picker without selecting a tool
   * This cancels the entire boss/pocket operation
   */
  closeBossPocketToolPicker(): void {
    this.showBossPocketToolPicker = false;
    // Closing the tool picker cancels the entire boss/pocket operation
    this.exitOperationMode(true);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BOSS/POCKET PREVIEW MODE (Drag-to-adjust height before applying)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Enter boss/pocket preview mode after drawing is complete
   * Shows draggable preview like drag-to-extrude
   */
  enterBossPocketPreviewMode(): void {
    if (!this.engine) return;

    this.isBossPocketPreviewMode = true;
    this.isBossPocketArrowDragging = false;

    // Store base position and normal for ray-to-axis projection during drag
    const center = this.getBossPocketProfileCenter();
    this.bossPocketDragBaseWorldPos = this.planeLocalToWorld(center);
    this.bossPocketDragNormal = new Vector3(
      this.customPlaneNormal[0],
      this.customPlaneNormal[1],
      this.customPlaneNormal[2]
    ).normalize();

    // Create preview elements
    this.createBossPocketArrowLabel();
    this.createBossPocketArrow3D();
    this.createBossPocketAxisLine();
    this.createBossPocketBodyPreview();

    // Setup document-level drag handlers
    this.bossPocketDragMoveHandler = (e: MouseEvent) => this.onBossPocketDragMove(e);
    this.bossPocketDragUpHandler = (e: MouseEvent) => this.onBossPocketDragUp(e);

    document.addEventListener('mousemove', this.bossPocketDragMoveHandler, true);
    document.addEventListener('mouseup', this.bossPocketDragUpHandler, true);

    this.cdr.detectChanges();
  }

  /**
   * Create CSS2D arrow label for boss/pocket preview
   */
  private createBossPocketArrowLabel(): void {
    if (!this.engine) return;

    // Get profile center
    const center = this.getBossPocketProfileCenter();

    // Create CSS2D arrow indicator
    this.bossPocketArrowElement = document.createElement('div');
    this.bossPocketArrowElement.className = 'bosspocket-arrow-indicator';
    this.bossPocketArrowElement.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      pointer-events: auto;
      transform: translate(-50%, -50%);
      cursor: ns-resize;
      user-select: none;
    `;

    // Make the arrow draggable
    this.bossPocketArrowElement.addEventListener('mousedown', this.onBossPocketArrowMouseDown);

    // Height label
    const heightLabel = document.createElement('div');
    heightLabel.className = 'height-label';
    const isPocket = this.extrudeOnFaceOperation === 'pocket';
    heightLabel.style.cssText = `
      background: ${isPocket ? 'rgba(255, 100, 100, 0.95)' : 'rgba(160, 255, 0, 0.95)'};
      color: #000;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: var(--font-size-lg);
      font-weight: 700;
      font-family: 'JetBrains Mono', monospace;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      pointer-events: none;
    `;
    heightLabel.textContent = `${Math.abs(this.extrudeOnFaceHeight).toFixed(1)} mm`;

    // Hint text
    const hintText = document.createElement('div');
    hintText.className = 'hint-text';
    hintText.style.cssText = `
      font-size: var(--font-size-sm);
      color: rgba(255, 255, 255, 0.9);
      text-shadow: 0 1px 4px rgba(0,0,0,0.8);
      pointer-events: none;
      text-align: center;
      line-height: 1.5;
      background: rgba(0, 0, 0, 0.5);
      padding: 8px 12px;
      border-radius: 6px;
      margin-top: 4px;
    `;
    hintText.innerHTML = `Drag ↕ to adjust ${isPocket ? 'depth' : 'height'}<br>Enter to apply • Esc to cancel`;

    this.bossPocketArrowElement.appendChild(heightLabel);
    this.bossPocketArrowElement.appendChild(hintText);

    // Create CSS2DObject and position it
    this.bossPocketArrowCSS2D = new CSS2DObject(this.bossPocketArrowElement);
    const worldPos = this.getBossPocketArrowWorldPosition(center);
    this.bossPocketArrowCSS2D.position.copy(worldPos);

    this.engine.scene.add(this.bossPocketArrowCSS2D);
  }

  /**
   * Create 3D arrow for boss/pocket preview
   */
  private createBossPocketArrow3D(): void {
    if (!this.engine) return;

    // Clean up existing
    if (this.bossPocketArrow3D) {
      this.engine.scene.remove(this.bossPocketArrow3D);
      this.bossPocketArrow3D.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.bossPocketArrow3D = null;
    }

    // Create arrow group
    this.bossPocketArrow3D = new Group();
    this.bossPocketArrow3D.name = 'BossPocketArrow3D';

    // Arrow dimensions
    const coneHeight = 12;
    const coneRadius = 5;
    const shaftHeight = 20;
    const shaftRadius = 2;

    // Color based on height sign: negative = pocket (red), positive = boss (green)
    const isPocket = this.extrudeOnFaceHeight < 0;
    const color = isPocket ? 0xff6464 : 0xa0ff00;

    // Create cone (arrow head)
    const coneGeometry = new ConeGeometry(coneRadius, coneHeight, 16);
    const coneMaterial = new MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    const cone = new Mesh(coneGeometry, coneMaterial);
    cone.position.y = shaftHeight + coneHeight / 2;
    cone.renderOrder = 200;

    // Create shaft (cylinder)
    const shaftGeometry = new CylinderGeometry(shaftRadius, shaftRadius, shaftHeight, 16);
    const shaftMaterial = new MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    const shaft = new Mesh(shaftGeometry, shaftMaterial);
    shaft.position.y = shaftHeight / 2;
    shaft.renderOrder = 200;

    this.bossPocketArrow3D.add(cone);
    this.bossPocketArrow3D.add(shaft);

    // Position and orient the arrow
    this.updateBossPocketArrow3DPosition();

    this.engine.scene.add(this.bossPocketArrow3D);
  }

  /**
   * Create axis line for boss/pocket preview
   */
  private createBossPocketAxisLine(): void {
    if (!this.engine) return;

    // Clean up existing
    if (this.bossPocketAxisLine) {
      this.engine.scene.remove(this.bossPocketAxisLine);
      this.bossPocketAxisLine.geometry.dispose();
      (this.bossPocketAxisLine.material as LineDashedMaterial).dispose();
      this.bossPocketAxisLine = null;
    }

    const center = this.getBossPocketProfileCenter();
    const basePos = this.planeLocalToWorld(center);
    const normal = new Vector3(this.customPlaneNormal[0], this.customPlaneNormal[1], this.customPlaneNormal[2]);
    // Use raw height - negative values extend in pocket direction
    const height = this.extrudeOnFaceHeight;
    const endPos = basePos.clone().add(normal.clone().multiplyScalar(height));

    // Color based on height sign: negative = pocket (red), positive = boss (green)
    const isPocket = height < 0;
    const color = isPocket ? 0xff6464 : 0xa0ff00;

    const geometry = new BufferGeometry().setFromPoints([basePos, endPos]);
    const material = new LineDashedMaterial({
      color: color,
      dashSize: 3,
      gapSize: 2,
      transparent: true,
      opacity: 0.8
    });

    this.bossPocketAxisLine = new Line(geometry, material);
    this.bossPocketAxisLine.computeLineDistances();
    this.bossPocketAxisLine.renderOrder = 150;

    this.engine.scene.add(this.bossPocketAxisLine);
  }

  /**
   * Create body preview mesh for boss/pocket
   */
  private createBossPocketBodyPreview(): void {
    if (!this.engine || this.extrudeOnFaceProfile.length < 3) return;

    this.updateBossPocketBodyPreview();
  }

  /**
   * Update the boss/pocket body preview when height changes
   */
  private updateBossPocketBodyPreview(): void {
    if (!this.engine || this.extrudeOnFaceProfile.length < 3) return;

    // Remove existing preview
    if (this.bossPocketBodyPreview) {
      this.engine.scene.remove(this.bossPocketBodyPreview);
      this.bossPocketBodyPreview.geometry.dispose();
      if (this.bossPocketBodyPreview.material instanceof MeshBasicMaterial) {
        this.bossPocketBodyPreview.material.dispose();
      }
      this.bossPocketBodyPreview = null;
    }

    // Use raw height - negative means pocket direction, positive means boss direction
    const height = this.extrudeOnFaceHeight;
    if (height === 0) return;

    // Create extruded geometry from profile
    const shape = new Shape();
    const points = this.extrudeOnFaceProfile;

    shape.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < points.length; i++) {
      shape.lineTo(points[i][0], points[i][1]);
    }
    shape.closePath();

    const extrudeSettings = {
      depth: Math.abs(height),
      bevelEnabled: false
    };

    const geometry = new ExtrudeGeometry(shape, extrudeSettings);

    // Transform geometry to world position
    const origin = new Vector3(this.customPlaneOrigin[0], this.customPlaneOrigin[1], this.customPlaneOrigin[2]);
    const normal = new Vector3(this.customPlaneNormal[0], this.customPlaneNormal[1], this.customPlaneNormal[2]);
    const xAxis = new Vector3(this.customPlaneXAxis[0], this.customPlaneXAxis[1], this.customPlaneXAxis[2]);
    const yAxis = new Vector3(this.customPlaneYAxis[0], this.customPlaneYAxis[1], this.customPlaneYAxis[2]);

    // Create transformation matrix
    const matrix = new Matrix4();
    matrix.makeBasis(xAxis, yAxis, normal);
    matrix.setPosition(origin);

    geometry.applyMatrix4(matrix);

    // If height is negative (pocket direction), flip the geometry to go into the surface
    if (height < 0) {
      geometry.translate(
        -normal.x * Math.abs(height),
        -normal.y * Math.abs(height),
        -normal.z * Math.abs(height)
      );
    }

    // Color based on height sign: negative = pocket (red), positive = boss (green)
    const isPocket = height < 0;
    const material = new MeshBasicMaterial({
      color: isPocket ? 0xff6464 : 0xa0ff00,
      transparent: true,
      opacity: 0.35,
      side: DoubleSide,
      depthWrite: false
    });

    this.bossPocketBodyPreview = new Mesh(geometry, material);
    this.bossPocketBodyPreview.name = 'BossPocketBodyPreview';
    this.bossPocketBodyPreview.renderOrder = 100;

    this.engine.scene.add(this.bossPocketBodyPreview);

    // Update arrow and axis line positions
    this.updateBossPocketArrowPosition();
  }

  /**
   * Get center of boss/pocket profile in 2D local coordinates
   */
  private getBossPocketProfileCenter(): { x: number; y: number } {
    if (this.extrudeOnFaceProfile.length === 0) return { x: 0, y: 0 };

    let centerX = 0, centerY = 0;
    for (const [x, y] of this.extrudeOnFaceProfile) {
      centerX += x;
      centerY += y;
    }
    centerX /= this.extrudeOnFaceProfile.length;
    centerY /= this.extrudeOnFaceProfile.length;

    return { x: centerX, y: centerY };
  }

  /**
   * Get world position for boss/pocket arrow
   */
  private getBossPocketArrowWorldPosition(center: { x: number; y: number }): Vector3 {
    const basePos = this.planeLocalToWorld(center);
    const normal = new Vector3(this.customPlaneNormal[0], this.customPlaneNormal[1], this.customPlaneNormal[2]);
    // Use raw height - negative values position arrow in pocket direction
    const height = this.extrudeOnFaceHeight;
    return basePos.clone().add(normal.clone().multiplyScalar(height));
  }

  /**
   * Update arrow position when height changes
   */
  private updateBossPocketArrowPosition(): void {
    const center = this.getBossPocketProfileCenter();
    const worldPos = this.getBossPocketArrowWorldPosition(center);

    // Update CSS2D label
    if (this.bossPocketArrowCSS2D) {
      this.bossPocketArrowCSS2D.position.copy(worldPos);

      // Update height text
      const heightLabel = this.bossPocketArrowElement?.querySelector('.height-label') as HTMLElement;
      if (heightLabel) {
        heightLabel.textContent = `${Math.abs(this.extrudeOnFaceHeight).toFixed(1)} mm`;
      }
    }

    // Update 3D arrow
    this.updateBossPocketArrow3DPosition();

    // Update axis line
    this.updateBossPocketAxisLine();
  }

  /**
   * Update 3D arrow position and orientation
   */
  private updateBossPocketArrow3DPosition(): void {
    if (!this.bossPocketArrow3D) return;

    const center = this.getBossPocketProfileCenter();
    const arrowPos = this.getBossPocketArrowWorldPosition(center);
    this.bossPocketArrow3D.position.copy(arrowPos);

    // Orient arrow to point in extrusion direction based on height sign
    const normal = new Vector3(this.customPlaneNormal[0], this.customPlaneNormal[1], this.customPlaneNormal[2]);
    const direction = this.extrudeOnFaceHeight < 0 ? normal.clone().negate() : normal;
    const up = new Vector3(0, 1, 0);
    const quaternion = new Quaternion().setFromUnitVectors(up, direction);
    this.bossPocketArrow3D.quaternion.copy(quaternion);
  }

  /**
   * Update axis line position
   */
  private updateBossPocketAxisLine(): void {
    if (!this.bossPocketAxisLine || !this.engine) return;

    this.engine.scene.remove(this.bossPocketAxisLine);
    this.bossPocketAxisLine.geometry.dispose();
    (this.bossPocketAxisLine.material as LineDashedMaterial).dispose();

    const center = this.getBossPocketProfileCenter();
    const basePos = this.planeLocalToWorld(center);
    const normal = new Vector3(this.customPlaneNormal[0], this.customPlaneNormal[1], this.customPlaneNormal[2]);
    // Use raw height - negative values extend in pocket direction
    const height = this.extrudeOnFaceHeight;
    const endPos = basePos.clone().add(normal.clone().multiplyScalar(height));

    // Color based on height sign: negative = pocket (red), positive = boss (green)
    const isPocket = height < 0;
    const color = isPocket ? 0xff6464 : 0xa0ff00;

    const geometry = new BufferGeometry().setFromPoints([basePos, endPos]);
    const material = new LineDashedMaterial({
      color: color,
      dashSize: 3,
      gapSize: 2,
      transparent: true,
      opacity: 0.8
    });

    this.bossPocketAxisLine = new Line(geometry, material);
    this.bossPocketAxisLine.computeLineDistances();
    this.bossPocketAxisLine.renderOrder = 150;

    this.engine.scene.add(this.bossPocketAxisLine);
  }

  /**
   * Arrow mouse down handler for boss/pocket
   */
  private onBossPocketArrowMouseDown = (event: MouseEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    this.isBossPocketArrowDragging = true;
    this.bossPocketDragInitialHeight = this.extrudeOnFaceHeight;

    // Change arrow color to indicate active dragging
    this.setBossPocketArrowDraggingStyle(true);

    // Set cursor
    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = 'ns-resize';
    }
  };

  /**
   * Handle mouse move during boss/pocket drag
   * Uses ray-to-axis projection (same as extrusion drag) for accurate plane-independent movement
   */
  private onBossPocketDragMove(event: MouseEvent): void {
    if (!this.isBossPocketPreviewMode || !this.isBossPocketArrowDragging) return;
    if (!this.engine || !this.bossPocketDragBaseWorldPos || !this.bossPocketDragNormal) return;

    event.preventDefault();
    event.stopPropagation();

    // Raycast from mouse to find intersection with a plane containing the extrusion axis
    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    // Find the closest point on the extrusion axis line to the ray
    // Extrusion axis: basePoint + t * normal
    // Ray: rayOrigin + s * rayDir
    const rayOrigin = raycaster.ray.origin;
    const rayDir = raycaster.ray.direction;
    const basePoint = this.bossPocketDragBaseWorldPos;
    const normal = this.bossPocketDragNormal;

    // Calculate closest approach between two lines
    const w0 = rayOrigin.clone().sub(basePoint);
    const a = normal.dot(normal); // always > 0
    const b = normal.dot(rayDir);
    const c = rayDir.dot(rayDir); // always > 0
    const d = normal.dot(w0);
    const e = rayDir.dot(w0);

    const denom = a * c - b * b;

    let newHeight: number;
    if (Math.abs(denom) < 0.0001) {
      // Lines are parallel - fallback to initial height
      newHeight = this.bossPocketDragInitialHeight;
    } else {
      // Parameter on the extrusion axis for closest point
      const t = (b * e - c * d) / denom;
      // Invert the sign so moving mouse up increases height
      newHeight = -t;
    }

    // Apply modifier keys for finer control (same as extrusion)
    // Ctrl = super fine (0.1mm precision), Shift = fine (1mm precision)
    if (event.ctrlKey) {
      newHeight = Math.round(newHeight * 10) / 10;
    } else if (event.shiftKey) {
      newHeight = Math.round(newHeight);
    }

    // Snap to grid increments (5mm steps) when snap is enabled
    // Keep sign but snap magnitude (same as Push/Pull)
    if (this.snapEnabled) {
      const sign = newHeight >= 0 ? 1 : -1;
      newHeight = sign * Math.round(Math.abs(newHeight) / 5) * 5;
    }

    // Allow negative values - positive = boss direction, negative = pocket direction
    // This allows flipping direction during drag (like extrusion)
    this.extrudeOnFaceHeight = newHeight;

    // Update preview
    this.updateBossPocketBodyPreview();

    this.cdr.detectChanges();
  }

  /**
   * Handle mouse up during boss/pocket drag
   */
  private onBossPocketDragUp(event: MouseEvent): void {
    if (event.button !== 0) return;

    if (this.isBossPocketArrowDragging) {
      this.setBossPocketArrowDraggingStyle(false);
    }

    this.isBossPocketArrowDragging = false;

    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = '';
    }

    this.cdr.detectChanges();
  }

  /**
   * Handle height input change - update preview if in preview mode
   */
  onBossPocketHeightChange(): void {
    if (this.isBossPocketPreviewMode) {
      this.updateBossPocketBodyPreview();
    }
  }

  /**
   * Set arrow visual style for dragging state
   */
  private setBossPocketArrowDraggingStyle(isDragging: boolean): void {
    if (!this.bossPocketArrowElement) return;

    const heightLabel = this.bossPocketArrowElement.querySelector('.height-label') as HTMLElement;
    // Use height sign to determine color: negative = pocket (red), positive = boss (green)
    const isPocket = this.extrudeOnFaceHeight < 0;
    const normalColor = isPocket ? 'rgba(255, 100, 100, 0.95)' : 'rgba(160, 255, 0, 0.95)';

    if (isDragging) {
      if (heightLabel) {
        heightLabel.style.background = 'rgba(255, 215, 0, 0.95)';
        heightLabel.style.transform = 'scale(1.1)';
      }
      if (this.bossPocketArrow3D) {
        this.bossPocketArrow3D.traverse((child: any) => {
          if (child.material) {
            child.material.color.setHex(0xffd700);
          }
        });
      }
    } else {
      if (heightLabel) {
        heightLabel.style.background = normalColor;
        heightLabel.style.transform = 'scale(1)';
      }
      if (this.bossPocketArrow3D) {
        const color = isPocket ? 0xff6464 : 0xa0ff00;
        this.bossPocketArrow3D.traverse((child: any) => {
          if (child.material) {
            child.material.color.setHex(color);
          }
        });
      }
    }
  }

  /**
   * Clear all boss/pocket preview elements
   */
  private clearBossPocketPreview(): void {
    // Clear CSS2D label
    if (this.bossPocketArrowCSS2D) {
      if (this.bossPocketArrowElement) {
        this.bossPocketArrowElement.removeEventListener('mousedown', this.onBossPocketArrowMouseDown);
        if (this.bossPocketArrowElement.parentNode) {
          this.bossPocketArrowElement.parentNode.removeChild(this.bossPocketArrowElement);
        }
      }
      if (this.engine) {
        this.engine.scene.remove(this.bossPocketArrowCSS2D);
      }
      this.bossPocketArrowCSS2D = null;
      this.bossPocketArrowElement = null;
    }

    // Clear 3D arrow
    if (this.bossPocketArrow3D && this.engine) {
      this.engine.scene.remove(this.bossPocketArrow3D);
      this.bossPocketArrow3D.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      this.bossPocketArrow3D = null;
    }

    // Clear axis line
    if (this.bossPocketAxisLine && this.engine) {
      this.engine.scene.remove(this.bossPocketAxisLine);
      this.bossPocketAxisLine.geometry.dispose();
      (this.bossPocketAxisLine.material as LineDashedMaterial).dispose();
      this.bossPocketAxisLine = null;
    }

    // Clear body preview
    if (this.bossPocketBodyPreview && this.engine) {
      this.engine.scene.remove(this.bossPocketBodyPreview);
      this.bossPocketBodyPreview.geometry.dispose();
      if (this.bossPocketBodyPreview.material instanceof MeshBasicMaterial) {
        this.bossPocketBodyPreview.material.dispose();
      }
      this.bossPocketBodyPreview = null;
    }

    // Remove document listeners
    if (this.bossPocketDragMoveHandler) {
      document.removeEventListener('mousemove', this.bossPocketDragMoveHandler, true);
      this.bossPocketDragMoveHandler = null;
    }
    if (this.bossPocketDragUpHandler) {
      document.removeEventListener('mouseup', this.bossPocketDragUpHandler, true);
      this.bossPocketDragUpHandler = null;
    }

    this.isBossPocketPreviewMode = false;
    this.isBossPocketArrowDragging = false;
  }

  /**
   * Apply boss/pocket operation from preview mode
   */
  async applyBossPocket(): Promise<void> {
    // Allow circle boss/pocket even with less than 3 profile points (circle data is separate)
    const hasCircleData = this.extrudeOnFaceCircle !== null;
    if (!this.isBossPocketPreviewMode || (!hasCircleData && this.extrudeOnFaceProfile.length < 3)) return;
    if (!this.selectedFaceInfo || this.selectedCADShapeIds.size !== 1) return;

    try {
      // Determine operation based on height sign (negative = pocket, positive = boss)
      const actualOperation: 'boss' | 'pocket' = this.extrudeOnFaceHeight >= 0 ? 'boss' : 'pocket';
      // Use absolute height since operation determines direction
      const actualHeight = Math.abs(this.extrudeOnFaceHeight);

      this.isProcessing = true;
      this.processingMessage = `Creating ${actualOperation}...`;

      // Clear the preview first
      this.clearBossPocketPreview();

      // Commit any pending transforms
      await this.commitPendingTransforms();

      const selectedId = Array.from(this.selectedCADShapeIds)[0];
      const baseShape = this.cadShapes.find((s) => s.id === selectedId);

      if (!baseShape) {
        throw new Error('Base shape not found');
      }

      const planeParams = {
        origin: this.customPlaneOrigin,
        normal: this.customPlaneNormal,
        xAxis: this.customPlaneXAxis,
        yAxis: this.customPlaneYAxis
      };

      let result;

      // Use true CAD circle for circle boss/pocket (smooth cylinder, not segmented)
      if (hasCircleData) {
        result = await this.cadKernel.extrudeCircleOnFace(
          baseShape,
          this.extrudeOnFaceCircle!.centerX,
          this.extrudeOnFaceCircle!.centerY,
          this.extrudeOnFaceCircle!.radius,
          actualHeight,
          actualOperation,
          planeParams
        );
      } else {
        // Apply polygon boss or pocket
        const faceIndex = this.selectedFaceInfo.faceIndex ?? 0;
        result = await this.cadKernel.extrudeOnFace(
          baseShape,
          this.extrudeOnFaceProfile,
          faceIndex,
          actualHeight,
          actualOperation,
          planeParams
        );
      }

      // Remove original from scene and list
      this.removeShapeFromScene(baseShape.id);
      this.cadShapes = this.cadShapes.filter((s) => s.id !== baseShape.id);

      // Add result
      this.cadShapes.push(result);
      this.addShapeToScene(result);

      // Select the result
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(result.id);
      this.updateShapeVisualState(result.id, true);
      this.attachTransformControls();

      // Reset boss/pocket mode
      this.isExtrudeOnFaceMode = false;
      this.extrudeOnFaceProfile = [];
      this.extrudeOnFaceCircle = null;
      this.selectedFaceInfo = null;
      this.cleanupSketchPlane();

      // Clear sketch plane settings
      this.sketchPlane = null;
      this.customPlaneOrigin = [0, 0, 0];
      this.customPlaneNormal = [0, 0, 1];
      this.customPlaneXAxis = [1, 0, 0];
      this.customPlaneYAxis = [0, 1, 0];

      // Clear active operation state
      this.restoreHiddenShapes();
      this.activeOperation = null;
      this.isBossPocketPreviewMode = false;

      // Collapse the panel on success
      this.expandedPatternGroup = null;

      this.toastService.success(`${actualOperation === 'boss' ? 'Boss' : 'Pocket'} created`);
    } catch (error) {
      console.error('Boss/pocket error:', error);
      // Determine operation from height sign for error message
      const failedOp = this.extrudeOnFaceHeight >= 0 ? 'boss' : 'pocket';
      this.toastService.error(`Failed to create ${failedOp}`);

      // Clean up on failure
      this.clearBossPocketPreview();
      this.isExtrudeOnFaceMode = false;
      this.isBossPocketPreviewMode = false;
      this.extrudeOnFaceProfile = [];
      this.extrudeOnFaceCircle = null;
      this.selectedFaceInfo = null;
      this.cleanupSketchPlane();
      this.restoreHiddenShapes();
      this.activeOperation = null;

      // Collapse the panel on failure
      this.expandedPatternGroup = null;
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Cancel boss/pocket preview mode
   */
  cancelBossPocketPreviewMode(): void {
    this.clearBossPocketPreview();
    this.extrudeOnFaceProfile = [];
    this.extrudeOnFaceCircle = null;
    this.cdr.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PATTERN OPERATIONS (Linear Array, Circular Array, Mirror)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check if linear array can be applied
   */
  canApplyLinearArray(): boolean {
    return this.selectedCADShapeIds.size === 1 && this.linearArrayCount >= 2 && this.linearArraySpacing > 0;
  }

  /**
   * Check if circular array can be applied
   */
  canApplyCircularArray(): boolean {
    return this.selectedCADShapeIds.size === 1 && this.circularArrayCount >= 2 && this.circularArrayAngle > 0;
  }

  /**
   * Apply linear array to selected shape
   */
  async applyLinearArray(): Promise<void> {
    if (!this.canApplyLinearArray()) return;

    const initialSelectedId = Array.from(this.selectedCADShapeIds)[0];
    if (!initialSelectedId) return;

    try {
      this.isProcessing = true;
      this.processingMessage = 'Creating linear array...';
      this.detachTransformControls();

      // Commit any pending transforms before pattern operation
      await this.commitPendingTransforms();

      // Re-fetch shape after commit - transform may have changed the shape ID and object
      const currentSelectedId = Array.from(this.selectedCADShapeIds)[0];
      const shape = this.cadShapes.find((s) => s.id === currentSelectedId);
      if (!shape) return;

      // Get direction vector
      const directionMap: { [key: string]: [number, number, number] } = {
        X: [1, 0, 0],
        Y: [0, 1, 0],
        Z: [0, 0, 1]
      };
      const direction = directionMap[this.linearArrayDirection];

      const newShapes = await this.cadKernel.linearArray(
        shape,
        direction,
        this.linearArrayCount,
        this.linearArraySpacing
      );

      // Add all new shapes to scene
      for (const newShape of newShapes) {
        this.cadShapes.push(newShape);
        this.addShapeToScene(newShape);
      }

      // Clear selection and select all array shapes
      this.selectedCADShapeIds.clear();
      for (const newShape of newShapes) {
        this.selectedCADShapeIds.add(newShape.id);
        this.updateShapeVisualState(newShape.id, true);
      }
      // Also keep original selected
      this.selectedCADShapeIds.add(shape.id);

      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Linear array failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Apply circular array to selected shape
   */
  async applyCircularArray(): Promise<void> {
    if (!this.canApplyCircularArray()) return;

    const initialSelectedId = Array.from(this.selectedCADShapeIds)[0];
    if (!initialSelectedId) return;

    try {
      this.isProcessing = true;
      this.processingMessage = 'Creating circular array...';
      this.detachTransformControls();

      // Commit any pending transforms before pattern operation
      await this.commitPendingTransforms();

      // Re-fetch shape after commit - transform may have changed the shape ID and object
      const currentSelectedId = Array.from(this.selectedCADShapeIds)[0];
      const shape = this.cadShapes.find((s) => s.id === currentSelectedId);
      if (!shape) return;

      // Get shape center for rotation center
      const group = this.sceneMeshes.get(shape.id);
      let center: [number, number, number] = [0, 0, 0];
      if (group) {
        const box = new Box3().setFromObject(group);
        const boxCenter = new Vector3();
        box.getCenter(boxCenter);
        center = [boxCenter.x, boxCenter.y, boxCenter.z];
      }

      const newShapes = await this.cadKernel.circularArray(
        shape,
        this.circularArrayAxis,
        center,
        this.circularArrayCount,
        this.circularArrayAngle
      );

      // Add all new shapes to scene
      for (const newShape of newShapes) {
        this.cadShapes.push(newShape);
        this.addShapeToScene(newShape);
      }

      // Clear selection and select all array shapes
      this.selectedCADShapeIds.clear();
      for (const newShape of newShapes) {
        this.selectedCADShapeIds.add(newShape.id);
        this.updateShapeVisualState(newShape.id, true);
      }
      // Also keep original selected
      this.selectedCADShapeIds.add(shape.id);

      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Circular array failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Apply mirror to selected shape
   */
  async applyMirror(): Promise<void> {
    if (this.selectedCADShapeIds.size !== 1) return;

    const initialSelectedId = Array.from(this.selectedCADShapeIds)[0];
    if (!initialSelectedId) return;

    try {
      this.isProcessing = true;
      this.processingMessage = 'Mirroring shape...';
      this.detachTransformControls();

      // Commit any pending transforms before mirror operation
      await this.commitPendingTransforms();

      // Re-fetch shape after commit - transform may have changed the shape ID and object
      const currentSelectedId = Array.from(this.selectedCADShapeIds)[0];
      const shape = this.cadShapes.find((s) => s.id === currentSelectedId);
      if (!shape) return;

      // Get shape center for mirror origin
      const group = this.sceneMeshes.get(shape.id);
      let origin: [number, number, number] = [0, 0, 0];
      if (group) {
        const box = new Box3().setFromObject(group);
        const boxCenter = new Vector3();
        box.getCenter(boxCenter);
        origin = [boxCenter.x, boxCenter.y, boxCenter.z];
      }

      const mirroredShape = await this.cadKernel.mirrorShape(shape, this.mirrorPlane, origin);

      // Add mirrored shape to scene
      this.cadShapes.push(mirroredShape);
      this.addShapeToScene(mirroredShape);

      // Select both original and mirrored
      this.selectedCADShapeIds.add(mirroredShape.id);
      this.updateShapeVisualState(mirroredShape.id, true);

      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Mirror failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP IMPORT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Open file picker for STEP import
   */
  openSTEPImport(): void {
    // Create file input if not exists
    if (!this.stepFileInput) {
      this.stepFileInput = document.createElement('input');
      this.stepFileInput.type = 'file';
      this.stepFileInput.accept = '.step,.stp,.STEP,.STP';
      this.stepFileInput.style.display = 'none';
      this.stepFileInput.addEventListener('change', (e) => this.handleSTEPFileSelect(e));
      document.body.appendChild(this.stepFileInput);
    }
    this.stepFileInput.click();
  }

  /**
   * Handle STEP file selection
   */
  private async handleSTEPFileSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    try {
      this.isProcessing = true;
      this.processingMessage = `Importing ${file.name}...`;

      const importedShapes = await this.cadKernel.importFromSTEPFile(file);

      // Add all imported shapes to scene
      for (const shape of importedShapes) {
        this.cadShapes.push(shape);
        this.addShapeToScene(shape);
      }

      // Select all imported shapes
      this.selectedCADShapeIds.clear();
      for (const shape of importedShapes) {
        this.selectedCADShapeIds.add(shape.id);
        this.updateShapeVisualState(shape.id, true);
      }

      await this.seedHistoryFromShapes(importedShapes);

      this.hasUnsavedChanges = true;
      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`STEP import failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      // Reset file input so same file can be selected again
      if (this.stepFileInput) {
        this.stepFileInput.value = '';
      }
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // IMPORT FROM MODELS LIST (S3 STEP Files)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Load available parts from the engine's assembly
   */
  loadAvailableModelParts(): void {
    if (!this.engine?.assembly?.parts) {
      this.availableModelParts = [];
      return;
    }

    this.availableModelParts = (this.engine.assembly.parts as any[])
      .filter((p: any) => p.id && p.name)
      .map((p: any) => ({
        id: p.id,
        name: p.name,
        color: p.color
      }));

    // Reset selection
    this.selectedModelPartId = '';
  }

  /**
   * Import a part from the models list by fetching its STEP file from S3
   */
  async importFromModelsList(): Promise<void> {
    if (!this.selectedModelPartId) {
      this.toastService.error('Please select a part to import');
      return;
    }

    const partInfo = this.availableModelParts.find((p) => p.id === this.selectedModelPartId);
    if (!partInfo) {
      this.toastService.error('Part not found');
      return;
    }

    const projectId = this.session?.project_id;
    if (!projectId) {
      this.toastService.error('No project loaded');
      return;
    }

    try {
      this.isProcessing = true;
      this.isLoadingModelParts = true;
      this.processingMessage = `Fetching ${partInfo.name}...`;
      this.cdr.detectChanges();

      // Step 1: Get presigned URL for the part's STEP file
      const presignedUrl = await lastValueFrom(
        this.http.get(`${environment.API_ADDRESS}/project/${projectId}/parts/${partInfo.id}/step`, {
          responseType: 'text'
        })
      );

      if (!presignedUrl) {
        throw new Error('Failed to get download URL for part');
      }

      this.processingMessage = `Downloading ${partInfo.name}...`;
      this.cdr.detectChanges();

      // Step 2: Download the STEP file content
      const stepContent = await lastValueFrom(this.http.get(presignedUrl, { responseType: 'text' }));

      if (!stepContent) {
        throw new Error('Failed to download STEP file');
      }

      this.processingMessage = `Importing ${partInfo.name}...`;
      this.cdr.detectChanges();

      // Step 3: Initialize kernel if needed
      await this.initializeKernel();

      // Step 4: Import the STEP content
      // Use streaming for large files (> 5MB) for progressive display
      const STREAMING_THRESHOLD = 5 * 1024 * 1024; // 5MB
      const isLargeFile = stepContent.length > STREAMING_THRESHOLD;
      const importedShapes: CADShape[] = [];

      if (isLargeFile) {
        // Use streaming import for large files - shapes appear progressively
        this.processingMessage = `Importing ${partInfo.name} (streaming)...`;
        this.processingProgress = 0;
        this.cdr.detectChanges();

        await this.cadKernel.importFromSTEPStreaming(
          stepContent,
          partInfo.name,
          (shape, index, total) => {
            // Apply original color if available
            if (partInfo.color) {
              shape.color = partInfo.color;
            }
            importedShapes.push(shape);
            this.cadShapes.push(shape);
            this.addShapeToScene(shape);

            // Update progress
            this.processingProgress = ((index + 1) / total) * 100;
            this.processingMessage = `Importing ${partInfo.name} (${index + 1}/${total})...`;
            this.cdr.detectChanges();
          }
        );
      } else {
        // Standard import for smaller files
        const shapes = await this.cadKernel.importFromSTEP(stepContent, partInfo.name);

        // Add imported shapes to scene
        for (const shape of shapes) {
          // Apply original color if available
          if (partInfo.color) {
            shape.color = partInfo.color;
          }
          importedShapes.push(shape);
          this.cadShapes.push(shape);
          this.addShapeToScene(shape);
        }
      }

      if (importedShapes.length === 0) {
        throw new Error('No shapes could be imported from STEP file');
      }

      // Select imported shapes
      this.selectedCADShapeIds.clear();
      for (const shape of importedShapes) {
        this.selectedCADShapeIds.add(shape.id);
        this.updateShapeVisualState(shape.id, true);
      }
      this.attachTransformControls();

      await this.seedHistoryFromShapes(importedShapes);

      this.hasUnsavedChanges = true;
      this.selectedModelPartId = ''; // Reset selection

      this.toastService.success(
        `Imported ${importedShapes.length} shape${importedShapes.length !== 1 ? 's' : ''} from ${partInfo.name}`
      );
    } catch (error: any) {
      this.toastService.error(`Import failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.isLoadingModelParts = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PERSISTENCE (Save/Load CAD Parts)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Save all CAD parts to the project
   */
  async saveCADParts(): Promise<void> {
    if (!this.session) {
      this.onError.emit('No session available for saving');
      return;
    }

    // Commit any pending transforms before saving
    await this.commitPendingTransforms();

    try {
      this.isSaving = true;
      this.saveProgress = 0;
      this.processingMessage = 'Saving CAD parts...';
      this.cdr.detectChanges();

      // Convert CAD shapes to serializable format (async for BREP export)
      const cadPartsData = await Promise.all(this.cadShapes.map((shape) => this.serializeCADShape(shape)));

      // Serialize sketches
      const sketchesData = this.sketchService.getAllSketches().map((sketch) => ({
        id: sketch.id,
        name: sketch.name,
        plane: sketch.plane,
        planeOffset: sketch.planeOffset,
        planeOrigin: sketch.planeOrigin,
        planeNormal: sketch.planeNormal,
        planeXAxis: sketch.planeXAxis,
        planeYAxis: sketch.planeYAxis,
        referenceFaceId: sketch.referenceFaceId,
        referenceBodyId: sketch.referenceBodyId,
        entities: sketch.entities.map((entity) => this.serializeSketchEntity(entity)),
        isVisible: sketch.isVisible
      }));

      // Update project with CAD parts and sketches data
      this.session.project.cadParts = cadPartsData;
      this.session.project.sketches = sketchesData;

      // Trigger project save (10% progress)
      this.saveProgress = 10;
      this.cdr.detectChanges();
      await this.session.saveProject();

      // If sync to backend is enabled, also upload STEP files to S3 and wait for processing
      if (this.syncToBackend && this.cadShapes.length > 0) {
        const { success, failed } = await this.syncShapesToBackend();

        if (failed.length > 0) {
          const failedNames = failed.map((id) => this.cadShapes.find((s) => s.id === id)?.name || id).join(', ');
          this.onError.emit(`Some parts failed to sync: ${failedNames}`);
        }

        if (success.length > 0) {
        }
      }

      this.saveProgress = 100;
      this.hasUnsavedChanges = false;
      this.lastSaveTime = new Date();
      this.processingMessage = '';

      this.onSave.emit(this.cadShapes);
    } catch (error: any) {
      // Same throttled toast as autosave, so a failing PATCH does not show two.
      this.session.notifySaveFailed(error);
    } finally {
      this.isSaving = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Sync CAD shapes to backend (batch upload STEP files to S3 and trigger Lambda processing)
   * Uses batch upload to avoid race conditions when multiple parts update assembly.proto
   * Returns a promise that resolves when all parts have been processed by the backend
   */
  async syncShapesToBackend(): Promise<{ success: string[]; failed: string[] }> {
    if (!this.session) return { success: [], failed: [] };

    const shapesToSync = this.cadShapes.filter((shape) => {
      const status = this.pendingUploads.get(shape.id);
      return !status || status === 'error'; // Only sync shapes not yet uploaded or failed
    });

    if (shapesToSync.length === 0) {
      return { success: [], failed: [] };
    }

    try {
      // Progress: 10-40% for exporting, 40-60% for uploading, 60-95% for processing
      const exportProgressStart = 10;
      const exportProgressEnd = 40;
      const uploadProgressEnd = 60;
      const processProgressEnd = 95;

      // 1. Export all shapes to STEP format
      this.processingMessage = `Exporting ${shapesToSync.length} parts...`;
      this.cdr.detectChanges();

      const partsToUpload: Array<{
        partId: string;
        partName: string;
        stepBlob: Blob;
        color: string;
      }> = [];

      for (let i = 0; i < shapesToSync.length; i++) {
        const shape = shapesToSync[i];
        this.processingMessage = `Exporting ${shape.name} (${i + 1}/${shapesToSync.length})...`;
        this.saveProgress = exportProgressStart + (i / shapesToSync.length) * (exportProgressEnd - exportProgressStart);
        this.cdr.detectChanges();

        this.pendingUploads.set(shape.id, 'uploading');
        const stepBlob = await this.cadKernel.exportToSTEP(shape);

        partsToUpload.push({
          partId: shape.id,
          partName: shape.name,
          stepBlob: stepBlob,
          color: shape.color || '#6366f1'
        });
      }

      // 2. Create a single promise that will resolve when batch processing completes
      const batchCompletionPromise = new Promise<{
        processedParts: Array<{ partId: string; partName: string }>;
        failedParts: Array<{ partId: string; error: string }>;
        assemblyProtoUrl: string;
      }>((resolve, reject) => {
        // Store resolver that will be called by WebSocket handler
        (this as any)._batchCompletionResolver = { resolve, reject };
      });

      // 3. Send batch upload request
      this.processingMessage = `Uploading ${partsToUpload.length} parts...`;
      this.saveProgress = exportProgressEnd;
      this.cdr.detectChanges();

      await this.session!.batchUploadBrowserCADParts(partsToUpload);

      // Mark all as pending (uploaded, waiting for Lambda)
      shapesToSync.forEach((shape) => this.pendingUploads.set(shape.id, 'pending'));
      this.saveProgress = uploadProgressEnd;

      // 4. Wait for batch completion WebSocket message (with timeout)
      this.processingMessage = `Processing ${partsToUpload.length} parts...`;
      this.cdr.detectChanges();

      const result = await Promise.race([
        batchCompletionPromise,
        new Promise<never>(
          (_, reject) => setTimeout(() => reject(new Error('Backend batch processing timeout')), 300000) // 5 minute timeout for batch
        )
      ]);

      // 5. Process results
      const success: string[] = [];
      const failed: string[] = [];

      // Update status for processed parts
      for (const part of result.processedParts) {
        this.pendingUploads.set(part.partId, 'complete');
        success.push(part.partId);
      }

      // Update status for failed parts
      for (const part of result.failedParts) {
        this.pendingUploads.set(part.partId, 'error');
        failed.push(part.partId);
      }

      // 6. Add all processed parts to engine (they're all in one proto update)
      if (result.assemblyProtoUrl && this.engine && result.processedParts.length > 0) {
        try {
          const protoData = await lastValueFrom(
            this.http.get(result.assemblyProtoUrl, { responseType: 'arraybuffer' })
          );

          for (const part of result.processedParts) {
            await this.engine.addGeneratedShapePart(protoData, part.partId, part.partName);
          }
        } catch (error) {}
      }

      return { success, failed };
    } catch (error: any) {
      // Mark all as error
      shapesToSync.forEach((shape) => this.pendingUploads.set(shape.id, 'error'));

      return {
        success: [],
        failed: shapesToSync.map((s) => s.id)
      };
    } finally {
      // Clean up resolver
      delete (this as any)._batchCompletionResolver;
    }
  }

  /**
   * Load CAD parts and sketches from the project
   */
  async loadCADParts(): Promise<void> {
    const hasCadParts = this.session?.project?.cadParts?.length;
    const hasSketches = this.session?.project?.sketches?.length;

    if (!hasCadParts && !hasSketches) {
      return;
    }

    try {
      this.isProcessing = true;
      this.processingMessage = 'Loading CAD data...';

      // Load CAD parts
      if (hasCadParts) {
        const cadPartsData = this.session!.project.cadParts!;
        const loadedShapes: CADShape[] = [];

        for (const partData of cadPartsData) {
          try {
            const shape = await this.deserializeCADShape(partData);
            if (shape) {
              this.cadShapes.push(shape);
              this.addShapeToScene(shape);
              loadedShapes.push(shape);

              // Apply saved transforms to the scene group
              // The saved position/rotation/scale are the group's transforms as they were saved,
              // which already includes the geometry center offset
              const group = this.sceneMeshes.get(shape.id);
              if (group && partData.position && partData.rotation && partData.scale) {
                // Apply saved position directly (it already includes geometry center offset from when it was saved)
                group.position.set(partData.position[0], partData.position[1], partData.position[2]);

                // Apply saved rotation
                group.quaternion.set(
                  partData.rotation[0],
                  partData.rotation[1],
                  partData.rotation[2],
                  partData.rotation[3]
                );

                // Apply saved scale
                group.scale.set(partData.scale[0], partData.scale[1], partData.scale[2]);
              }
            }
          } catch (partError) {}
        }

        await this.seedHistoryFromShapes(loadedShapes);
      }

      // Load sketches
      if (hasSketches) {
        this.processingMessage = 'Loading sketches...';
        this.cdr.detectChanges();

        for (const sketchData of this.session!.project.sketches!) {
          try {
            // Create sketch with saved properties
            const sketch = this.sketchService.createSketch(
              sketchData.plane as 'XY' | 'XZ' | 'YZ' | 'custom',
              sketchData.name,
              {
                planeOffset: sketchData.planeOffset,
                planeOrigin: sketchData.planeOrigin,
                planeNormal: sketchData.planeNormal,
                planeXAxis: sketchData.planeXAxis,
                planeYAxis: sketchData.planeYAxis,
                referenceFaceId: sketchData.referenceFaceId,
                referenceBodyId: sketchData.referenceBodyId
              }
            );

            // Apply visibility if present
            if (sketchData.isVisible !== undefined) sketch.isVisible = sketchData.isVisible;

            // Deserialize entities
            for (const entityData of sketchData.entities) {
              this.deserializeSketchEntity(sketch.id, entityData);
            }
          } catch (sketchError) {
            console.warn('Failed to load sketch:', sketchData.id, sketchError);
          }
        }

        // Render loaded sketches
        this.renderAllVisibleSketches();
      }

      this.processingMessage = '';
      this.hasUnsavedChanges = false;
    } catch (error: any) {
      this.onError.emit(`Load failed: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Serialize a CAD shape to a persistable format
   * For derived shapes (boolean results, fillets, etc.), exports BREP data
   */
  private async serializeCADShape(shape: CADShape): Promise<any> {
    // Get transform from scene mesh
    const group = this.sceneMeshes.get(shape.id);
    let position: [number, number, number] = [0, 0, 0];
    let rotation: [number, number, number, number] = [0, 0, 0, 1];
    let scale: [number, number, number] = [1, 1, 1];

    if (group) {
      position = [group.position.x, group.position.y, group.position.z];
      rotation = [group.quaternion.x, group.quaternion.y, group.quaternion.z, group.quaternion.w];
      scale = [group.scale.x, group.scale.y, group.scale.z];
    }

    // Determine source type and get BREP data if needed
    const isDirectEdit = !!shape.isDirectEdit;
    let sourceType: 'primitive' | 'extrusion' | 'brep' = 'primitive';
    let brepData: string | undefined;

    if (!isDirectEdit && shape.extrudeParams) {
      sourceType = 'extrusion';
    } else if (!isDirectEdit && shape.primitiveParams) {
      sourceType = 'primitive';
    } else {
      // Derived shape (boolean result, fillet, chamfer, shell, etc.)
      // Export BREP data for restoration
      sourceType = 'brep';
      try {
        brepData = await this.cadKernel.exportBrep(shape);
      } catch (error) {
        console.error('Failed to export BREP for shape:', shape.id, error);
        if (!isDirectEdit && (shape.extrudeParams || shape.primitiveParams)) {
          sourceType = shape.extrudeParams ? 'extrusion' : 'primitive';
        }
      }
    }

    return {
      id: shape.id,
      name: shape.name,
      color: shape.color,
      position,
      rotation,
      scale,
      sourceType,
      isDirectEdit,
      primitiveParams: !isDirectEdit && shape.primitiveParams
        ? {
            type: shape.primitiveParams.type,
            dimensions: shape.primitiveParams.dimensions
          }
        : undefined,
      extrudeParams: !isDirectEdit && shape.extrudeParams
        ? {
            points: shape.extrudeParams.points,
            height: shape.extrudeParams.height,
            sketchPlane: shape.extrudeParams.sketchPlane,
            planeOffset: shape.extrudeParams.planeOffset,
            direction: shape.extrudeParams.direction,
            holes: shape.extrudeParams.holes,
            circleHoles: shape.extrudeParams.circleHoles,
            customPlane: shape.extrudeParams.customPlane
          }
        : undefined,
      brepData,
      // Surface data for extracted faces
      isSurface: shape.isSurface,
      surfaceType: shape.surfaceType,
      cylinderData: shape.cylinderData,
      coneData: shape.coneData,
      sphereData: shape.sphereData,
      torusData: shape.torusData,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
  }

  /**
   * Deserialize a CAD shape from saved data
   */
  private async deserializeCADShape(data: any): Promise<CADShape | null> {
    try {
      let shape: CADShape;

      if (data.sourceType === 'extrusion' && data.extrudeParams) {
        // Recreate extruded shape with all parameters
        shape = await this.cadKernel.extrudePolygon({
          points: data.extrudeParams.points,
          height: data.extrudeParams.height,
          sketchPlane: data.extrudeParams.sketchPlane || 'XY',
          planeOffset: data.extrudeParams.planeOffset || 0,
          direction: data.extrudeParams.direction,
          holes: data.extrudeParams.holes,
          circleHoles: data.extrudeParams.circleHoles,
          customPlane: data.extrudeParams.customPlane
        });
      } else if (data.sourceType === 'primitive' && data.primitiveParams) {
        // Recreate primitive
        const dims = data.primitiveParams.dimensions;
        switch (data.primitiveParams.type) {
          case 'box':
            shape = await this.cadKernel.createBox(dims[0], dims[1], dims[2]);
            break;
          case 'cylinder':
            shape = await this.cadKernel.createCylinder(dims[0], dims[1]);
            break;
          case 'sphere':
            shape = await this.cadKernel.createSphere(dims[0]);
            break;
          case 'ring':
            shape = await this.cadKernel.createRing(dims[0], dims[1], dims[2]); // outerRadius, innerRadius, height
            break;
          default:
            return null;
        }
      } else if (data.sourceType === 'brep' && data.brepData) {
        // Restore from BREP data (derived shapes like boolean results, fillets, etc.)
        shape = await this.cadKernel.importBrep(data.brepData, data.name);
      } else {
        return null;
      }

      // Apply saved properties
      shape.id = data.id;
      shape.name = data.name;
      shape.color = data.color;
      shape.position = data.position;
      shape.rotation = data.rotation;
      shape.scale = data.scale;
      shape.isDirectEdit = !!data.isDirectEdit || data.sourceType === 'brep';

      if (data.sourceType === 'primitive' && data.primitiveParams) {
        shape.primitiveParams = {
          type: data.primitiveParams.type,
          dimensions: [...data.primitiveParams.dimensions]
        };
      }

      // Restore surface data for extracted faces
      if (data.isSurface) {
        shape.isSurface = true;
        shape.surfaceType = data.surfaceType;
        shape.cylinderData = data.cylinderData;
        shape.coneData = data.coneData;
        shape.sphereData = data.sphereData;
        shape.torusData = data.torusData;
      }

      return shape;
    } catch (error) {
      console.error('Failed to deserialize CAD shape:', data.id, error);
      return null;
    }
  }

  /**
   * Mark that there are unsaved changes
   */
  markUnsaved(): void {
    this.hasUnsavedChanges = true;
  }

  /**
   * Serialize a sketch entity to a plain object for export
   */
  private serializeSketchEntity(entity: SketchEntity): any {
    const base = {
      id: entity.id,
      type: entity.type,
      isConstruction: entity.isConstruction,
      isSelected: false, // Don't persist selection state
      isHovered: false
    };

    switch (entity.type) {
      case 'line':
        return { ...base, startX: entity.startX, startY: entity.startY, endX: entity.endX, endY: entity.endY };
      case 'circle':
        return { ...base, centerX: entity.centerX, centerY: entity.centerY, radius: entity.radius };
      case 'rectangle':
        return { ...base, x: entity.x, y: entity.y, width: entity.width, height: entity.height };
      case 'arc':
        return {
          ...base,
          centerX: entity.centerX,
          centerY: entity.centerY,
          radius: entity.radius,
          startAngle: entity.startAngle,
          endAngle: entity.endAngle
        };
      case 'polygon':
        return {
          ...base,
          centerX: entity.centerX,
          centerY: entity.centerY,
          radius: entity.radius,
          sides: entity.sides
        };
      case 'slot':
        return {
          ...base,
          startX: entity.startX,
          startY: entity.startY,
          endX: entity.endX,
          endY: entity.endY,
          width: entity.width
        };
      default:
        return base;
    }
  }

  /**
   * Deserialize a sketch entity from saved data and add to sketch
   */
  private deserializeSketchEntity(sketchId: string, data: any): void {
    // Set active sketch for entity creation
    this.sketchService.setActiveSketch(sketchId);

    const isConstruction = data.isConstruction || false;

    switch (data.type) {
      case 'line':
        this.sketchService.createLine(
          parseFloat(data.startX),
          parseFloat(data.startY),
          parseFloat(data.endX),
          parseFloat(data.endY),
          isConstruction
        );
        break;
      case 'circle':
        this.sketchService.createCircle(
          parseFloat(data.centerX),
          parseFloat(data.centerY),
          parseFloat(data.radius),
          isConstruction
        );
        break;
      case 'rectangle':
        this.sketchService.createRectangle(
          parseFloat(data.x),
          parseFloat(data.y),
          parseFloat(data.width),
          parseFloat(data.height),
          0, // rotation
          isConstruction
        );
        break;
      case 'arc':
        this.sketchService.createArc(
          parseFloat(data.centerX),
          parseFloat(data.centerY),
          parseFloat(data.radius),
          parseFloat(data.startAngle),
          parseFloat(data.endAngle),
          false, // clockwise
          isConstruction
        );
        break;
      case 'polygon':
        this.sketchService.createPolygon(
          parseFloat(data.centerX),
          parseFloat(data.centerY),
          parseFloat(data.radius),
          parseInt(data.sides, 10),
          0, // rotation
          isConstruction
        );
        break;
      case 'slot':
        this.sketchService.createSlot(
          parseFloat(data.startX),
          parseFloat(data.startY),
          parseFloat(data.endX),
          parseFloat(data.endY),
          parseFloat(data.width),
          isConstruction
        );
        break;
    }

    // Clear active sketch after entity creation
    this.sketchService.setActiveSketch(null);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LOCAL SAVE/LOAD (JSON FILE)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Export CAD parts to a JSON file with actual STEP geometry (local download)
   */
  async exportCADPartsToFile(): Promise<void> {
    const hasData = this.cadShapes.length > 0 || this.sketchService.getAllSketches().length > 0;
    if (!hasData) {
      return;
    }

    // Commit any pending transforms before export
    await this.commitPendingTransforms();

    try {
      this.isProcessing = true;
      this.processingMessage = 'Exporting CAD data...';
      this.cdr.detectChanges();

      // Export each shape to STEP and convert to base64
      const partsWithGeometry: any[] = [];

      for (let i = 0; i < this.cadShapes.length; i++) {
        const shape = this.cadShapes[i];
        this.processingMessage = `Exporting body ${i + 1}/${this.cadShapes.length}...`;
        this.cdr.detectChanges();

        try {
          // Get transform from scene mesh
          const group = this.sceneMeshes.get(shape.id);
          let position: [number, number, number] = [0, 0, 0];
          let rotation: [number, number, number, number] = [0, 0, 0, 1];
          let scale: [number, number, number] = [1, 1, 1];

          if (group) {
            position = [group.position.x, group.position.y, group.position.z];
            rotation = [group.quaternion.x, group.quaternion.y, group.quaternion.z, group.quaternion.w];
            scale = [group.scale.x, group.scale.y, group.scale.z];
          }

          // Export STEP geometry
          let stepBase64: string | null = null;
          try {
            const stepBlob = await this.cadKernel.exportToSTEP(shape);
            stepBase64 = await this.blobToBase64(stepBlob);
          } catch (stepError) {}

          partsWithGeometry.push({
            id: shape.id,
            name: shape.name,
            color: shape.color,
            isSurface: (shape as any).isSurface || false, // Preserve surface flag for extracted faces
            position,
            rotation,
            scale,
            stepGeometry: stepBase64 // Actual STEP geometry as base64 (may be null)
          });
        } catch (e) {}
      }

      // Export sketches
      const sketchesData = this.sketchService.getAllSketches().map((sketch) => ({
        id: sketch.id,
        name: sketch.name,
        plane: sketch.plane,
        planeOffset: sketch.planeOffset,
        planeOrigin: sketch.planeOrigin,
        planeNormal: sketch.planeNormal,
        planeXAxis: sketch.planeXAxis,
        planeYAxis: sketch.planeYAxis,
        referenceFaceId: sketch.referenceFaceId,
        referenceBodyId: sketch.referenceBodyId,
        entities: sketch.entities.map((entity) => this.serializeSketchEntity(entity)),
        isVisible: sketch.isVisible
      }));

      // Create export data with metadata
      // Version 2.1 = includes STEP geometry + sketches
      const exportData = {
        version: '2.1',
        author: 'InfinitForm CAD Editor',
        exportedAt: new Date().toISOString(),
        projectName: this.session?.project?.name || 'unknown',
        partsCount: partsWithGeometry.length,
        sketchesCount: sketchesData.length,
        parts: partsWithGeometry,
        sketches: sketchesData
      };

      // Convert to JSON string
      const jsonString = JSON.stringify(exportData);

      // Create blob and download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `cad-project-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.processingMessage = '';

      const exportMessages: string[] = [];
      if (partsWithGeometry.length > 0) {
        exportMessages.push(`${partsWithGeometry.length} bod${partsWithGeometry.length !== 1 ? 'ies' : 'y'}`);
      }
      if (sketchesData.length > 0) {
        exportMessages.push(`${sketchesData.length} sketch${sketchesData.length !== 1 ? 'es' : ''}`);
      }
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Export CAD shapes to a single STEP file (direct CAD file download)
   * If shapes are selected, exports only selected shapes. Otherwise exports all shapes.
   */
  async exportToSTEPFile(): Promise<void> {
    if (this.cadShapes.length === 0) {
      return;
    }

    // Commit any pending transforms before export
    await this.commitPendingTransforms();

    try {
      this.isProcessing = true;
      this.processingMessage = 'Exporting to STEP...';
      this.cdr.detectChanges();

      // Determine which shapes to export (selected or all)
      const shapesToExport = this.selectedCADShapeIds.size > 0
        ? this.cadShapes.filter(s => this.selectedCADShapeIds.has(s.id))
        : this.cadShapes;

      if (shapesToExport.length === 0) {
        this.toastService.error('No shapes to export');
        return;
      }

      this.processingMessage = `Exporting ${shapesToExport.length} shape${shapesToExport.length !== 1 ? 's' : ''} to STEP...`;
      this.cdr.detectChanges();

      // Export all shapes to a single STEP file
      const projectName = this.session?.project?.name || 'cad-export';
      const filename = `${projectName}-${new Date().toISOString().slice(0, 10)}.step`;
      const stepBlob = await this.cadKernel.exportMultiToSTEP(shapesToExport, filename);
      this.downloadBlob(stepBlob, filename);

      this.toastService.success(
        `Exported ${shapesToExport.length} shape${shapesToExport.length !== 1 ? 's' : ''} to STEP`
      );
    } catch (error: any) {
      console.error('STEP export failed:', error);
      this.toastService.error('STEP export failed: ' + (error.message || 'Unknown error'));
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.processingProgress = 0;
      this.cdr.detectChanges();
    }
  }

  /**
   * Download a blob as a file
   */
  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Convert Blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Remove the data URL prefix (e.g., "data:application/step;base64,")
        const base64Data = base64.split(',')[1] || base64;
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Convert base64 string to Blob
   */
  private base64ToBlob(base64: string, mimeType: string = 'application/step'): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  /**
   * Trigger file input for importing CAD parts
   */
  triggerCADPartsImport(): void {
    if (!this.cadPartsFileInput) {
      this.cadPartsFileInput = document.createElement('input');
      this.cadPartsFileInput.type = 'file';
      this.cadPartsFileInput.accept = '.json';
      this.cadPartsFileInput.style.display = 'none';
      this.cadPartsFileInput.addEventListener('change', (e) => this.handleCADPartsFileSelect(e));
      document.body.appendChild(this.cadPartsFileInput);
    }
    this.cadPartsFileInput.click();
  }

  /**
   * Handle CAD parts file selection
   */
  private async handleCADPartsFileSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    try {
      this.isProcessing = true;
      this.processingMessage = 'Loading CAD data...';
      this.cdr.detectChanges();

      // Read file
      const text = await file.text();
      const importData = JSON.parse(text);

      // Validate format - parts array is required for backward compatibility
      if (!importData.parts || !Array.isArray(importData.parts)) {
        throw new Error('Invalid file format: missing parts array');
      }

      await this.initializeKernel();

      let loadedCount = 0;
      let failedCount = 0;
      const version = importData.version || '1.0';
      const importedShapes: CADShape[] = [];

      for (let i = 0; i < importData.parts.length; i++) {
        const partData = importData.parts[i];
        this.processingMessage = `Loading part ${i + 1}/${importData.parts.length}...`;
        this.cdr.detectChanges();

        try {
          let shape: CADShape | null = null;

          // Check if part has STEP geometry (version 2.0+)
          // Use stepGeometry presence as the primary check (more reliable than version string)
          if (partData.stepGeometry) {
            // Import from STEP geometry

            const stepContent = atob(partData.stepGeometry); // Decode base64 to string
            const stepShapes = await this.cadKernel.importFromSTEP(stepContent, partData.name || 'imported');

            if (stepShapes.length > 0) {
              shape = stepShapes[0];
              // Preserve original ID, name, and color from export
              shape.id = partData.id || shape.id;
              shape.name = partData.name || shape.name;
              shape.color = partData.color || shape.color;
              // Restore isSurface flag for extracted surfaces
              if (partData.isSurface) {
                (shape as any).isSurface = true;
              }
            } else {
            }
          } else {
            // Fallback to parametric recreation (version 1.0 or missing STEP data)

            shape = await this.deserializeCADShape(partData);
            // Restore isSurface flag for extracted surfaces
            if (shape && partData.isSurface) {
              (shape as any).isSurface = true;
            }
          }

          if (shape) {
            const hasStepGeometry = !!partData.stepGeometry;
            // Check if we need to apply transforms
            const hasTransform =
              partData.position &&
              partData.rotation &&
              partData.scale &&
              // Check if transform is non-identity
              (partData.position[0] !== 0 ||
                partData.position[1] !== 0 ||
                partData.position[2] !== 0 ||
                partData.rotation[0] !== 0 ||
                partData.rotation[1] !== 0 ||
                partData.rotation[2] !== 0 ||
                partData.rotation[3] !== 1 ||
                partData.scale[0] !== 1 ||
                partData.scale[1] !== 1 ||
                partData.scale[2] !== 1);

            if (hasTransform && shape.workerShapeId && !hasStepGeometry) {
              // Sync transform to kernel by baking it into the geometry
              // Build 4x4 transform matrix from position, quaternion, and scale
              const pos = new Vector3(partData.position[0], partData.position[1], partData.position[2]);
              const quat = new Quaternion(
                partData.rotation[0],
                partData.rotation[1],
                partData.rotation[2],
                partData.rotation[3]
              );
              const scl = new Vector3(partData.scale[0], partData.scale[1], partData.scale[2]);

              const matrix = new Matrix4();
              matrix.compose(pos, quat, scl);
              const matrixElements = matrix.elements.slice(); // Get flat array of 16 elements

              try {
                // Transform the kernel shape (bakes transform into geometry)
                const transformedShape = await this.cadKernel.transformShape(shape, matrixElements);
                if (transformedShape) {
                  // Preserve original properties (keep the new shape's ID to match session.shapes registry)
                  transformedShape.name = partData.name || transformedShape.name;
                  transformedShape.color = partData.color || transformedShape.color;
                  if ((shape as any).isSurface || partData.isSurface) {
                    (transformedShape as any).isSurface = true;
                  }
                  shape = transformedShape;
                }
              } catch (transformError) {
                console.warn('Failed to sync transform to kernel, using visual-only transform:', transformError);
                // Fall back to visual-only transform (will be applied below)
              }
            }

            this.cadShapes.push(shape);
            this.addShapeToScene(shape);

            importedShapes.push(shape);

            // Apply transform to Three.js group (for visual positioning)
            // Note: If kernel transform succeeded, the group stays at identity since transform is baked in
            // If kernel transform failed, this applies visual-only transform
            const group = this.sceneMeshes.get(shape.id);
            if (group && partData.position && partData.rotation && partData.scale) {
              // Only apply to group if kernel transform was NOT applied (fallback case)
              // When kernel transform succeeds, the geometry is already in world space
              if (!hasTransform || !shape.workerShapeId) {
                group.position.set(partData.position[0], partData.position[1], partData.position[2]);
                group.quaternion.set(
                  partData.rotation[0],
                  partData.rotation[1],
                  partData.rotation[2],
                  partData.rotation[3]
                );
                group.scale.set(partData.scale[0], partData.scale[1], partData.scale[2]);
              }
              // If kernel transform succeeded, group stays at identity (default)
            }

            loadedCount++;
          } else {
            failedCount++;
          }
        } catch (e) {
          failedCount++;
        }
      }

      await this.seedHistoryFromShapes(importedShapes);

      // Import sketches (version 2.1+)
      let sketchesLoaded = 0;
      if (importData.sketches && Array.isArray(importData.sketches)) {
        this.processingMessage = 'Loading sketches...';
        this.cdr.detectChanges();

        for (const sketchData of importData.sketches) {
          try {
            // Create sketch with saved properties
            // createSketch signature: (plane, name?, options?)
            const sketch = this.sketchService.createSketch(sketchData.plane, sketchData.name, {
              planeOffset: sketchData.planeOffset,
              planeOrigin: sketchData.planeOrigin,
              planeNormal: sketchData.planeNormal,
              planeXAxis: sketchData.planeXAxis,
              planeYAxis: sketchData.planeYAxis,
              referenceFaceId: sketchData.referenceFaceId,
              referenceBodyId: sketchData.referenceBodyId
            });

            // Add entities to sketch
            if (sketchData.entities && Array.isArray(sketchData.entities)) {
              for (const entityData of sketchData.entities) {
                this.deserializeSketchEntity(sketch.id, entityData);
              }
            }

            sketchesLoaded++;
          } catch (sketchError) {}
        }
      }

      this.hasUnsavedChanges = true;
      this.processingMessage = '';

      const messages: string[] = [];
      if (loadedCount > 0) {
        messages.push(`${loadedCount} part${loadedCount !== 1 ? 's' : ''}`);
      }
      if (sketchesLoaded > 0) {
        messages.push(`${sketchesLoaded} sketch${sketchesLoaded !== 1 ? 'es' : ''}`);
      }

      if (messages.length > 0) {
      } else {
      }
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      input.value = ''; // Reset input
      this.cdr.detectChanges();
    }
  }

  /**
   * Clear all CAD parts (with confirmation)
   */
  clearAllCADParts(): void {
    if (this.cadShapes.length === 0) return;

    this.showConfirm(
      'Clear All Parts',
      `Are you sure you want to clear all ${this.cadShapes.length} CAD parts? This cannot be undone.`,
      () => {
        // Detach transform controls first
        this.detachTransformControls();

        // Remove all from scene
        this.sceneMeshes.forEach((_, id) => {
          this.removeShapeFromScene(id);
        });

        // Clear kernel
        this.cadKernel.clearSession();

        // Clear local state
        this.cadShapes = [];
        this.selectedCADShapeIds.clear();
        this.booleanSelectionOrder = [];
        this.hasUnsavedChanges = true;

        this.cdr.detectChanges();
      }
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SKETCH PLANE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Set the active sketch plane
   */
  setSketchPlane(plane: 'XY' | 'XZ' | 'YZ' | null): void {
    this.sketchPlane = plane;
    this.updateSketchPlaneVisual();

    // Sync with snap engine
    if (plane) {
      this.snapEngine.setSketchPlane(plane);

      // Rotate camera to face the selected plane
      if (this.engine?.viewHelper) {
        switch (plane) {
          case 'XY':
            this.engine.viewHelper.rotateCameraToSide('posZ'); // Top view
            break;
          case 'XZ':
            this.engine.viewHelper.rotateCameraToSide('negY'); // Front view
            break;
          case 'YZ':
            this.engine.viewHelper.rotateCameraToSide('posX'); // Right view
            break;
        }
      }
    }

    this.cdr.detectChanges();
  }

  /**
   * Update sketch plane offset
   */
  setSketchPlaneOffset(offset: number): void {
    this.sketchPlaneOffset = offset;
    this.updateSketchPlaneVisual();
  }

  /**
   * Start face picking mode - allows selecting a face to use as sketch plane
   */
  startFacePickingMode(): void {
    if (!this.engine?.canvas) {
      return;
    }

    // Hide gizmo while picking
    this.detachTransformControls();

    // Clean up any existing handlers first (without changing state)
    this.cleanupFacePickingHandlers();

    this.isFacePickingMode = true;
    this.selectedFaceInfo = null;

    // Setup event handlers using arrow functions to preserve 'this'
    this.boundOnFacePickClick = (e: MouseEvent) => this.onFacePickClick(e);
    this.boundOnFacePickMove = (e: MouseEvent) => this.onFacePickMove(e);

    // Add listeners to both canvas and document to debug
    this.engine.canvas.addEventListener('click', this.boundOnFacePickClick, { capture: true });

    // Try document-level listener as fallback
    document.addEventListener('mousemove', this.boundOnFacePickMove, { capture: true });

    // Change cursor
    this.engine.canvas.style.cursor = 'crosshair';

    this.cdr.detectChanges();
  }

  /**
   * Clean up face picking event handlers only
   */
  private cleanupFacePickingHandlers(): void {
    if (this.boundOnFacePickClick && this.engine?.canvas) {
      this.engine.canvas.removeEventListener('click', this.boundOnFacePickClick, { capture: true });
      this.boundOnFacePickClick = null;
    }
    if (this.boundOnFacePickMove) {
      // Remove from document since we added there
      document.removeEventListener('mousemove', this.boundOnFacePickMove, { capture: true });
      this.boundOnFacePickMove = null;
    }
  }

  /**
   * Stop face picking mode
   */
  stopFacePickingMode(clearSelection = true): void {
    // Clean up event handlers
    this.cleanupFacePickingHandlers();

    // Remove highlight mesh and wire
    if (this.faceHighlightMesh && this.engine) {
      this.engine.scene.remove(this.faceHighlightMesh);
      this.faceHighlightMesh.geometry?.dispose();
      this.faceHighlightMesh.material?.dispose();
      this.faceHighlightMesh = null;
    }
    if (this.faceHighlightWire && this.engine) {
      this.engine.scene.remove(this.faceHighlightWire);
      this.faceHighlightWire.geometry?.dispose();
      this.faceHighlightWire.material?.dispose();
      this.faceHighlightWire = null;
    }

    // Restore cursor
    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = 'default';
    }

    this.isFacePickingMode = false;
    if (clearSelection) {
      this.selectedFaceInfo = null;
    }

    this.cdr.detectChanges();
  }

  /**
   * Handle mouse move during face picking - highlight faces under cursor
   */
  private onFacePickMove(event: MouseEvent): void {
    if (!this.engine || !this.isFacePickingMode) return;

    // Check if mouse is over the canvas
    const rect = this.engine.canvas.getBoundingClientRect();
    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    ) {
      return;
    }

    const faceInfo = this.getFaceUnderCursor(event);

    // Remove old highlight (mesh and wire)
    if (this.faceHighlightMesh) {
      this.engine.scene.remove(this.faceHighlightMesh);
      this.faceHighlightMesh.geometry?.dispose();
      if (this.faceHighlightMesh.material) {
        (this.faceHighlightMesh.material as any).dispose();
      }
      this.faceHighlightMesh = null;
    }
    if (this.faceHighlightWire) {
      this.engine.scene.remove(this.faceHighlightWire);
      this.faceHighlightWire.geometry?.dispose();
      if (this.faceHighlightWire.material) {
        (this.faceHighlightWire.material as any).dispose();
      }
      this.faceHighlightWire = null;
    }

    if (faceInfo) {
      // In extract face mode, only highlight CAD faces (not assembly parts)
      if (this.isExtractFacePickingMode && faceInfo.sourceType !== 'cad') {
        this.engine.canvas.style.cursor = 'not-allowed';
        return;
      }
      // Create highlight for the face (with wire)
      this.createFaceHighlight(faceInfo);
      this.engine.canvas.style.cursor = 'pointer';
    } else {
      this.engine.canvas.style.cursor = 'crosshair';
    }
  }

  /**
   * Handle click during face picking - select the face
   */
  private onFacePickClick(event: MouseEvent): void {
    if (!this.engine || !this.isFacePickingMode) return;

    // Stop propagation to prevent click from falling through to onCanvasClick
    event.stopPropagation();

    const faceInfo = this.getFaceUnderCursor(event);

    if (faceInfo) {
      // For Boss/Pocket or Extract Face operations, validate that the face is on the selected CAD shape
      if (this.isExtrudeOnFaceMode || this.isExtractFacePickingMode) {
        const selectedShapeId = Array.from(this.selectedCADShapeIds)[0];

        // Must be a CAD face (not assembly)
        if (faceInfo.sourceType !== 'cad') {
          this.onError.emit('Please select a face on the CAD shape, not assembly parts');
          return;
        }

        // Must be on the selected shape
        if (faceInfo.sourceId !== selectedShapeId) {
          this.onError.emit('Please select a face on the currently selected shape');
          return;
        }

        // Must have a valid face index for CAD operations
        if (faceInfo.faceIndex === undefined) {
          this.onError.emit('Could not determine face index. Please try selecting another face.');
          return;
        }
      }

      // Handle extract face mode specially - no camera rotation, multi-selection support
      if (this.isExtractFacePickingMode) {
        this.toggleExtractFaceSelection(faceInfo.faceIndex!, event.ctrlKey || event.metaKey);
        // Keep picking mode active for multi-selection - user can press Enter or click Extract button
        this.cdr.detectChanges();
        return;
      }

      this.selectedFaceInfo = faceInfo;
      this.setPlaneFromFace(faceInfo);
      this.stopFacePickingMode(false);

      // If in extrude on face mode, show tool picker for drawing
      if (this.isExtrudeOnFaceMode) {
        this.sketchPlane = 'custom';
        // Show the floating tool picker near the click position
        this.showBossPocketToolPicker = true;
        this.bossPocketToolPickerPosition = {
          x: event.clientX,
          y: event.clientY
        };
      }
    } else {
      // Clicked on empty space - cancel face picking and boss/pocket mode
      if (this.isExtrudeOnFaceMode) {
        // Use exitOperationMode to properly clean up activeOperation and restore shapes
        this.exitOperationMode(true);
      } else if (this.isExtractFacePickingMode) {
        // For extract face, empty click does NOT cancel - user must press Esc or use buttons
        // This allows selecting faces without accidental cancellation
        return;
      } else {
        this.stopFacePickingMode(true);
      }
    }
  }

  /**
   * Get face info from raycast at cursor position
   */
  private getFaceUnderCursor(event: MouseEvent): {
    position: [number, number, number];
    normal: [number, number, number];
    sourceType: 'cad' | 'assembly';
    sourceId?: string;
    geometry?: any;
    faceIndex?: number;
    extractedFace?: {
      vertices: number[];
      indices: number[];
      center: [number, number, number];
    };
  } | null {
    if (!this.engine) return null;

    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    // Collect all meshes to test (CAD parts + assembly parts)
    const meshesToTest: any[] = [];

    // Helper function to check if object is visible (including parent chain)
    const isObjectVisible = (obj: any): boolean => {
      if (!obj) return true;
      if (!obj.visible) return false;
      if (obj.parent) return isObjectVisible(obj.parent);
      return true;
    };

    // For Boss/Pocket mode, only raycast against the selected CAD shape
    // This prevents assembly meshes in front from blocking face selection
    const selectedShapeIdForBossPocket = this.isExtrudeOnFaceMode
      ? Array.from(this.selectedCADShapeIds)[0]
      : null;

    // Add CAD parts (only if visible)
    this.sceneMeshes.forEach((group, shapeId) => {
      // In Boss/Pocket mode, only include the selected shape
      if (selectedShapeIdForBossPocket && shapeId !== selectedShapeIdForBossPocket) return;

      // Check if the group itself is visible
      if (!isObjectVisible(group)) return;

      group.traverse((child: any) => {
        if (child.isMesh && isObjectVisible(child)) {
          child.userData['sourceType'] = 'cad';
          child.userData['sourceId'] = shapeId;
          child.userData['parentGroup'] = group;
          meshesToTest.push(child);
        }
      });
    });

    // Add assembly parts from the engine scene (only if visible)
    // Skip this when in Boss/Pocket mode or Extract Face mode - we only want CAD faces
    if (this.engine.scene && !this.isExtrudeOnFaceMode && !this.isExtractFacePickingMode) {
      this.engine.scene.traverse((child: any) => {
        if (
          child.isMesh &&
          !child.userData['isCADShape'] &&
          child.name !== 'SketchPlaneHelper' &&
          child.name !== 'SketchGridHelper' &&
          child.name !== 'DrawingPreview' &&
          child.name !== 'FaceHighlight' &&
          child.name !== 'GridHelperGroundPlane' &&
          child.name !== 'ThickenDirectionArrow' &&
          !child.name?.startsWith('GridHelper')
        ) {
          // Check if it's part of the assembly (not a helper) and fully visible
          if (child.geometry && isObjectVisible(child)) {
            child.userData['sourceType'] = 'assembly';
            meshesToTest.push(child);
          }
        }
      });
    }

    const intersects = raycaster.intersectObjects(meshesToTest, false);

    if (intersects.length > 0) {
      const hit = intersects[0];
      const face = hit.face;

      if (face && hit.object) {
        // Get world normal
        const normalMatrix = new Matrix3().getNormalMatrix(hit.object.matrixWorld);
        const worldNormal = face.normal.clone().applyMatrix3(normalMatrix).normalize();

        const sourceType = hit.object.userData['sourceType'] || 'assembly';
        const sourceId = hit.object.userData['sourceId'];

        let extractedFace = undefined;

        // For CAD parts, try to find the actual CAD face from extracted faces
        let actualFaceIndex = hit.faceIndex ?? undefined;

        if (sourceType === 'cad' && sourceId) {
          const group = this.sceneMeshes.get(sourceId);
          const extractedFaces = group?.userData['extractedFaces'];
          const triangleToFaceMap = group?.userData['triangleToFaceMap'] as number[] | undefined;

          if (extractedFaces && hit.faceIndex != null) {
            // Find which extracted face contains this triangle
            // Uses O(1) lookup via triangleToFaceMap when available
            extractedFace = this.findExtractedFaceContainingTriangle(
              extractedFaces,
              hit.faceIndex,
              (hit.object as any).geometry,
              triangleToFaceMap
            );

            // Use the actual OpenCascade face index if found
            if (extractedFace) {
              actualFaceIndex = extractedFace.faceIndex;
            }
          }
        }

        return {
          position: [hit.point.x, hit.point.y, hit.point.z],
          normal: [worldNormal.x, worldNormal.y, worldNormal.z],
          sourceType,
          sourceId,
          geometry: (hit.object as any).geometry,
          faceIndex: actualFaceIndex,
          extractedFace
        };
      }
    }

    return null;
  }

  /**
   * Find which extracted OpenCascade face contains the given triangle
   * Finds which face contains the hit triangle by matching vertices
   * @param extractedFaces Array of extracted face data from OCCT
   * @param triangleIndex Index of the hit triangle in the mesh
   * @param meshGeometry The mesh's buffer geometry
   * @param _triangleToFaceMap Unused - kept for API compatibility
   */
  private findExtractedFaceContainingTriangle(
    extractedFaces: any[],
    triangleIndex: number,
    meshGeometry: BufferGeometry,
    _triangleToFaceMap?: number[]
  ): { vertices: number[]; indices: number[]; center: [number, number, number]; faceIndex: number } | undefined {
    // NOTE: triangleToFaceMap is disabled - mesh triangles are in different order than the map
    // Using vertex matching instead which actually checks the geometry
    const posAttr = meshGeometry.getAttribute('position');
    const indexAttr = meshGeometry.getIndex();

    if (posAttr && indexAttr && extractedFaces.length > 0) {
      const triBaseIdx = triangleIndex * 3;
      if (triBaseIdx + 2 < indexAttr.count) {
        const v0Idx = indexAttr.getX(triBaseIdx);
        const v1Idx = indexAttr.getX(triBaseIdx + 1);
        const v2Idx = indexAttr.getX(triBaseIdx + 2);

        // Get triangle vertices
        const v0 = new Vector3(posAttr.getX(v0Idx), posAttr.getY(v0Idx), posAttr.getZ(v0Idx));
        const v1 = new Vector3(posAttr.getX(v1Idx), posAttr.getY(v1Idx), posAttr.getZ(v1Idx));
        const v2 = new Vector3(posAttr.getX(v2Idx), posAttr.getY(v2Idx), posAttr.getZ(v2Idx));

        // Find ALL faces that contain all three triangle vertices
        // Then pick the smallest one (most specific match)
        // This handles shared edge vertices (e.g., cylinder caps share edges with curved surface)
        const tolerance = 0.001;
        const matchingFaces: { face: any; vertexCount: number }[] = [];

        for (const face of extractedFaces) {
          const faceVerts = face.vertices;
          if (!faceVerts || faceVerts.length === 0) continue;

          let matchCount = 0;

          for (const triVert of [v0, v1, v2]) {
            for (let i = 0; i < faceVerts.length; i += 3) {
              const dx = Math.abs(faceVerts[i] - triVert.x);
              const dy = Math.abs(faceVerts[i + 1] - triVert.y);
              const dz = Math.abs(faceVerts[i + 2] - triVert.z);
              if (dx < tolerance && dy < tolerance && dz < tolerance) {
                matchCount++;
                break;
              }
            }
          }

          if (matchCount === 3) {
            matchingFaces.push({ face, vertexCount: faceVerts.length / 3 });
          }
        }

        // Return the face with fewest vertices (most specific match)
        if (matchingFaces.length > 0) {
          matchingFaces.sort((a, b) => a.vertexCount - b.vertexCount);
          return matchingFaces[0].face;
        }

        // FALLBACK 2: Center distance
        const triCenter = new Vector3().addVectors(v0, v1).add(v2).divideScalar(3);
        let bestFace = undefined;
        let bestDist = Infinity;

        for (const face of extractedFaces) {
          if (!face.center) continue;
          const faceCenter = new Vector3(face.center[0], face.center[1], face.center[2]);
          const dist = triCenter.distanceTo(faceCenter);
          if (dist < bestDist) {
            bestDist = dist;
            bestFace = face;
          }
        }

        if (bestFace) {
          return bestFace;
        }
      }
    }

    return undefined;
  }

  /**
   * Create a highlight mesh for the face under cursor using real face geometry
   * Also creates wire/edge highlight for the face boundary
   */
  private createFaceHighlight(faceInfo: any): void {
    if (!this.engine) return;

    let geometry: BufferGeometry;
    let wireGeometry: BufferGeometry | null = null;

    // If we have extracted face data, use it to create accurate highlight
    if (faceInfo.extractedFace && faceInfo.extractedFace.vertices.length > 0) {
      const face = faceInfo.extractedFace;

      geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(face.vertices), 3));
      geometry.setIndex(face.indices);
      geometry.computeVertexNormals();
      
      // Extract boundary edges for wireframe
      wireGeometry = this.extractFaceBoundaryEdges(face.vertices, face.indices);
    } else {
      // Fallback: Create a small plane at the face position
      const highlightSize = 20;
      geometry = new PlaneGeometry(highlightSize, highlightSize);
      
      // Create edge geometry for the plane
      wireGeometry = new EdgesGeometry(geometry);
    }

    const material = new MeshBasicMaterial({
      color: 0x00ffff,  // Bright cyan for high visibility
      transparent: true,
      opacity: 0.55,
      side: DoubleSide,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2
    });

    this.faceHighlightMesh = new Mesh(geometry, material);
    this.faceHighlightMesh.name = 'FaceHighlight';
    this.faceHighlightMesh.renderOrder = 998;
    
    // Create wire highlight for boundary edges
    if (wireGeometry) {
      const wireMaterial = new LineBasicMaterial({
        color: 0x00ffff,  // Bright cyan for high visibility
        linewidth: 2,
        depthTest: true,
        depthWrite: false,
        transparent: false
      });
      this.faceHighlightWire = new LineSegments(wireGeometry, wireMaterial);
      this.faceHighlightWire.name = 'FaceHighlightWire';
      this.faceHighlightWire.renderOrder = 1001;
    }

    // Position the highlight
    if (faceInfo.extractedFace) {
      // For extracted face, the vertices are in original shape coordinates
      // We need to apply the same transformation as the mesh in the scene
      if (faceInfo.sourceId && faceInfo.sourceType === 'cad') {
        const group = this.sceneMeshes.get(faceInfo.sourceId);
        if (group) {
          // The vertices need to be offset by -geometryCenter (same as the mesh)
          const geometryCenter = group.userData['geometryCenter'] as Vector3;
          if (geometryCenter) {
            // Offset the highlight geometry vertices
            const posAttr = this.faceHighlightMesh.geometry.getAttribute('position');
            for (let i = 0; i < posAttr.count; i++) {
              posAttr.setXYZ(
                i,
                posAttr.getX(i) - geometryCenter.x,
                posAttr.getY(i) - geometryCenter.y,
                posAttr.getZ(i) - geometryCenter.z
              );
            }
            posAttr.needsUpdate = true;
            
            // Also offset wire geometry
            if (this.faceHighlightWire) {
              const wirePos = this.faceHighlightWire.geometry.getAttribute('position');
              for (let i = 0; i < wirePos.count; i++) {
                wirePos.setXYZ(
                  i,
                  wirePos.getX(i) - geometryCenter.x,
                  wirePos.getY(i) - geometryCenter.y,
                  wirePos.getZ(i) - geometryCenter.z
                );
              }
              wirePos.needsUpdate = true;
            }
          }

          // Apply the group's world transformation (no normal offset - using polygonOffset instead)
          this.faceHighlightMesh.position.copy(group.position);
          this.faceHighlightMesh.rotation.copy(group.rotation);
          this.faceHighlightMesh.scale.copy(group.scale);
          
          if (this.faceHighlightWire) {
            this.faceHighlightWire.position.copy(group.position);
            this.faceHighlightWire.rotation.copy(group.rotation);
            this.faceHighlightWire.scale.copy(group.scale);
          }
        }
      }
      // No position offset needed - using polygonOffset on material for z-fighting prevention
    } else {
      // Fallback positioning for plane
      this.faceHighlightMesh.position.set(faceInfo.position[0], faceInfo.position[1], faceInfo.position[2]);

      // Orient the plane to match the face normal
      const normal = new Vector3(faceInfo.normal[0], faceInfo.normal[1], faceInfo.normal[2]);
      const quaternion = new Quaternion();
      quaternion.setFromUnitVectors(new Vector3(0, 0, 1), normal);
      this.faceHighlightMesh.quaternion.copy(quaternion);
      
      if (this.faceHighlightWire) {
        this.faceHighlightWire.position.copy(this.faceHighlightMesh.position);
        this.faceHighlightWire.quaternion.copy(this.faceHighlightMesh.quaternion);
      }
    }

    this.engine.scene.add(this.faceHighlightMesh);
    if (this.faceHighlightWire) {
      this.engine.scene.add(this.faceHighlightWire);
    }
  }
  
  /**
   * Extract boundary edges from face triangles
   * Returns only the edges that appear once (boundary) not shared between triangles
   */
  private extractFaceBoundaryEdges(vertices: number[], indices: number[]): BufferGeometry {
    // Build edge map to find boundary edges (edges that appear only once)
    const edgeMap = new Map<string, { v1: number; v2: number; count: number }>();
    
    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i];
      const b = indices[i + 1];
      const c = indices[i + 2];
      
      // Add three edges of this triangle
      const edges = [
        [Math.min(a, b), Math.max(a, b)],
        [Math.min(b, c), Math.max(b, c)],
        [Math.min(c, a), Math.max(c, a)]
      ];
      
      for (const [v1, v2] of edges) {
        const key = `${v1}-${v2}`;
        const existing = edgeMap.get(key);
        if (existing) {
          existing.count++;
        } else {
          edgeMap.set(key, { v1, v2, count: 1 });
        }
      }
    }
    
    // Collect boundary edges (count === 1) and build line segments
    const linePositions: number[] = [];
    
    for (const edge of edgeMap.values()) {
      if (edge.count === 1) {
        // This is a boundary edge
        const v1x = vertices[edge.v1 * 3];
        const v1y = vertices[edge.v1 * 3 + 1];
        const v1z = vertices[edge.v1 * 3 + 2];
        const v2x = vertices[edge.v2 * 3];
        const v2y = vertices[edge.v2 * 3 + 1];
        const v2z = vertices[edge.v2 * 3 + 2];
        
        linePositions.push(v1x, v1y, v1z, v2x, v2y, v2z);
      }
    }
    
    const wireGeometry = new BufferGeometry();
    wireGeometry.setAttribute('position', new BufferAttribute(new Float32Array(linePositions), 3));
    
    return wireGeometry;
  }

  /**
   * Get a world-space plane origin/normal from extracted face data
   */
  private getExtractFacePlaneInfo(
    shapeId: string,
    faceIndex: number
  ): { position: [number, number, number]; normal: [number, number, number] } | null {
    const shape = this.cadShapes.find((s) => s.id === shapeId);
    const face = shape?.extractedFaces?.find((f) => f.faceIndex === faceIndex);
    const group = this.sceneMeshes.get(shapeId);

    if (!face || !group) return null;

    group.updateMatrixWorld(true);
    const geometryCenter = group.userData['geometryCenter'] as Vector3 | undefined;

    const centerWorld = new Vector3(face.center[0], face.center[1], face.center[2]);
    if (geometryCenter) {
      centerWorld.sub(geometryCenter);
    }
    centerWorld.applyMatrix4(group.matrixWorld);

    const normalWorld = new Vector3(face.normal[0], face.normal[1], face.normal[2]);
    const normalMatrix = new Matrix3().getNormalMatrix(group.matrixWorld);
    normalWorld.applyMatrix3(normalMatrix).normalize();

    return {
      position: [centerWorld.x, centerWorld.y, centerWorld.z],
      normal: [normalWorld.x, normalWorld.y, normalWorld.z]
    };
  }

  /**
   * Set sketch plane from selected face
   */
  private setPlaneFromFace(faceInfo: { position: [number, number, number]; normal: [number, number, number] }): void {
    // Store custom plane parameters
    this.customPlaneOrigin = [...faceInfo.position] as [number, number, number];
    this.customPlaneNormal = [...faceInfo.normal] as [number, number, number];

    // Calculate local X and Y axes for the plane
    const normal = new Vector3(faceInfo.normal[0], faceInfo.normal[1], faceInfo.normal[2]);

    // Choose a reference vector that's not parallel to the normal
    let refVec = new Vector3(1, 0, 0);
    if (Math.abs(normal.dot(refVec)) > 0.9) {
      refVec = new Vector3(0, 1, 0);
    }

    // Calculate local X axis (perpendicular to normal)
    const xAxis = new Vector3().crossVectors(refVec, normal).normalize();
    // Calculate local Y axis
    const yAxis = new Vector3().crossVectors(normal, xAxis).normalize();

    this.customPlaneXAxis = [xAxis.x, xAxis.y, xAxis.z];
    this.customPlaneYAxis = [yAxis.x, yAxis.y, yAxis.z];

    // Set sketch plane to custom mode
    this.sketchPlane = 'custom';
    this.sketchPlaneOffset = 0; // Offset is baked into origin

    // Sync with snap engine
    this.snapEngine.setSketchPlane(
      'custom',
      this.customPlaneNormal,
      this.customPlaneOrigin,
      this.customPlaneXAxis,
      this.customPlaneYAxis
    );

    // Rotate camera to face the selected face and center on the face position
    if (this.engine?.viewHelper) {
      const facePosition = new Vector3(faceInfo.position[0], faceInfo.position[1], faceInfo.position[2]);
      this.engine.viewHelper.rotateCameraToNormal(normal, facePosition);
    }

    this.updateSketchPlaneVisual();
    this.cdr.detectChanges();
  }

  /**
   * Clear custom plane and reset to no selection
   */
  clearCustomPlane(): void {
    this.selectedFaceInfo = null;
    this.sketchPlane = null;
    this.cleanupSketchPlane();
    this.cdr.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EDGE SELECTION MODE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start edge selection mode for fillet/chamfer
   */
  async startEdgeSelectMode(): Promise<void> {
    if (!this.engine?.canvas) return;
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape to pick edges');
      return;
    }

    // Commit pending transforms before edge selection (OCCT geometry must match visual)
    await this.commitPendingTransforms();

    // Re-extract faces and edges for the selected shape after transform commit
    await this.refreshShapeFacesAndEdges();

    // Hide gizmo while picking
    this.detachTransformControls();

    this.isEdgeSelectMode = true;
    this.selectedEdges = [];
    this.clearEdgeHighlights();

    // Setup event handlers
    this.boundOnEdgePickMove = this.onEdgePickMove.bind(this);
    this.boundOnEdgePickClick = this.onEdgePickClick.bind(this);

    this.engine.canvas.addEventListener('mousemove', this.boundOnEdgePickMove, { capture: true });
    this.engine.canvas.addEventListener('click', this.boundOnEdgePickClick, { capture: true });
    this.engine.canvas.style.cursor = 'crosshair';

    this.cdr.detectChanges();
  }

  /**
   * Stop edge selection mode (keep selected edges)
   */
  stopEdgeSelectMode(): void {
    this.isEdgeSelectMode = false;
    this.cleanupEdgePickingHandlers();
    this.clearEdgeHighlights();

    // Keep the selected edges but redraw them as persistent highlights
    this.updateSelectedEdgeHighlights();

    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = '';
    }

    // Re-attach gizmo if a single shape is selected
    if (this.selectedCADShapeIds.size === 1) {
      this.attachTransformControls();
    }

    this.cdr.detectChanges();
  }

  /**
   * Cancel edge selection mode (clear selected edges)
   */
  cancelEdgeSelectMode(): void {
    this.isEdgeSelectMode = false;
    this.selectedEdges = [];
    this.cleanupEdgePickingHandlers();
    this.clearEdgeHighlights();

    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = '';
    }

    // Re-attach gizmo if a single shape is selected
    if (this.selectedCADShapeIds.size === 1) {
      this.attachTransformControls();
    }

    this.cdr.detectChanges();
  }

  /**
   * Cleanup edge picking event handlers
   */
  private cleanupEdgePickingHandlers(): void {
    if (this.engine?.canvas) {
      if (this.boundOnEdgePickMove) {
        this.engine.canvas.removeEventListener('mousemove', this.boundOnEdgePickMove, { capture: true });
        this.boundOnEdgePickMove = null;
      }
      if (this.boundOnEdgePickClick) {
        this.engine.canvas.removeEventListener('click', this.boundOnEdgePickClick, { capture: true });
        this.boundOnEdgePickClick = null;
      }
    }
  }

  /**
   * Handle mouse move during edge picking - highlight edges under cursor
   */
  private onEdgePickMove(event: MouseEvent): void {
    if (!this.engine || !this.isEdgeSelectMode) return;
    event.stopImmediatePropagation();

    // Clear previous highlights and redraw selected ones
    this.clearEdgeHighlights();
    this.updateSelectedEdgeHighlights();

    // Find edge under cursor
    const edgeInfo = this.getEdgeUnderCursor(event);

    if (edgeInfo) {
      // Check if this edge is already selected
      const isSelected = this.selectedEdges.some(
        (e) => e.shapeId === edgeInfo.shapeId && e.edgeIndex === edgeInfo.edgeIndex
      );

      // Only show hover highlight if not already selected (selected ones are already green)
      if (!isSelected) {
        this.highlightEdge(edgeInfo.shapeId, edgeInfo.edgeIndex, 0x00ffff); // Cyan for hover
      }
      this.engine.canvas.style.cursor = 'pointer';
    } else {
      this.engine.canvas.style.cursor = 'crosshair';
    }
  }

  /**
   * Handle click during edge picking - toggle edge selection
   */
  private onEdgePickClick(event: MouseEvent): void {
    if (!this.engine || !this.isEdgeSelectMode) return;
    event.stopImmediatePropagation();
    event.preventDefault();

    const edgeInfo = this.getEdgeUnderCursor(event);

    if (edgeInfo) {
      // Check if already selected
      const existingIndex = this.selectedEdges.findIndex(
        (e) => e.shapeId === edgeInfo.shapeId && e.edgeIndex === edgeInfo.edgeIndex
      );

      if (existingIndex >= 0) {
        // Deselect
        this.selectedEdges.splice(existingIndex, 1);
      } else {
        // Select
        this.selectedEdges.push({ shapeId: edgeInfo.shapeId, edgeIndex: edgeInfo.edgeIndex });
      }

      // Redraw selection highlights
      this.updateSelectedEdgeHighlights();
      this.cdr.detectChanges();
    } else {
    }
  }

  /**
   * Get edge under cursor using screen-space distance checking
   */
  private getEdgeUnderCursor(event: MouseEvent): { shapeId: string; edgeIndex: number } | null {
    if (!this.engine) return null;

    const rect = this.engine.canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const threshold = 12; // Pixel threshold for edge selection (increased for easier picking)
    let closestEdge: { shapeId: string; edgeIndex: number } | null = null;
    let closestDistance = threshold;

    // Check all edges from all shapes
    this.sceneMeshes.forEach((group, shapeId) => {
      const extractedEdges = group.userData['extractedEdges'];
      if (!extractedEdges) {
        return;
      }

      // Get group transformation - ensure matrixWorld is updated
      group.updateMatrixWorld(true);
      const groupMatrix = group.matrixWorld;
      const geometryCenter = group.userData['geometryCenter'] as Vector3;

      for (const edge of extractedEdges) {
        // Points are in flat array format: [x1,y1,z1, x2,y2,z2, ...]
        // Need at least 6 values for 2 points
        if (edge.points.length < 6) continue;

        const numPoints = edge.points.length / 3;

        // Check each segment of the edge (consecutive point pairs)
        for (let i = 0; i < numPoints - 1; i++) {
          const idx1 = i * 3;
          const idx2 = (i + 1) * 3;
          const p1World = new Vector3(edge.points[idx1], edge.points[idx1 + 1], edge.points[idx1 + 2]);
          const p2World = new Vector3(edge.points[idx2], edge.points[idx2 + 1], edge.points[idx2 + 2]);

          // Apply geometry center offset (edges are stored relative to shape center)
          if (geometryCenter) {
            p1World.sub(geometryCenter);
            p2World.sub(geometryCenter);
          }

          // Apply group transformation
          p1World.applyMatrix4(groupMatrix);
          p2World.applyMatrix4(groupMatrix);

          // Project to screen space
          const p1Screen = p1World.project(this.engine!.camera);
          const p2Screen = p2World.project(this.engine!.camera);

          const x1 = (p1Screen.x * 0.5 + 0.5) * rect.width;
          const y1 = (1 - (p1Screen.y * 0.5 + 0.5)) * rect.height;
          const x2 = (p2Screen.x * 0.5 + 0.5) * rect.width;
          const y2 = (1 - (p2Screen.y * 0.5 + 0.5)) * rect.height;

          // Calculate distance from mouse to line segment
          const dist = this.pointToSegmentDistance(mouseX, mouseY, x1, y1, x2, y2);

          if (dist < closestDistance) {
            closestDistance = dist;
            closestEdge = { shapeId, edgeIndex: edge.edgeIndex };
          }
        }
      }
    });

    if (closestEdge !== null) {
    }
    return closestEdge;
  }

  /**
   * Calculate distance from a point to a line segment in 2D
   */
  private pointToSegmentDistance(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) {
      // Segment is a point
      return Math.sqrt((px - x1) * (px - x1) + (py - y1) * (py - y1));
    }

    // Calculate projection parameter
    let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
    t = Math.max(0, Math.min(1, t)); // Clamp to segment

    // Find closest point on segment
    const closestX = x1 + t * dx;
    const closestY = y1 + t * dy;

    return Math.sqrt((px - closestX) * (px - closestX) + (py - closestY) * (py - closestY));
  }

  /**
   * Highlight a specific edge
   */
  private highlightEdge(shapeId: string, edgeIndex: number, color: number): void {
    const group = this.sceneMeshes.get(shapeId);
    if (!group || !this.engine) return;

    const extractedEdges = group.userData['extractedEdges'];
    if (!extractedEdges) return;

    const edge = extractedEdges.find((e: any) => e.edgeIndex === edgeIndex);
    // Points are in flat array format: [x1,y1,z1, x2,y2,z2, ...], need at least 6 values for 2 points
    if (!edge || edge.points.length < 6) return;

    // Create highlight line using Line2
    // edge.points is already in flat array format that setPositions expects
    const geometry = new LineGeometry();
    geometry.setPositions(edge.points);

    const canvasWidth = this.engine.canvas?.clientWidth || 1920;
    const canvasHeight = this.engine.canvas?.clientHeight || 1080;

    const material = new LineMaterial({
      color: color,
      linewidth: 4, // Width in pixels for highlight
      resolution: new Vector2(canvasWidth, canvasHeight),
      depthTest: true,
      depthWrite: false,
      transparent: true,
      polygonOffset: true,
      polygonOffsetFactor: -4, // Pull towards camera
      polygonOffsetUnits: -4
    });

    const line = new Line2(geometry, material);
    line.name = 'EdgeHighlight';
    line.renderOrder = 100; // Render after solid geometry but on top
    line.computeLineDistances();

    // Position line at same offset as mesh within group (edges are in local coordinates)
    const geometryCenter = group.userData['geometryCenter'] as Vector3;
    if (geometryCenter) {
      line.position.set(-geometryCenter.x, -geometryCenter.y, -geometryCenter.z);
    }

    // Add as child of group so it inherits all transforms
    group.add(line);
    this.edgeHighlightLines.push(line);
  }

  /**
   * Update highlights for all selected edges
   */
  private updateSelectedEdgeHighlights(): void {
    this.clearEdgeHighlights();

    for (const edge of this.selectedEdges) {
      this.highlightEdge(edge.shapeId, edge.edgeIndex, 0x00ff00); // Green for selected
    }
  }

  /**
   * Clear edge highlight visuals
   */
  private clearEdgeHighlights(): void {
    for (const line of this.edgeHighlightLines) {
      // Remove from parent (could be group or scene)
      line.parent?.remove(line);
      line.geometry?.dispose();
      line.material?.dispose();
    }
    this.edgeHighlightLines = [];
  }

  /**
   * Check if in edge selection mode with selections
   */
  hasSelectedEdges(): boolean {
    return this.selectedEdges.length > 0;
  }

  /**
   * Get count of selected edges
   */
  getSelectedEdgeCount(): number {
    return this.selectedEdges.length;
  }

  /**
   * Clear selected edges
   */
  clearSelectedEdges(): void {
    this.selectedEdges = [];
    this.clearEdgeHighlights();
    this.cdr.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ASSISTANT FACE PICKING
  // ═══════════════════════════════════════════════════════════════════════════

  toggleAssistantFacePickMode(): void {
    if (this.isAssistantFacePickMode) {
      this.stopAssistantFacePickMode();
    } else {
      this.startAssistantFacePickMode();
    }
  }

  /** Click faces of any body to pick or unpick them for the assistant. */
  startAssistantFacePickMode(): void {
    if (!this.engine?.canvas || this.isAssistantFacePickMode) return;
    if (this.activeOperation) {
      this.exitOperationMode(true);
    }
    this.detachTransformControls();
    this.isAssistantFacePickMode = true;
    this.boundOnAssistantPickClick = this.onAssistantPickClick.bind(this);
    this.boundOnAssistantPickMove = this.onAssistantPickMove.bind(this);
    this.engine.canvas.addEventListener('click', this.boundOnAssistantPickClick, { capture: true });
    this.engine.canvas.addEventListener('mousemove', this.boundOnAssistantPickMove, { capture: true });
    this.engine.canvas.style.cursor = 'crosshair';
    this.cdr.detectChanges();
  }

  /** Leave picking mode; the picked faces stay until sent or cleared. */
  stopAssistantFacePickMode(): void {
    if (this.engine?.canvas) {
      if (this.boundOnAssistantPickClick) {
        this.engine.canvas.removeEventListener('click', this.boundOnAssistantPickClick, { capture: true });
      }
      if (this.boundOnAssistantPickMove) {
        this.engine.canvas.removeEventListener('mousemove', this.boundOnAssistantPickMove, { capture: true });
      }
      if (this.isAssistantFacePickMode) {
        this.engine.canvas.style.cursor = '';
      }
    }
    this.boundOnAssistantPickClick = null;
    this.boundOnAssistantPickMove = null;
    if (!this.isAssistantFacePickMode) return;
    this.isAssistantFacePickMode = false;
    if (this.selectedCADShapeIds.size === 1) {
      this.attachTransformControls();
    }
    this.cdr.detectChanges();
  }

  private onAssistantPickMove(event: MouseEvent): void {
    if (!this.engine || !this.isAssistantFacePickMode) return;
    this.engine.canvas.style.cursor = this.getAssistantPickUnderCursor(event) ? 'pointer' : 'crosshair';
  }

  private onAssistantPickClick(event: MouseEvent): void {
    if (!this.isAssistantFacePickMode) return;
    event.stopImmediatePropagation();
    event.preventDefault();
    const pick = this.getAssistantPickUnderCursor(event);
    if (pick) {
      this.assistantFaces.toggle(pick);
    }
  }

  /**
   * The CAD face under the cursor, with the hit point and normal in the body's own model
   * coordinates: the mesh geometry holds the tessellated model as imported, so worldToLocal
   * on the hit mesh undoes the display offset and any view transform.
   */
  private getAssistantPickUnderCursor(event: MouseEvent): AssistantFacePick | null {
    if (!this.engine) return null;
    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    const meshes: Mesh[] = [];
    const shapeOf = new Map<Object3D, string>();
    this.sceneMeshes.forEach((group, shapeId) => {
      if (!group.visible) return;
      group.traverse((child: any) => {
        if (child.isMesh && child.visible) {
          meshes.push(child);
          shapeOf.set(child, shapeId);
        }
      });
    });

    const hit = raycaster.intersectObjects(meshes, false)[0];
    const shapeId = hit ? shapeOf.get(hit.object) : undefined;
    if (!hit?.face || hit.faceIndex == null || !shapeId) return null;

    const group = this.sceneMeshes.get(shapeId);
    const extractedFace = group?.userData['extractedFaces']
      ? this.findExtractedFaceContainingTriangle(
          group.userData['extractedFaces'],
          hit.faceIndex,
          (hit.object as Mesh).geometry,
          group.userData['triangleToFaceMap']
        )
      : undefined;
    if (!extractedFace) return null;

    const point = hit.object.worldToLocal(hit.point.clone());
    const normal = hit.face.normal.clone().normalize();
    const shape = this.cadShapes.find((s) => s.id === shapeId);
    const round = (v: number) => Math.round(v * 1e4) / 1e4;
    return {
      shapeId,
      shapeName: shape?.name ?? shapeId,
      faceIndex: extractedFace.faceIndex,
      point: [round(point.x), round(point.y), round(point.z)],
      normal: [round(normal.x), round(normal.y), round(normal.z)]
    };
  }

  /** Highlight the picked faces (face triangles from the kernel, placed like the body). */
  private updateAssistantPickHighlights(picks: AssistantFacePick[]): void {
    for (const mesh of this.assistantPickHighlightMeshes) {
      mesh.parent?.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as MeshBasicMaterial).dispose();
    }
    this.assistantPickHighlightMeshes = [];
    if (!this.engine) return;

    for (const pick of picks) {
      const group = this.sceneMeshes.get(pick.shapeId);
      const faces = group?.userData['extractedFaces'] as
        | { faceIndex: number; vertices: number[]; indices: number[] }[]
        | undefined;
      const face = faces?.find((f) => f.faceIndex === pick.faceIndex);
      if (!group || !face) continue;

      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new Float32BufferAttribute(face.vertices, 3));
      if (face.indices) {
        geometry.setIndex(face.indices);
      }
      const mesh = new Mesh(
        geometry,
        new MeshBasicMaterial({
          color: 0x2f80ed,
          transparent: true,
          opacity: 0.5,
          side: DoubleSide,
          depthTest: true,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -1,
          polygonOffsetUnits: -1
        })
      );
      mesh.name = 'AssistantFacePick';
      mesh.renderOrder = 999;
      // Same placement as the body's mesh: the group's transform, then minus the geometry centre.
      const center = (group.userData['geometryCenter'] as Vector3 | undefined) ?? new Vector3();
      group.updateMatrixWorld(true);
      mesh.matrixAutoUpdate = false;
      mesh.matrix.copy(group.matrixWorld).multiply(new Matrix4().makeTranslation(-center.x, -center.y, -center.z));
      this.engine.scene.add(mesh);
      this.assistantPickHighlightMeshes.push(mesh);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SHELL FACE SELECTION MODE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start shell face selection mode
   */
  async startShellFaceSelectMode(): Promise<void> {
    if (!this.engine?.canvas) return;
    if (this.selectedCADShapeIds.size !== 1) {
      this.onError.emit('Select exactly 1 shape to pick a face to remove');
      return;
    }

    // Commit pending transforms before face selection (OCCT geometry must match visual)
    await this.commitPendingTransforms();

    // Re-extract faces for the selected shape after transform commit
    await this.refreshShapeFacesAndEdges();

    // Hide gizmo while picking
    this.detachTransformControls();

    this.isShellFaceSelectMode = true;
    this.selectedShellFaceIndex = null;
    this.clearShellFaceHighlight();

    // Setup event handlers
    this.boundOnShellFaceMove = this.onShellFaceMove.bind(this);
    this.boundOnShellFaceClick = this.onShellFaceClick.bind(this);

    this.engine.canvas.addEventListener('mousemove', this.boundOnShellFaceMove, { capture: true });
    this.engine.canvas.addEventListener('click', this.boundOnShellFaceClick, { capture: true });
    this.engine.canvas.style.cursor = 'crosshair';

    this.cdr.detectChanges();
  }

  /**
   * Stop shell face selection mode
   */
  stopShellFaceSelectMode(keepSelection = false): void {
    this.isShellFaceSelectMode = false;
    this.cleanupShellFaceHandlers();

    if (!keepSelection) {
      this.selectedShellFaceIndex = null;
    }
    this.clearShellFaceHighlight();

    if (this.engine?.canvas) {
      this.engine.canvas.style.cursor = '';
    }

    // Re-attach gizmo if a single shape is selected
    if (this.selectedCADShapeIds.size === 1) {
      this.attachTransformControls();
    }

    this.cdr.detectChanges();
  }

  /**
   * Cleanup shell face picking handlers
   */
  private cleanupShellFaceHandlers(): void {
    if (this.engine?.canvas) {
      if (this.boundOnShellFaceMove) {
        this.engine.canvas.removeEventListener('mousemove', this.boundOnShellFaceMove, { capture: true });
        this.boundOnShellFaceMove = null;
      }
      if (this.boundOnShellFaceClick) {
        this.engine.canvas.removeEventListener('click', this.boundOnShellFaceClick, { capture: true });
        this.boundOnShellFaceClick = null;
      }
    }
  }

  /**
   * Handle mouse move during shell face picking
   */
  private onShellFaceMove(event: MouseEvent): void {
    if (!this.engine || !this.isShellFaceSelectMode) return;

    const selectedShapeId = Array.from(this.selectedCADShapeIds)[0];
    const faceInfo = this.getCADFaceUnderCursor(event, selectedShapeId);

    this.clearShellFaceHighlight();

    if (faceInfo) {
      this.createShellFaceHighlight(faceInfo);
      this.engine.canvas.style.cursor = 'pointer';
    } else {
      this.engine.canvas.style.cursor = 'crosshair';
    }
  }

  /**
   * Handle click during shell face picking
   */
  private onShellFaceClick(event: MouseEvent): void {
    if (!this.engine || !this.isShellFaceSelectMode) return;
    event.stopImmediatePropagation();

    const selectedShapeId = Array.from(this.selectedCADShapeIds)[0];
    const faceInfo = this.getCADFaceUnderCursor(event, selectedShapeId);

    if (faceInfo) {
      this.selectedShellFaceIndex = faceInfo.faceIndex;

      this.stopShellFaceSelectMode(true);
    }
  }

  /**
   * Get CAD face under cursor for a specific shape
   * Returns the actual OpenCascade face index with face geometry data
   */
  private getCADFaceUnderCursor(
    event: MouseEvent,
    shapeId: string
  ): {
    faceIndex: number;
    position: [number, number, number];
    normal: [number, number, number];
    extractedFace?: {
      vertices: number[];
      indices: number[];
      center: [number, number, number];
    };
  } | null {
    if (!this.engine) return null;

    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    // Get the shape's mesh
    const group = this.sceneMeshes.get(shapeId);
    if (!group) return null;

    const meshesToTest: any[] = [];
    group.traverse((child: any) => {
      if (child.isMesh && child.visible) {
        meshesToTest.push(child);
      }
    });

    const intersects = raycaster.intersectObjects(meshesToTest, false);

    if (intersects.length > 0) {
      const hit = intersects[0];
      const face = hit.face;

      if (face && hit.object) {
        const normalMatrix = new Matrix3().getNormalMatrix(hit.object.matrixWorld);
        const worldNormal = face.normal.clone().applyMatrix3(normalMatrix).normalize();

        // Find the actual OpenCascade face from extracted faces
        const extractedFaces = group.userData['extractedFaces'];
        const triangleToFaceMap = group.userData['triangleToFaceMap'] as number[] | undefined;
        let extractedFace = undefined;
        let actualFaceIndex = 0;

        if (extractedFaces && hit.faceIndex != null) {
          // Find which extracted face this triangle belongs to
          // Uses O(1) lookup via triangleToFaceMap when available
          extractedFace = this.findExtractedFaceContainingTriangle(
            extractedFaces,
            hit.faceIndex,
            (hit.object as any).geometry,
            triangleToFaceMap
          );

          if (extractedFace) {
            actualFaceIndex = extractedFace.faceIndex;
          }
        }

        return {
          faceIndex: actualFaceIndex,
          position: [hit.point.x, hit.point.y, hit.point.z],
          normal: [worldNormal.x, worldNormal.y, worldNormal.z],
          extractedFace
        };
      }
    }

    return null;
  }

  /**
   * Create highlight for shell face with wire/edge highlight
   */
  private createShellFaceHighlight(faceInfo: {
    position: [number, number, number];
    normal: [number, number, number];
    extractedFace?: { vertices: number[]; indices: number[]; center: [number, number, number] };
  }): void {
    if (!this.engine) return;

    this.clearShellFaceHighlight();

    let geometry: BufferGeometry;
    let wireGeometry: BufferGeometry | null = null;

    // Use real face geometry if available
    if (faceInfo.extractedFace && faceInfo.extractedFace.vertices.length > 0) {
      const face = faceInfo.extractedFace;
      geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(face.vertices), 3));
      geometry.setIndex(face.indices);
      geometry.computeVertexNormals();
      
      // Extract boundary edges for wireframe
      wireGeometry = this.extractFaceBoundaryEdges(face.vertices, face.indices);
    } else {
      // Fallback to plane
      const highlightSize = 15;
      geometry = new PlaneGeometry(highlightSize, highlightSize);
      wireGeometry = new EdgesGeometry(geometry);
    }

    const material = new MeshBasicMaterial({
      color: 0xff4444,  // Bright red for high visibility
      transparent: true,
      opacity: 0.55,
      side: DoubleSide,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2
    });

    this.shellFaceHighlightMesh = new Mesh(geometry, material);
    this.shellFaceHighlightMesh.name = 'ShellFaceHighlight';
    this.shellFaceHighlightMesh.renderOrder = 998;
    
    // Create wire highlight for boundary edges
    if (wireGeometry) {
      const wireMaterial = new LineBasicMaterial({
        color: 0xff0000,  // Bright red for high visibility
        linewidth: 2,
        depthTest: false,
        depthWrite: false,
        transparent: true
      });
      this.shellFaceHighlightWire = new LineSegments(wireGeometry, wireMaterial);
      this.shellFaceHighlightWire.name = 'ShellFaceHighlightWire';
      this.shellFaceHighlightWire.renderOrder = 1001;
    }

    if (faceInfo.extractedFace) {
      // For extracted face, apply transform from the parent shape group
      const selectedShapeId = Array.from(this.selectedCADShapeIds)[0];
      const group = this.sceneMeshes.get(selectedShapeId);
      if (group) {
        // The vertices need to be offset by -geometryCenter (same as the mesh)
        const geometryCenter = group.userData['geometryCenter'] as Vector3;
        if (geometryCenter) {
          // Offset the highlight geometry vertices
          const posAttr = this.shellFaceHighlightMesh.geometry.getAttribute('position');
          for (let i = 0; i < posAttr.count; i++) {
            posAttr.setXYZ(
              i,
              posAttr.getX(i) - geometryCenter.x,
              posAttr.getY(i) - geometryCenter.y,
              posAttr.getZ(i) - geometryCenter.z
            );
          }
          posAttr.needsUpdate = true;
          
          // Also offset wire geometry
          if (this.shellFaceHighlightWire) {
            const wirePos = this.shellFaceHighlightWire.geometry.getAttribute('position');
            for (let i = 0; i < wirePos.count; i++) {
              wirePos.setXYZ(
                i,
                wirePos.getX(i) - geometryCenter.x,
                wirePos.getY(i) - geometryCenter.y,
                wirePos.getZ(i) - geometryCenter.z
              );
            }
            wirePos.needsUpdate = true;
          }
        }

        // Apply the group's world transformation (no normal offset - using polygonOffset instead)
        this.shellFaceHighlightMesh.position.copy(group.position);
        this.shellFaceHighlightMesh.rotation.copy(group.rotation);
        this.shellFaceHighlightMesh.scale.copy(group.scale);
        
        if (this.shellFaceHighlightWire) {
          this.shellFaceHighlightWire.position.copy(group.position);
          this.shellFaceHighlightWire.rotation.copy(group.rotation);
          this.shellFaceHighlightWire.scale.copy(group.scale);
        }
      }
      // No position offset needed - using polygonOffset on material for z-fighting prevention
    } else {
      // Fallback positioning for plane
      this.shellFaceHighlightMesh.position.set(faceInfo.position[0], faceInfo.position[1], faceInfo.position[2]);
      const normal = new Vector3(faceInfo.normal[0], faceInfo.normal[1], faceInfo.normal[2]);
      const quaternion = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), normal);
      this.shellFaceHighlightMesh.quaternion.copy(quaternion);
      
      if (this.shellFaceHighlightWire) {
        this.shellFaceHighlightWire.position.copy(this.shellFaceHighlightMesh.position);
        this.shellFaceHighlightWire.quaternion.copy(this.shellFaceHighlightMesh.quaternion);
      }
    }

    this.engine.scene.add(this.shellFaceHighlightMesh);
    if (this.shellFaceHighlightWire) {
      this.engine.scene.add(this.shellFaceHighlightWire);
    }
  }

  /**
   * Clear shell face highlight (mesh and wire)
   */
  private clearShellFaceHighlight(): void {
    if (this.shellFaceHighlightMesh && this.engine) {
      this.engine.scene.remove(this.shellFaceHighlightMesh);
      this.shellFaceHighlightMesh.geometry?.dispose();
      this.shellFaceHighlightMesh.material?.dispose();
      this.shellFaceHighlightMesh = null;
    }
    if (this.shellFaceHighlightWire && this.engine) {
      this.engine.scene.remove(this.shellFaceHighlightWire);
      this.shellFaceHighlightWire.geometry?.dispose();
      this.shellFaceHighlightWire.material?.dispose();
      this.shellFaceHighlightWire = null;
    }
  }

  /**
   * Check if a shell face is selected
   */
  hasSelectedShellFace(): boolean {
    return this.selectedShellFaceIndex !== null;
  }

  /**
   * Clear selected shell face
   */
  clearSelectedShellFace(): void {
    this.selectedShellFaceIndex = null;
    this.clearShellFaceHighlight();
    this.cdr.detectChanges();
  }

  /**
   * Update the sketch plane visual helper in the scene
   */
  private async updateSketchPlaneVisual(): Promise<void> {
    if (!this.engine) return;

    // Import Three.js helpers dynamically
    const { PlaneGeometry, MeshBasicMaterial, Mesh, GridHelper, DoubleSide, Group } = await import('three');

    // Remove existing helpers
    if (this.sketchPlaneHelper) {
      this.engine.scene.remove(this.sketchPlaneHelper);
      // Dispose of all children
      this.sketchPlaneHelper.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m: any) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      this.sketchPlaneHelper = null;
    }
    if (this.sketchGridHelper) {
      this.engine.scene.remove(this.sketchGridHelper);
      this.sketchGridHelper.dispose?.();
      this.sketchGridHelper = null;
    }

    if (!this.sketchPlane) return;

    // Create a plane that matches the grid size
    const planeSize = 2000; // Match grid size
    const gridDivisions = 200; // 200 divisions = 10 unit cells

    // Create plane with basic material (simpler, more reliable)
    const planeGeometry = new PlaneGeometry(planeSize, planeSize);
    const planeMaterial = new MeshBasicMaterial({
      color: 0x6366f1,
      transparent: true,
      opacity: 0.06,
      side: DoubleSide,
      depthWrite: false
    });

    const planeMesh = new Mesh(planeGeometry, planeMaterial);
    planeMesh.name = 'SketchPlaneHelper';
    planeMesh.renderOrder = -2;

    // Create main grid (fine) - same size as plane
    const mainGrid = new GridHelper(planeSize, gridDivisions, 0x6366f1, 0x4f46e5);
    mainGrid.name = 'SketchGridMain';
    (mainGrid.material as any).transparent = true;
    (mainGrid.material as any).opacity = 0.25;
    (mainGrid.material as any).depthWrite = false;

    // Create secondary grid (coarse) for large scale reference - same size as plane
    const coarseGrid = new GridHelper(planeSize, 40, 0x3730a3, 0x312e81);
    coarseGrid.name = 'SketchGridCoarse';
    (coarseGrid.material as any).transparent = true;
    (coarseGrid.material as any).opacity = 0.12;
    (coarseGrid.material as any).depthWrite = false;

    // Create a group to hold all plane visuals
    const planeGroup = new Group();
    planeGroup.name = 'SketchPlaneGroup';
    planeGroup.add(planeMesh);

    // Create a separate group for grids (we need to apply different rotations)
    const gridGroup = new Group();
    gridGroup.name = 'SketchGridGroup';
    gridGroup.add(mainGrid);
    gridGroup.add(coarseGrid);

    this.sketchPlaneHelper = planeGroup;
    this.sketchGridHelper = gridGroup;

    // Position based on plane - rotations must match preview and kernel transforms
    switch (this.sketchPlane) {
      case 'XY':
        this.sketchPlaneHelper.position.set(0, 0, this.sketchPlaneOffset);
        this.sketchGridHelper.rotation.x = Math.PI / 2;
        this.sketchGridHelper.position.set(0, 0, this.sketchPlaneOffset);
        break;
      case 'XZ':
        // XZ plane: the plane lies flat at Y = offset
        // The PlaneHelper needs to be rotated so it faces up (normal is Y axis)
        this.sketchPlaneHelper.rotation.set(-Math.PI / 2, 0, 0);
        this.sketchPlaneHelper.position.set(0, this.sketchPlaneOffset, 0);
        // GridHelper default already lies in XZ plane, just position it
        this.sketchGridHelper.rotation.set(0, 0, 0);
        this.sketchGridHelper.position.set(0, this.sketchPlaneOffset, 0);
        break;
      case 'YZ':
        // Use rotation matrix matching kernel transform: Local X → World Y, Local Y → World Z
        const yzPlaneMatrix = new Matrix4();
        yzPlaneMatrix.makeBasis(
          new Vector3(0, 1, 0),
          new Vector3(0, 0, 1),
          new Vector3(1, 0, 0)
        );
        this.sketchPlaneHelper.rotation.setFromRotationMatrix(yzPlaneMatrix);
        this.sketchPlaneHelper.position.set(this.sketchPlaneOffset, 0, 0);
        // GridHelper default lies in XZ plane, need to rotate to match YZ orientation
        const yzGridQuat = new Quaternion();
        yzGridQuat.setFromRotationMatrix(yzPlaneMatrix);
        const gridPreRotateYZ = new Quaternion();
        gridPreRotateYZ.setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI / 2);
        const gridQuatYZ = new Quaternion();
        gridQuatYZ.multiplyQuaternions(yzGridQuat, gridPreRotateYZ);
        this.sketchGridHelper.quaternion.copy(gridQuatYZ);
        this.sketchGridHelper.position.set(this.sketchPlaneOffset, 0, 0);
        break;
      case 'custom':
        // Custom plane from face selection
        const origin = this.customPlaneOrigin;
        const normal = new Vector3(this.customPlaneNormal[0], this.customPlaneNormal[1], this.customPlaneNormal[2]);

        // Position at the plane origin
        this.sketchPlaneHelper.position.set(origin[0], origin[1], origin[2]);
        this.sketchGridHelper.position.set(origin[0], origin[1], origin[2]);

        // PlaneGeometry default normal is +Z, rotate to match custom normal
        const planeQuat = new Quaternion();
        planeQuat.setFromUnitVectors(new Vector3(0, 0, 1), normal);
        this.sketchPlaneHelper.quaternion.copy(planeQuat);

        // GridHelper default lies in XZ plane (normal +Y)
        // First rotate it -90° around X to make it lie in XY plane (like PlaneGeometry)
        // Then apply the same rotation as the plane
        const gridPreRotate = new Quaternion();
        gridPreRotate.setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI / 2);
        const gridQuat = new Quaternion();
        gridQuat.multiplyQuaternions(planeQuat, gridPreRotate);
        this.sketchGridHelper.quaternion.copy(gridQuat);
        break;
    }

    // Set visibility based on active sketch visibility
    const isSketchVisible = this.activeSketch?.isVisible !== false;
    this.sketchPlaneHelper.visible = isSketchVisible;
    this.sketchGridHelper.visible = isSketchVisible;

    this.engine.scene.add(this.sketchPlaneHelper);
    this.engine.scene.add(this.sketchGridHelper);
  }

  // Secondary grid helpers stored in the grid group
  private sketchGridMain: any = null;
  private sketchGridCoarse: any = null;

  /**
   * Clean up sketch plane visuals
   */
  private cleanupSketchPlane(): void {
    if (this.sketchPlaneHelper && this.engine) {
      this.engine.scene.remove(this.sketchPlaneHelper);
      // Dispose of all children in the group
      this.sketchPlaneHelper.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m: any) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      this.sketchPlaneHelper = null;
    }
    if (this.sketchGridHelper && this.engine) {
      this.engine.scene.remove(this.sketchGridHelper);
      // Dispose of all children in the grid group
      this.sketchGridHelper.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m: any) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      this.sketchGridHelper = null;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 2D DRAWING TOOLS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Set drawing mode
   */
  setDrawingMode(mode: 'none' | 'rectangle' | 'circle' | 'line' | 'arc' | 'polygon' | 'slot'): void {
    // If clicking the same mode, toggle it off
    if (this.drawingMode === mode && mode !== 'none') {
      this.drawingMode = 'none';
    } else {
      this.drawingMode = mode;
    }

    // Reset drawing state
    this.isDrawing = false;
    this.drawingStartPoint = null;
    this.drawingCurrentPoint = null;
    this.linePoints = [];
    this.clearDrawingPreview();
    this.hideSnapIndicator();
    this.snapEngine.clearLastPoint();

    // Reset arc drawing state
    this.arcDrawingState = { startPoint: null, midPoint: null, endPoint: null, step: 'start' };

    // Reset slot drawing state
    this.slotDrawingState = { startPoint: null, endPoint: null, step: 'start' };

    // Clear visual helpers
    this.clearPointMarkers();
    if (mode === 'none') {
      this.hideCursorIndicator();
    }

    // Setup or cleanup drawing handlers
    if (this.drawingMode !== 'none') {
      this.setupDrawingHandlers();
      // Disable transform controls while drawing
      this.detachTransformControls();

      // Sync sketch plane with snap engine
      if (this.sketchPlane) {
        if (this.sketchPlane === 'custom') {
          this.snapEngine.setSketchPlane(
            'custom',
            this.customPlaneNormal,
            this.customPlaneOrigin,
            this.customPlaneXAxis,
            this.customPlaneYAxis
          );
        } else {
          this.snapEngine.setSketchPlane(this.sketchPlane);
        }
      }
    } else {
      this.cleanupDrawingHandlers();
    }

    this.cdr.detectChanges();
  }

  /**
   * Check if drawing mode is active
   */
  isDrawingModeActive(): boolean {
    return this.drawingMode !== 'none' && this.sketchPlane !== null;
  }

  /**
   * Setup drawing event handlers
   */
  private boundOnDrawKeyDown: ((e: KeyboardEvent) => void) | null = null;

  private setupDrawingHandlers(): void {
    if (!this.engine?.canvas) return;

    this.cleanupDrawingHandlers();

    this.boundOnDrawMouseDown = this.onDrawMouseDown.bind(this);
    this.boundOnDrawMouseMove = this.onDrawMouseMove.bind(this);
    this.boundOnDrawMouseUp = this.onDrawMouseUp.bind(this);
    this.boundOnDrawKeyDown = this.onDrawKeyDown.bind(this);

    // Use capture phase to intercept events before other handlers
    this.engine.canvas.addEventListener('mousedown', this.boundOnDrawMouseDown, { capture: true });
    this.engine.canvas.addEventListener('mousemove', this.boundOnDrawMouseMove);
    this.engine.canvas.addEventListener('mouseup', this.boundOnDrawMouseUp, { capture: true });

    // Add keydown listener for Tab key during drawing
    window.addEventListener('keydown', this.boundOnDrawKeyDown, { capture: true });
  }

  /**
   * Handle keydown events during drawing (for Tab key)
   */
  private onDrawKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      const canOpenDimensionInput =
        (this.isDrawing && this.drawingStartPoint) || // Rectangle, Circle, Polygon
        (this.drawingMode === 'slot' && this.slotDrawingState.startPoint) || // Slot
        (this.drawingMode === 'arc' && this.arcDrawingState.startPoint); // Arc

      if (canOpenDimensionInput && !this.showDimensionInput) {
        event.preventDefault();
        event.stopPropagation();
        this.openDimensionInput();
      }
    }
  }

  /**
   * Cleanup drawing event handlers
   */
  private cleanupDrawingHandlers(): void {
    if (!this.engine?.canvas) return;

    if (this.boundOnDrawMouseDown) {
      this.engine.canvas.removeEventListener('mousedown', this.boundOnDrawMouseDown, { capture: true });
      this.boundOnDrawMouseDown = null;
    }
    if (this.boundOnDrawMouseMove) {
      this.engine.canvas.removeEventListener('mousemove', this.boundOnDrawMouseMove);
      this.boundOnDrawMouseMove = null;
    }
    if (this.boundOnDrawMouseUp) {
      this.engine.canvas.removeEventListener('mouseup', this.boundOnDrawMouseUp, { capture: true });
      this.boundOnDrawMouseUp = null;
    }
    if (this.boundOnDrawKeyDown) {
      window.removeEventListener('keydown', this.boundOnDrawKeyDown, { capture: true });
      this.boundOnDrawKeyDown = null;
    }
  }

  /**
   * Handle mouse down for drawing
   */
  private onDrawMouseDown(event: MouseEvent): void {
    if (!this.isDrawingModeActive() || event.button !== 0 || this.isProcessing) return;

    let point = this.getPointOnSketchPlane(event);
    if (!point) return;

    // Apply snapping if enabled
    if (this.snapEnabled) {
      const snapResult = this.snapEngine.findSnapPoint(new Vector3(point.x, point.y, point.z));

      if (snapResult && snapResult.distance < this.snapEngine.getConfig().vertexSnapDistance * 2) {
        point = { x: snapResult.point[0], y: snapResult.point[1], z: snapResult.point[2] };

        // Update last point for horizontal/vertical constraints
        this.snapEngine.setLastPoint(new Vector3(point.x, point.y, point.z));
      }
    }

    if (this.drawingMode === 'line') {
      // For line mode, add point on each click
      this.linePoints.push(point);
      this.snapEngine.setLastPoint(new Vector3(point.x, point.y, point.z));
      this.updateDrawingPreview();
    } else if (this.drawingMode === 'arc') {
      // 3-point arc: click start, mid, end
      if (this.arcDrawingState.step === 'start') {
        this.arcDrawingState.startPoint = point;
        this.arcDrawingState.step = 'mid';
        this.addPointMarker(point, 0xa0ff00); // Green marker for start
      } else if (this.arcDrawingState.step === 'mid') {
        this.arcDrawingState.midPoint = point;
        this.arcDrawingState.step = 'end';
        this.addPointMarker(point, 0x00ffff); // Cyan marker for mid
      } else if (this.arcDrawingState.step === 'end') {
        this.arcDrawingState.endPoint = point;
        // Create the arc shape
        this.finishArcDrawing();
      }
      this.updateDrawingPreview();
      this.cdr.detectChanges();
    } else if (this.drawingMode === 'slot') {
      // Slot: click start, then end
      if (this.slotDrawingState.step === 'start') {
        this.slotDrawingState.startPoint = point;
        this.slotDrawingState.step = 'end';
        this.drawingStartPoint = point;
        this.addPointMarker(point, 0xa0ff00); // Green marker for start
      } else if (this.slotDrawingState.step === 'end') {
        this.slotDrawingState.endPoint = point;
        // Create the slot shape
        this.finishSlotDrawing();
      }
      this.updateDrawingPreview();
      this.cdr.detectChanges();
    } else {
      // For rectangle/circle/polygon, start drawing
      this.isDrawing = true;
      this.drawingStartPoint = point;
      this.drawingCurrentPoint = point;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  /**
   * Handle mouse move for drawing
   */
  private onDrawMouseMove(event: MouseEvent): void {
    if (!this.isDrawingModeActive()) return;

    // Don't update position when dimension input is open
    if (this.showDimensionInput) return;

    let point = this.getPointOnSketchPlane(event);
    if (!point) return;

    // Apply snapping if enabled
    if (this.snapEnabled) {
      const snapResult = this.snapEngine.findSnapPoint(new Vector3(point.x, point.y, point.z));

      // Use larger threshold for non-grid snaps
      const threshold =
        snapResult && snapResult.type !== 'grid'
          ? this.snapEngine.getConfig().vertexSnapDistance * 5
          : this.snapEngine.getConfig().vertexSnapDistance * 2;

      if (snapResult && snapResult.distance < threshold) {
        point = { x: snapResult.point[0], y: snapResult.point[1], z: snapResult.point[2] };
        this.currentSnapResult = snapResult;
        this.showSnapIndicator(snapResult);

        // Update snap feedback label
        this.updateSnapFeedback(snapResult, event);
      } else {
        this.currentSnapResult = null;
        this.hideSnapIndicator();
        this.hideSnapFeedback();
      }
    }

    this.drawingCurrentPoint = point;

    // Update cursor indicator position
    this.updateCursorIndicator(point);

    if (this.drawingMode === 'line' && this.linePoints.length > 0) {
      // Show preview line to current point
      this.updateDrawingPreview();
    } else if (this.drawingMode === 'arc' && this.arcDrawingState.startPoint) {
      // Update arc preview as mouse moves
      this.updateDrawingPreview();
      this.updateDrawingDimensions();
      this.drawingDimensionsPosition = { x: event.clientX + 20, y: event.clientY + 20 };
    } else if (this.drawingMode === 'slot' && this.slotDrawingState.startPoint) {
      // Update slot preview as mouse moves
      this.updateDrawingPreview();
      this.updateDrawingDimensions();
      this.drawingDimensionsPosition = { x: event.clientX + 20, y: event.clientY + 20 };
    } else if (this.isDrawing && this.drawingStartPoint) {
      // Update preview for rectangle/circle/polygon
      this.updateDrawingPreview();
      // Update drawing dimensions display with cursor position
      this.updateDrawingDimensions();
      this.drawingDimensionsPosition = {
        x: event.clientX + 20,
        y: event.clientY + 20
      };
    }

    this.cdr.detectChanges();
  }

  /**
   * Handle mouse up for drawing
   */
  private onDrawMouseUp(event: MouseEvent): void {
    if (!this.isDrawingModeActive() || event.button !== 0 || this.isProcessing) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.drawingMode === 'line') {
      // Line mode uses double-click to finish (handled separately)
      return;
    }

    // If dimension input is active, don't finish drawing - wait for input
    if (this.showDimensionInput) {
      return;
    }

    if (this.isDrawing && this.drawingStartPoint && this.drawingCurrentPoint) {
      // Set isDrawing to false BEFORE finishing to prevent re-entry
      this.isDrawing = false;
      // Finish drawing rectangle or circle
      this.finishDrawing();
    } else {
      this.isDrawing = false;
    }
  }

  /**
   * Finish drawing a line (double-click or button)
   */
  finishLineDrawing(): void {
    if (this.drawingMode !== 'line' || this.linePoints.length < 2) return;
    this.finishDrawing();
  }

  /**
   * Increment polygon sides (min 3, max 24)
   */
  incrementPolygonSides(): void {
    this.polygonSides = Math.min(24, this.polygonSides + 1);
  }

  /**
   * Decrement polygon sides (min 3, max 24)
   */
  decrementPolygonSides(): void {
    this.polygonSides = Math.max(3, this.polygonSides - 1);
  }

  /**
   * Cancel current drawing
   */
  cancelDrawing(): void {
    this.isDrawing = false;
    this.drawingStartPoint = null;
    this.drawingCurrentPoint = null;
    this.linePoints = [];
    this.clearDrawingPreview();
    this.hideSnapIndicator();
    this.hideSnapFeedback();
    this.clearDrawingDimensions();
    this.snapEngine.clearLastPoint();

    // Reset arc and slot states
    this.arcDrawingState = { startPoint: null, midPoint: null, endPoint: null, step: 'start' };
    this.slotDrawingState = { startPoint: null, endPoint: null, step: 'start' };

    // Clear visual helpers
    this.hideCursorIndicator();
    this.clearPointMarkers();

    this.cdr.detectChanges();
  }

  /**
   * Cancel drawing from the UI cancel button.
   * If in Boss/Pocket mode, exits the entire operation.
   * Otherwise just cancels the drawing.
   */
  cancelDrawingFromUI(): void {
    if (this.isExtrudeOnFaceMode) {
      // In Boss/Pocket mode - cancel the entire operation
      this.exitOperationMode(true);
    } else {
      // Normal drawing - just cancel and reset mode
      this.cancelDrawing();
      this.setDrawingMode('none');
    }
  }

  /**
   * Finish arc drawing - create a filled arc sector shape
   */
  private async finishArcDrawing(): Promise<void> {
    if (!this.arcDrawingState.startPoint || !this.arcDrawingState.midPoint || !this.arcDrawingState.endPoint) {
      this.cancelDrawing();
      return;
    }

    // Sketch mode: Add arc entity instead of 3D shape
    if (this.isSketchMode && this.activeSketch) {
      // Calculate arc parameters
      const start2D = this.worldToPlaneLocal(this.arcDrawingState.startPoint);
      const mid2D = this.worldToPlaneLocal(this.arcDrawingState.midPoint);
      const end2D = this.worldToPlaneLocal(this.arcDrawingState.endPoint);

      // Calculate circle center from 3 points
      const ax = start2D.x,
        ay = start2D.y;
      const bx = mid2D.x,
        by = mid2D.y;
      const cx = end2D.x,
        cy = end2D.y;

      const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
      if (Math.abs(d) > 0.0001) {
        const ux =
          ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d;
        const uy =
          ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d;
        const radius = Math.sqrt((ax - ux) * (ax - ux) + (ay - uy) * (ay - uy));

        const startAngle = Math.atan2(ay - uy, ax - ux);
        const endAngle = Math.atan2(cy - uy, cx - ux);

        this.sketchService.createArc(ux, uy, radius, startAngle, endAngle, false, this.isConstructionMode);
        this.renderSketchEntities();
      }

      this.cancelDrawing();
      this.setDrawingMode('none');
      return;
    }

    // Calculate arc points
    const arcPoints = this.calculateArcPoints(
      this.arcDrawingState.startPoint,
      this.arcDrawingState.midPoint,
      this.arcDrawingState.endPoint
    );

    if (arcPoints.length < 3) {
      this.cancelDrawing();
      return;
    }

    // Convert to 2D points for extrusion
    const points2D: number[][] = arcPoints.map((p) => {
      switch (this.sketchPlane) {
        case 'XY':
          return [p.x, p.y];
        case 'XZ':
          return [p.x, p.z];
        case 'YZ':
          return [p.y, p.z];
        case 'custom':
          const pt2D = this.worldToPlaneLocal({ x: p.x, y: p.y, z: p.z });
          return [pt2D.x, pt2D.y];
        default:
          return [p.x, p.y];
      }
    });

    // Close the shape by adding center point (for pie/sector shape)
    // Or just use the arc as a closed path
    // For now, create a closed polygon from the arc

    // Create the shape
    try {
      this.isProcessing = true;
      await this.initializeKernel();

      // Check if we're in boss/pocket mode
      if (this.isExtrudeOnFaceMode && this.selectedFaceInfo && this.selectedCADShapeIds.size === 1) {
        // Determine operation based on height sign (negative = pocket, positive = boss)
        const actualOperation: 'boss' | 'pocket' = this.extrudeOnFaceHeight >= 0 ? 'boss' : 'pocket';
        const actualHeight = Math.abs(this.extrudeOnFaceHeight);

        // Boss/Pocket: Modify existing shape
        this.processingMessage = `Creating ${actualOperation}...`;

        // Commit any pending transforms before boss/pocket operation
        await this.commitPendingTransforms();

        const selectedId = Array.from(this.selectedCADShapeIds)[0];
        const baseShape = this.cadShapes.find((s) => s.id === selectedId);

        if (!baseShape) {
          throw new Error('Base shape not found');
        }

        const faceIndex = this.selectedFaceInfo.faceIndex ?? 0;

        const planeParams = {
          origin: this.customPlaneOrigin,
          normal: this.customPlaneNormal,
          xAxis: this.customPlaneXAxis,
          yAxis: this.customPlaneYAxis
        };

        const result = await this.cadKernel.extrudeOnFace(
          baseShape,
          points2D,
          faceIndex,
          actualHeight,
          actualOperation,
          planeParams
        );

        this.removeShapeFromScene(baseShape.id);
        this.cadShapes = this.cadShapes.filter((s) => s.id !== baseShape.id);

        this.cadShapes.push(result);
        this.addShapeToScene(result);

        this.selectedCADShapeIds.clear();
        this.selectedCADShapeIds.add(result.id);
        this.updateShapeVisualState(result.id, true);
        this.attachTransformControls();

        this.isExtrudeOnFaceMode = false;
        this.selectedFaceInfo = null;
        this.cleanupSketchPlane();

        // Clear sketch plane settings
        this.sketchPlane = null;
        this.customPlaneOrigin = [0, 0, 0];
        this.customPlaneNormal = [0, 0, 1];
        this.customPlaneXAxis = [1, 0, 0];
        this.customPlaneYAxis = [0, 1, 0];

        // Clear active operation state
        this.restoreHiddenShapes();
        this.activeOperation = null;

        // Collapse the panel on success
        this.expandedPatternGroup = null;

        this.toastService.success(`Arc ${actualOperation} created`);
      } else {
        // Regular extrusion: Create new standalone shape
        const arcParams: ExtrudeParams = {
          points: points2D,
          height: this.extrudeHeight,
          sketchPlane: this.sketchPlane === 'custom' ? undefined : (this.sketchPlane as 'XY' | 'XZ' | 'YZ'),
          planeOffset: this.sketchPlaneOffset,
          customPlane:
            this.sketchPlane === 'custom'
              ? {
                  origin: this.customPlaneOrigin,
                  normal: this.customPlaneNormal,
                  xAxis: this.customPlaneXAxis,
                  yAxis: this.customPlaneYAxis
                }
              : undefined
        };
        const cadShape = await this.cadKernel.extrudePolygon(arcParams);

        if (cadShape) {
          cadShape.name = `Arc_${this.cadShapes.length + 1}`;
          cadShape.color = '#6366f1';
          this.cadShapes.push(cadShape);
          this.addShapeToScene(cadShape);
          this.hasUnsavedChanges = true;

          // Select the new shape
          this.selectedCADShapeIds.clear();
          this.selectedCADShapeIds.add(cadShape.id);
          this.updateShapeVisualState(cadShape.id, true);
          this.attachTransformControls();

          this.toastService.success('Arc created');
        }
      }
    } catch (error: any) {
      this.toastService.error('Failed to create arc');

      // Clean up boss/pocket mode if we were in it
      if (this.isExtrudeOnFaceMode) {
        this.isExtrudeOnFaceMode = false;
        this.isBossPocketPreviewMode = false;
        this.selectedFaceInfo = null;
        this.cleanupSketchPlane();
        this.restoreHiddenShapes();
        this.activeOperation = null;
        this.expandedPatternGroup = null;
      }
    } finally {
      this.isProcessing = false;
      this.cancelDrawing();
      this.setDrawingMode('none');
    }
  }

  /**
   * Finish slot drawing - create a slot (stadium/pill) shape
   */
  private async finishSlotDrawing(): Promise<void> {
    // Sketch mode: Add slot entity instead of 3D shape
    if (this.isSketchMode && this.activeSketch && this.slotDrawingState.startPoint && this.slotDrawingState.endPoint) {
      const start2D = this.worldToPlaneLocal(this.slotDrawingState.startPoint);
      const end2D = this.worldToPlaneLocal(this.slotDrawingState.endPoint);

      this.sketchService.createSlot(start2D.x, start2D.y, end2D.x, end2D.y, this.slotWidth, this.isConstructionMode);
      this.renderSketchEntities();

      this.cancelDrawing();
      this.setDrawingMode('none');
      return;
    }

    const slotShape = this.getSlotShape();
    if (!slotShape) {
      this.cancelDrawing();
      return;
    }

    // Extract points from shape path and deduplicate
    const rawPoints: number[][] = [];
    const path = slotShape.getPoints(32); // Medium resolution for smooth curves

    for (const p of path) {
      rawPoints.push([p.x, p.y]);
    }

    // Remove duplicate/near-duplicate points (within 0.01 tolerance)
    const points2D: number[][] = [];
    const tolerance = 0.01;
    for (const pt of rawPoints) {
      if (points2D.length === 0) {
        points2D.push(pt);
      } else {
        const last = points2D[points2D.length - 1];
        const dist = Math.sqrt((pt[0] - last[0]) ** 2 + (pt[1] - last[1]) ** 2);
        if (dist > tolerance) {
          points2D.push(pt);
        }
      }
    }

    // Also check if first and last points are near-duplicates
    if (points2D.length > 1) {
      const first = points2D[0];
      const last = points2D[points2D.length - 1];
      const dist = Math.sqrt((first[0] - last[0]) ** 2 + (first[1] - last[1]) ** 2);
      if (dist < tolerance) {
        points2D.pop(); // Remove the last point as it duplicates the first
      }
    }

    if (points2D.length < 3) {
      this.cancelDrawing();
      return;
    }

    try {
      this.isProcessing = true;
      await this.initializeKernel();

      // Check if we're in boss/pocket mode
      if (this.isExtrudeOnFaceMode && this.selectedFaceInfo && this.selectedCADShapeIds.size === 1) {
        // Determine operation based on height sign (negative = pocket, positive = boss)
        const actualOperation: 'boss' | 'pocket' = this.extrudeOnFaceHeight >= 0 ? 'boss' : 'pocket';
        const actualHeight = Math.abs(this.extrudeOnFaceHeight);

        // Boss/Pocket: Modify existing shape
        this.processingMessage = `Creating ${actualOperation}...`;

        // Commit any pending transforms before boss/pocket operation
        await this.commitPendingTransforms();

        const selectedId = Array.from(this.selectedCADShapeIds)[0];
        const baseShape = this.cadShapes.find((s) => s.id === selectedId);

        if (!baseShape) {
          throw new Error('Base shape not found');
        }

        const faceIndex = this.selectedFaceInfo.faceIndex ?? 0;

        const planeParams = {
          origin: this.customPlaneOrigin,
          normal: this.customPlaneNormal,
          xAxis: this.customPlaneXAxis,
          yAxis: this.customPlaneYAxis
        };

        const result = await this.cadKernel.extrudeOnFace(
          baseShape,
          points2D,
          faceIndex,
          actualHeight,
          actualOperation,
          planeParams
        );

        this.removeShapeFromScene(baseShape.id);
        this.cadShapes = this.cadShapes.filter((s) => s.id !== baseShape.id);

        this.cadShapes.push(result);
        this.addShapeToScene(result);

        this.selectedCADShapeIds.clear();
        this.selectedCADShapeIds.add(result.id);
        this.updateShapeVisualState(result.id, true);
        this.attachTransformControls();

        this.isExtrudeOnFaceMode = false;
        this.selectedFaceInfo = null;
        this.cleanupSketchPlane();

        // Clear sketch plane settings
        this.sketchPlane = null;
        this.customPlaneOrigin = [0, 0, 0];
        this.customPlaneNormal = [0, 0, 1];
        this.customPlaneXAxis = [1, 0, 0];
        this.customPlaneYAxis = [0, 1, 0];

        // Clear active operation state
        this.restoreHiddenShapes();
        this.activeOperation = null;

        // Collapse the panel on success
        this.expandedPatternGroup = null;

        this.toastService.success(`Slot ${actualOperation} created`);
      } else {
        // Regular extrusion: Create new standalone shape using proper slot primitive
        // Get slot parameters from drawing state
        const startPt = this.slotDrawingState.startPoint!;
        const endPt = this.slotDrawingState.endPoint!;
        const start2D = this.worldToPlaneLocal(startPt);
        const end2D = this.worldToPlaneLocal(endPt);

        const cadShape = await this.cadKernel.extrudeSlot({
          startX: start2D.x,
          startY: start2D.y,
          endX: end2D.x,
          endY: end2D.y,
          width: this.slotWidth,
          height: this.extrudeHeight,
          sketchPlane: this.sketchPlane === 'custom' ? undefined : (this.sketchPlane as 'XY' | 'XZ' | 'YZ'),
          planeOffset: this.sketchPlaneOffset,
          customPlane:
            this.sketchPlane === 'custom'
              ? {
                  origin: this.customPlaneOrigin,
                  normal: this.customPlaneNormal,
                  xAxis: this.customPlaneXAxis,
                  yAxis: this.customPlaneYAxis
                }
              : undefined
        });

        if (cadShape) {
          cadShape.name = `Slot_${this.cadShapes.length + 1}`;
          cadShape.color = '#6366f1';
          this.cadShapes.push(cadShape);
          this.addShapeToScene(cadShape);
          this.hasUnsavedChanges = true;

          // Select the new shape
          this.selectedCADShapeIds.clear();
          this.selectedCADShapeIds.add(cadShape.id);
          this.updateShapeVisualState(cadShape.id, true);
          this.attachTransformControls();

          this.toastService.success('Slot created');
        }
      }
    } catch (error: any) {
      this.toastService.error('Failed to create slot');

      // Clean up boss/pocket mode if we were in it
      if (this.isExtrudeOnFaceMode) {
        this.isExtrudeOnFaceMode = false;
        this.isBossPocketPreviewMode = false;
        this.selectedFaceInfo = null;
        this.cleanupSketchPlane();
        this.restoreHiddenShapes();
        this.activeOperation = null;
        this.expandedPatternGroup = null;
      }
    } finally {
      this.isProcessing = false;
      this.cancelDrawing();
      this.setDrawingMode('none');
    }
  }

  /**
   * Get point on sketch plane from mouse event
   */
  private getPointOnSketchPlane(event: MouseEvent): { x: number; y: number; z: number } | null {
    if (!this.engine || !this.sketchPlane) return null;

    const rect = this.engine.canvas.getBoundingClientRect();
    const mouse = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    // Create a raycaster
    const raycaster = new Raycaster();
    raycaster.setFromCamera(mouse, this.engine.camera);

    // Create plane for intersection
    let planeNormal: Vector3;
    let planePoint: Vector3;

    switch (this.sketchPlane) {
      case 'XY':
        planeNormal = new Vector3(0, 0, 1);
        planePoint = new Vector3(0, 0, this.sketchPlaneOffset);
        break;
      case 'XZ':
        planeNormal = new Vector3(0, 1, 0);
        planePoint = new Vector3(0, this.sketchPlaneOffset, 0);
        break;
      case 'YZ':
        planeNormal = new Vector3(1, 0, 0);
        planePoint = new Vector3(this.sketchPlaneOffset, 0, 0);
        break;
      case 'custom':
        planeNormal = new Vector3(this.customPlaneNormal[0], this.customPlaneNormal[1], this.customPlaneNormal[2]);
        planePoint = new Vector3(this.customPlaneOrigin[0], this.customPlaneOrigin[1], this.customPlaneOrigin[2]);
        break;
      default:
        return null;
    }

    const plane = new Plane();
    plane.setFromNormalAndCoplanarPoint(planeNormal, planePoint);

    const intersection = new Vector3();
    raycaster.ray.intersectPlane(plane, intersection);

    if (!intersection) return null;

    // Apply grid snapping if enabled - only snap the in-plane coordinates
    if (this.snapEnabled) {
      const gridSize = 5; // 5mm grid
      switch (this.sketchPlane) {
        case 'XY':
          intersection.x = Math.round(intersection.x / gridSize) * gridSize;
          intersection.y = Math.round(intersection.y / gridSize) * gridSize;
          // Keep z exactly on the plane
          intersection.z = this.sketchPlaneOffset;
          break;
        case 'XZ':
          intersection.x = Math.round(intersection.x / gridSize) * gridSize;
          intersection.z = Math.round(intersection.z / gridSize) * gridSize;
          // Keep y exactly on the plane
          intersection.y = this.sketchPlaneOffset;
          break;
        case 'YZ':
          intersection.y = Math.round(intersection.y / gridSize) * gridSize;
          intersection.z = Math.round(intersection.z / gridSize) * gridSize;
          // Keep x exactly on the plane
          intersection.x = this.sketchPlaneOffset;
          break;
        case 'custom':
          // For custom plane, snap in local coordinates then convert back
          // For now, just snap all coordinates
          intersection.x = Math.round(intersection.x / gridSize) * gridSize;
          intersection.y = Math.round(intersection.y / gridSize) * gridSize;
          intersection.z = Math.round(intersection.z / gridSize) * gridSize;
          break;
      }
    }

    return { x: intersection.x, y: intersection.y, z: intersection.z };
  }

  /**
   * Update drawing preview mesh (synchronous to avoid race conditions)
   */
  private updateDrawingPreview(): void {
    if (!this.engine) return;

    this.clearDrawingPreview();

    let geometry: BufferGeometry | undefined;
    let material: MeshBasicMaterial | LineBasicMaterial | undefined;

    if (this.drawingMode === 'rectangle' && this.drawingStartPoint && this.drawingCurrentPoint) {
      // Create rectangle preview
      const points = this.getRectanglePoints();
      if (points.length < 4) return;

      const shape = new Shape();
      shape.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        shape.lineTo(points[i].x, points[i].y);
      }
      shape.closePath();

      geometry = new ShapeGeometry(shape);
      material = new MeshBasicMaterial({
        color: 0x6366f1,
        transparent: true,
        opacity: 0.3,
        side: DoubleSide,
        depthWrite: false
      });

      this.drawingPreviewMesh = new Mesh(geometry, material);
      this.positionPreviewOnPlane(this.drawingPreviewMesh);
    } else if (this.drawingMode === 'circle' && this.drawingStartPoint && this.drawingCurrentPoint) {
      // Create circle preview
      const radius = this.getCircleRadius();
      if (radius < 1) return;

      const shape = new Shape();
      shape.absarc(0, 0, radius, 0, Math.PI * 2, false);

      geometry = new ShapeGeometry(shape, 32);
      material = new MeshBasicMaterial({
        color: 0x6366f1,
        transparent: true,
        opacity: 0.3,
        side: DoubleSide,
        depthWrite: false
      });

      this.drawingPreviewMesh = new Mesh(geometry, material);
      this.positionCirclePreviewOnPlane(this.drawingPreviewMesh);
    } else if (this.drawingMode === 'line' && this.linePoints.length > 0) {
      // Create line preview
      const points3D: Vector3[] = [];
      for (const pt of this.linePoints) {
        points3D.push(new Vector3(pt.x, pt.y, pt.z));
      }
      if (this.drawingCurrentPoint) {
        points3D.push(new Vector3(this.drawingCurrentPoint.x, this.drawingCurrentPoint.y, this.drawingCurrentPoint.z));
      }

      geometry = new BufferGeometry().setFromPoints(points3D);
      material = new LineBasicMaterial({ color: 0x6366f1, linewidth: 2 });
      this.drawingPreviewMesh = new Line(geometry, material);
    } else if (this.drawingMode === 'arc') {
      // Create 3-point arc preview
      const arcPoints = this.getArcPreviewPoints();
      if (arcPoints.length > 1) {
        geometry = new BufferGeometry().setFromPoints(arcPoints);
        material = new LineBasicMaterial({ color: 0x6366f1, linewidth: 2 });
        this.drawingPreviewMesh = new Line(geometry, material);
      }
    } else if (this.drawingMode === 'polygon' && this.drawingStartPoint && this.drawingCurrentPoint) {
      // Create polygon preview
      const polygonPoints = this.getPolygonPoints();
      if (polygonPoints.length >= 3) {
        const shape = new Shape();
        shape.moveTo(polygonPoints[0].x, polygonPoints[0].y);
        for (let i = 1; i < polygonPoints.length; i++) {
          shape.lineTo(polygonPoints[i].x, polygonPoints[i].y);
        }
        shape.closePath();

        geometry = new ShapeGeometry(shape);
        material = new MeshBasicMaterial({
          color: 0x6366f1,
          transparent: true,
          opacity: 0.3,
          side: DoubleSide,
          depthWrite: false
        });

        this.drawingPreviewMesh = new Mesh(geometry, material);
        this.positionPreviewOnPlane(this.drawingPreviewMesh);
      }
    } else if (this.drawingMode === 'slot') {
      // Create slot preview
      const slotShape = this.getSlotShape();
      if (slotShape) {
        geometry = new ShapeGeometry(slotShape, 32);
        material = new MeshBasicMaterial({
          color: 0x6366f1,
          transparent: true,
          opacity: 0.3,
          side: DoubleSide,
          depthWrite: false
        });

        this.drawingPreviewMesh = new Mesh(geometry, material);
        this.positionPreviewOnPlane(this.drawingPreviewMesh);
      }
    }

    if (this.drawingPreviewMesh) {
      this.drawingPreviewMesh.name = 'DrawingPreview';
      this.engine.scene.add(this.drawingPreviewMesh);
    }
  }

  /**
   * Get points for 3-point arc preview
   */
  private getArcPreviewPoints(): Vector3[] {
    const points: Vector3[] = [];

    if (this.arcDrawingState.startPoint) {
      points.push(
        new Vector3(
          this.arcDrawingState.startPoint.x,
          this.arcDrawingState.startPoint.y,
          this.arcDrawingState.startPoint.z
        )
      );
    }

    if (this.arcDrawingState.midPoint) {
      points.push(
        new Vector3(this.arcDrawingState.midPoint.x, this.arcDrawingState.midPoint.y, this.arcDrawingState.midPoint.z)
      );
    }

    // If we have start and mid points, try to calculate arc through them
    if (this.arcDrawingState.startPoint && this.arcDrawingState.midPoint && this.drawingCurrentPoint) {
      const arcCurvePoints = this.calculateArcPoints(
        this.arcDrawingState.startPoint,
        this.arcDrawingState.midPoint,
        this.drawingCurrentPoint
      );
      return arcCurvePoints;
    }

    // Just show points if we don't have enough for arc calculation
    if (this.drawingCurrentPoint && points.length > 0) {
      points.push(new Vector3(this.drawingCurrentPoint.x, this.drawingCurrentPoint.y, this.drawingCurrentPoint.z));
    }

    return points;
  }

  /**
   * Calculate smooth arc points through 3 points
   */
  private calculateArcPoints(
    p1: { x: number; y: number; z: number },
    p2: { x: number; y: number; z: number },
    p3: { x: number; y: number; z: number }
  ): Vector3[] {
    // Convert 3D world points to 2D local plane coordinates
    let a: { x: number; y: number };
    let b: { x: number; y: number };
    let c: { x: number; y: number };

    switch (this.sketchPlane) {
      case 'XY':
        a = { x: p1.x, y: p1.y };
        b = { x: p2.x, y: p2.y };
        c = { x: p3.x, y: p3.y };
        break;
      case 'XZ':
        a = { x: p1.x, y: p1.z };
        b = { x: p2.x, y: p2.z };
        c = { x: p3.x, y: p3.z };
        break;
      case 'YZ':
        // Kernel transform: localX = world.y, localY = world.z
        a = { x: p1.y, y: p1.z };
        b = { x: p2.y, y: p2.z };
        c = { x: p3.y, y: p3.z };
        break;
      case 'custom':
        a = this.worldToPlaneLocal(p1);
        b = this.worldToPlaneLocal(p2);
        c = this.worldToPlaneLocal(p3);
        break;
      default:
        a = { x: p1.x, y: p1.y };
        b = { x: p2.x, y: p2.y };
        c = { x: p3.x, y: p3.y };
    }

    const ax = a.x,
      ay = a.y;
    const bx = b.x,
      by = b.y;
    const cx = c.x,
      cy = c.y;

    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
    if (Math.abs(d) < 0.0001) {
      // Points are collinear, just return straight line
      return [new Vector3(p1.x, p1.y, p1.z), new Vector3(p2.x, p2.y, p2.z), new Vector3(p3.x, p3.y, p3.z)];
    }

    const ux =
      ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d;
    const uy =
      ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d;

    const radius = Math.sqrt((ax - ux) * (ax - ux) + (ay - uy) * (ay - uy));

    // Calculate angles
    const angle1 = Math.atan2(ay - uy, ax - ux);
    const angle2 = Math.atan2(by - uy, bx - ux);
    const angle3 = Math.atan2(cy - uy, cx - ux);

    // Determine arc direction
    let startAngle = angle1;
    let endAngle = angle3;

    // Check if we need to go the other way around
    const midAngle = angle2;
    const normalizedMid = (((midAngle - startAngle) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const normalizedEnd = (((endAngle - startAngle) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

    const clockwise = normalizedMid > normalizedEnd;

    // Generate arc points in 2D local coordinates
    const points2D: { x: number; y: number }[] = [];
    const segments = 32;

    let angleDiff = endAngle - startAngle;
    if (clockwise && angleDiff > 0) angleDiff -= 2 * Math.PI;
    if (!clockwise && angleDiff < 0) angleDiff += 2 * Math.PI;

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = startAngle + angleDiff * t;
      const x = ux + radius * Math.cos(angle);
      const y = uy + radius * Math.sin(angle);
      points2D.push({ x, y });
    }

    // Convert back to 3D world coordinates
    return points2D.map((pt) => {
      const world = this.planeLocalToWorld(pt);
      return new Vector3(world.x, world.y, world.z);
    });
  }

  /**
   * Get points for regular polygon preview
   */
  private getPolygonPoints(): { x: number; y: number }[] {
    if (!this.drawingStartPoint || !this.drawingCurrentPoint || !this.sketchPlane) return [];

    // Convert 3D world points to 2D local plane coordinates
    let centerX: number, centerY: number, currentX: number, currentY: number;

    switch (this.sketchPlane) {
      case 'XY':
        centerX = this.drawingStartPoint.x;
        centerY = this.drawingStartPoint.y;
        currentX = this.drawingCurrentPoint.x;
        currentY = this.drawingCurrentPoint.y;
        break;
      case 'XZ':
        centerX = this.drawingStartPoint.x;
        centerY = this.drawingStartPoint.z;
        currentX = this.drawingCurrentPoint.x;
        currentY = this.drawingCurrentPoint.z;
        break;
      case 'YZ':
        // Kernel transform: localX = world.y, localY = world.z
        centerX = this.drawingStartPoint.y;
        centerY = this.drawingStartPoint.z;
        currentX = this.drawingCurrentPoint.y;
        currentY = this.drawingCurrentPoint.z;
        break;
      case 'custom':
        const center2D = this.worldToPlaneLocal(this.drawingStartPoint);
        const current2D = this.worldToPlaneLocal(this.drawingCurrentPoint);
        centerX = center2D.x;
        centerY = center2D.y;
        currentX = current2D.x;
        currentY = current2D.y;
        break;
      default:
        return [];
    }

    const dx = currentX - centerX;
    const dy = currentY - centerY;
    const radius = Math.sqrt(dx * dx + dy * dy);

    if (radius < 1) return [];

    const points: { x: number; y: number }[] = [];
    const angleOffset = -Math.PI / 2; // Start from top

    for (let i = 0; i < this.polygonSides; i++) {
      const angle = angleOffset + (2 * Math.PI * i) / this.polygonSides;
      points.push({
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      });
    }

    return points;
  }

  /**
   * Get shape for slot preview (pill/stadium shape)
   */
  private getSlotShape(): Shape | null {
    let startPt3D = this.slotDrawingState.startPoint;
    let endPt3D = this.slotDrawingState.endPoint || this.drawingCurrentPoint;

    if (!startPt3D) {
      startPt3D = this.drawingStartPoint;
    }

    if (!startPt3D || !endPt3D || !this.sketchPlane) return null;

    // Convert 3D world points to 2D local plane coordinates (same as getRectanglePoints)
    let x1: number, y1: number, x2: number, y2: number;

    switch (this.sketchPlane) {
      case 'XY':
        x1 = startPt3D.x;
        y1 = startPt3D.y;
        x2 = endPt3D.x;
        y2 = endPt3D.y;
        break;
      case 'XZ':
        x1 = startPt3D.x;
        y1 = startPt3D.z;
        x2 = endPt3D.x;
        y2 = endPt3D.z;
        break;
      case 'YZ':
        // Kernel transform: localX = world.y, localY = world.z
        x1 = startPt3D.y;
        y1 = startPt3D.z;
        x2 = endPt3D.y;
        y2 = endPt3D.z;
        break;
      case 'custom':
        const start2D = this.worldToPlaneLocal(startPt3D);
        const end2D = this.worldToPlaneLocal(endPt3D);
        x1 = start2D.x;
        y1 = start2D.y;
        x2 = end2D.x;
        y2 = end2D.y;
        break;
      default:
        return null;
    }

    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length < 1) return null;

    const halfWidth = this.slotWidth / 2;
    const angle = Math.atan2(dy, dx);

    // Calculate perpendicular offset
    const perpX = -Math.sin(angle) * halfWidth;
    const perpY = Math.cos(angle) * halfWidth;

    const shape = new Shape();

    // Start at left side of start point
    shape.moveTo(x1 + perpX, y1 + perpY);

    // Line to left side of end point
    shape.lineTo(x2 + perpX, y2 + perpY);

    // Arc around end cap (clockwise from left to right)
    shape.absarc(x2, y2, halfWidth, angle + Math.PI / 2, angle - Math.PI / 2, true);

    // Line to right side of start point
    shape.lineTo(x1 - perpX, y1 - perpY);

    // Arc around start cap (clockwise from right to left)
    shape.absarc(x1, y1, halfWidth, angle - Math.PI / 2, angle + Math.PI / 2, true);

    shape.closePath();

    return shape;
  }

  /**
   * Position preview mesh on sketch plane for rectangle
   * Rotation must match the kernel transformation for consistency
   */
  private positionPreviewOnPlane(mesh: any): void {
    if (!this.sketchPlane) return;

    // Reset position and rotation before applying new transforms
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);

    switch (this.sketchPlane) {
      case 'XY':
        mesh.position.z = this.sketchPlaneOffset + 0.1;
        break;
      case 'XZ':
        // XZ plane: rotate +90° around X axis to lay flat
        // This maps local (x, y, 0) to world (x, 0, y)
        mesh.rotation.x = Math.PI / 2;
        mesh.position.y = this.sketchPlaneOffset + 0.1;
        break;
      case 'YZ':
        // Use rotation matrix matching kernel transform: (localX, localY, 0) → (0, localX, localY)
        // Local X → World Y, Local Y → World Z, Local Z → World X
        const yzMatrix = new Matrix4();
        yzMatrix.makeBasis(
          new Vector3(0, 1, 0),  // Local X → World Y
          new Vector3(0, 0, 1),  // Local Y → World Z
          new Vector3(1, 0, 0)   // Local Z → World X
        );
        mesh.rotation.setFromRotationMatrix(yzMatrix);
        mesh.position.x = this.sketchPlaneOffset + 0.1;
        break;
      case 'custom':
        // For custom plane, the rectangle points are in local plane coordinates
        // We need to position at origin and orient to match the plane
        const origin = this.customPlaneOrigin;
        const normal = new Vector3(this.customPlaneNormal[0], this.customPlaneNormal[1], this.customPlaneNormal[2]);

        // Get local axes
        const xAxis = new Vector3(this.customPlaneXAxis[0], this.customPlaneXAxis[1], this.customPlaneXAxis[2]);
        const yAxis = new Vector3(this.customPlaneYAxis[0], this.customPlaneYAxis[1], this.customPlaneYAxis[2]);

        // Create rotation matrix from local axes
        const rotMatrix = new Matrix4();
        rotMatrix.makeBasis(xAxis, yAxis, normal);
        mesh.rotation.setFromRotationMatrix(rotMatrix);

        // Position at plane origin (local coords are relative to this)
        mesh.position.set(origin[0], origin[1], origin[2]);
        // Offset slightly along normal to prevent z-fighting
        mesh.position.addScaledVector(normal, 0.1);
        break;
    }
  }

  /**
   * Position circle preview on sketch plane
   * Rotation must match the kernel transformation for consistency
   */
  private positionCirclePreviewOnPlane(mesh: any): void {
    if (!this.sketchPlane || !this.drawingStartPoint) return;

    // Reset rotation before applying new transforms (position is set explicitly)
    mesh.rotation.set(0, 0, 0);

    switch (this.sketchPlane) {
      case 'XY':
        mesh.position.set(this.drawingStartPoint.x, this.drawingStartPoint.y, this.sketchPlaneOffset + 0.1);
        break;
      case 'XZ':
        // XZ plane: rotate +90° around X axis to lay flat
        mesh.rotation.x = Math.PI / 2;
        mesh.position.set(this.drawingStartPoint.x, this.sketchPlaneOffset + 0.1, this.drawingStartPoint.z);
        break;
      case 'YZ':
        // Use rotation matrix matching kernel transform: Local X → World Y, Local Y → World Z
        const yzCircleMatrix = new Matrix4();
        yzCircleMatrix.makeBasis(
          new Vector3(0, 1, 0),
          new Vector3(0, 0, 1),
          new Vector3(1, 0, 0)
        );
        mesh.rotation.setFromRotationMatrix(yzCircleMatrix);
        mesh.position.set(this.sketchPlaneOffset + 0.1, this.drawingStartPoint.y, this.drawingStartPoint.z);
        break;
      case 'custom':
        // Position at circle center (start point) projected onto plane
        const circleNormal = new Vector3(
          this.customPlaneNormal[0],
          this.customPlaneNormal[1],
          this.customPlaneNormal[2]
        );

        // Get local axes
        const circleXAxis = new Vector3(this.customPlaneXAxis[0], this.customPlaneXAxis[1], this.customPlaneXAxis[2]);
        const circleYAxis = new Vector3(this.customPlaneYAxis[0], this.customPlaneYAxis[1], this.customPlaneYAxis[2]);

        // Create rotation matrix from local axes
        const circleRotMatrix = new Matrix4();
        circleRotMatrix.makeBasis(circleXAxis, circleYAxis, circleNormal);
        mesh.rotation.setFromRotationMatrix(circleRotMatrix);

        // Position at the actual drawing start point (world coordinates)
        mesh.position.set(this.drawingStartPoint.x, this.drawingStartPoint.y, this.drawingStartPoint.z);
        // Offset slightly along normal to prevent z-fighting
        mesh.position.addScaledVector(circleNormal, 0.1);
        break;
    }
  }

  /**
   * Get rectangle points in 2D
   */
  /**
   * Convert a 3D world point to 2D local coordinates on the custom plane
   */
  private worldToPlaneLocal(worldPoint: { x: number; y: number; z: number }): { x: number; y: number } {
    // Handle standard planes directly
    switch (this.sketchPlane) {
      case 'XY':
        // XY plane: local X = world X, local Y = world Y
        return {
          x: worldPoint.x,
          y: worldPoint.y
        };
      case 'XZ':
        // XZ plane: local X = world X, local Y = world Z
        return {
          x: worldPoint.x,
          y: worldPoint.z
        };
      case 'YZ':
        // YZ plane: local X = world Y, local Y = world Z (matches kernel transformation)
        return {
          x: worldPoint.y,
          y: worldPoint.z
        };
      case 'custom':
        // Custom plane: project onto local axes
        const dx = worldPoint.x - this.customPlaneOrigin[0];
        const dy = worldPoint.y - this.customPlaneOrigin[1];
        const dz = worldPoint.z - this.customPlaneOrigin[2];

        // Project onto local X axis
        const localX = dx * this.customPlaneXAxis[0] + dy * this.customPlaneXAxis[1] + dz * this.customPlaneXAxis[2];
        // Project onto local Y axis
        const localY = dx * this.customPlaneYAxis[0] + dy * this.customPlaneYAxis[1] + dz * this.customPlaneYAxis[2];

        return { x: localX, y: localY };
      default:
        // Fallback to XY
        return { x: worldPoint.x, y: worldPoint.y };
    }
  }

  private getRectanglePoints(): { x: number; y: number }[] {
    if (!this.drawingStartPoint || !this.drawingCurrentPoint || !this.sketchPlane) return [];

    let x1: number, y1: number, x2: number, y2: number;

    switch (this.sketchPlane) {
      case 'XY':
        x1 = this.drawingStartPoint.x;
        y1 = this.drawingStartPoint.y;
        x2 = this.drawingCurrentPoint.x;
        y2 = this.drawingCurrentPoint.y;
        break;
      case 'XZ':
        x1 = this.drawingStartPoint.x;
        y1 = this.drawingStartPoint.z;
        x2 = this.drawingCurrentPoint.x;
        y2 = this.drawingCurrentPoint.z;
        break;
      case 'YZ':
        x1 = this.drawingStartPoint.y;
        y1 = this.drawingStartPoint.z;
        x2 = this.drawingCurrentPoint.y;
        y2 = this.drawingCurrentPoint.z;
        break;
      case 'custom':
        const start2D = this.worldToPlaneLocal(this.drawingStartPoint);
        const end2D = this.worldToPlaneLocal(this.drawingCurrentPoint);
        x1 = start2D.x;
        y1 = start2D.y;
        x2 = end2D.x;
        y2 = end2D.y;
        break;
      default:
        return [];
    }

    return [
      { x: x1, y: y1 },
      { x: x2, y: y1 },
      { x: x2, y: y2 },
      { x: x1, y: y2 }
    ];
  }

  /**
   * Get circle radius
   */
  private getCircleRadius(): number {
    if (!this.drawingStartPoint || !this.drawingCurrentPoint || !this.sketchPlane) return 0;

    let dx: number, dy: number;

    switch (this.sketchPlane) {
      case 'XY':
        dx = this.drawingCurrentPoint.x - this.drawingStartPoint.x;
        dy = this.drawingCurrentPoint.y - this.drawingStartPoint.y;
        break;
      case 'XZ':
        dx = this.drawingCurrentPoint.x - this.drawingStartPoint.x;
        dy = this.drawingCurrentPoint.z - this.drawingStartPoint.z;
        break;
      case 'YZ':
        dx = this.drawingCurrentPoint.y - this.drawingStartPoint.y;
        dy = this.drawingCurrentPoint.z - this.drawingStartPoint.z;
        break;
      case 'custom':
        const start2D = this.worldToPlaneLocal(this.drawingStartPoint);
        const end2D = this.worldToPlaneLocal(this.drawingCurrentPoint);
        dx = end2D.x - start2D.x;
        dy = end2D.y - start2D.y;
        break;
      default:
        return 0;
    }

    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Clear drawing preview mesh
   */
  private clearDrawingPreview(): void {
    if (this.drawingPreviewMesh && this.engine) {
      this.engine.scene.remove(this.drawingPreviewMesh);
      this.drawingPreviewMesh.geometry?.dispose();
      this.drawingPreviewMesh.material?.dispose();
      this.drawingPreviewMesh = null;
    }
  }

  /**
   * Show snap indicator at snap point
   */
  private showSnapIndicator(snapResult: SnapResult): void {
    if (!this.engine) return;

    this.hideSnapIndicator();

    // Determine indicator color based on snap type
    let color: number;
    switch (snapResult.type) {
      case 'vertex':
        color = 0x00ff00; // Green for vertices
        break;
      case 'center':
        color = 0xff0088; // Pink/magenta for centers
        break;
      case 'midpoint':
        color = 0x00ffff; // Cyan for midpoints
        break;
      case 'edge':
        color = 0xffff00; // Yellow for edges
        break;
      case 'grid':
        color = 0xffffff; // White for grid
        break;
      case 'horizontal':
      case 'vertical':
        color = 0xff00ff; // Magenta for H/V constraints
        break;
      case 'parallel':
        color = 0xff8800; // Orange for parallel
        break;
      case 'perpendicular':
        color = 0x8800ff; // Purple for perpendicular
        break;
      default:
        color = 0xffffff;
    }

    // Calculate size based on camera distance for consistent screen size
    const snapPoint = new Vector3(snapResult.point[0], snapResult.point[1], snapResult.point[2]);
    const distance = this.engine.camera.getWorldPosition(new Vector3()).distanceTo(snapPoint);
    const baseSize = Math.max(1, distance * 0.015); // Scale with distance
    const indicatorSize = snapResult.type === 'vertex' ? baseSize * 1.5 : baseSize;

    // Create container group
    const container = new Group();
    container.position.copy(snapPoint);
    container.renderOrder = 999;

    // Create cross shape
    const crossGeometry = new BufferGeometry();
    const crossPositions = new Float32Array([
      // Horizontal line
      -indicatorSize, 0, 0, indicatorSize, 0, 0,
      // Vertical line
      0, -indicatorSize, 0, 0, indicatorSize, 0,
      // Depth line (for 3D)
      0, 0, -indicatorSize, 0, 0, indicatorSize
    ]);
    crossGeometry.setAttribute('position', new BufferAttribute(crossPositions, 3));

    const crossMaterial = new LineBasicMaterial({
      color,
      linewidth: 2,
      depthTest: false
    });
    const cross = new LineSegments(crossGeometry, crossMaterial);
    container.add(cross);

    // Create ring for vertex, center, and midpoint snaps
    if (snapResult.type === 'vertex' || snapResult.type === 'center' || snapResult.type === 'midpoint') {
      const ringRadius = indicatorSize * 0.8;
      const ringSegments = 24;
      const ringPositions: number[] = [];

      for (let i = 0; i <= ringSegments; i++) {
        const theta = (i / ringSegments) * Math.PI * 2;
        const nextTheta = ((i + 1) / ringSegments) * Math.PI * 2;

        ringPositions.push(
          Math.cos(theta) * ringRadius, Math.sin(theta) * ringRadius, 0,
          Math.cos(nextTheta) * ringRadius, Math.sin(nextTheta) * ringRadius, 0
        );
      }

      const ringGeometry = new BufferGeometry();
      ringGeometry.setAttribute('position', new BufferAttribute(new Float32Array(ringPositions), 3));

      const ringMaterial = new LineBasicMaterial({
        color,
        linewidth: 2,
        depthTest: false,
        transparent: true,
        opacity: 0.7
      });

      const ring = new LineSegments(ringGeometry, ringMaterial);
      container.add(ring);
    }

    // Create constraint line for horizontal/vertical snaps
    if ((snapResult.type === 'horizontal' || snapResult.type === 'vertical') && this.drawingStartPoint) {
      const constraintGeometry = new BufferGeometry();
      const lineLength = indicatorSize * 5;

      if (snapResult.type === 'horizontal') {
        constraintGeometry.setAttribute('position', new BufferAttribute(new Float32Array([
          -lineLength, 0, 0, lineLength, 0, 0
        ]), 3));
      } else {
        constraintGeometry.setAttribute('position', new BufferAttribute(new Float32Array([
          0, -lineLength, 0, 0, lineLength, 0
        ]), 3));
      }

      const constraintMaterial = new LineBasicMaterial({
        color,
        linewidth: 1,
        depthTest: false,
        transparent: true,
        opacity: 0.5
      });

      const constraintLine = new LineSegments(constraintGeometry, constraintMaterial);
      container.add(constraintLine);
    }

    this.engine.scene.add(container);
    this.snapIndicator = container as any;
  }

  /**
   * Hide snap indicator
   */
  private hideSnapIndicator(): void {
    if (this.snapIndicator && this.engine) {
      this.engine.scene.remove(this.snapIndicator);

      // Dispose all children (Group contains LineSegments)
      this.snapIndicator.traverse((child) => {
        if (child instanceof LineSegments) {
          child.geometry?.dispose();
          if (child.material instanceof LineBasicMaterial) {
            child.material.dispose();
          }
        }
      });

      this.snapIndicator = null;
    }
    this.currentSnapResult = null;
  }

  /**
   * Update cursor indicator (visual dot following the cursor on sketch plane)
   */
  private updateCursorIndicator(point: { x: number; y: number; z: number }): void {
    if (!this.engine || this.drawingMode === 'none') {
      this.hideCursorIndicator();
      return;
    }

    // Create cursor indicator if it doesn't exist
    if (!this.cursorIndicator) {
      const geometry = new SphereGeometry(1.5, 16, 16);
      const material = new MeshBasicMaterial({
        color: 0x6366f1, // Indigo to match preview
        transparent: true,
        opacity: 0.8,
        depthTest: false
      });
      this.cursorIndicator = new Mesh(geometry, material);
      this.cursorIndicator.name = 'CursorIndicator';
      this.cursorIndicator.renderOrder = 1000;
      this.engine.scene.add(this.cursorIndicator);
    }

    // Update position
    this.cursorIndicator.position.set(point.x, point.y, point.z);
  }

  /**
   * Hide cursor indicator
   */
  private hideCursorIndicator(): void {
    if (this.cursorIndicator && this.engine) {
      this.engine.scene.remove(this.cursorIndicator);
      this.cursorIndicator.geometry?.dispose();
      (this.cursorIndicator.material as MeshBasicMaterial)?.dispose();
      this.cursorIndicator = null;
    }
  }

  /**
   * Add a point marker at a clicked position (for arc, slot)
   */
  private addPointMarker(point: { x: number; y: number; z: number }, color: number = 0xa0ff00): void {
    if (!this.engine) return;

    const geometry = new SphereGeometry(2, 16, 16);
    const material = new MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    const marker = new Mesh(geometry, material);
    marker.position.set(point.x, point.y, point.z);
    marker.name = 'PointMarker';
    marker.renderOrder = 999;
    this.engine.scene.add(marker);
    this.pointMarkers.push(marker);
  }

  /**
   * Clear all point markers
   */
  private clearPointMarkers(): void {
    if (!this.engine) return;

    for (const marker of this.pointMarkers) {
      this.engine.scene.remove(marker);
      marker.geometry?.dispose();
      (marker.material as MeshBasicMaterial)?.dispose();
    }
    this.pointMarkers = [];
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SNAP FEEDBACK & DRAWING DIMENSIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Update snap feedback label position and text
   */
  private updateSnapFeedback(snapResult: SnapResult, event: MouseEvent): void {
    // Label text colour per snap type: the 3D indicator hues, lightened where
    // needed to stay readable on the label's dark background.
    const colors: { [key: string]: string } = {
      vertex: '#00ff00',     // Green
      center: '#ff75c0',     // Pink/magenta
      midpoint: '#00ffff',   // Cyan
      edge: '#ffff00',       // Yellow
      grid: '#ffffff',       // White
      horizontal: '#ff70ff', // Magenta
      vertical: '#ff70ff',   // Magenta
      parallel: '#ff8800',   // Orange
      perpendicular: '#c299ff' // Purple
    };
    this.snapFeedbackColor = colors[snapResult.type] || '#ffffff';

    // Use custom label if provided, otherwise map snap type to user-friendly label
    if (snapResult.label) {
      // Use emoji based on type + custom label
      const icons: { [key: string]: string } = {
        grid: '⊞',
        vertex: '●',
        edge: '─',
        midpoint: '◆',
        center: '◎',
        horizontal: '↔',
        vertical: '↕',
        parallel: '∥',
        perpendicular: '⊥'
      };
      this.snapFeedbackLabel = `${icons[snapResult.type] || '●'} ${snapResult.label}`;
    } else {
      // Default labels
      const labels: { [key: string]: string } = {
        grid: '⊞ Grid',
        vertex: '● Vertex',
        edge: '─ Edge',
        midpoint: '◆ Midpoint',
        center: '◎ Center',
        horizontal: '↔ Horizontal',
        vertical: '↕ Vertical',
        parallel: '∥ Parallel',
        perpendicular: '⊥ Perpendicular'
      };
      this.snapFeedbackLabel = labels[snapResult.type] || snapResult.type;
    }

    this.snapFeedbackPosition = {
      x: event.clientX + 15,
      y: event.clientY - 25
    };
  }

  /**
   * Hide snap feedback label
   */
  private hideSnapFeedback(): void {
    this.snapFeedbackLabel = null;
  }

  /**
   * Update drawing dimensions based on current drawing mode
   * Uses local 2D plane coordinates for accurate measurements
   */
  private updateDrawingDimensions(): void {
    if (this.drawingMode === 'rectangle') {
      if (!this.drawingStartPoint || !this.drawingCurrentPoint) {
        this.drawingDimensions = null;
        return;
      }

      // Convert to local 2D coordinates for accurate measurement
      const startLocal = this.worldToPlaneLocal(this.drawingStartPoint);
      const currentLocal = this.worldToPlaneLocal(this.drawingCurrentPoint);

      const width = Math.abs(currentLocal.x - startLocal.x);
      const height = Math.abs(currentLocal.y - startLocal.y);

      this.drawingDimensions = { width, height };
    } else if (this.drawingMode === 'circle' || this.drawingMode === 'polygon') {
      if (!this.drawingStartPoint || !this.drawingCurrentPoint) {
        this.drawingDimensions = null;
        return;
      }

      // Convert to local 2D coordinates for accurate radius
      const startLocal = this.worldToPlaneLocal(this.drawingStartPoint);
      const currentLocal = this.worldToPlaneLocal(this.drawingCurrentPoint);

      const dx = currentLocal.x - startLocal.x;
      const dy = currentLocal.y - startLocal.y;
      const radius = Math.sqrt(dx * dx + dy * dy);

      this.drawingDimensions = { width: radius * 2, height: radius * 2, radius };
    } else if (this.drawingMode === 'arc') {
      // For arc, show distance from start to current point
      if (this.arcDrawingState.startPoint && this.drawingCurrentPoint) {
        const startLocal = this.worldToPlaneLocal(this.arcDrawingState.startPoint);
        const currentLocal = this.worldToPlaneLocal(this.drawingCurrentPoint);

        const dx = currentLocal.x - startLocal.x;
        const dy = currentLocal.y - startLocal.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        this.drawingDimensions = { width: length, height: 0 };
      }
    } else if (this.drawingMode === 'slot') {
      // For slot, show length and width
      if (this.slotDrawingState.startPoint && this.drawingCurrentPoint) {
        const startLocal = this.worldToPlaneLocal(this.slotDrawingState.startPoint);
        const currentLocal = this.worldToPlaneLocal(this.drawingCurrentPoint);

        const dx = currentLocal.x - startLocal.x;
        const dy = currentLocal.y - startLocal.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        this.drawingDimensions = { width: length, height: this.slotWidth };
      }
    } else {
      this.drawingDimensions = null;
    }
  }

  /**
   * Clear drawing dimensions display
   */
  private clearDrawingDimensions(): void {
    this.drawingDimensions = null;
  }

  /**
   * Open dimension input panel (triggered by Tab key)
   */
  openDimensionInput(): void {
    if (this.drawingMode === 'none' || !this.drawingStartPoint) return;

    // Pre-fill with current dimensions
    if (this.drawingDimensions) {
      this.dimensionInputWidth = Math.round(this.drawingDimensions.width * 10) / 10;
      this.dimensionInputHeight = Math.round(this.drawingDimensions.height * 10) / 10;
      // Show diameter (radius * 2) for consistency - input expects diameter
      this.dimensionInputDiameter = this.drawingDimensions.radius
        ? Math.round(this.drawingDimensions.radius * 2 * 10) / 10
        : null;
    }

    // Store fixed position (current dimension display position)
    this.dimensionInputPosition = { ...this.drawingDimensionsPosition };

    this.showDimensionInput = true;
    this.cdr.detectChanges();
  }

  /**
   * Update preview based on dimension input values (called on input change)
   */
  onDimensionInputChange(): void {
    if (!this.drawingStartPoint) return;

    if (this.drawingMode === 'rectangle') {
      const width = this.dimensionInputWidth || 0;
      const height = this.dimensionInputHeight || 0;

      if (width > 0 && height > 0) {
        this.drawingCurrentPoint = {
          x: this.drawingStartPoint.x + width,
          y: this.drawingStartPoint.y + height,
          z: this.drawingStartPoint.z
        };
        this.updateDrawingPreview();
      }
    } else if (this.drawingMode === 'circle' || this.drawingMode === 'polygon') {
      const diameter = this.dimensionInputDiameter || 0;
      const radius = diameter / 2;

      if (radius > 0) {
        this.drawingCurrentPoint = {
          x: this.drawingStartPoint.x + radius,
          y: this.drawingStartPoint.y,
          z: this.drawingStartPoint.z
        };
        this.updateDrawingPreview();
      }
    } else if (this.drawingMode === 'slot') {
      const length = this.dimensionInputWidth || 0;

      if (length > 0 && this.slotDrawingState.startPoint) {
        this.drawingCurrentPoint = {
          x: this.slotDrawingState.startPoint.x + length,
          y: this.slotDrawingState.startPoint.y,
          z: this.slotDrawingState.startPoint.z
        };
        this.updateDrawingPreview();
      }
    }
  }

  /**
   * Close dimension input panel
   */
  closeDimensionInput(cancelDrawing: boolean = true): void {
    this.showDimensionInput = false;
    this.dimensionInputWidth = null;
    this.dimensionInputHeight = null;
    this.dimensionInputDiameter = null;

    // If canceling, also cancel the drawing
    if (cancelDrawing && this.isDrawing) {
      this.isDrawing = false;
      this.cancelDrawing();
    }

    this.cdr.detectChanges();
  }

  /**
   * Handle Tab key in dimension input (cycle through fields)
   */
  onDimensionInputTab(event: KeyboardEvent): void {
    // Allow default tab behavior between fields
  }

  /**
   * Apply dimension input and finish drawing
   */
  applyDimensionInput(): void {
    if (!this.drawingStartPoint) {
      this.closeDimensionInput(true);
      return;
    }

    if (this.drawingMode === 'rectangle') {
      const width = this.dimensionInputWidth || 10;
      const height = this.dimensionInputHeight || 10;

      // Calculate end point based on start point and dimensions
      this.drawingCurrentPoint = {
        x: this.drawingStartPoint.x + width,
        y: this.drawingStartPoint.y + height,
        z: this.drawingStartPoint.z
      };

      this.closeDimensionInput(false);
      this.finishDrawing();
    } else if (this.drawingMode === 'circle' || this.drawingMode === 'polygon') {
      const diameter = this.dimensionInputDiameter || 20;
      const radius = diameter / 2;

      // Calculate end point based on start point and radius
      this.drawingCurrentPoint = {
        x: this.drawingStartPoint.x + radius,
        y: this.drawingStartPoint.y,
        z: this.drawingStartPoint.z
      };

      this.closeDimensionInput(false);
      this.finishDrawing();
    } else if (this.drawingMode === 'slot') {
      const length = this.dimensionInputWidth || 20;

      // If we don't have a slot start point yet, set current point
      if (!this.slotDrawingState.startPoint) {
        this.slotDrawingState.startPoint = this.drawingStartPoint;
      }

      // Calculate end point based on start point and length (horizontal)
      this.slotDrawingState.endPoint = {
        x: this.slotDrawingState.startPoint.x + length,
        y: this.slotDrawingState.startPoint.y,
        z: this.slotDrawingState.startPoint.z
      };

      this.closeDimensionInput(false);
      this.finishSlotDrawing();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSTRAINT VISUALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Set constraint mode for drawing
   */
  setConstraintMode(mode: 'none' | 'horizontal' | 'vertical' | 'parallel' | 'perpendicular'): void {
    this.constraintMode = mode;

    if (mode === 'parallel' || mode === 'perpendicular') {
      // For parallel/perpendicular, need a reference edge
      // Auto-detect from nearest edge if drawing
      if (this.linePoints.length > 0) {
        const lastPoint = this.linePoints[this.linePoints.length - 1];
        const nearestEdge = this.snapEngine.findNearestEdge(new Vector3(lastPoint.x, lastPoint.y, lastPoint.z));
        if (nearestEdge) {
          this.snapEngine.setReferenceEdge(
            new Vector3(nearestEdge.start[0], nearestEdge.start[1], nearestEdge.start[2]),
            new Vector3(nearestEdge.end[0], nearestEdge.end[1], nearestEdge.end[2])
          );
          this.snapEngine.setConstraintMode(mode);
          this.activeConstraintType = mode;
        } else {
          this.activeConstraintType = null;
          this.constraintMode = 'none';
        }
      }
    } else if (mode === 'horizontal' || mode === 'vertical') {
      // For H/V, just set the mode
      this.activeConstraintType = mode;
      this.snapEngine.setConstraintMode('none'); // Clear any parallel/perpendicular
    } else {
      this.clearConstraintMode();
      return; // clearConstraintMode handles visualization and detection
    }

    this.updateConstraintVisualization();
    this.cdr.detectChanges();
  }

  /**
   * Clear active constraint
   */
  clearConstraintMode(): void {
    this.constraintMode = 'none';
    this.snapEngine.clearReferenceEdge();
    this.activeConstraintType = null;
    this.hideConstraintGuideLine();
    this.cdr.detectChanges();
  }

  /**
   * Update constraint guide line visualization
   */
  private updateConstraintVisualization(): void {
    this.hideConstraintGuideLine();

    if (!this.activeConstraintType || !this.engine || this.linePoints.length === 0) {
      return;
    }

    const lastPoint = this.linePoints[this.linePoints.length - 1];
    const startPos = new Vector3(lastPoint.x, lastPoint.y, lastPoint.z);

    // Determine direction and color based on constraint type
    let direction: Vector3;
    let color: number;
    const lineLength = 500; // Extended line length

    switch (this.activeConstraintType) {
      case 'horizontal':
        direction = new Vector3(1, 0, 0);
        color = 0xff00ff; // Magenta
        break;
      case 'vertical':
        direction = new Vector3(0, 1, 0);
        color = 0xff00ff; // Magenta
        break;
      case 'parallel':
        // Get reference direction from snap engine
        const refDir = this.snapEngine.getReferenceDirection();
        if (refDir) {
          direction = refDir.clone();
        } else {
          return;
        }
        color = 0xff8800; // Orange
        break;
      case 'perpendicular':
        const perpRef = this.snapEngine.getReferenceDirection();
        if (perpRef) {
          // Perpendicular in 2D: rotate 90 degrees
          direction = new Vector3(-perpRef.y, perpRef.x, 0).normalize();
        } else {
          return;
        }
        color = 0x8800ff; // Purple
        break;
      default:
        return;
    }

    // Create line geometry extending in both directions
    const positions = new Float32Array([
      startPos.x - direction.x * lineLength,
      startPos.y - direction.y * lineLength,
      startPos.z - direction.z * lineLength,
      startPos.x + direction.x * lineLength,
      startPos.y + direction.y * lineLength,
      startPos.z + direction.z * lineLength
    ]);

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));

    const material = new LineBasicMaterial({
      color,
      linewidth: 1,
      depthTest: false,
      transparent: true,
      opacity: 0.6
    });

    this.constraintGuideLine = new LineSegments(geometry, material);
    this.constraintGuideLine.renderOrder = 998;
    this.engine.scene.add(this.constraintGuideLine);
  }

  /**
   * Hide constraint guide line
   */
  private hideConstraintGuideLine(): void {
    if (this.constraintGuideLine && this.engine) {
      this.engine.scene.remove(this.constraintGuideLine);
      this.constraintGuideLine.geometry?.dispose();
      if (this.constraintGuideLine.material instanceof LineBasicMaterial) {
        this.constraintGuideLine.material.dispose();
      }
      this.constraintGuideLine = null;
    }
  }

  /**
   * Toggle keyboard shortcuts panel
   */
  toggleKeyboardShortcuts(): void {
    this.showKeyboardShortcuts = !this.showKeyboardShortcuts;
    this.cdr.detectChanges();
  }

  /**
   * Finish drawing and create the shape
   */
  private async finishDrawing(): Promise<void> {
    // Guard against multiple calls
    if (this.isProcessing) {
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // SKETCH MODE: Create entities instead of 3D shapes
    // ═══════════════════════════════════════════════════════════════════════
    if (this.isSketchMode && this.activeSketch) {
      this.finishDrawingAsSketchEntity();
      return;
    }

    let points2D: number[][] = [];

    if (this.drawingMode === 'rectangle') {
      const rectPoints = this.getRectanglePoints();
      if (rectPoints.length < 4) {
        this.cancelDrawing();
        return;
      }
      points2D = rectPoints.map((p) => [p.x, p.y]);
    } else if (this.drawingMode === 'circle') {
      const radius = this.getCircleRadius();
      if (radius < 1) {
        this.cancelDrawing();
        return;
      }

      // Get center in 2D plane coordinates
      let cx: number, cy: number;

      switch (this.sketchPlane) {
        case 'XY':
          cx = this.drawingStartPoint!.x;
          cy = this.drawingStartPoint!.y;
          break;
        case 'XZ':
          cx = this.drawingStartPoint!.x;
          cy = this.drawingStartPoint!.z;
          break;
        case 'YZ':
          cx = this.drawingStartPoint!.y;
          cy = this.drawingStartPoint!.z;
          break;
        case 'custom':
          const center2D = this.worldToPlaneLocal(this.drawingStartPoint!);
          cx = center2D.x;
          cy = center2D.y;
          break;
        default:
          this.cancelDrawing();
          return;
      }

      // If in boss/pocket mode, use true CAD circle (not segmented polygon)
      if (this.isExtrudeOnFaceMode && this.selectedFaceInfo && this.selectedCADShapeIds.size === 1) {
        // Store circle data for true CAD cylinder boss/pocket
        this.extrudeOnFaceCircle = { centerX: cx, centerY: cy, radius: radius };
        // Create minimal polygon points just for preview (the actual operation will use the circle data)
        const segments = 64;
        for (let i = 0; i < segments; i++) {
          const angle = (i / segments) * Math.PI * 2;
          points2D.push([cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius]);
        }
        // Continue to boss/pocket handling below (preview will use polygon, but apply will use circle)
      } else {
        // Use extrudeCircle for smooth cylinder (not polygon) - regular mode
        try {
          this.isProcessing = true;
          this.processingMessage = 'Creating circle...';

          await this.initializeKernel();

          const cadShape = await this.cadKernel.extrudeCircle({
            centerX: cx,
            centerY: cy,
            radius: radius,
            height: this.extrudeHeight,
            sketchPlane: this.sketchPlane as 'XY' | 'XZ' | 'YZ',
            planeOffset: this.sketchPlaneOffset,
            customPlane:
              this.sketchPlane === 'custom'
                ? {
                    origin: this.customPlaneOrigin,
                    normal: this.customPlaneNormal,
                    xAxis: this.customPlaneXAxis,
                    yAxis: this.customPlaneYAxis
                  }
                : undefined
          });

          cadShape.name = `Circle_${this.cadShapes.length + 1}`;
          cadShape.color = '#6366f1';

          // Add to scene
          this.cadShapes.push(cadShape);
          this.addShapeToScene(cadShape);

          // Select the new shape
          this.selectedCADShapeIds.clear();
          this.selectedCADShapeIds.add(cadShape.id);
          this.updateShapeVisualState(cadShape.id, true);

          this.processingMessage = '';
        } catch (error: any) {
          this.onError.emit(`Failed to create circle: ${error.message}`);
        } finally {
          this.isProcessing = false;
          this.cancelDrawing();
          this.cdr.detectChanges();
        }
        return; // Early return - circle is handled in regular mode
      }
    } else if (this.drawingMode === 'line') {
      if (this.linePoints.length < 3) {
        this.cancelDrawing();
        return;
      }

      // Convert 3D points to 2D based on plane
      for (const pt of this.linePoints) {
        switch (this.sketchPlane) {
          case 'XY':
            points2D.push([pt.x, pt.y]);
            break;
          case 'XZ':
            points2D.push([pt.x, pt.z]);
            break;
          case 'YZ':
            points2D.push([pt.y, pt.z]);
            break;
          case 'custom':
            const pt2D = this.worldToPlaneLocal(pt);
            points2D.push([pt2D.x, pt2D.y]);
            break;
        }
      }
    } else if (this.drawingMode === 'polygon') {
      const polyPoints = this.getPolygonPoints();
      if (polyPoints.length < 3) {
        this.cancelDrawing();
        return;
      }
      points2D = polyPoints.map((p) => [p.x, p.y]);
    } else if (this.drawingMode === 'arc') {
      // Handle arc in boss/pocket mode
      if (!this.arcDrawingState.startPoint || !this.arcDrawingState.midPoint || !this.arcDrawingState.endPoint) {
        this.cancelDrawing();
        return;
      }

      const arcPoints = this.calculateArcPoints(
        this.arcDrawingState.startPoint,
        this.arcDrawingState.midPoint,
        this.arcDrawingState.endPoint
      );

      if (arcPoints.length < 3) {
        this.cancelDrawing();
        return;
      }

      points2D = arcPoints.map((p) => {
        switch (this.sketchPlane) {
          case 'XY':
            return [p.x, p.y];
          case 'XZ':
            return [p.x, p.z];
          case 'YZ':
            return [p.y, p.z];
          case 'custom':
            const pt2D = this.worldToPlaneLocal({ x: p.x, y: p.y, z: p.z });
            return [pt2D.x, pt2D.y];
          default:
            return [p.x, p.y];
        }
      });
    } else if (this.drawingMode === 'slot') {
      // Handle slot in boss/pocket mode
      const slotShape = this.getSlotShape();
      if (!slotShape) {
        this.cancelDrawing();
        return;
      }

      const rawPoints: number[][] = [];
      const path = slotShape.getPoints(32);

      for (const p of path) {
        rawPoints.push([p.x, p.y]);
      }

      // Remove duplicate/near-duplicate points
      const tolerance = 0.01;
      for (const pt of rawPoints) {
        if (points2D.length === 0) {
          points2D.push(pt);
        } else {
          const last = points2D[points2D.length - 1];
          const dist = Math.sqrt((pt[0] - last[0]) ** 2 + (pt[1] - last[1]) ** 2);
          if (dist > tolerance) {
            points2D.push(pt);
          }
        }
      }

      // Check first/last duplicate
      if (points2D.length > 1) {
        const first = points2D[0];
        const last = points2D[points2D.length - 1];
        const dist = Math.sqrt((first[0] - last[0]) ** 2 + (first[1] - last[1]) ** 2);
        if (dist < tolerance) {
          points2D.pop();
        }
      }
    }

    if (points2D.length < 3) {
      this.cancelDrawing();
      return;
    }

    // Create extrudable shape and add to scene
    try {
      this.isProcessing = true;

      await this.initializeKernel();

      // Check if we're in boss/pocket mode
      if (this.isExtrudeOnFaceMode && this.selectedFaceInfo && this.selectedCADShapeIds.size === 1) {
        // Boss/Pocket: Enter preview mode instead of immediately creating
        // Store the profile points for later use
        this.extrudeOnFaceProfile = points2D;

        // Enter preview mode with draggable height adjustment
        this.enterBossPocketPreviewMode();

        // Clear drawing state but keep boss/pocket mode active
        this.drawingStartPoint = null;
        this.linePoints = [];
        this.drawingCurrentPoint = null;
        this.isDrawing = false;
        this.drawingMode = 'none';
        this.clearDrawingPreview();
        this.hideSnapFeedback();
        this.clearDrawingDimensions();

        this.isProcessing = false;
        this.processingMessage = '';
        this.cdr.detectChanges();
        return; // Don't continue to regular extrusion flow
      } else {
        // Regular extrusion: Create new standalone shape
        this.processingMessage = 'Creating shape...';

        // Extrude with default height on the correct sketch plane
        const params: ExtrudeParams = {
          points: points2D,
          height: this.extrudeHeight,
          sketchPlane: this.sketchPlane === 'custom' ? undefined : (this.sketchPlane as 'XY' | 'XZ' | 'YZ'),
          planeOffset: this.sketchPlaneOffset,
          customPlane:
            this.sketchPlane === 'custom'
              ? {
                  origin: this.customPlaneOrigin,
                  normal: this.customPlaneNormal,
                  xAxis: this.customPlaneXAxis,
                  yAxis: this.customPlaneYAxis
                }
              : undefined
        };

        const cadShape = await this.cadKernel.extrudePolygon(params);
        cadShape.name = `${this.drawingMode.charAt(0).toUpperCase() + this.drawingMode.slice(1)}_${this.cadShapes.length + 1}`;
        cadShape.color = '#6366f1';

        // Add to scene
        this.cadShapes.push(cadShape);
        this.addShapeToScene(cadShape);

        // Select the new shape
        this.selectedCADShapeIds.clear();
        this.selectedCADShapeIds.add(cadShape.id);
        this.updateShapeVisualState(cadShape.id, true);
      }

      this.processingMessage = '';
    } catch (error: any) {
      this.onError.emit(`Failed to create shape: ${error.message}`);
    } finally {
      this.isProcessing = false;
      this.cancelDrawing();
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SNAP SETTINGS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Toggle snap to edges
   */
  toggleSnapToEdges(): void {
    this.snapToEdges = !this.snapToEdges;
    this.snapEngine.setConfig({
      edgeSnapDistance: this.snapToEdges ? 3 : 0
    });
  }

  /**
   * Toggle snap enabled
   */
  toggleSnap(): void {
    this.snapEnabled = !this.snapEnabled;
    this.snapEngine.setEnabled(this.snapEnabled);
  }

  /**
   * Toggle parallel constraint
   */
  toggleParallelConstraint(): void {
    this.setConstraintMode(this.constraintMode === 'parallel' ? 'none' : 'parallel');
  }

  /**
   * Toggle perpendicular constraint
   */
  togglePerpendicularConstraint(): void {
    this.setConstraintMode(this.constraintMode === 'perpendicular' ? 'none' : 'perpendicular');
  }

  /**
   * Schedule a debounced snap vertices update.
   * Batches multiple rapid calls into a single update to avoid O(N²) on bulk imports.
   */
  private scheduleSnapUpdate(): void {
    if (this.snapUpdateTimer) {
      clearTimeout(this.snapUpdateTimer);
    }
    this.snapUpdateTimer = setTimeout(() => {
      this.snapUpdateTimer = null;
      this.updateSnapVertices();
    }, 50); // 50ms debounce - fast enough to feel responsive, slow enough to batch
  }

  /**
   * Update snap engine with CAD shape vertices for snapping
   */
  private updateSnapVertices(): void {
    const shapesForSnap = this.cadShapes
      .filter((s) => this.sceneMeshes.has(s.id));

    const signatureParts: string[] = [];
    for (const shape of shapesForSnap) {
      const group = this.sceneMeshes.get(shape.id);
      if (!group) continue;
      group.updateWorldMatrix(true, false);
      const rawExtractedEdges = group.userData?.['extractedEdges'] || [];
      const rawExtractedFaces = group.userData?.['extractedFaces'] || [];
      const geometryCenter = group.userData?.['geometryCenter'] as Vector3 | undefined;
      const centerSignature = geometryCenter
        ? `${geometryCenter.x.toFixed(4)},${geometryCenter.y.toFixed(4)},${geometryCenter.z.toFixed(4)}`
        : 'none';
      const matrixSignature = group.matrixWorld.elements.map((val) => val.toFixed(5)).join(',');
      signatureParts.push(`${shape.id}|${rawExtractedEdges.length}|${rawExtractedFaces.length}|${centerSignature}|${matrixSignature}`);
    }

    const signature = signatureParts.join('||');
    if (signature === this.snapDataSignature) {
      return;
    }
    this.snapDataSignature = signature;

    const shapesWithData = shapesForSnap
      .map((s) => {
        const group = this.sceneMeshes.get(s.id);
        if (!group) {
          return null;
        }
        const rawExtractedEdges = group?.userData?.['extractedEdges'] || [];
        const rawExtractedFaces = group?.userData?.['extractedFaces'] || [];

        // Get the geometry center offset (the extracted data is in local coordinates)
        const geometryCenter = group?.userData?.['geometryCenter'] as Vector3 | undefined;
        group?.updateWorldMatrix(true, false);

        // Transform extracted edges to world coordinates
        // Edge points are in OCCT coordinates; the mesh is offset by -center in group-local space.
        // Apply the center offset and then the group's world matrix to capture translation/rotation/scale.
        const extractedEdges = rawExtractedEdges.map((edge: { edgeIndex: number; points: number[] | number[][] }) => {
          const edgePoints = this.normalizeEdgePoints(edge.points);
          const transformedPoints = edgePoints.map((pt) => {
            // Transform from local to world: subtract geometry center, then apply group transform
            const worldPt = new Vector3(pt[0], pt[1], pt[2]);
            if (geometryCenter) {
              worldPt.sub(geometryCenter);
            }
            worldPt.applyMatrix4(group.matrixWorld);

            return [worldPt.x, worldPt.y, worldPt.z] as [number, number, number];
          });
          return {
            edgeIndex: edge.edgeIndex,
            points: transformedPoints
          };
        });

        // Transform face centers to world coordinates
        const extractedFaces = rawExtractedFaces.map((face: { center: [number, number, number] }) => {
          if (face.center) {
            const worldCenter = new Vector3(face.center[0], face.center[1], face.center[2]);
            if (geometryCenter) {
              worldCenter.sub(geometryCenter);
            }
            worldCenter.applyMatrix4(group.matrixWorld);
            return {
              ...face,
              center: [worldCenter.x, worldCenter.y, worldCenter.z] as [number, number, number]
            };
          }
          return face;
        });

        // Calculate bounding box from the group (already in world coordinates)
        let boundingBox: { min: [number, number, number]; max: [number, number, number] } | undefined;
        if (group) {
          const box = new Box3().setFromObject(group);
          if (!box.isEmpty()) {
            boundingBox = {
              min: [box.min.x, box.min.y, box.min.z],
              max: [box.max.x, box.max.y, box.max.z]
            };
          }
        }

        return {
          id: s.id,
          extractedEdges,
          extractedFaces,
          boundingBox
        };
      })
      .filter((shape): shape is NonNullable<typeof shape> => shape !== null);

    this.snapEngine.loadCADShapeVertices(shapesWithData);
  }

  private normalizeEdgePoints(points: number[] | number[][]): number[][] {
    if (points.length === 0) {
      return [];
    }

    if (Array.isArray(points[0])) {
      return points as number[][];
    }

    const flatPoints = points as number[];
    const normalized: number[][] = [];
    for (let i = 0; i < flatPoints.length - 2; i += 3) {
      normalized.push([flatPoints[i], flatPoints[i + 1], flatPoints[i + 2]]);
    }
    return normalized;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CAD SHAPE SELECTION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Toggle CAD shape selection (used internally for Ctrl+click multi-select)
   */
  toggleCADShapeSelection(id: string): void {
    if (this.selectedCADShapeIds.has(id)) {
      this.selectedCADShapeIds.delete(id);
      // Remove from boolean order
      this.booleanSelectionOrder = this.booleanSelectionOrder.filter((i) => i !== id);
      // Clean up arrow if we deselected a surface
      this.cleanupThickenDirectionArrow();
    } else {
      this.selectedCADShapeIds.add(id);
      // Add to boolean order (tracks selection sequence)
      this.booleanSelectionOrder.push(id);
    }
    this.cdr.detectChanges();
  }

  /**
   * Select CAD shape from bodies list - single select by default, multi-select with Ctrl
   */
  selectCADShapeFromList(id: string, event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey) {
      // Multi-select with Ctrl/Cmd
      this.toggleCADShapeSelection(id);
    } else {
      // Single select - clear others first
      this.selectedCADShapeIds.clear();
      this.booleanSelectionOrder = [];
      this.selectedCADShapeIds.add(id);
      this.booleanSelectionOrder.push(id);
      // Clean up arrow when changing selection
      this.cleanupThickenDirectionArrow();
    }

    // Update visual states for all shapes
    this.sceneMeshes.forEach((_, shapeId) => {
      this.updateShapeVisualState(shapeId, this.selectedCADShapeIds.has(shapeId));
    });

    // Attach/detach transform controls (supports both single and multi-selection)
    if (this.selectedCADShapeIds.size > 0) {
      this.attachTransformControls();
    } else {
      this.detachTransformControls();
    }

    // Update measurements
    this.updateMeasurements();

    this.cdr.detectChanges();
  }

  /**
   * Handle hover on body list item - highlight body in scene
   */
  onBodyListItemHover(shapeId: string | null): void {
    this.updateShapeHoverState(shapeId);
  }

  /**
   * Check if CAD shape is selected
   */
  isCADShapeSelected(id: string): boolean {
    return this.selectedCADShapeIds.has(id);
  }

  /**
   * Clear CAD shape selection
   */
  clearCADSelection(): void {
    this.selectedCADShapeIds.clear();
    this.booleanSelectionOrder = [];
    this.cdr.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SHAPE RENAMING
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start editing shape name
   */
  startRenameShape(shapeId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const shape = this.cadShapes.find((s) => s.id === shapeId);
    if (shape) {
      this.renamingShapeId = shapeId;
      this.renamingShapeName = shape.name || `Shape ${this.cadShapes.indexOf(shape) + 1}`;
      this.cdr.detectChanges();

      // Focus input after render
      setTimeout(() => {
        const input = document.querySelector('.shape-name-input') as HTMLInputElement;
        if (input) {
          input.focus();
          input.select();
        }
      }, 10);
    }
  }

  /**
   * Save edited shape name
   */
  saveShapeName(): void {
    if (this.renamingShapeId && this.renamingShapeName.trim()) {
      const shape = this.cadShapes.find((s) => s.id === this.renamingShapeId);
      if (shape) {
        shape.name = this.renamingShapeName.trim();
        this.hasUnsavedChanges = true;
      }
    }
    this.cancelRenameShape();
  }

  /**
   * Cancel shape rename
   */
  cancelRenameShape(): void {
    this.renamingShapeId = null;
    this.renamingShapeName = '';
    this.cdr.detectChanges();
  }

  /**
   * Toggle visibility of a CAD shape
   */
  toggleShapeVisibility(shapeId: string, event: Event): void {
    event.stopPropagation();
    const shape = this.cadShapes.find((s) => s.id === shapeId);
    if (shape) {
      shape.isVisible = shape.isVisible === false ? true : false;
      
      // Update the Three.js mesh visibility
      const meshGroup = this.sceneMeshes.get(shapeId);
      if (meshGroup) {
        meshGroup.visible = shape.isVisible;
      }
      
      this.hasUnsavedChanges = true;
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CONTEXT MENU
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Show context menu on right-click
   */
  onShapeContextMenu(event: MouseEvent, shapeId: string): void {
    event.preventDefault();
    event.stopPropagation();

    // Select the shape if not already selected
    if (!this.selectedCADShapeIds.has(shapeId)) {
      this.selectedCADShapeIds.clear();
      this.booleanSelectionOrder = [];
      this.selectedCADShapeIds.add(shapeId);
      this.booleanSelectionOrder.push(shapeId);
      this.sceneMeshes.forEach((_, id) => {
        this.updateShapeVisualState(id, this.selectedCADShapeIds.has(id));
      });
    }

    this.contextMenuTarget = shapeId;
    this.contextMenuPosition = { x: event.clientX, y: event.clientY };
    this.showContextMenu = true;
    this.cdr.detectChanges();
  }

  /**
   * Close context menu
   */
  closeContextMenu(): void {
    this.showContextMenu = false;
    this.contextMenuTarget = null;
    this.cdr.detectChanges();
  }

  /**
   * Handle context menu action
   */
  onContextMenuAction(action: string): void {
    const shapeId = this.contextMenuTarget;
    this.closeContextMenu();

    if (!shapeId) return;

    switch (action) {
      case 'rename':
        this.startRenameShape(shapeId);
        break;
      case 'duplicate':
        this.duplicateSelected();

        break;
      case 'delete':
        this.confirmDeleteSelected();
        break;
      case 'copy':
        this.copySelected();

        break;
      case 'isolate':
        this.isolateShape(shapeId);

        break;
    }
  }

  /**
   * Isolate shape (hide all others)
   */
  isolateShape(shapeId: string): void {
    this.sceneMeshes.forEach((group, id) => {
      group.visible = id === shapeId;
    });
    this.isIsolateMode = true;
    this.isolatedShapeId = shapeId;
    this.cdr.detectChanges();
  }

  /**
   * Show all shapes (exit isolation mode)
   */
  showAllShapes(): void {
    this.sceneMeshes.forEach((group) => {
      group.visible = true;
    });
    this.isIsolateMode = false;
    this.isolatedShapeId = null;
    this.cdr.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION VIEW / CLIPPING PLANES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Toggle section view on/off
   */
  toggleSectionView(): void {
    this.sectionViewEnabled = !this.sectionViewEnabled;

    if (this.sectionViewEnabled) {
      this.initSectionPlane();
      this.updateSectionPlaneBounds();
    } else {
      this.disableSectionPlane();
    }

    this.cdr.detectChanges();
  }

  /**
   * Initialize section plane
   */
  private initSectionPlane(): void {
    if (!this.engine) return;

    // Enable local clipping on renderer
    this.engine.renderer.localClippingEnabled = true;

    // Create the clipping plane
    const normal = this.getSectionPlaneNormal();
    this.sectionPlane = new Plane(normal, -this.sectionPlanePosition);

    // Create hatching texture for stencil
    this.createHatchTexture();

    // Apply clipping plane to CAD meshes and edges only
    this.applyClippingToCADGroup();

    // Create stencil group for hatched cross-section visualization
    this.createSectionStencil();

    // Create visual helper for the section plane boundary
    this.createSectionPlaneHelper();
  }

  /**
   * Get section plane normal based on axis
   */
  private getSectionPlaneNormal(): Vector3 {
    switch (this.sectionPlaneAxis) {
      case 'x':
        return new Vector3(1, 0, 0);
      case 'y':
        return new Vector3(0, 1, 0);
      case 'z':
        return new Vector3(0, 0, 1);
    }
  }

  /**
   * Create diagonal hatching texture
   */
  private createHatchTexture(): void {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    // Light gray background for cross-section
    ctx.fillStyle = '#d0d0d0';
    ctx.fillRect(0, 0, size, size);

    // Draw diagonal orange lines
    ctx.strokeStyle = '#ff6600';
    ctx.lineWidth = 2;
    ctx.lineCap = 'square';

    // Draw diagonal hatching pattern (45 degrees)
    const spacing = 8;
    for (let i = -size * 2; i < size * 2; i += spacing) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + size, size);
      ctx.stroke();
    }

    this.sectionHatchTexture = new CanvasTexture(canvas);
    this.sectionHatchTexture.wrapS = RepeatWrapping;
    this.sectionHatchTexture.wrapT = RepeatWrapping;
    this.sectionHatchTexture.repeat.set(10, 10);
  }

  /**
   * Apply clipping plane to CAD group only (meshes and edges)
   */
  private applyClippingToCADGroup(): void {
    if (!this.sectionPlane) return;

    // Only apply clipping to objects inside sceneMeshes (CAD shapes)
    this.sceneMeshes.forEach((group) => {
      group.traverse((obj) => {
        // Apply to meshes
        if (obj instanceof Mesh && obj.material) {
          const material = obj.material as MeshPhysicalMaterial;
          material.clippingPlanes = [this.sectionPlane!];
          material.clipShadows = true;
          material.needsUpdate = true;
        }
        // Apply to edges (LineSegments)
        if (obj instanceof LineSegments && obj.material) {
          const material = obj.material as LineBasicMaterial;
          material.clippingPlanes = [this.sectionPlane!];
          material.needsUpdate = true;
        }
        // Apply to Line2 (fat lines)
        if (obj instanceof Line2 && obj.material) {
          const material = obj.material as LineMaterial;
          material.clippingPlanes = [this.sectionPlane!];
          material.needsUpdate = true;
        }
      });
    });
  }

  /**
   * Remove clipping plane from CAD group
   */
  private removeClippingFromCADGroup(): void {
    this.sceneMeshes.forEach((group) => {
      group.traverse((obj) => {
        if (obj instanceof Mesh && obj.material) {
          const material = obj.material as MeshPhysicalMaterial;
          material.clippingPlanes = [];
          material.needsUpdate = true;
        }
        if (obj instanceof LineSegments && obj.material) {
          const material = obj.material as LineBasicMaterial;
          material.clippingPlanes = [];
          material.needsUpdate = true;
        }
        if (obj instanceof Line2 && obj.material) {
          const material = obj.material as LineMaterial;
          material.clippingPlanes = [];
          material.needsUpdate = true;
        }
      });
    });
  }

  /**
   * Create stencil group - clone meshes and render back faces with hatching
   * This shows the diagonal hatching only on the actual cross-section of the mesh
   */
  private createSectionStencil(): void {
    if (!this.engine || !this.sectionPlane || !this.sectionHatchTexture) return;

    this.removeSectionStencil();

    this.sectionStencilGroup = new Group();
    this.sectionStencilGroup.name = 'SectionStencilGroup';

    // UV scale for hatching pattern (units per texture repeat)
    const uvScale = 0.05;

    // For each CAD shape, clone the mesh geometry and render back faces with hatching
    this.sceneMeshes.forEach((group) => {
      group.traverse((obj) => {
        if (obj instanceof Mesh && obj.geometry && obj.name !== 'EdgesPlaceholder') {
          // Clone the geometry
          const clonedGeometry = obj.geometry.clone();

          // Generate world-space UVs for proper hatching pattern
          this.generateWorldSpaceUVs(clonedGeometry, obj, uvScale);

          // Create hatching material for back faces
          const hatchMaterial = new MeshBasicMaterial({
            map: this.sectionHatchTexture,
            side: BackSide, // Only render back faces (the cut surface)
            clippingPlanes: [this.sectionPlane!],
            transparent: false,
            depthWrite: true
          });

          const hatchMesh = new Mesh(clonedGeometry, hatchMaterial);

          // Copy world transform from original mesh
          obj.updateWorldMatrix(true, false);
          hatchMesh.applyMatrix4(obj.matrixWorld);

          hatchMesh.renderOrder = 1;
          this.sectionStencilGroup!.add(hatchMesh);
        }
      });
    });

    this.engine.scene.add(this.sectionStencilGroup);
  }

  /**
   * Generate world-space UV coordinates for hatching texture
   */
  private generateWorldSpaceUVs(geometry: BufferGeometry, mesh: Mesh, scale: number): void {
    const positionAttr = geometry.getAttribute('position');
    if (!positionAttr) return;

    const uvs: number[] = [];
    const tempPos = new Vector3();

    // Get world matrix for transforming positions
    mesh.updateWorldMatrix(true, false);

    for (let i = 0; i < positionAttr.count; i++) {
      tempPos.set(
        positionAttr.getX(i),
        positionAttr.getY(i),
        positionAttr.getZ(i)
      );

      // Transform to world space
      tempPos.applyMatrix4(mesh.matrixWorld);

      // Project UVs based on section plane axis
      let u: number, v: number;
      switch (this.sectionPlaneAxis) {
        case 'x':
          u = tempPos.y * scale;
          v = tempPos.z * scale;
          break;
        case 'y':
          u = tempPos.x * scale;
          v = tempPos.z * scale;
          break;
        case 'z':
        default:
          u = tempPos.x * scale;
          v = tempPos.y * scale;
          break;
      }

      uvs.push(u, v);
    }

    geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
  }

  /**
   * Remove section stencil group
   */
  private removeSectionStencil(): void {
    if (this.sectionStencilGroup && this.engine) {
      this.sectionStencilGroup.traverse((obj) => {
        if (obj instanceof Mesh) {
          obj.geometry.dispose();
          if (obj.material instanceof MeshBasicMaterial) {
            obj.material.dispose();
          }
        }
      });
      this.engine.scene.remove(this.sectionStencilGroup);
      this.sectionStencilGroup = null;
    }
  }

  /**
   * Create visual helper for section plane (border outline)
   */
  private createSectionPlaneHelper(): void {
    if (!this.engine) return;

    this.removeSectionPlaneHelper();

    // Create a semi-transparent plane to visualize the section boundary
    const size = 200;
    const geometry = new PlaneGeometry(size, size);
    const material = new MeshBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.08,
      side: DoubleSide,
      depthWrite: false
    });

    this.sectionPlaneHelper = new Mesh(geometry, material);
    this.sectionPlaneHelper.renderOrder = 999;

    this.updateSectionPlaneHelperTransform();
    this.engine.scene.add(this.sectionPlaneHelper);
  }

  /**
   * Update section plane helper position and rotation
   */
  private updateSectionPlaneHelperTransform(): void {
    if (!this.sectionPlaneHelper) return;

    // Position based on axis and position
    switch (this.sectionPlaneAxis) {
      case 'x':
        this.sectionPlaneHelper.position.set(this.sectionPlanePosition, 0, 0);
        this.sectionPlaneHelper.rotation.set(0, Math.PI / 2, 0);
        break;
      case 'y':
        this.sectionPlaneHelper.position.set(0, this.sectionPlanePosition, 0);
        this.sectionPlaneHelper.rotation.set(Math.PI / 2, 0, 0);
        break;
      case 'z':
        this.sectionPlaneHelper.position.set(0, 0, this.sectionPlanePosition);
        this.sectionPlaneHelper.rotation.set(0, 0, 0);
        break;
    }
  }

  /**
   * Update section stencil - with the cloned mesh approach,
   * position updates are automatic via shared clipping plane reference
   */
  private updateSectionStencilPositions(): void {
    // The clipping plane is shared by reference, so position updates
    // are automatically reflected in the hatch meshes
    // No action needed here
  }

  /**
   * Remove section plane helper
   */
  private removeSectionPlaneHelper(): void {
    if (this.sectionPlaneHelper && this.engine) {
      this.engine.scene.remove(this.sectionPlaneHelper);
      this.sectionPlaneHelper.geometry.dispose();
      (this.sectionPlaneHelper.material as MeshBasicMaterial).dispose();
      this.sectionPlaneHelper = null;
    }
  }

  /**
   * Disable section plane
   */
  private disableSectionPlane(): void {
    if (!this.engine) return;

    this.removeClippingFromCADGroup();
    this.removeSectionStencil();
    this.removeSectionPlaneHelper();

    // Dispose hatch texture
    if (this.sectionHatchTexture) {
      this.sectionHatchTexture.dispose();
      this.sectionHatchTexture = null;
    }

    this.engine.renderer.localClippingEnabled = false;
    this.sectionPlane = null;
  }

  /**
   * Update section plane position
   */
  onSectionPlanePositionChange(position: number): void {
    this.sectionPlanePosition = position;

    if (this.sectionPlane) {
      // Update clipping plane constant (negative of position along normal)
      this.sectionPlane.constant = -position;
    }

    this.updateSectionPlaneHelperTransform();
    this.updateSectionStencilPositions();
  }

  /**
   * Change section plane axis
   */
  onSectionPlaneAxisChange(axis: 'x' | 'y' | 'z'): void {
    this.sectionPlaneAxis = axis;
    this.updateSectionPlaneBounds();

    if (this.sectionPlane) {
      // Update normal and recalculate constant
      this.sectionPlane.normal.copy(this.getSectionPlaneNormal());
      this.sectionPlane.constant = -this.sectionPlanePosition;
    }

    this.updateSectionPlaneHelperTransform();

    // Recreate stencil for new axis orientation
    if (this.sectionViewEnabled) {
      this.createSectionStencil();
    }
  }

  /**
   * Calculate section plane bounds based on scene content
   */
  private updateSectionPlaneBounds(): void {
    if (this.sceneMeshes.size === 0) {
      this.sectionPlaneMin = -100;
      this.sectionPlaneMax = 100;
      this.sectionPlanePosition = 0;
      return;
    }

    // Calculate bounding box of all meshes
    const box = new Box3();
    this.sceneMeshes.forEach((group) => {
      const groupBox = new Box3().setFromObject(group);
      box.union(groupBox);
    });

    // Set bounds based on axis
    switch (this.sectionPlaneAxis) {
      case 'x':
        this.sectionPlaneMin = box.min.x - 10;
        this.sectionPlaneMax = box.max.x + 10;
        break;
      case 'y':
        this.sectionPlaneMin = box.min.y - 10;
        this.sectionPlaneMax = box.max.y + 10;
        break;
      case 'z':
        this.sectionPlaneMin = box.min.z - 10;
        this.sectionPlaneMax = box.max.z + 10;
        break;
    }

    // Center the slider initially
    this.sectionPlanePosition = (this.sectionPlaneMin + this.sectionPlaneMax) / 2;

    // Update plane if active
    if (this.sectionPlane) {
      this.sectionPlane.constant = -this.sectionPlanePosition;
    }
  }

  /**
   * Get the body shape for boolean operations (first selected)
   */
  getBooleanBodyShape(): CADShape | null {
    if (this.booleanSelectionOrder.length === 0) return null;
    return this.cadShapes.find((s) => s.id === this.booleanSelectionOrder[0]) || null;
  }

  /**
   * Get the tool shape for boolean operations (second selected)
   */
  getBooleanToolShape(): CADShape | null {
    if (this.booleanSelectionOrder.length < 2) return null;
    return this.cadShapes.find((s) => s.id === this.booleanSelectionOrder[1]) || null;
  }

  /**
   * Swap body and tool for boolean operations
   */
  swapBooleanRoles(): void {
    if (this.booleanSelectionOrder.length >= 2) {
      // Swap first two elements
      const temp = this.booleanSelectionOrder[0];
      this.booleanSelectionOrder[0] = this.booleanSelectionOrder[1];
      this.booleanSelectionOrder[1] = temp;
      this.cdr.detectChanges();
    }
  }

  /**
   * Get selected shapes in boolean order (body first, then tools)
   */
  getSelectedCADShapesInOrder(): CADShape[] {
    return this.booleanSelectionOrder
      .filter((id) => this.selectedCADShapeIds.has(id))
      .map((id) => this.cadShapes.find((s) => s.id === id))
      .filter((s): s is CADShape => s !== undefined);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIMITIVES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Create a box primitive
   */
  async createBox(width: number, height: number, depth: number): Promise<void> {
    try {
      this.isProcessing = true;
      this.processingMessage = 'Creating box...';
      await this.initializeKernel();

      const cadShape = await this.cadKernel.createBox(width, height, depth);
      cadShape.name = `Box_${this.cadShapes.length + 1}`;
      cadShape.primitiveParams = { type: 'box', dimensions: [width, height, depth] };

      // Add to local list and scene
      this.cadShapes.push(cadShape);
      this.addShapeToScene(cadShape);

      // Select the new shape
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(cadShape.id);
      this.updateShapeVisualState(cadShape.id, true);
      this.attachTransformControls();

      this.hasUnsavedChanges = true;
      this.processingMessage = '';

      this.recordHistory({
        id: this.createHistoryId('create'),
        createdAt: Date.now(),
        type: 'create-primitive',
        outputId: cadShape.id,
        params: {
          primitiveType: 'box',
          dimensions: [width, height, depth]
        },
        name: cadShape.name,
        color: cadShape.color
      });
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Create a cylinder primitive
   */
  async createCylinder(radius: number, height: number): Promise<void> {
    try {
      this.isProcessing = true;
      this.processingMessage = 'Creating cylinder...';
      await this.initializeKernel();

      const cadShape = await this.cadKernel.createCylinder(radius, height);
      cadShape.name = `Cylinder_${this.cadShapes.length + 1}`;
      cadShape.primitiveParams = { type: 'cylinder', dimensions: [radius, height] };

      // Add to local list and scene
      this.cadShapes.push(cadShape);
      this.addShapeToScene(cadShape);

      // Select the new shape
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(cadShape.id);
      this.updateShapeVisualState(cadShape.id, true);
      this.attachTransformControls();

      this.hasUnsavedChanges = true;
      this.processingMessage = '';

      this.recordHistory({
        id: this.createHistoryId('create'),
        createdAt: Date.now(),
        type: 'create-primitive',
        outputId: cadShape.id,
        params: {
          primitiveType: 'cylinder',
          dimensions: [radius, height]
        },
        name: cadShape.name,
        color: cadShape.color
      });
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Create a sphere primitive
   */
  async createSphere(radius: number): Promise<void> {
    try {
      this.isProcessing = true;
      this.processingMessage = 'Creating sphere...';
      await this.initializeKernel();

      const cadShape = await this.cadKernel.createSphere(radius);
      cadShape.name = `Sphere_${this.cadShapes.length + 1}`;
      cadShape.primitiveParams = { type: 'sphere', dimensions: [radius] };

      // Add to local list and scene
      this.cadShapes.push(cadShape);
      this.addShapeToScene(cadShape);

      // Select the new shape
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(cadShape.id);
      this.updateShapeVisualState(cadShape.id, true);
      this.attachTransformControls();

      this.hasUnsavedChanges = true;

      this.recordHistory({
        id: this.createHistoryId('create'),
        createdAt: Date.now(),
        type: 'create-primitive',
        outputId: cadShape.id,
        params: {
          primitiveType: 'sphere',
          dimensions: [radius]
        },
        name: cadShape.name,
        color: cadShape.color
      });
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Create a ring (hollow cylinder) primitive
   */
  async createRing(outerRadius: number, innerRadius: number, height: number): Promise<void> {
    try {
      this.isProcessing = true;
      this.processingMessage = 'Creating ring...';
      await this.initializeKernel();

      const cadShape = await this.cadKernel.createRing(outerRadius, innerRadius, height);
      cadShape.name = `Ring_${this.cadShapes.length + 1}`;
      cadShape.primitiveParams = { type: 'ring', dimensions: [outerRadius, innerRadius, height] };

      // Add to local list and scene
      this.cadShapes.push(cadShape);
      this.addShapeToScene(cadShape);

      // Select the new shape
      this.selectedCADShapeIds.clear();
      this.selectedCADShapeIds.add(cadShape.id);
      this.updateShapeVisualState(cadShape.id, true);
      this.attachTransformControls();

      this.hasUnsavedChanges = true;

      this.recordHistory({
        id: this.createHistoryId('create'),
        createdAt: Date.now(),
        type: 'create-primitive',
        outputId: cadShape.id,
        params: {
          primitiveType: 'ring',
          dimensions: [outerRadius, innerRadius, height]
        },
        name: cadShape.name,
        color: cadShape.color
      });
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Create primitive based on current selection
   */
  async createPrimitive(): Promise<void> {
    switch (this.primitiveType) {
      case 'box':
        await this.createBox(this.boxWidth, this.boxHeight, this.boxDepth);
        break;
      case 'cylinder':
        await this.createCylinder(this.cylinderRadius, this.cylinderHeight);
        break;
      case 'ring':
        await this.createRing(this.ringOuterRadius, this.ringInnerRadius, this.ringHeight);
        break;
      case 'sphere':
        await this.createSphere(this.sphereRadius);
        break;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get loading state label
   */
  getLoadingStateLabel(): string {
    switch (this.loadingState) {
      case 'idle':
        return 'Not loaded';
      case 'loading':
        return 'Loading...';
      case 'ready':
        return 'Ready';
      case 'error':
        return 'Error';
      default:
        return '';
    }
  }

  /**
   * Confirm delete of selected CAD shapes
   */
  confirmDeleteSelected(): void {
    if (this.selectedCADShapeIds.size === 0) return;

    const count = this.selectedCADShapeIds.size;
    const message =
      count === 1 ? 'Are you sure you want to delete this shape?' : `Are you sure you want to delete ${count} shapes?`;

    this.showConfirm('Delete Shape(s)', message, () => {
      this.deleteSelected();
    });
  }

  /**
   * Delete selected CAD shapes (immediate, no confirmation)
   */
  deleteSelected(): void {
    if (this.selectedCADShapeIds.size === 0) return;
    const deletedIds = Array.from(this.selectedCADShapeIds);

    // Detach transform controls FIRST to avoid errors
    this.detachTransformControls();

    for (const id of this.selectedCADShapeIds) {
      // Remove from scene
      this.removeShapeFromScene(id);
      // Remove from kernel
      this.cadKernel.deleteShape(id);
      // Remove from local list
      this.cadShapes = this.cadShapes.filter((s) => s.id !== id);
    }
    this.selectedCADShapeIds.clear();
    this.booleanSelectionOrder = [];
    this.hasUnsavedChanges = true;
    this.cdr.detectChanges();

    this.recordHistory({
      id: this.createHistoryId('delete'),
      createdAt: Date.now(),
      type: 'delete',
      params: {
        shapeIds: deletedIds
      }
    });
  }

  /**
   * Confirm clear all CAD shapes
   */
  confirmClearAll(): void {
    if (this.cadShapes.length === 0) return;

    this.showConfirm(
      'Clear All Shapes',
      `Are you sure you want to delete all ${this.cadShapes.length} shapes? This cannot be undone.`,
      () => {
        this.clearAllShapes();
      }
    );
  }

  /**
   * Clear all CAD shapes from scene (immediate, no confirmation)
   */
  clearAllShapes(): void {
    const deletedIds = this.cadShapes.map((shape) => shape.id);

    // Detach transform controls FIRST
    this.detachTransformControls();

    // Remove all from scene
    this.sceneMeshes.forEach((_, id) => {
      this.removeShapeFromScene(id);
    });
    // Clear kernel
    this.cadKernel.clearSession();
    // Clear all sketches
    this.sketchService.clear();
    // Clear local state
    this.cadShapes = [];
    this.selectedCADShapeIds.clear();
    this.booleanSelectionOrder = []; // Clear stale IDs
    this.hasUnsavedChanges = true;
    this.cdr.detectChanges();

    if (deletedIds.length > 0) {
      this.recordHistory({
        id: this.createHistoryId('delete'),
        createdAt: Date.now(),
        type: 'delete',
        params: {
          shapeIds: deletedIds
        }
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // HISTORY
  // ═══════════════════════════════════════════════════════════════════════════

  private createHistoryId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private recordHistory(operation: CADHistoryOperation): void {
    if (this.isHistoryReplay) return;
    this.historyService.record(operation);
  }

  get canUndo(): boolean {
    return this.historyService.canUndo();
  }

  get canRedo(): boolean {
    return this.historyService.canRedo();
  }

  get historyOperations(): CADHistoryOperation[] {
    return this.historyService.getOperations();
  }

  get historyIndex(): number {
    return this.historyService.getCurrentIndex();
  }

  getHistoryLabel(operation: CADHistoryOperation): string {
    const nameSuffix = operation.name ? ` (${operation.name})` : '';

    switch (operation.type) {
      case 'create-primitive':
        return `Create ${operation.params.primitiveType}${nameSuffix}`;
      case 'extrude-profiles':
      case 'extrude-polygon':
        return `Extrude${nameSuffix}`;
      case 'boolean':
        return `${operation.params.operation.charAt(0).toUpperCase()}${operation.params.operation.slice(1)}${nameSuffix}`;
      case 'delete':
        return operation.params.shapeIds.length > 1 ? `Delete ${operation.params.shapeIds.length} shapes` : 'Delete shape';
      case 'transform':
        return `Transform${nameSuffix}`;
      case 'import-brep':
        return `Import${nameSuffix}`;
    }
  }

  async undoHistory(): Promise<void> {
    if (this.isProcessing || this.isHistoryReplay) return;
    if (!this.historyService.canUndo()) return;
    this.historyService.undo();
    await this.replayHistory();
  }

  async redoHistory(): Promise<void> {
    if (this.isProcessing || this.isHistoryReplay) return;
    if (!this.historyService.canRedo()) return;
    this.historyService.redo();
    await this.replayHistory();
  }

  private resetCadStateForHistoryReplay(): void {
    this.detachTransformControls();
    this.selectedCADShapeIds.clear();
    this.booleanSelectionOrder = [];
    this.selectedEdges = [];
    this.pendingTransforms.clear();

    for (const id of Array.from(this.sceneMeshes.keys())) {
      this.removeShapeFromScene(id);
    }
    this.cadKernel.clearSession();
    this.cadShapes = [];
  }

  private async replayHistory(): Promise<void> {
    const operations = this.historyService.getOperations();
    const targetIndex = this.historyService.getCurrentIndex();

    try {
      await this.cadKernel.ensureLoaded();

      this.isHistoryReplay = true;
      this.isProcessing = true;
      this.processingMessage = 'Rebuilding history...';
      this.cdr.detectChanges();

      this.resetCadStateForHistoryReplay();

      for (let i = 0; i <= targetIndex; i++) {
        const operation = operations[i];
        if (!operation) continue;
        await this.applyHistoryOperation(operation);
        this.processingMessage = `Rebuilding history... ${i + 1}/${targetIndex + 1}`;
        this.cdr.detectChanges();
      }

      this.hasUnsavedChanges = targetIndex >= 0;
    } finally {
      this.isHistoryReplay = false;
      this.isProcessing = false;
      this.processingMessage = '';
      this.cdr.detectChanges();
    }
  }

  private async applyHistoryOperation(operation: CADHistoryOperation): Promise<void> {
    switch (operation.type) {
      case 'create-primitive': {
        const { primitiveType, dimensions } = operation.params;
        let shape: CADShape | null = null;

        if (primitiveType === 'box') {
          shape = await this.cadKernel.createBox(dimensions[0], dimensions[1], dimensions[2]);
        } else if (primitiveType === 'cylinder') {
          shape = await this.cadKernel.createCylinder(dimensions[0], dimensions[1]);
        } else if (primitiveType === 'sphere') {
          shape = await this.cadKernel.createSphere(dimensions[0]);
        } else if (primitiveType === 'ring') {
          shape = await this.cadKernel.createRing(dimensions[0], dimensions[1], dimensions[2]);
        }

        if (!shape) return;
        if (operation.name) shape.name = operation.name;
        if (operation.color) shape.color = operation.color;

        this.remapShapeIdIfNeeded(shape, operation.outputId);
        this.cadShapes.push(shape);
        this.addShapeToScene(shape);
        return;
      }
      case 'extrude-profiles': {
        const { profiles, height, sketchPlane, planeOffset, customPlane } = operation.params;
        const shape = await this.extrudeProfilesFromHistory({
          profiles,
          height,
          sketchPlane,
          planeOffset,
          customPlane
        });

        if (!shape) return;
        if (operation.name) shape.name = operation.name;
        if (operation.color) shape.color = operation.color;

        this.remapShapeIdIfNeeded(shape, operation.outputId);
        this.cadShapes.push(shape);
        this.addShapeToScene(shape);
        return;
      }
      case 'extrude-polygon': {
        const shape = await this.cadKernel.extrudePolygon(operation.params);
        if (operation.name) shape.name = operation.name;
        if (operation.color) shape.color = operation.color;

        this.remapShapeIdIfNeeded(shape, operation.outputId);
        this.cadShapes.push(shape);
        this.addShapeToScene(shape);
        return;
      }
      case 'boolean': {
        const { operation: opType, inputIds } = operation.params;
        const inputShapes = inputIds
          .map((id) => this.cadShapes.find((s) => s.id === id))
          .filter((s): s is CADShape => !!s);

        if (inputShapes.length < 2) return;

        let result: CADShape | null = null;

        if (opType === 'union') {
          const intermediateIds: string[] = [];
          let current = inputShapes[0];
          for (let i = 1; i < inputShapes.length; i++) {
            const next = await this.cadKernel.booleanUnion(current, inputShapes[i]);
            if (i > 1) {
              intermediateIds.push(current.id);
            }
            current = next;
          }
          result = current;

          for (const id of intermediateIds) {
            this.cadKernel.deleteShape(id);
          }
        } else if (opType === 'subtract') {
          result = await this.cadKernel.booleanSubtract(inputShapes[0], inputShapes[1]);
        } else if (opType === 'intersect') {
          result = await this.cadKernel.booleanIntersect(inputShapes[0], inputShapes[1]);
        }

        if (!result) return;
        if (operation.name) result.name = operation.name;
        if (operation.color) result.color = operation.color;

        for (const id of inputIds) {
          this.removeShapeFromScene(id);
          this.cadKernel.deleteShape(id);
        }
        this.cadShapes = this.cadShapes.filter((s) => !inputIds.includes(s.id));

        this.remapShapeIdIfNeeded(result, operation.outputId);
        this.cadShapes.push(result);
        this.addShapeToScene(result);
        return;
      }
      case 'delete': {
        const { shapeIds } = operation.params;
        for (const id of shapeIds) {
          this.removeShapeFromScene(id);
          this.cadKernel.deleteShape(id);
        }
        this.cadShapes = this.cadShapes.filter((s) => !shapeIds.includes(s.id));
        return;
      }
      case 'transform': {
        const shape = this.cadShapes.find((s) => s.id === operation.inputId);
        if (!shape) return;

        const transformed = await this.cadKernel.transformShape(shape, operation.params.matrix);
        if (!transformed) return;

        this.removeShapeFromScene(operation.inputId);
        this.replaceShapeInList(operation.inputId, transformed);

        this.remapShapeIdIfNeeded(transformed, operation.outputId);
        this.addShapeToScene(transformed);
        return;
      }
      case 'import-brep': {
        const shape = await this.cadKernel.importBrep(operation.params.brep, operation.name);
        if (operation.name) shape.name = operation.name;
        if (operation.color) shape.color = operation.color;

        this.remapShapeIdIfNeeded(shape, operation.outputId);
        this.cadShapes.push(shape);
        this.addShapeToScene(shape);
        return;
      }
    }
  }

  private async extrudeProfilesFromHistory(params: {
    profiles: ExtrudeProfileData[];
    height: number;
    sketchPlane: 'XY' | 'XZ' | 'YZ';
    planeOffset: number;
    customPlane?: {
      origin: [number, number, number];
      normal: [number, number, number];
      xAxis: [number, number, number];
      yAxis: [number, number, number];
    };
  }): Promise<CADShape | null> {
    const { profiles, height, sketchPlane, planeOffset, customPlane } = params;
    if (profiles.length === 0) return null;

    let cadShape: CADShape | null = null;

    if (profiles.length === 1) {
      const profileData = profiles[0];

      if (profileData.isRing && profileData.ringData) {
        cadShape = await this.cadKernel.extrudeRing({
          centerX: profileData.ringData.centerX,
          centerY: profileData.ringData.centerY,
          outerRadius: profileData.ringData.outerRadius,
          innerRadius: profileData.ringData.innerRadius,
          height,
          sketchPlane,
          planeOffset,
          customPlane
        });
      } else if (profileData.isCircle && profileData.circleData) {
        cadShape = await this.cadKernel.extrudeCircle({
          centerX: profileData.circleData.centerX,
          centerY: profileData.circleData.centerY,
          radius: profileData.circleData.radius,
          height,
          sketchPlane,
          planeOffset,
          customPlane
        });

        if (profileData.circleHoles && profileData.circleHoles.length > 0 && cadShape) {
          cadShape = await this.cadKernel.subtractCircleHoles(
            cadShape,
            profileData.circleHoles,
            height,
            sketchPlane,
            planeOffset,
            customPlane
          );
        }

        if (profileData.holes && profileData.holes.length > 0 && cadShape) {
          cadShape = await this.cadKernel.subtractPolygonHoles(
            cadShape,
            profileData.holes,
            height,
            sketchPlane,
            planeOffset,
            customPlane
          );
        }
      } else if (profileData.isSlot && profileData.slotData) {
        cadShape = await this.cadKernel.extrudeSlot({
          startX: profileData.slotData.startX,
          startY: profileData.slotData.startY,
          endX: profileData.slotData.endX,
          endY: profileData.slotData.endY,
          width: profileData.slotData.width,
          height,
          sketchPlane,
          planeOffset,
          customPlane
        });
      } else {
        const extrudeParams: ExtrudeParams = {
          points: profileData.outer,
          height,
          circleHoles: profileData.circleHoles,
          sketchPlane,
          planeOffset,
          customPlane
        };
        cadShape = await this.cadKernel.extrudePolygon(extrudeParams);

        if (profileData.holes && profileData.holes.length > 0 && cadShape) {
          cadShape = await this.cadKernel.subtractPolygonHoles(
            cadShape,
            profileData.holes,
            height,
            sketchPlane,
            planeOffset,
            customPlane
          );
        }
      }
    } else {
      cadShape = await this.cadKernel.extrudeMultipleProfiles(
        profiles,
        height,
        sketchPlane,
        planeOffset,
        customPlane
      );
    }

    return cadShape;
  }

  private remapShapeIdIfNeeded(shape: CADShape, desiredId: string): void {
    if (!desiredId || shape.id === desiredId) return;
    this.cadKernel.remapShapeId(shape.id, desiredId);
  }

  private replaceShapeInList(oldId: string, newShape: CADShape): void {
    const index = this.cadShapes.findIndex((s) => s.id === oldId);
    if (index === -1) {
      this.cadShapes.push(newShape);
      return;
    }
    this.cadShapes[index] = newShape;
  }

  private cloneProfilesData(profiles: ExtrudeProfileData[]): ExtrudeProfileData[] {
    return profiles.map((profile) => ({
      outer: profile.outer.map((point) => [...point]),
      holes: profile.holes.map((hole) => hole.map((point) => [...point])),
      isCircle: profile.isCircle,
      isRing: profile.isRing,
      isSlot: profile.isSlot,
      circleData: profile.circleData ? { ...profile.circleData } : undefined,
      ringData: profile.ringData ? { ...profile.ringData } : undefined,
      slotData: profile.slotData ? { ...profile.slotData } : undefined,
      circleHoles: profile.circleHoles ? profile.circleHoles.map((hole) => ({ ...hole })) : undefined
    }));
  }

  private cloneExtrudeParams(params: ExtrudeParams): ExtrudeParams {
    return {
      points: params.points.map((point) => [...point]),
      height: params.height,
      direction: params.direction ? [...params.direction] as [number, number, number] : undefined,
      holes: params.holes ? params.holes.map((hole) => hole.map((point) => [...point])) : undefined,
      circleHoles: params.circleHoles ? params.circleHoles.map((hole) => ({ ...hole })) : undefined,
      sketchPlane: params.sketchPlane,
      planeOffset: params.planeOffset,
      customPlane: params.customPlane
        ? {
            origin: [...params.customPlane.origin] as [number, number, number],
            normal: [...params.customPlane.normal] as [number, number, number],
            xAxis: [...params.customPlane.xAxis] as [number, number, number],
            yAxis: [...params.customPlane.yAxis] as [number, number, number]
          }
        : undefined
    };
  }

  private async seedHistoryFromShapes(shapes: CADShape[]): Promise<void> {
    if (shapes.length === 0) return;

    await this.cadKernel.ensureLoaded();

    for (const shape of shapes) {
      let recorded = false;
      const canUseParams = !shape.isDirectEdit;

      if (canUseParams && shape.primitiveParams) {
        this.recordHistory({
          id: this.createHistoryId('baseline'),
          createdAt: Date.now(),
          type: 'create-primitive',
          outputId: shape.id,
          params: {
            primitiveType: shape.primitiveParams.type,
            dimensions: [...shape.primitiveParams.dimensions]
          },
          name: shape.name,
          color: shape.color
        });
        recorded = true;
      } else if (canUseParams && shape.extrudeParams) {
        this.recordHistory({
          id: this.createHistoryId('baseline'),
          createdAt: Date.now(),
          type: 'extrude-polygon',
          outputId: shape.id,
          params: this.cloneExtrudeParams(shape.extrudeParams),
          name: shape.name,
          color: shape.color
        });
        recorded = true;
      } else {
        try {
          const brep = await this.cadKernel.exportBrep(shape);
          this.recordHistory({
            id: this.createHistoryId('baseline'),
            createdAt: Date.now(),
            type: 'import-brep',
            outputId: shape.id,
            params: {
              brep
            },
            name: shape.name,
            color: shape.color
          });
          recorded = true;
        } catch {}
      }

      if (!recorded) {
        continue;
      }

      const group = this.sceneMeshes.get(shape.id);
      if (!group) {
        continue;
      }

      const hasTransform =
        group.position.x !== 0 ||
        group.position.y !== 0 ||
        group.position.z !== 0 ||
        group.quaternion.x !== 0 ||
        group.quaternion.y !== 0 ||
        group.quaternion.z !== 0 ||
        group.quaternion.w !== 1 ||
        group.scale.x !== 1 ||
        group.scale.y !== 1 ||
        group.scale.z !== 1;

      if (!hasTransform) {
        continue;
      }

      const geometryCenter = group.userData['geometryCenter'] as Vector3 | undefined;
      const matrix = new Matrix4();
      group.updateWorldMatrix(true, false);
      matrix.copy(group.matrixWorld);

      if (geometryCenter) {
        const offsetMatrix = new Matrix4().makeTranslation(
          -geometryCenter.x,
          -geometryCenter.y,
          -geometryCenter.z
        );
        matrix.multiply(offsetMatrix);
      }

      this.recordHistory({
        id: this.createHistoryId('baseline-transform'),
        createdAt: Date.now(),
        type: 'transform',
        inputId: shape.id,
        outputId: shape.id,
        params: {
          matrix: matrix.elements.slice()
        }
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TRANSFORM GIZMO
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Setup transform controls
   */
  private setupTransformControls(): void {
    if (!this.engine) return;

    this.transformControls = new TransformControls(this.engine.camera, this.engine.canvas);
    this.transformControls.setMode(this.transformMode);
    this.transformControls.setSize(0.4); // Smaller gizmo (50% of original)

    // Style the gizmo
    this.transformControls.getHelper().traverse((child) => {
      if ((child as any).material) {
        (child as any).material.depthTest = false;
        child.renderOrder = 999;
      }
    });

    // Add to scene
    this.engine.scene.add(this.transformControls.getHelper());

    // Handle transform start/end - disable orbit controls and mark pending transform
    this.transformControls.addEventListener('dragging-changed', (event: any) => {
      const wasDragging = this.isTransforming;
      this.isTransforming = event.value;

      if (this.engine?.controls) {
        this.engine.controls.enabled = !event.value;
      }

      // Show/hide transform value display
      if (event.value) {
        this.showTransformValueDisplay();
        if (this.transformMode === 'scale' && this.transformControls?.object) {
          this.transformStartScale = this.transformControls.object.scale.clone();
          this.transformStartObjectUuid = this.transformControls.object.uuid;
        } else {
          this.transformStartScale = null;
          this.transformStartObjectUuid = null;
        }
      } else {
        this.hideTransformValueDisplay();
        this.transformStartScale = null;
        this.transformStartObjectUuid = null;
      }

      // When dragging ends, mark shapes as having pending transform (deferred OCCT sync)
      if (wasDragging && !event.value && this.selectedCADShapeIds.size > 0) {
        // Mark all selected shapes as pending
        for (const shapeId of this.selectedCADShapeIds) {
          this.pendingTransforms.add(shapeId);
        }
        this.cadKernel.markDirty();

        // For multi-selection, cleanup pivot and restore shapes to cadGroup
        if (this.multiSelectPivot) {
          this.cleanupMultiSelectPivot();
          // Re-setup pivot for continued transformations
          if (this.selectedCADShapeIds.size > 1) {
            this.setupMultiSelectPivot();
          }
        }

        // Set flag to prevent accidental deselection from click event
        this.recentlyTransformed = true;
        setTimeout(() => {
          this.recentlyTransformed = false;
        }, 100);

        void this.commitPendingTransforms(true);
      }
    });

    // Handle transform change - update shape position (visual feedback during drag)
    this.transformControls.addEventListener('objectChange', () => {
      this.onTransformChange();
    });
  }

  /**
   * Dispose transform controls
   */
  private disposeTransformControls(): void {
    if (this.transformControls) {
      this.detachTransformControls();
      this.transformControls.dispose();
      this.engine?.scene.remove(this.transformControls.getHelper());
      this.transformControls = null;
    }
  }

  /**
   * Set transform mode
   */
  setTransformMode(mode: 'translate' | 'rotate' | 'scale'): void {
    this.transformMode = mode;
    if (this.transformControls) {
      this.transformControls.setMode(mode);
    }
    this.cdr.detectChanges();
  }

  /**
   * Attach transform controls to selected shape(s)
   * Supports both single and multi-selection
   */
  private attachTransformControls(): void {
    if (!this.transformControls || this.selectedCADShapeIds.size === 0) {
      this.detachTransformControls();
      return;
    }

    if (this.selectedCADShapeIds.size === 1) {
      // Single selection - attach directly to the shape
      this.cleanupMultiSelectPivot();
      const selectedId = Array.from(this.selectedCADShapeIds)[0];
      const group = this.sceneMeshes.get(selectedId);
      if (group) {
        this.transformControls.attach(group);
      }
    } else {
      // Multi-selection - create a pivot group at the center of all selected shapes
      this.setupMultiSelectPivot();
    }
  }

  /**
   * Setup pivot group for multi-selection transforms
   */
  private setupMultiSelectPivot(): void {
    if (!this.engine || !this.transformControls) return;

    // Clean up existing pivot
    this.cleanupMultiSelectPivot();

    // Calculate center of all selected shapes
    const center = new Vector3();
    let count = 0;

    for (const shapeId of this.selectedCADShapeIds) {
      const group = this.sceneMeshes.get(shapeId);
      if (group) {
        center.add(group.position);
        count++;
      }
    }

    if (count === 0) return;
    center.divideScalar(count);

    // Create pivot group at center
    this.multiSelectPivot = new Group();
    this.multiSelectPivot.name = 'MultiSelectPivot';
    this.multiSelectPivot.position.copy(center);
    this.engine.scene.add(this.multiSelectPivot);

    // Store original transforms and reparent shapes to pivot
    this.multiSelectOriginalTransforms.clear();
    for (const shapeId of this.selectedCADShapeIds) {
      const group = this.sceneMeshes.get(shapeId);
      if (group) {
        // Store original world transform
        this.multiSelectOriginalTransforms.set(shapeId, {
          position: group.position.clone(),
          rotation: group.rotation.clone(),
          scale: group.scale.clone()
        });

        // Reparent to pivot (adjust position to be relative to pivot)
        const relativePos = group.position.clone().sub(center);
        this.cadGroup.remove(group);
        group.position.copy(relativePos);
        this.multiSelectPivot.add(group);
      }
    }

    // Attach transform controls to pivot
    this.transformControls.attach(this.multiSelectPivot);
  }

  /**
   * Cleanup multi-selection pivot and restore shapes to cadGroup
   */
  private cleanupMultiSelectPivot(): void {
    if (!this.multiSelectPivot || !this.engine) return;

    // Restore shapes to cadGroup with their current world transforms
    const children = [...this.multiSelectPivot.children];
    for (const child of children) {
      if (child instanceof Group) {
        // Get world position
        const worldPos = new Vector3();
        const worldQuat = new Quaternion();
        const worldScale = new Vector3();
        child.getWorldPosition(worldPos);
        child.getWorldQuaternion(worldQuat);
        child.getWorldScale(worldScale);

        // Reparent to cadGroup
        this.multiSelectPivot.remove(child);
        this.cadGroup.add(child);

        // Apply world transform
        child.position.copy(worldPos);
        child.quaternion.copy(worldQuat);
        child.scale.copy(worldScale);
      }
    }

    // Remove pivot from scene
    this.engine.scene.remove(this.multiSelectPivot);
    this.multiSelectPivot = null;
    this.multiSelectOriginalTransforms.clear();
  }

  /**
   * Detach transform controls
   */
  private detachTransformControls(): void {
    // Cleanup multi-select pivot first
    this.cleanupMultiSelectPivot();

    if (this.transformControls) {
      this.transformControls.detach();
    }
  }

  /**
   * Handle transform change from gizmo (called during drag)
   */
  private onTransformChange(): void {
    // Mark session as dirty - actual sync happens on drag end
    this.cadKernel.markDirty();
    if (this.transformMode === 'scale') {
      this.enforceUniformScale();
    }
    // Update live transform value display
    this.updateTransformValueDisplay();
  }

  private enforceUniformScale(): void {
    if (!this.transformControls?.object || !this.transformStartScale) return;
    if (this.transformStartObjectUuid && this.transformControls.object.uuid !== this.transformStartObjectUuid) return;

    const obj = this.transformControls.object;
    const start = this.transformStartScale;
    const startX = start.x === 0 ? 1 : start.x;
    const startY = start.y === 0 ? 1 : start.y;
    const startZ = start.z === 0 ? 1 : start.z;

    const deltaX = Math.abs(obj.scale.x - start.x);
    const deltaY = Math.abs(obj.scale.y - start.y);
    const deltaZ = Math.abs(obj.scale.z - start.z);

    let factor = 1;
    if (deltaX >= deltaY && deltaX >= deltaZ) {
      factor = obj.scale.x / startX;
    } else if (deltaY >= deltaZ) {
      factor = obj.scale.y / startY;
    } else {
      factor = obj.scale.z / startZ;
    }

    if (!Number.isFinite(factor)) return;

    const clamped = Math.max(0.01, Math.abs(factor));
    obj.scale.set(start.x * clamped, start.y * clamped, start.z * clamped);
  }

  /**
   * Show transform value display near the gizmo
   */
  private showTransformValueDisplay(): void {
    if (!this.engine || !this.transformControls?.object) return;

    // Create element if not exists
    if (!this.transformValueElement) {
      this.transformValueElement = document.createElement('div');
      this.transformValueElement.style.cssText = `
        background: rgba(0, 0, 0, 0.85);
        color: #fff;
        padding: 8px 12px;
        border-radius: 6px;
        font-family: 'JetBrains Mono', monospace;
        font-size: var(--font-size-xs);
        white-space: nowrap;
        pointer-events: none;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        border: 1px solid rgba(255,255,255,0.1);
      `;
    }

    // Create CSS2DObject if not exists
    if (!this.transformValueDisplay) {
      this.transformValueDisplay = new CSS2DObject(this.transformValueElement);
      this.transformValueDisplay.name = 'TransformValueDisplay';
    }

    // Add to scene
    this.engine.scene.add(this.transformValueDisplay);
    this.updateTransformValueDisplay();
  }

  /**
   * Hide transform value display
   */
  private hideTransformValueDisplay(): void {
    if (this.transformValueDisplay && this.engine) {
      this.engine.scene.remove(this.transformValueDisplay);
    }
  }

  /**
   * Update transform value display with current values
   */
  private updateTransformValueDisplay(): void {
    if (!this.transformValueElement || !this.transformControls?.object || !this.transformValueDisplay) return;

    const obj = this.transformControls.object;
    let text = '';
    let color = '#fff';

    switch (this.transformMode) {
      case 'translate':
        color = '#4ade80'; // Green
        text = `Position: X: ${obj.position.x.toFixed(2)}  Y: ${obj.position.y.toFixed(2)}  Z: ${obj.position.z.toFixed(2)}`;
        break;
      case 'rotate':
        color = '#60a5fa'; // Blue
        const rx = (obj.rotation.x * 180 / Math.PI).toFixed(1);
        const ry = (obj.rotation.y * 180 / Math.PI).toFixed(1);
        const rz = (obj.rotation.z * 180 / Math.PI).toFixed(1);
        text = `Rotation: X: ${rx}°  Y: ${ry}°  Z: ${rz}°`;
        break;
      case 'scale':
        color = '#f472b6'; // Pink
        text = `Scale: X: ${obj.scale.x.toFixed(2)}  Y: ${obj.scale.y.toFixed(2)}  Z: ${obj.scale.z.toFixed(2)}`;
        break;
    }

    this.transformValueElement.innerHTML = `<span style="color: ${color}">${text}</span>`;

    // Position display above the object
    const pos = obj.position.clone();
    pos.y += 30; // Offset above
    this.transformValueDisplay.position.copy(pos);
  }

  /**
   * Sync Three.js transform to OpenCascade shape
   * Called when transform gizmo drag ends
   */
  private async syncTransformToKernel(shapeId: string, recordHistory: boolean = false): Promise<void> {
    const group = this.sceneMeshes.get(shapeId);
    const cadShape = this.cadShapes.find((s) => s.id === shapeId);

    if (!group || !cadShape) return;

    try {
      // Get the geometry center offset that was stored when adding to scene
      const geometryCenter = group.userData['geometryCenter'] as Vector3 | undefined;

      // Build the full transformation matrix
      // We need to account for: original center offset + current group transform
      const matrix = new Matrix4();

      // Start with the group's world matrix
      group.updateWorldMatrix(true, false);
      matrix.copy(group.matrixWorld);

      // Account for the mesh offset within the group
      // In Three.js, the mesh is offset by -geometryCenter from the group origin
      // So the effective world transform for a vertex V is: groupMatrix * translate(-geometryCenter) * V
      // We must include this offset when transforming the OCCT shape
      if (geometryCenter) {
        const offsetMatrix = new Matrix4().makeTranslation(
          -geometryCenter.x,
          -geometryCenter.y,
          -geometryCenter.z
        );
        matrix.multiply(offsetMatrix);
      }

      const matrixElements = matrix.elements.slice();

      // Apply the transformation to the OpenCascade shape
      const transformedShape = await this.cadKernel.transformShape(
        cadShape,
        matrixElements,
        geometryCenter ? [geometryCenter.x, geometryCenter.y, geometryCenter.z] : undefined
      );

      if (transformedShape) {
        // Update the shape in our local list
        const index = this.cadShapes.findIndex((s) => s.id === shapeId);
        if (index !== -1) {
          this.cadShapes[index] = transformedShape;
        }

        // Reset the Three.js group transform since it's now baked into the geometry
        // Re-add the shape to scene with updated geometry
        this.removeShapeFromScene(shapeId);
        this.sceneMeshes.delete(shapeId);
        this.invalidateCADMeshCache();
        this.addShapeToScene(transformedShape);

        // Update selection and booleanSelectionOrder if shape ID changed
        if (shapeId !== transformedShape.id) {
          // Update booleanSelectionOrder
          const orderIndex = this.booleanSelectionOrder.indexOf(shapeId);
          if (orderIndex !== -1) {
            this.booleanSelectionOrder[orderIndex] = transformedShape.id;
          }

          // Update selectedCADShapeIds - replace old ID with new ID, keep other selections
          this.selectedCADShapeIds.delete(shapeId);
          this.selectedCADShapeIds.add(transformedShape.id);

          // Update selectedEdges - replace old shapeId with new shapeId
          for (const edge of this.selectedEdges) {
            if (edge.shapeId === shapeId) {
              edge.shapeId = transformedShape.id;
            }
          }
        }

        this.updateShapeVisualState(transformedShape.id, true);
        this.attachTransformControls();

        // Update thicken direction arrow if in thicken mode
        if (this.activeOperation === 'thicken' && transformedShape.isSurface) {
          this.updateThickenDirectionArrow();
        }

        this.hasUnsavedChanges = true;

        if (recordHistory) {
          this.recordHistory({
            id: this.createHistoryId('transform'),
            createdAt: Date.now(),
            type: 'transform',
            inputId: shapeId,
            outputId: transformedShape.id,
            params: {
              matrix: matrixElements
            }
          });
        }
      }
    } catch (error: any) {
      console.error('Failed to sync transform to kernel:', error);
      this.onError.emit(`Transform failed: ${error?.message || 'Unknown error'}`);
    }
  }

  /**
   * Commit all pending transforms to OpenCascade kernel
   * Called before operations that need accurate OCCT geometry (boolean, save, export)
   */
  private async commitPendingTransforms(recordHistory: boolean = false): Promise<void> {
    if (this.transformCommitInFlight) {
      await this.transformCommitInFlight;
    }

    if (this.pendingTransforms.size === 0) return;

    const pendingIds = Array.from(this.pendingTransforms);
    this.pendingTransforms.clear();

    const commitPromise = (async () => {
      for (const shapeId of pendingIds) {
        await this.syncTransformToKernel(shapeId, recordHistory);
      }
    })();

    this.transformCommitInFlight = commitPromise;

    try {
      await commitPromise;
    } finally {
      if (this.transformCommitInFlight === commitPromise) {
        this.transformCommitInFlight = null;
      }
    }

    if (this.pendingTransforms.size > 0) {
      await this.commitPendingTransforms(recordHistory);
      return;
    }

    this.updateMeasurements();
  }

  /**
   * Check if any shapes have pending transforms
   */
  hasPendingTransforms(): boolean {
    return this.pendingTransforms.size > 0;
  }

  /**
   * Refresh extracted faces and edges for the selected shape.
   * Called after committing transforms to ensure face/edge data matches the updated geometry.
   */
  private async refreshShapeFacesAndEdges(): Promise<void> {
    if (this.selectedCADShapeIds.size !== 1) return;

    const shapeId = Array.from(this.selectedCADShapeIds)[0];
    const shape = this.cadShapes.find((s) => s.id === shapeId);
    const group = this.sceneMeshes.get(shapeId);

    if (!shape || !group) return;

    // Use pre-computed face data from shape (worker-only architecture)
    if (shape.extractedFaces) {
      group.userData['extractedFaces'] = shape.extractedFaces;
    }

    // Keep existing triangleToFaceMap - mesh triangles haven't changed
    if (!group.userData['triangleToFaceMap'] && shape.triangleToFaceMap) {
      group.userData['triangleToFaceMap'] = Array.from(shape.triangleToFaceMap);
    }

    // Use pre-computed edge data from shape (worker-only architecture)
    if (shape.edges) {
      group.userData['extractedEdges'] = shape.edges;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SELECTION HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Select all CAD shapes
   */
  selectAll(): void {
    this.selectedCADShapeIds.clear();
    this.booleanSelectionOrder = [];
    for (const shape of this.cadShapes) {
      this.selectedCADShapeIds.add(shape.id);
      this.booleanSelectionOrder.push(shape.id);
    }
    this.sceneMeshes.forEach((_, id) => {
      this.updateShapeVisualState(id, true);
    });
    // Don't attach gizmo when multiple selected
    this.detachTransformControls();
    this.cdr.detectChanges();
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    // Cancel any active operation when selection is cleared
    if (this.activeOperation) {
      this.exitOperationMode(true);
    }

    this.selectedCADShapeIds.clear();
    this.booleanSelectionOrder = [];
    this.sceneMeshes.forEach((_, id) => {
      this.updateShapeVisualState(id, false);
    });
    this.detachTransformControls();
    this.cleanupThickenDirectionArrow();
    this.cdr.detectChanges();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // COPY/PASTE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Copy selected shapes to clipboard
   */
  copySelected(): void {
    this.clipboard = this.cadShapes.filter((s) => this.selectedCADShapeIds.has(s.id));
    if (this.clipboard.length > 0) {
    }
  }

  /**
   * Paste shapes from clipboard
   */
  async pasteFromClipboard(): Promise<void> {
    if (this.clipboard.length === 0) return;

    try {
      this.isProcessing = true;
      this.processingMessage = 'Pasting shapes...';

      await this.initializeKernel();

      const newShapes: CADShape[] = [];

      for (const originalShape of this.clipboard) {
        // Clone the shape using kernel
        const clonedShape = await this.cadKernel.cloneShape(originalShape);

        if (clonedShape) {
          this.cadShapes.push(clonedShape);
          this.addShapeToScene(clonedShape);

          // Offset the cloned shape in scene (after adding to scene)
          const originalGroup = this.sceneMeshes.get(originalShape.id);
          const newGroup = this.sceneMeshes.get(clonedShape.id);

          if (originalGroup && newGroup) {
            // Copy original position and add offset
            newGroup.position.copy(originalGroup.position);
            newGroup.position.x += 20;
            newGroup.position.y += 20;
          }

          newShapes.push(clonedShape);
        }
      }

      // Select the new shapes
      this.selectedCADShapeIds.clear();
      newShapes.forEach((s) => this.selectedCADShapeIds.add(s.id));
      this.sceneMeshes.forEach((_, id) => {
        this.updateShapeVisualState(id, this.selectedCADShapeIds.has(id));
      });

      this.processingMessage = '';
      this.hasUnsavedChanges = true;
    } catch (error: any) {
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Duplicate selected shapes (copy + paste in one action)
   */
  async duplicateSelected(): Promise<void> {
    this.copySelected();
    await this.pasteFromClipboard();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIRMATION DIALOG (using ModalService)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Show confirmation dialog using the app's ModalService
   * Prevents multiple dialogs from stacking
   */
  showConfirm(title: string, message: string, onConfirm: () => void): void {
    // Prevent opening multiple confirm dialogs
    if (this.isConfirmDialogOpen) return;

    this.isConfirmDialogOpen = true;

    const modalRef = this.modalService.openComponent(CADConfirmModalComponent, {
      data: { title, message },
      size: 'sm',
      hasBackdrop: true
    });

    modalRef.afterClosed().subscribe((result: string) => {
      this.isConfirmDialogOpen = false;
      if (result === 'confirm') {
        onConfirm();
      }
    });
  }
}
